/**
 * UI overlays — title screen, game-over screen, sign-in panel.
 *
 * Built as plain DOM in #ui-root, not on the canvas. Easier to style, easier
 * to handle clicks/inputs, and keeps the canvas pure-vector.
 */

import type { DeathmatchRules, GameState } from './types.js';
import { ARCADE_ORIGIN, isArcadeHandoffSession, isArcadeSignerSession } from './arcade-handoff.js';
import { SoloScoreOutbox } from './solo-score-outbox.js';
import { WAVE_LORE, gameOverArcLine } from './types.js';
import { getActiveRelays, getKnownRelays, isRelayEnabled, isDefaultRelay, setRelayEnabled, addRelay, removeRelay, resetRelays } from './relays.js';
import { getTouchMode, setTouchMode, type TouchInputMode } from './touch.js';
import { getPadFlightMode, setPadFlightMode, getPadAutoThrust, setPadAutoThrust, getPadFlightModeForSlot, setPadFlightModeForSlot, clearPadFlightModeBySlot, setBoothPadSlotBinding, clearBoothPadSlotBinding, driveMenuFromPad, padButtonDown, getPadBindings, setPadBinding, resetPadBindings, setPadInputSuppressed, PAD_ACTIONS, PAD_BUTTON_NAMES, type PadFlightMode, type PadAction, type MenuNavState } from './gamepads.js';
import { getDisplayMode, setDisplayMode, type DisplayMode } from './display.js';
import {
  VISUAL_CATEGORIES,
  getVisualStyleRaw,
  setVisualStyle,
  setAllVisualStyles,
  visualStyleIsUniform,
  hasStoredVisualStyle,
  setForcedVisualTier,
  getTheme,
  setTheme,
  getAsciiCols,
  setAsciiCols,
  getBitDepth,
  setBitDepth,
  getBitColour,
  setBitColour,
  BRIGHTNESS,
  getQualityPreference, setQualityPreference,
  getBrightness,
  setBrightness,
  mobileRuntimeActive,
  type VisualCategory,
  type VisualTier,
} from './visual-style.js';
import { THEMES, applyPostFx, ASCII_COLS, BIT_DEPTH, type ThemeId } from './postfx/index.js';
import {
  getReducedMotionPref, setReducedMotionPref, type ReducedMotionPref,
  getPalette, setPalette, type ColourPalette,
} from './a11y.js';
import { getHapticsEnabled, setHapticsEnabled, hapticsSupported } from './haptics.js';
import * as auth from './auth.js';
import type { SignetSession } from 'signet-login';
import { addLocalHighScore, getLocalHighScores, isHighScore, subscribeGlobalHighScores, clearLocalHighScores, publishCoopCampaignScore, type GlobalHighScore, type ScoreBoardId } from './score.js';
import { submitCoopScore, submitSoloScore, fetchFlagged, requestDeleteFlag, fetchAdminState, setAdminCaps, setAdminPause, applyAdminPreset, saveAdminSettings, fetchAdminPlayer, setAdminPlayerFlag, adjustAdminPlayerBalance, setAdminPlayerTier, isAdminSession, type FlaggedEntry, type AdminStateResult, type AdminPlayer } from './faucet.js';
import {
  fetchReviewCases,
  generateJuryIdentity,
  getStoredJuryIdentity,
  setStoredJuryIdentity,
  clearStoredJuryIdentity,
  publishDelegation,
  submitVote,
  hasVotedOnCase,
  type ReviewCase,
  type StoredJuryIdentity,
  type VoteSubmitResult,
} from './jury.js';
import { renderLegalFooter } from './legal.js';
import { shouldAskV4v, markV4vPaid, markV4vDeclined, v4vNudge, V4V_LIGHTNING_ADDRESS, V4V_GEYSER_URL, V4V_KOFI_URL, V4V_GIFT_LINE } from './v4v.js';
import { startGame, startDeathReplay, clearEntitiesForTitle, toastNow, getSanctumStats } from './game.js';
import * as audio from './audio.js';
import { signalRelicFaces } from './sanctum-art.js';
import { listTracks, currentTrackId, musicPreviewPlay, musicForceRefresh, musicStop, musicWarmUpAll, musicResetElements, getMusicDebugSnapshot, listAlbums, getActiveAlbumId, setActiveAlbum, musicSetFreeplay } from './music.js';
import { getMusicAnalyser } from './audio.js';
import { fetchProfile, getCachedProfile, bestName, type NostrProfile } from './profile.js';
import { type Difficulty, getStoredDifficulty, setStoredDifficulty, lockInDifficulty } from './difficulty.js';
import { getStoredDailyPref, setStoredDailyPref, todayUTC, getActiveSeed } from './seed.js';
import { getStoredMode, setStoredMode, lockInMode, currentMode, MODE_LIST, isCoopCampaignMode, type RunMode } from './mode.js';
import { DEV } from './credits.js';
import {
  isStandalone, isIosSafari, isMobileViewport, canInstallNow,
  triggerInstall, tryEnterFullscreen, hasFullscreenAPI,
  isInstallHintDismissed, dismissInstallHint,
} from './install-fullscreen.js';
import { type ParallaxTier, getParallaxTier, setParallaxTier } from './parallax.js';
import { type BounceMode, getBounceMode, setBounceMode } from './bounce.js';
import { getRadarVisible, setRadarVisible, getRadarLandscape, setRadarLandscape, getRadarTilt, setRadarTilt, type RadarTilt } from './radar.js';
import { type KnockbackMode, getKnockbackMode, setKnockbackMode } from './knockback.js';
import {
  DEATHMATCH_DEFAULT_TIME_LIMIT_MS,
  defaultDeathmatchKillLimit,
  defaultDeathmatchRespawns,
  makeDeathmatchRules,
} from './deathmatch.js';
import { followUser, shareCompletion, endorseSubject, rankFromWave } from './social.js';
import { shareRunCard } from './sharecard.js';
import { requestZapInvoice, requestZapTo, devLnurl, hasWebLN, payViaWebLN, type ZapRecipient } from './zap.js';
import { subscribeRecentRuns, timeAgo, dismissWatchEntry, getDismissedWatchEntries, LIVE_FRESHNESS_MS, type WatchEntry } from './watch.js';
import { decodeNpub, encodeNpub, encodeNsec } from './bech32.js';
import { subscribeZapTotals, type ZapTotalsByPubkey } from './zaps.js';
import { isGuestSession, setGuestName, clearGuestIdentity, getGuestPrivkeyHex, getGuestRecord, loadOrCreateGuest } from './guest.js';
import { getReplayBuffer, type ReplayFrameRaw } from './stream-session.js';
import { publishGhost, prefetchTopGhost, getCachedGhost, fetchGhostByScoreEventId, findScoreIdForLatestGhost, ghostPoseAt, ghostScoreAt, publishReplay, gzipReplayFrames, findReplayByAuthor, fetchReplayByScoreEventId, fetchReplayByEventId, type GhostRun } from './ghost.js';
import { publishPallasiteMark } from './mark.js';
import { preloadBackground, render, setRenderMode, setHudHidden, PLAYER_COLOURS, type RenderModeInfo } from './render.js';
import { createTheatreState, populateTheatreState } from './theatre-adapter.js';
import { musicSetTrackForState } from './music.js';
import { savePersonalGhost } from './personal-ghost.js';
import { canCaptureClip, captureClip, shareClip, shareDailyStats } from './clip.js';
import { REPLAY_TOTAL_WALL_MS, REPLAY_EXPLOSION_WALL_MS } from './types.js';
import { getStreak, getBestStreak, markDailyCompleted, buildDailyShareText } from './streak.js';
import { markAchievement } from './achievements.js';
import { WORLD_W as PALL_WORLD_W, WORLD_H as PALL_WORLD_H } from './types.js';
import {
  SKINS, getActiveSkinId, setActiveSkinId, isSkinUnlocked,
  publishSkinUnlocks, syncSkinUnlocksFromNostr,
  type SkinDef,
} from './skins.js';
import {
  asteroidPreview, minePreview, sniperPreview, powerupPreview,
  dustPreview, satCoinPreview,
} from './previews.js';
import QRCode from 'qrcode';
import { getFlavour } from './flavour.js';
import { CONTROLLER_WS_ENDPOINT_DEFAULT } from './controller-types.js';

const root = document.getElementById('ui-root')!;

let onStartCb: (() => void) | null = null;
let onResumeCb: (() => void) | null = null;
let onStartCouchCb: (() => void) | null = null;

export function bindActions(opts: {
  onStart: () => void;
  onResume: () => void;
  onStartCouch?: () => void;
}): void {
  onStartCb = opts.onStart;
  onResumeCb = opts.onResume;
  onStartCouchCb = opts.onStartCouch ?? null;
}

/** Start a two-player couch run in place (no reload), enabling couch input
 *  routing at runtime. Used by the booth dual-login so both signed-in
 *  identities survive. Falls back to the normal start if unbound. */
function startCouchInPlace(): void {
  (onStartCouchCb ?? onStartCb)?.();
}

export function renderSignerRecovery(
  state: GameState,
  opts: {
    heading?: string;
    sub?: string;
    onResolved?: (s: SignetSession) => void;
    onClose?: () => void;
  } = {},
): void {
  clearOverlay();
  const overlay = el('div', { className: 'overlay', parent: root });
  setupOverlayArrowNav(overlay);

  el('h2', { parent: overlay, text: opts.heading ?? 'SIGNER NOT LIVE' });
  const sub = el('p', {
    parent: overlay,
    text: opts.sub ?? 'My Signet proved who you are, but Pallasite still needs a signer that can approve game events.',
  });
  sub.style.cssText = 'font-size:0.88rem;color:rgba(220,210,255,0.75);margin:6px auto 18px;max-width:560px;text-align:center;line-height:1.5;';

  const status = el('p', { parent: overlay });
  status.style.cssText = 'font-size:0.82rem;color:rgba(180,140,255,0.86);min-height:1.3em;margin:0 0 12px;text-align:center;letter-spacing:0.04em;';

  const row = el('div', { className: 'menu-row', parent: overlay });
  const buttons: HTMLButtonElement[] = [];
  const makeButton = (label: string, secondary = false): HTMLButtonElement => {
    const btn = el('button', { className: secondary ? 'menu-btn secondary' : 'menu-btn', parent: row, text: label }) as HTMLButtonElement;
    buttons.push(btn);
    return btn;
  };

  const setBusy = (busy: boolean): void => {
    for (const btn of buttons) btn.disabled = busy;
  };
  const finish = (session: SignetSession): void => {
    if (opts.onResolved) opts.onResolved(session);
    else state.session = session;
    (opts.onClose ?? (() => renderTitle(state)))();
  };
  const run = async (method: auth.SignInMethod, label: string): Promise<void> => {
    setBusy(true);
    status.textContent = `Opening ${label}...`;
    status.style.color = 'rgba(180,140,255,0.86)';
    try {
      const session = await auth.signInWith(method);
      if (session) {
        finish(session);
        return;
      }
      status.textContent = 'No live signer attached. Try another method.';
      status.style.color = '#ff8a3a';
    } catch (err) {
      if (auth.isAuthOnlySignerError(err)) {
        status.textContent = 'That Signet approval was still auth-only. Use a live bunker, extension, or Signet QR from another device.';
        status.style.color = '#ffd84a';
      } else {
        status.textContent = err instanceof auth.SignInTimeoutError
          ? `Timeout - ${err.message}`
          : `Sign-in failed: ${err instanceof Error ? err.message : String(err)}`;
        status.style.color = '#ff5050';
      }
    } finally {
      setBusy(false);
    }
  };

  const qrBtn = makeButton('SIGNET QR');
  qrBtn.addEventListener('click', () => { void run('qr', 'Signet QR'); });
  const bunkerBtn = makeButton('PASTE BUNKER', true);
  bunkerBtn.addEventListener('click', () => { void run('bunker', 'bunker'); });
  if (typeof window !== 'undefined' && 'nostr' in window && Boolean((window as { nostr?: unknown }).nostr)) {
    const extBtn = makeButton('EXTENSION', true);
    extBtn.addEventListener('click', () => { void run('nip07', 'extension'); });
  }
  const backBtn = makeButton('BACK', true);
  backBtn.addEventListener('click', () => (opts.onClose ?? (() => renderTitle(state)))());
}

/** Programmatically trigger the bound start callback (the same code path
 *  the IGNITE button takes). Used by duel mode to auto-start both clients
 *  the moment peer-joined fires, so the two sims advance in lockstep
 *  without either player having to hit IGNITE first. No-op if no callback
 *  is bound yet. */
export function simulateStart(): void {
  onStartCb?.();
}

type ConnectingMatchKind = 'duel' | 'coop-campaign' | 'deathmatch';

/** Show a "Connecting to duel" placeholder while peer.connect() is in
 *  flight. The overlay is cleared by simulateStart()'s subsequent
 *  clearOverlay() call (via onStartCb → clearOverlay), so there's no
 *  flicker between this and the game's first render. */
export function renderDuelConnecting(slot: number, players = 2, spectating = false, kind: ConnectingMatchKind = 'duel'): void {
  clearOverlay();
  const overlay = el('div', { className: 'overlay', parent: root });
  setupOverlayArrowNav(overlay);

  const card = el('div', { parent: overlay });
  card.style.cssText = 'display:flex;flex-direction:column;align-items:center;gap:14px;padding:24px 28px;border-radius:10px;border:1px solid rgba(184,144,255,0.35);background:rgba(15,8,32,0.55);max-width:420px;text-align:center;';

  const waitingForHost = !spectating && slot > 0;
  el('h2', { parent: card, text: waitingForHost ? 'WAITING FOR HOST' : 'CONNECTING' }).style.cssText = 'margin:0;font-size:1.45rem;letter-spacing:0.2em;color:var(--hud-yellow);';
  const otherPilots = Math.max(1, players - 1);
  const waitingText = spectating
    ? `Waiting for ${players > 2 ? `${players} pilots` : 'the pilots'} to join the arena.`
    : waitingForHost
      ? kind === 'coop-campaign'
        ? 'Co-pilot connected. Waiting for the host to start the co-op run.'
        : 'Pilot connected. Waiting for the host to start the game.'
    : kind === 'coop-campaign'
      ? 'Waiting for your co-pilot to join the campaign.'
    : players > 2
      ? `Waiting for ${otherPilots} other pilot${otherPilots === 1 ? '' : 's'} to join the arena.`
      : 'Waiting for opponent to join the arena.';
  el('p', { parent: card, text: waitingText })
    .style.cssText = 'margin:0;font-size:0.9rem;color:rgba(220,210,255,0.8);line-height:1.5;';

  const slotChip = el('p', { parent: card, text: spectating ? `Watching ${players} pilot${players === 1 ? '' : 's'}.` : `You are P${slot + 1}.` });
  slotChip.style.cssText = 'margin:0;font-family:monospace;font-size:0.78rem;color:rgba(180,140,255,0.65);letter-spacing:0.12em;';

  // Animated dots so the screen never looks frozen. Pure CSS-free fallback
  // (the gameover overlay already uses inline-styled animations elsewhere).
  const dots = el('div', { parent: card, text: '·  ·  ·' });
  dots.style.cssText = 'margin:0;font-size:1.6rem;letter-spacing:0.5em;color:rgba(184,144,255,0.7);animation:peer-stall-fade 1.2s ease-in-out infinite alternate;';

  const cancel = el('button', { className: 'menu-btn secondary', parent: card, text: 'CANCEL · BACK TO LOBBY' });
  cancel.addEventListener('click', () => { window.location.assign('/duel'); });
}

export function clearOverlay(): void {
  root.innerHTML = '';
}

export function renderGamepadTestPage(onBack?: () => void): void {
  clearOverlay();
  const overlay = el('div', { className: 'overlay', parent: root });
  // Opened from Settings (booth operator flow): make it pad/keyboard navigable so
  // Ⓑ / Escape backs out. Standalone (/?gamepad-test) keeps the reload-to-/ back.
  if (onBack) setupOverlayArrowNav(overlay, onBack);

  el('h2', { parent: overlay, text: 'CONTROLLER TEST' });
  const status = el('p', { parent: overlay, text: 'Gamepad API empty - press A/B/X/Y or nudge a stick while this tab is focused.' });
  status.style.cssText = 'font-size:0.86rem;color:rgba(220,210,255,0.7);letter-spacing:0.05em;max-width:760px;text-align:center;';

  const grid = el('div', { parent: overlay });
  grid.style.cssText = 'display:grid;grid-template-columns:repeat(auto-fit,minmax(240px,1fr));gap:14px;max-width:900px;width:100%;';

  const makeCard = (title: string): HTMLElement => {
    const card = el('div', { parent: grid });
    card.style.cssText = 'border:1px solid rgba(140,120,255,0.25);background:rgba(8,12,26,0.55);border-radius:8px;padding:14px;min-height:120px;';
    const h = el('p', { parent: card, text: title });
    h.style.cssText = 'margin:0 0 10px;color:#ffd84a;font-size:0.8rem;letter-spacing:0.18em;';
    return card;
  };

  const apiCard = makeCard('BROWSER');
  const apiRead = el('pre', { parent: apiCard });
  apiRead.style.cssText = 'margin:0;color:#dcd2ff;font-size:0.82rem;line-height:1.55;white-space:pre-wrap;';

  const keyCard = makeCard('KEY EVENTS');
  const keyRead = el('pre', { parent: keyCard });
  keyRead.style.cssText = 'margin:0;color:#dcd2ff;font-size:0.82rem;line-height:1.55;white-space:pre-wrap;';

  const axesCard = makeCard('STICKS');
  const axesRead = el('pre', { parent: axesCard });
  axesRead.style.cssText = 'margin:0;color:#dcd2ff;font-size:0.82rem;line-height:1.55;white-space:pre-wrap;';

  const triggerCard = makeCard('TRIGGERS');
  const triggerRead = el('pre', { parent: triggerCard });
  triggerRead.style.cssText = 'margin:0;color:#dcd2ff;font-size:0.82rem;line-height:1.55;white-space:pre-wrap;';

  const buttonsCard = makeCard('BUTTONS');
  const buttonsGrid = el('div', { parent: buttonsCard });
  buttonsGrid.style.cssText = 'display:grid;grid-template-columns:repeat(auto-fill,minmax(88px,1fr));gap:6px;';

  const row = el('div', { className: 'menu-row', parent: overlay });
  const back = el('button', { className: 'menu-btn', parent: row, text: onBack ? '< BACK' : '< BACK TO GAME' });
  back.addEventListener('click', () => { if (onBack) onBack(); else window.location.assign('/'); });

  let lastGamepadEvent = 'none';
  let lastKey = 'none';
  let keyDownCount = 0;
  let keyUpCount = 0;
  const onGamepadConnected = (e: GamepadEvent): void => {
    lastGamepadEvent = `connected #${e.gamepad.index} ${e.gamepad.id}`;
  };
  const onGamepadDisconnected = (e: GamepadEvent): void => {
    lastGamepadEvent = `disconnected #${e.gamepad.index} ${e.gamepad.id}`;
  };
  const onKeyDown = (e: KeyboardEvent): void => {
    keyDownCount++;
    lastKey = `down code=${e.code || '-'} key=${e.key || '-'} trusted=${e.isTrusted ? 'yes' : 'no'} repeat=${e.repeat ? 'yes' : 'no'}`;
  };
  const onKeyUp = (e: KeyboardEvent): void => {
    keyUpCount++;
    lastKey = `up   code=${e.code || '-'} key=${e.key || '-'} trusted=${e.isTrusted ? 'yes' : 'no'} repeat=${e.repeat ? 'yes' : 'no'}`;
  };
  window.addEventListener('gamepadconnected', onGamepadConnected);
  window.addEventListener('gamepaddisconnected', onGamepadDisconnected);
  window.addEventListener('keydown', onKeyDown, true);
  window.addEventListener('keyup', onKeyUp, true);
  let listenersRemoved = false;
  const cleanup = (): void => {
    if (listenersRemoved) return;
    listenersRemoved = true;
    window.removeEventListener('gamepadconnected', onGamepadConnected);
    window.removeEventListener('gamepaddisconnected', onGamepadDisconnected);
    window.removeEventListener('keydown', onKeyDown, true);
    window.removeEventListener('keyup', onKeyUp, true);
  };

  const labels: Record<number, string> = {
    0: 'A', 1: 'B', 2: 'X', 3: 'Y', 4: 'LB', 5: 'RB', 6: 'LT', 7: 'RT',
    8: 'View', 9: 'Menu', 10: 'L3', 11: 'R3', 12: 'D-Up', 13: 'D-Down',
    14: 'D-Left', 15: 'D-Right', 16: 'Guide',
  };
  let buttonEls: HTMLElement[] = [];
  const ensureButtons = (n: number): void => {
    if (buttonEls.length === n) return;
    buttonsGrid.innerHTML = '';
    buttonEls = [];
    for (let i = 0; i < n; i++) {
      const b = el('div', { parent: buttonsGrid, text: `${i} ${labels[i] ?? 'B' + i}` });
      b.style.cssText = 'border:1px solid rgba(140,120,255,0.25);border-radius:5px;padding:6px 8px;font-size:0.74rem;color:rgba(220,210,255,0.75);background:rgba(2,5,13,0.5);';
      buttonEls.push(b);
    }
  };
  const fmt = (n: number | undefined): string => (n ?? 0).toFixed(2).padStart(5, ' ');
  const tick = (): void => {
    if (!overlay.isConnected) { cleanup(); return; }
    const hasApi = typeof navigator.getGamepads === 'function';
    const pads = hasApi ? navigator.getGamepads() : [];
    const padsSeen = pads ? Array.from(pads).filter(Boolean).length : 0;
    let pad: Gamepad | null = null;
    for (const p of pads) {
      if (p && p.connected) { pad = p; break; }
    }
    apiRead.textContent = `Gamepad API ${hasApi ? 'available' : 'unavailable'}\nvisible pads ${padsSeen}\npage focus ${document.hasFocus() ? 'yes' : 'no'}\nvisibility ${document.visibilityState}\nevent ${lastGamepadEvent}`;
    keyRead.textContent = `keydowns ${keyDownCount}\nkeyups   ${keyUpCount}\nlast ${lastKey}`;
    if (!pad) {
      status.textContent = hasApi
        ? 'Gamepad API empty - press A/B/X/Y or nudge a stick while this tab is focused.'
        : 'Gamepad API unavailable in this browser.';
      axesRead.textContent = 'left  x  0.00  y  0.00\nright x  0.00  y  0.00';
      triggerRead.textContent = 'LT 0.00\nRT 0.00';
      ensureButtons(0);
      requestAnimationFrame(tick);
      return;
    }
    status.textContent = `Connected: ${pad.id} | mapping: ${pad.mapping || 'non-standard'} | axes: ${pad.axes.length} | buttons: ${pad.buttons.length}`;
    axesRead.textContent = `left  x ${fmt(pad.axes[0])}  y ${fmt(pad.axes[1])}\nright x ${fmt(pad.axes[2])}  y ${fmt(pad.axes[3])}`;
    triggerRead.textContent = `LT ${fmt(pad.buttons[6]?.value)}\nRT ${fmt(pad.buttons[7]?.value)}`;
    ensureButtons(pad.buttons.length);
    for (let i = 0; i < buttonEls.length; i++) {
      const pressed = !!pad.buttons[i] && (pad.buttons[i].pressed || pad.buttons[i].value > 0.5);
      buttonEls[i].style.background = pressed ? '#58ff58' : 'rgba(2,5,13,0.5)';
      buttonEls[i].style.color = pressed ? '#021' : 'rgba(220,210,255,0.75)';
      buttonEls[i].style.borderColor = pressed ? '#58ff58' : 'rgba(140,120,255,0.25)';
    }
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

/**
 * Bind a tap handler that fires reliably on touch devices.
 *
 * `click` alone is unreliable on iOS/Android when the body cascade is
 * `touch-action: none` and a tap happens to be reclassified mid-gesture
 * by the browser. Listening to `pointerup` (filtered to non-mouse) catches
 * those cases. A `firing` flag dedupes the rare device that fires both
 * pointerup and click from a single tap. Mouse devices stay on click so
 * desktop keyboard activation (Enter on focused button) still works.
 */
function onTap(btn: HTMLElement, fn: () => void): void {
  let firing = false;
  const run = (): void => {
    if (firing) return;
    firing = true;
    try { fn(); } finally {
      // Reset on the next microtask so a second deliberate tap after
      // the handler completes still works.
      Promise.resolve().then(() => { firing = false; });
    }
  };
  btn.addEventListener('click', run);
  btn.addEventListener('pointerup', e => {
    if (e.pointerType !== 'mouse') run();
  });
}

/**
 * Bind a long-press handler to the title logo. Used by the secret music-
 * player easter egg. Hold for 700ms with minimal drift to fire; pointer
 * release, leave, or significant motion cancels.
 */
function bindLogoLongPress(target: HTMLElement, fn: () => void): void {
  const HOLD_MS = 700;
  const MOVE_TOL = 8;
  let timer: number | null = null;
  let sx = 0, sy = 0;
  const clear = (): void => { if (timer !== null) { clearTimeout(timer); timer = null; } };
  target.addEventListener('pointerdown', e => {
    // Pre-warm audio on the gesture that initiates the long-press. The
    // setTimeout fn() runs without a gesture context, so unlocking later
    // (in the row-tap handler) is unreliable on iOS. By the time the
    // player opens 700ms after this, the AudioContext is already running.
    void audio.unlockAudio();
    sx = e.clientX; sy = e.clientY;
    clear();
    timer = window.setTimeout(() => { timer = null; fn(); }, HOLD_MS);
  });
  target.addEventListener('pointermove', e => {
    if (timer === null) return;
    if (Math.hypot(e.clientX - sx, e.clientY - sy) > MOVE_TOL) clear();
  });
  target.addEventListener('pointerup',     clear);
  target.addEventListener('pointercancel', clear);
  target.addEventListener('pointerleave',  clear);
}

// ── First-run onboarding ─────────────────────────────────────────────────────

const ONBOARDING_KEY = 'pallasite:onboarded';

function hasCompletedOnboarding(): boolean {
  // Fail-safe to "true" -- if storage is blocked the player should not get
  // stuck behind a tutorial on every IGNITE.
  try { return localStorage.getItem(ONBOARDING_KEY) === '1'; } catch { return true; }
}

function markOnboardingComplete(): void {
  try { localStorage.setItem(ONBOARDING_KEY, '1'); } catch { /* ignore */ }
}

interface OnboardingCard {
  step: string;
  title: string;
  body: string;
}

type InputKind = 'gamepad' | 'touch' | 'keyboard';

/** Which control hints to show. A connected/awake pad wins (booth kiosks drive
 *  the menu with it, so it is always awake by the time onboarding shows); then
 *  a coarse pointer means touch; otherwise keyboard. */
function detectInputKind(): InputKind {
  try {
    const pads = typeof navigator !== 'undefined' && navigator.getGamepads ? navigator.getGamepads() : [];
    for (const p of pads) if (p && p.connected) return 'gamepad';
  } catch { /* ignore */ }
  const coarse = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
  if (coarse || (typeof window !== 'undefined' && 'ontouchstart' in window)) return 'touch';
  return 'keyboard';
}

/** Per-input control copy for the first two onboarding cards. Each teaches
 *  flying (DRIFT) then firing + the two specials (FIRE) in that input's terms. */
const CONTROL_COPY: Record<InputKind, { drift: string; fire: string }> = {
  gamepad: {
    drift: 'Push the left stick to fly — the ship points where you push and drifts on. Squeeze the right trigger for an extra burst. There is no friction here; you keep moving until you turn.',
    fire: 'Left trigger or A fires — or push the right stick to aim and auto-fire. Right bumper raises a shield; left bumper jumps to hyperspace when you are cornered. Quick consecutive kills chain a multiplier up to 5×. Hunt the gold rocks for the sats counter.',
  },
  touch: {
    drift: 'Drag the stick to fly — the ship points where you push, then drifts on. There is no friction here; you keep moving until you turn.',
    fire: 'Tap FIRE to shoot. ⛨ raises a shield; ⚡ jumps to hyperspace when you are cornered. Quick consecutive kills chain a multiplier up to 5×. Hunt the gold rocks for the sats counter.',
  },
  keyboard: {
    drift: 'Rotate ◀ ▶ to face. Thrust ▲ to drift. There is no friction here; mass keeps moving until you point the other way.',
    fire: 'Space to fire. ▼ raises a shield; Shift jumps to hyperspace when you are cornered. Quick consecutive kills chain a multiplier up to 5×. Hunt the gold rocks for the sats counter.',
  },
};

/** Build the three onboarding cards with control hints for the live input. */
function buildOnboardingCards(): OnboardingCard[] {
  const kind = detectInputKind();
  const c = CONTROL_COPY[kind];
  // The default gamepad copy describes pickup-and-go (Point & Fly); swap in the
  // wording for whichever scheme is actually selected so the card matches the pad.
  let drift = c.drift;
  if (kind === 'gamepad') {
    const m = getPadFlightMode();
    if (m === 'throttle') drift = 'Point the left stick to aim — the ship turns to face it. Squeeze the right trigger to thrust that way. There is no friction here; you drift on until you turn.';
    else if (m === 'classic') drift = 'Tap the d-pad left or right to rotate, and squeeze the right trigger (or d-pad up) to thrust. There is no friction here; you drift on until you turn.';
  }
  return [
    { step: '1 / 3', title: 'DRIFT', body: drift },
    { step: '2 / 3', title: 'FIRE', body: c.fire },
    {
      step: '3 / 3',
      title: 'BANK',
      body: 'Survive each wave to bank what you earned. Twenty-five waves to the horizon. Sign in with Nostr to stake your name on the leaderboard.',
    },
  ];
}

/**
 * First-run cinematic. Three brand-voice cards then a BEGIN button that
 * fires the actual game start. Skippable. Triggered once after the first
 * IGNITE click; localStorage flag suppresses it on return.
 *
 * Marks `data-onboarding="open"` on the overlay so the global Enter-to-start
 * handler in main.ts can refuse to fire while the cinematic is up. Without
 * the gate, pressing Enter would bypass the cinematic and start the game on
 * an un-watched intro -- defeats the whole point.
 */
function renderOnboarding(onBegin: () => void): void {
  clearOverlay();
  const overlay = el('div', { className: 'overlay', parent: root, attrs: { 'data-onboarding': 'open' } });
  setupOverlayArrowNav(overlay);

  const cards = buildOnboardingCards();
  let idx = 0;

  const stepEl = el('p', { parent: overlay });
  stepEl.style.cssText = 'font-size:0.78rem;letter-spacing:0.32em;color:rgba(180,140,255,0.85);margin:0;';

  const titleEl = el('h2', { parent: overlay });
  titleEl.style.cssText = 'font-size:2.4rem;letter-spacing:0.18em;color:var(--hud-yellow);text-shadow:0 0 12px rgba(255,216,74,0.5);margin:8px 0 16px;';

  const bodyEl = el('p', { parent: overlay });
  bodyEl.style.cssText = 'max-width:540px;font-size:1rem;line-height:1.6;color:rgba(220,210,255,0.92);margin:0 0 8px;text-align:center;';

  const row = el('div', { className: 'menu-row', parent: overlay });
  const nextBtn = el('button', { className: 'menu-btn', parent: row, text: 'NEXT' });

  const skipRow = el('div', { parent: overlay });
  skipRow.style.cssText = 'margin-top:18px;';
  const skipLink = el('button', { parent: skipRow, text: 'Skip intro' });
  skipLink.style.cssText = 'background:transparent;border:none;color:rgba(180,140,255,0.65);text-decoration:underline;cursor:pointer;font-size:0.9rem;letter-spacing:0.06em;padding:4px 8px;';

  const finish = (): void => {
    markOnboardingComplete();
    clearOverlay();
    onBegin();
  };

  const renderCard = (): void => {
    const card = cards[idx];
    stepEl.textContent = `STEP ${card.step}`;
    titleEl.textContent = card.title;
    bodyEl.textContent = card.body;
    const isLast = idx === cards.length - 1;
    nextBtn.textContent = isLast ? 'BEGIN · IGNITE' : 'NEXT ▶';
  };

  nextBtn.addEventListener('click', () => {
    if (idx === cards.length - 1) {
      finish();
    } else {
      idx += 1;
      renderCard();
      nextBtn.focus();
    }
  });

  skipLink.addEventListener('click', finish);

  renderCard();
}

/**
 * Gate any IGNITE-style entry path behind the first-run cinematic.
 *
 * If the player has already completed onboarding, calls `onReady` straight
 * away. Otherwise shows the cinematic and calls `onReady` after they
 * finish or skip. Keeps both the IGNITE-button path and the global
 * Enter-to-start path consistent so first-timers can't skip the intro just
 * by pressing Enter.
 */
export function gateBehindOnboarding(onReady: () => void): void {
  if (hasCompletedOnboarding()) {
    onReady();
  } else {
    renderOnboarding(onReady);
  }
}

// ── Value for value ──────────────────────────────────────────────────────────

/**
 * Pre-run gate for normal solo starts from the title menu: onboarding
 * first (first-timers only), then the V4V tip ask — before EVERY run.
 * Kiosk/booth walk-ups (event lobby, ?p1/?p2 wizard) and the duel/lobby
 * routes stay on plain gateBehindOnboarding — a tip screen has no place
 * in a walk-up flow, and those paths never show it.
 * Retry/restart buttons call onStartCb directly and bypass this.
 */
export function gateBehindPreRun(onReady: () => void): void {
  gateBehindOnboarding(() => {
    if (shouldAskV4v()) renderV4v(onReady);
    else onReady();
  });
}

/** The ask: Lightning-first (QR + address), Geyser/Ko-Fi fallbacks, the
 *  gift line printed on the overlay, I PAID (thanks only; unlocks nothing) and
 *  NEXT TIME (counts the decline, starts the run anyway). */
function renderV4v(onStart: () => void): void {
  clearOverlay();
  const overlay = el('div', { className: 'overlay', parent: root });
  setupOverlayArrowNav(overlay);

  const kicker = el('p', { parent: overlay, text: 'VALUE FOR VALUE' });
  kicker.style.cssText = 'font-size:0.78rem;letter-spacing:0.32em;color:rgba(180,140,255,0.95);margin:0;';
  el('h2', { parent: overlay, text: 'FUEL THE SHIP' });

  const nudge = el('p', { parent: overlay, text: v4vNudge() });
  nudge.style.cssText = 'font-size:0.92rem;color:rgba(220,210,255,0.85);letter-spacing:0.06em;max-width:480px;text-align:center;line-height:1.5;margin:0;';

  // Lightning QR — same white-box treatment as the booth donate screens.
  const box = el('div', { parent: overlay });
  box.style.cssText = 'width:168px;height:168px;background:#fff;padding:8px;border-radius:10px;box-sizing:content-box;';
  box.setAttribute('aria-label', 'Lightning address QR code');
  try { void renderQRInto(box, `lightning:${V4V_LIGHTNING_ADDRESS}`); } catch { /* ignore */ }

  const bless = el('p', { parent: overlay, text: V4V_GIFT_LINE });
  bless.style.cssText = 'font-size:0.82rem;letter-spacing:0.14em;color:#ffd84a;text-shadow:0 0 8px rgba(255,216,74,0.5);text-align:center;margin:0;';

  // Address button — copies AND offers the wallet deep link in one gesture.
  const addr = el('button', { parent: overlay, text: V4V_LIGHTNING_ADDRESS });
  addr.style.cssText = 'background:rgba(15,8,32,0.7);border:1px solid rgba(255,216,74,0.45);border-radius:6px;color:#ffd84a;font:0.85rem/1.4 ui-monospace,monospace;letter-spacing:0.06em;padding:8px 14px;cursor:pointer;';
  onTap(addr, () => {
    void navigator.clipboard?.writeText(V4V_LIGHTNING_ADDRESS).then(() => {
      addr.textContent = 'COPIED!';
      setTimeout(() => { addr.textContent = V4V_LIGHTNING_ADDRESS; }, 1400);
    }).catch(() => undefined);
    window.location.href = `lightning:${V4V_LIGHTNING_ADDRESS}`;
  });

  const links = el('div', { className: 'menu-row', parent: overlay });
  for (const [label, url] of [['GEYSER', V4V_GEYSER_URL], ['☕ KO-FI', V4V_KOFI_URL]] as const) {
    const a = el('a', { className: 'menu-btn secondary', parent: links, text: label }) as HTMLAnchorElement;
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener';
    a.style.textDecoration = 'none';
  }

  const row = el('div', { className: 'menu-row', parent: overlay });
  const paid = el('button', { className: 'menu-btn', parent: row, text: 'I PAID' });
  onTap(paid, () => { markV4vPaid(); playV4vThanksVoice(); renderV4vThanks(onStart); });
  const later = el('button', { className: 'menu-btn secondary', parent: row, text: 'NEXT TIME' });
  onTap(later, () => { markV4vDeclined(); onStart(); });
  setTimeout(() => tryFocusVisible(paid), 0);
}

/** The donkey says thank you in person — same recorded lines as the ledger. */
const V4V_THANKS_VOICES = ['/sfx/thanks-fren.m4a', '/sfx/thanks-legend.m4a'];

function playV4vThanksVoice(): void {
  if (audio.isMuted()) return;
  try {
    const clip = new Audio(V4V_THANKS_VOICES[Math.floor(Math.random() * V4V_THANKS_VOICES.length)]!);
    clip.volume = 0.9;
    void clip.play().catch(() => undefined);
  } catch { /* a lost clip costs a sound, never the flow */ }
}

/** Thank-you panel after I PAID. A thank-you only; one button to launch. */
function renderV4vThanks(onStart: () => void): void {
  clearOverlay();
  const overlay = el('div', { className: 'overlay', parent: root });
  setupOverlayArrowNav(overlay);

  const kicker = el('p', { parent: overlay, text: 'FROM THECRYPTODONKEY' });
  kicker.style.cssText = 'font-size:0.78rem;letter-spacing:0.32em;color:rgba(180,140,255,0.95);margin:0;';
  el('h2', { parent: overlay, text: 'THANK YOU · SATS APPRECIATED' });

  const meme = el('img', { parent: overlay }) as HTMLImageElement;
  meme.src = '/memes/donkey-thankz.webp';
  meme.alt = 'THANKZ';
  meme.style.cssText = 'width:min(60vw,240px);height:auto;border-radius:12px;display:block;';

  const bless = el('p', { parent: overlay, text: 'Thank you. Everyone flies the same ship.' });
  bless.style.cssText = 'font-size:0.82rem;letter-spacing:0.14em;color:#ffd84a;text-shadow:0 0 8px rgba(255,216,74,0.5);text-align:center;margin:0;';

  const row = el('div', { className: 'menu-row', parent: overlay });
  const start = el('button', { className: 'menu-btn', parent: row, text: 'IGNITE ▶' });
  onTap(start, onStart);
  setTimeout(() => tryFocusVisible(start), 0);
}

/**
 * Wire arrow-key navigation across the focusable buttons in an overlay.
 *
 * Geometric, not DOM-order: ↑ / ↓ moves between visual rows (different
 * top coordinates), ← / → moves between buttons on the SAME row
 * (close top, different left). The mode picker / difficulty picker /
 * daily toggle on the mission-select screen each live on their own
 * row, so ↑ ↓ skips between those rows cleanly and ← → cycles values
 * within whichever row is focused. Buttons that are alone on a row
 * (IGNITE, HOW TO PLAY, SETTINGS) get visited by ↑ ↓ too.
 *
 * Falls back to a linear FIFO cycle if no geometric neighbour exists
 * in the requested direction — keeps simple two-button dialogs
 * (BACK / CONFIRM) navigable without needing explicit row grouping.
 *
 * Also explicitly fires .click() on the focused button for Enter /
 * Space. The browser is supposed to synthesise this from a focused
 * button's default action, but the synthesis varies across mobile
 * browsers and the controller-PWA-over-WS keyboard-event path —
 * being explicit means SELECT (A → Enter) ALWAYS activates the
 * focused button, regardless of browser quirks.
 *
 * Focus is applied with {focusVisible: true} where supported, so the
 * loud :focus-visible CSS treatment kicks in even though the focus
 * arrived programmatically rather than via Tab.
 */
function setupOverlayArrowNav(overlay: HTMLElement, onBack?: () => void): void {
  const handler = (e: KeyboardEvent): void => {
    if (!document.body.contains(overlay)) {
      window.removeEventListener('keydown', handler);
      return;
    }
    // A modal <dialog> (e.g. the Signet login picker) is open on top and owns
    // ALL nav input — bail so this overlay's nav doesn't also process the
    // gamepad's synthetic keys behind it and fight over focus.
    if (document.querySelector('dialog[open]')) return;
    // Escape = Back/Close. The gamepad maps B (and the pause button) to Escape
    // in menu phases, so this is how a pad backs out of a menu — players expect
    // B = Back. Handled BEFORE the input-focus skip so a focused slider (e.g. a
    // settings volume row) doesn't swallow it.
    if (e.code === 'Escape') {
      e.preventDefault();
      if (onBack) { onBack(); return; }
      // Fallback for overlays that didn't pass an explicit back: click a
      // back/close/done/cancel-style button if the overlay has one.
      const back = Array.from(overlay.querySelectorAll<HTMLButtonElement>('button:not([disabled])'))
        .reverse()
        .find((b) => /^(←\s*)?(back|close|done|cancel|exit)\b/i.test((b.textContent ?? '').trim()));
      back?.click();
      return;
    }
    const isArrow = e.code === 'ArrowUp' || e.code === 'ArrowDown'
                 || e.code === 'ArrowLeft' || e.code === 'ArrowRight';
    const isActivate = e.code === 'Enter' || e.code === 'Space';
    if (!isArrow && !isActivate) return;

    // Skip when an input/textarea is focused for real keyboard events — the
    // form field owns typing and cursor movement. Synthetic controller/gamepad
    // nav is different: if focus is stranded in a field, arrows should escape
    // back to overlay buttons instead of making the pad feel dead.
    const active = document.activeElement as HTMLElement | null;
    if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA') && e.isTrusted) return;

    const buttons = [
      ...overlay.querySelectorAll<HTMLButtonElement>('button:not([disabled])'),
      ...document.querySelectorAll<HTMLButtonElement>('#pal-score-recovery button:not([disabled])'),
    ]
      .filter((b) => b.offsetParent !== null);
    if (buttons.length === 0) return;

    if (isActivate) {
      // Defensive activation. Browsers normally synthesise click()
      // when Enter / Space is pressed on a focused button, but the
      // synthesis is unreliable across the controller-PWA-WS path on
      // some Android Chromium builds. Explicit click here means
      // SELECT always works on the focused button.
      const focused = active instanceof HTMLButtonElement && buttons.includes(active) ? active : null;
      if (focused) {
        e.preventDefault();
        focused.click();
      }
      return;
    }

    if (!active || !(active instanceof HTMLButtonElement) || !buttons.includes(active)) {
      // No button focused yet — first arrow keypress focuses the
      // first visible button. Pass focusVisible:true so the loud
      // :focus-visible CSS treatment shows immediately.
      tryFocusVisible(buttons[0]);
      e.preventDefault();
      return;
    }

    // Geometric neighbour search. Match "same row" by close top
    // coordinate (within half the button height — generous for
    // wrap-flex layouts where row-mates can be staggered a couple
    // pixels). Pick the nearest centre-aligned neighbour in the
    // requested direction.
    const cur = active.getBoundingClientRect();
    const curCx = cur.left + cur.width / 2;
    const curCy = cur.top + cur.height / 2;
    const SAME_ROW = Math.max(cur.height / 2, 8);

    interface Cand { btn: HTMLButtonElement; dx: number; dy: number; absDx: number; absDy: number; }
    const cands: Cand[] = [];
    for (const btn of buttons) {
      if (btn === active) continue;
      const r = btn.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      cands.push({ btn, dx: cx - curCx, dy: cy - curCy, absDx: Math.abs(cx - curCx), absDy: Math.abs(cy - curCy) });
    }

    const horizontal = e.code === 'ArrowLeft' || e.code === 'ArrowRight';
    const positive = e.code === 'ArrowDown' || e.code === 'ArrowRight';

    let pick: Cand | null = null;
    if (horizontal) {
      // Prefer same-row neighbours in the requested left/right
      // direction. Pick the one with smallest horizontal distance.
      const sameRow = cands.filter((c) => c.absDy <= SAME_ROW && (positive ? c.dx > 0 : c.dx < 0));
      sameRow.sort((a, b) => a.absDx - b.absDx);
      pick = sameRow[0] ?? null;
    } else {
      // Vertical: pick a different-row neighbour in the requested
      // up/down direction. Weight horizontal distance into the
      // sort so the visually-nearest column wins ties.
      const otherRow = cands.filter((c) => positive ? c.dy > SAME_ROW : c.dy < -SAME_ROW);
      otherRow.sort((a, b) => (a.absDy + a.absDx * 0.25) - (b.absDy + b.absDx * 0.25));
      pick = otherRow[0] ?? null;
    }

    // Fallback: no geometric neighbour exists in that direction.
    // Wrap to the opposite end so a small dialog with two buttons in
    // a row still cycles cleanly when the user presses ← past the
    // first button. This also covers single-column overlays where
    // ↑ at the top wraps to the bottom.
    if (!pick) {
      const all = cands.slice();
      if (horizontal) {
        all.sort((a, b) => a.dx - b.dx);
        pick = positive ? all[0] : all[all.length - 1];
      } else {
        all.sort((a, b) => a.dy - b.dy);
        pick = positive ? all[0] : all[all.length - 1];
      }
    }

    if (pick) {
      tryFocusVisible(pick.btn);
      e.preventDefault();
    }
  };
  window.addEventListener('keydown', handler);
}

/** Programmatically focus a button so the :focus-visible CSS treatment
 *  fires. Chrome/Edge support {focusVisible: true}; Safari/Firefox fall
 *  through to plain .focus() and the heuristic decides — usually they
 *  treat programmatic focus during a keydown handler as "keyboard-
 *  caused" and apply :focus-visible anyway. */
function tryFocusVisible(btn: HTMLButtonElement): void {
  try {
    (btn as HTMLButtonElement & { focus(opts?: { focusVisible?: boolean }): void }).focus({ focusVisible: true });
  } catch {
    btn.focus();
  }
}


/**
 * Variable-length name picker. Same arcade ▲▼ + ←→ + BKSP grammar as
 * renderArcadeInitials but designed for up to 25 characters by showing
 * a single big editor slot for the currently-active position above a
 * preview line of the full name. The fixed-slot picker was right for
 * 4-char high-score initials; this surface is right for guest-mode
 * display names where players want their handle ("MORG OF ALDEBARAN")
 * rather than initials.
 *
 * The controls are *all* native <button> elements so the controller
 * PWA in d-pad mode (which emits ArrowUp/Down/Left/Right via the
 * gamepad bridge) can navigate through them via setupOverlayArrowNav,
 * land on ▲ / ▼ / ◀ / ▶ / BKSP / DONE, and activate with Enter
 * (the PWA's A button). Keyboard users get the same buttons via
 * arrows/Tab, plus typing a letter / digit directly types into the
 * active slot and auto-advances the cursor.
 *
 * Returns a `getCurrentName()` accessor so callers (the IGNITE
 * fallback path) can read the in-progress value without waiting for
 * onSubmit.
 */
function renderArcadeName(
  parent: HTMLElement,
  opts: {
    onSubmit?: (name: string) => void;
    initialValue?: string;
    maxLen?: number;
  },
): { getCurrentName: () => string } {
  const MAX_LEN = opts.maxLen ?? 25;
  // A-Z + 0-9 + space — the classic arcade charset the user picked.
  // Order: A first so a freshly empty slot starts at A on first ▲.
  // Space last so a cursor that "overflowed" reads as blank.
  const CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789 ';
  const SPACE_IDX = CHARS.length - 1;

  // Initial value: pad to MAX_LEN with spaces. Cursor lands on the
  // first space so typing a fresh name doesn't require manual cursor
  // navigation. If a returning guest is renaming, cursor lands at
  // end-of-name + 1 (or last slot if full).
  const initial = (opts.initialValue ?? '').toUpperCase().slice(0, MAX_LEN);
  const slots: number[] = [];
  for (let i = 0; i < MAX_LEN; i++) {
    const ch = i < initial.length ? initial[i] : ' ';
    const idx = CHARS.indexOf(ch);
    slots.push(idx === -1 ? SPACE_IDX : idx);
  }
  let cursor = Math.min(initial.length, MAX_LEN - 1);
  let submitted = false;

  const wrap = el('div', { parent });
  wrap.style.cssText = 'display:flex;flex-direction:column;align-items:center;gap:6px;margin:4px 0;width:100%;';
  // Same opt-out marker the global Enter-restarts-game handler in
  // main.ts already checks for — keeps Enter from re-triggering the
  // game while the player is mid-name.
  wrap.dataset.arcadeInitials = 'open';

  // Preview line — the whole 25-char name with the cursor position
  // visually highlighted as an underline. Reads at a glance ("you've
  // typed MORG, cursor is on slot 5") without needing to look at the
  // big editor below.
  const preview = el('div', { parent: wrap });
  preview.style.cssText = 'display:flex;flex-wrap:wrap;justify-content:center;gap:2px;font-family:monospace;font-size:1rem;letter-spacing:0.04em;padding:6px 8px;background:rgba(2,5,13,0.4);border:1px solid rgba(140,255,180,0.2);border-radius:4px;min-height:1.6em;max-width:480px;width:100%;';
  const previewChars: HTMLSpanElement[] = [];
  for (let i = 0; i < MAX_LEN; i++) {
    const span = el('span', { parent: preview, text: '_' });
    span.style.cssText = 'display:inline-block;min-width:0.65em;text-align:center;color:rgba(220,210,255,0.55);border-bottom:1px solid transparent;transition:color 80ms, border-color 80ms;';
    previewChars.push(span);
  }

  // Big editor — current character, ▲ above, ▼ below, ◀ ▶ left/right.
  // Sized so it's tap-friendly on mobile and reads at TV-distance for
  // controller PWA users.
  const editor = el('div', { parent: wrap });
  editor.style.cssText = 'display:grid;grid-template-columns:auto auto auto;grid-template-rows:auto auto auto;gap:6px;align-items:center;justify-items:center;margin:4px 0;';
  // Row 1: empty, ▲, empty
  const upBtn = el('button', { parent: editor, text: '▲' }) as HTMLButtonElement;
  upBtn.type = 'button';
  upBtn.style.cssText = 'grid-column:2;grid-row:1;width:56px;height:40px;background:rgba(88,255,88,0.12);border:2px solid rgba(88,255,88,0.4);color:#58ff58;font-size:1.1rem;cursor:pointer;-webkit-tap-highlight-color:transparent;touch-action:manipulation;user-select:none;';
  // Row 2: ◀, big slot, ▶
  const leftBtn = el('button', { parent: editor, text: '◀' }) as HTMLButtonElement;
  leftBtn.type = 'button';
  leftBtn.style.cssText = 'grid-column:1;grid-row:2;width:48px;height:54px;background:rgba(140,140,255,0.10);border:2px solid rgba(140,140,255,0.35);color:#9b9bff;font-size:1.1rem;cursor:pointer;-webkit-tap-highlight-color:transparent;touch-action:manipulation;user-select:none;';
  const slotBox = el('div', { parent: editor }) as HTMLDivElement;
  slotBox.style.cssText = 'grid-column:2;grid-row:2;width:64px;height:64px;display:flex;align-items:center;justify-content:center;background:rgba(2,5,13,0.6);border:2px solid #ffd84a;color:#ffd84a;font-family:inherit;font-size:2rem;font-weight:bold;letter-spacing:0;text-shadow:0 0 10px rgba(255,216,74,0.5);';
  const rightBtn = el('button', { parent: editor, text: '▶' }) as HTMLButtonElement;
  rightBtn.type = 'button';
  rightBtn.style.cssText = 'grid-column:3;grid-row:2;width:48px;height:54px;background:rgba(140,140,255,0.10);border:2px solid rgba(140,140,255,0.35);color:#9b9bff;font-size:1.1rem;cursor:pointer;-webkit-tap-highlight-color:transparent;touch-action:manipulation;user-select:none;';
  // Row 3: empty, ▼, empty
  const downBtn = el('button', { parent: editor, text: '▼' }) as HTMLButtonElement;
  downBtn.type = 'button';
  downBtn.style.cssText = 'grid-column:2;grid-row:3;width:56px;height:40px;background:rgba(88,255,88,0.12);border:2px solid rgba(88,255,88,0.4);color:#58ff58;font-size:1.1rem;cursor:pointer;-webkit-tap-highlight-color:transparent;touch-action:manipulation;user-select:none;';

  // Actions row — backspace + done
  const actions = el('div', { parent: wrap });
  actions.style.cssText = 'display:flex;gap:8px;margin-top:4px;';
  const bkspBtn = el('button', { parent: actions, text: 'BKSP' }) as HTMLButtonElement;
  bkspBtn.type = 'button';
  bkspBtn.style.cssText = 'padding:8px 16px;background:rgba(255,120,120,0.12);border:2px solid rgba(255,120,120,0.4);color:#ff8a8a;font-family:inherit;letter-spacing:0.16em;cursor:pointer;-webkit-tap-highlight-color:transparent;touch-action:manipulation;';
  const doneBtn = el('button', { parent: actions, text: 'DONE' }) as HTMLButtonElement;
  doneBtn.type = 'button';
  doneBtn.style.cssText = 'padding:8px 24px;background:rgba(255,216,74,0.18);border:2px solid #ffd84a;color:#ffd84a;font-family:inherit;letter-spacing:0.16em;cursor:pointer;-webkit-tap-highlight-color:transparent;touch-action:manipulation;';

  const hint = el('p', { parent: wrap, text: '▲▼ CYCLE · ◀▶ CURSOR · BKSP · DONE' });
  hint.style.cssText = 'font-size:0.65rem;color:rgba(180,140,255,0.55);letter-spacing:0.16em;margin:0;';

  const getCurrentName = (): string => {
    let out = '';
    for (let i = 0; i < MAX_LEN; i++) out += CHARS[slots[i]];
    return out.trimEnd();
  };

  const render = (): void => {
    for (let i = 0; i < MAX_LEN; i++) {
      const ch = CHARS[slots[i]];
      previewChars[i].textContent = ch === ' ' ? '_' : ch;
      const isActive = i === cursor;
      previewChars[i].style.color = isActive ? '#ffd84a' : (ch === ' ' ? 'rgba(220,210,255,0.35)' : '#fff5d8');
      previewChars[i].style.borderBottomColor = isActive ? '#ffd84a' : 'transparent';
    }
    const ch = CHARS[slots[cursor]];
    slotBox.textContent = ch === ' ' ? '_' : ch;
  };
  render();

  const cycle = (dir: 1 | -1): void => {
    slots[cursor] = (slots[cursor] + dir + CHARS.length) % CHARS.length;
    render();
    try { audio.initialCycle(); } catch { /* ignore */ }
  };
  const move = (dir: 1 | -1): void => {
    const next = cursor + dir;
    if (next < 0 || next >= MAX_LEN) return;
    cursor = next;
    render();
    try { audio.initialMove(); } catch { /* ignore */ }
  };
  const bksp = (): void => {
    if (slots[cursor] !== SPACE_IDX) {
      slots[cursor] = SPACE_IDX;
    } else if (cursor > 0) {
      cursor -= 1;
      slots[cursor] = SPACE_IDX;
    }
    render();
    try { audio.initialBackspace(); } catch { /* ignore */ }
  };
  const commit = (): void => {
    if (submitted) return;
    submitted = true;
    window.removeEventListener('keydown', keyHandler, true);
    const name = getCurrentName();
    opts.onSubmit?.(name);
  };

  // Bind buttons (pointerdown + click + Enter on focused button).
  // pointerdown is touch-friendly (avoids the iOS 300ms delay), click
  // covers mouse + keyboard activation (Enter / Space on focused
  // button fires synthetic click).
  const bindTap = (btn: HTMLElement, fn: () => void): void => {
    let handledPointer = false;
    btn.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      handledPointer = true;
      fn();
    });
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      if (handledPointer) {
        handledPointer = false;
        return;
      }
      fn();
    });
  };
  bindTap(upBtn,    () => cycle(1));
  bindTap(downBtn,  () => cycle(-1));
  bindTap(leftBtn,  () => move(-1));
  bindTap(rightBtn, () => move(1));
  bindTap(bkspBtn,  () => bksp());
  bindTap(doneBtn,  () => commit());

  // Keyboard fast path — typing a letter / digit replaces the active
  // slot and advances the cursor. Arrow / Enter remains overlay button
  // navigation so OS-mapped gamepads behave like gamepads.
  //
  // Capture phase + stopImmediatePropagation on consumed keys so the
  // global IGNITE-on-Enter handler in main.ts doesn't fire mid-edit.
  const keyHandler = (e: KeyboardEvent): void => {
    if (submitted) return;
    if (!document.body.contains(wrap)) {
      window.removeEventListener('keydown', keyHandler, true);
      return;
    }
    // Shortcut combos (Cmd/Ctrl/Alt + key) are never initials input, so
    // let them through. Otherwise Cmd/Ctrl+V paste into another focused
    // field (e.g. the bunker URI input) is swallowed by this capture
    // handler before the input sees it.
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    const active = document.activeElement as Element | null;
    const activeIsButton = active instanceof HTMLButtonElement;
    const isOverlayNavKey = e.code === 'ArrowUp' || e.code === 'ArrowDown'
      || e.code === 'ArrowLeft' || e.code === 'ArrowRight'
      || e.code === 'Enter' || e.code === 'Space';
    // Gamepads can arrive two ways:
    //   - our Gamepad API bridge dispatches synthetic Arrow / Enter events;
    //   - macOS / Steam / browser accessibility can translate the pad into
    //     trusted keyboard events. In both cases, focused buttons must behave
    //     like menu buttons, not like the name editor's text shortcuts.
    if (isOverlayNavKey && activeIsButton) return;
    if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')) return;
    // Arrow keys belong to overlay navigation. The editor still exposes real
    // ▲▼◀▶ buttons for changing characters/cursor, and direct letter typing
    // below remains the fast path for keyboard users.
    if (e.code === 'ArrowUp' || e.code === 'ArrowDown' || e.code === 'ArrowLeft' || e.code === 'ArrowRight') {
      return;
    }
    let consumed = true;
    switch (e.code) {
      case 'ArrowUp': cycle(1); break;
      case 'ArrowDown': cycle(-1); break;
      case 'ArrowRight': move(1); break;
      case 'ArrowLeft': move(-1); break;
      case 'Backspace': bksp(); break;
      case 'Enter':
        // Enter on DONE submits; Enter elsewhere advances cursor and,
        // if at end, commits. Lets a kb user "type then press Enter"
        // without hunting for the DONE button.
        if (document.activeElement === doneBtn) { commit(); }
        else if (cursor < MAX_LEN - 1) { move(1); }
        else { commit(); }
        break;
      default: {
        // Letter / digit / space — type into the current slot. Match
        // is case-insensitive (we normalise to upper for CHARS).
        const ch = e.key.toUpperCase();
        if (ch.length === 1 && CHARS.indexOf(ch) !== -1) {
          slots[cursor] = CHARS.indexOf(ch);
          render();
          if (cursor < MAX_LEN - 1) cursor += 1;
          render();
        } else {
          consumed = false;
        }
        break;
      }
    }
    if (consumed) {
      e.preventDefault();
      e.stopImmediatePropagation();
    }
  };
  window.addEventListener('keydown', keyHandler, true);

  return { getCurrentName };
}

/**
 * Floating banner that appears when the service worker has a new version
 * waiting. Tap RELOAD → posts SKIP_WAITING to the worker → controllerchange
 * triggers a clean reload into the new build.
 */
export function showUpdateBanner(onReload: () => void, labels?: { message: string; action: string; pending: string }): void {
  if (document.getElementById('pal-update-banner')) return;
  const banner = document.createElement('div');
  banner.id = 'pal-update-banner';
  // z-index 9999 so the banner stacks above any overlay (.overlay has no
  // explicit z-index and creates a stacking context via inset:0). The body
  // sets touch-action:none which can prevent click-ready taps on iOS for
  // descendants without their own touch-action — declare manipulation on
  // the banner so the button below resolves taps without delay.
  banner.style.cssText = [
    'position:fixed',
    'top:max(12px, env(safe-area-inset-top, 0px))',
    'left:50%', 'transform:translateX(-50%)',
    'z-index:9999',
    'background:rgba(10,4,24,0.95)',
    'border:2px solid #ffd84a', 'border-radius:10px',
    'padding:12px 16px',
    "font-family:'VT323',ui-monospace,monospace",
    'font-size:1rem', 'letter-spacing:0.12em',
    'color:#ffd84a',
    'text-shadow:0 0 8px rgba(255,216,74,0.6)',
    'box-shadow:0 0 20px rgba(255,216,74,0.35)',
    'display:flex', 'gap:14px', 'align-items:center',
    'pointer-events:auto',
    'touch-action:manipulation',
    '-webkit-tap-highlight-color:rgba(255,216,74,0.18)',
  ].join(';');
  banner.innerHTML = `<span>NEW VERSION READY</span><button type="button" style="font-family:inherit;font-size:0.95rem;letter-spacing:0.18em;padding:10px 16px;min-height:44px;background:rgba(255,216,74,0.15);border:1px solid #ffd84a;color:#ffd84a;border-radius:6px;cursor:pointer;touch-action:manipulation;-webkit-tap-highlight-color:rgba(255,216,74,0.32);">RELOAD</button>`;
  const reloadBtn = banner.querySelector('button') as HTMLButtonElement;
  if (labels) {
    banner.querySelector('span')!.textContent = labels.message;
    reloadBtn.textContent = labels.action;
  }
  // Bind both click and pointerup — some mobile browsers swallow click on
  // elements layered above touch-action:none ancestors. Pointerup fires
  // reliably; click stays as the primary path. Guard against double-fire
  // with a fired flag.
  let fired = false;
  const trigger = (): void => {
    if (fired) return;
    fired = true;
    banner.querySelector('span')!.textContent = labels?.pending ?? 'UPDATING...';
    reloadBtn.disabled = true;
    onReload();
  };
  reloadBtn.addEventListener('click', trigger);
  reloadBtn.addEventListener('pointerup', trigger);
  document.body.appendChild(banner);
}

function escapeHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, opts?: {
  className?: string;
  text?: string;
  html?: string;
  parent?: HTMLElement;
  attrs?: Record<string, string>;
}): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (opts?.className) e.className = opts.className;
  if (opts?.text) e.textContent = opts.text;
  if (opts?.html) e.innerHTML = opts.html;
  if (opts?.attrs) for (const [k, v] of Object.entries(opts.attrs)) e.setAttribute(k, v);
  if (opts?.parent) opts.parent.appendChild(e);
  return e;
}

// ── Title screen ──────────────────────────────────────────────────────────────

/** Kiosk identity — `?p1` = screen 1, `?p2` = screen 2. When set, the
 *  title/attract surfaces collapse to a stripped booth lobby (login → campaign
 *  → 1-screen co-op), hiding every normal menu. 0 = not a kiosk. */
const boothKiosk: number = (() => {
  try {
    const q = new URLSearchParams(window.location.search);
    return q.has('p1') ? 1 : q.has('p2') ? 2 : 0;
  } catch { return 0; }
})();

// Desktop downloads — stable latest-release asset URLs (version-less names, see
// desktop/electron-builder.public.yml). Live once the GitHub release is published.
const RELEASE_DL = 'https://github.com/TheCryptoDonkey/pallasite-releases/releases/latest/download';
const DESKTOP_BUILDS: ReadonlyArray<{ label: string; sub: string; file: string; ua: RegExp }> = [
  { label: 'Windows', sub: '.exe installer', file: 'Pallasite-win-x64.exe', ua: /Windows/i },
  { label: 'macOS · Apple Silicon', sub: '.dmg — M1/M2/M3/M4', file: 'Pallasite-mac-arm64.dmg', ua: /Mac/i },
  { label: 'macOS · Intel', sub: '.dmg', file: 'Pallasite-mac-x64.dmg', ua: /Mac/i },
  { label: 'Linux', sub: '.AppImage', file: 'Pallasite-linux-x86_64.AppImage', ua: /Linux|X11/i },
];

/** "Download or play online" overlay reached from the title's GET THE APP button
 *  (web only). Play-online just returns to the game you're already in; the
 *  download buttons link to the latest GitHub release assets. */
function renderDownload(onBack: () => void): void {
  clearOverlay();
  const overlay = el('div', { className: 'overlay', parent: root });
  setupOverlayArrowNav(overlay, onBack);
  el('h2', { parent: overlay, text: 'PLAY PALLASITE' });

  const sub = el('p', { parent: overlay, text: 'Play instantly in your browser — or install the desktop app for a fullscreen, gamepad-ready build.' });
  sub.style.cssText = 'font-size:0.9rem;color:rgba(220,210,255,0.78);letter-spacing:0.04em;max-width:520px;text-align:center;line-height:1.5;margin:0;';

  const playBtn = el('button', { className: 'menu-btn', parent: overlay, text: '▶ PLAY ONLINE' });
  playBtn.style.cssText += 'margin-top:8px;';
  playBtn.addEventListener('click', onBack);

  const divider = el('p', { parent: overlay, text: '— OR DOWNLOAD —' });
  divider.style.cssText = 'font-size:0.74rem;letter-spacing:0.32em;color:rgba(180,140,255,0.6);margin:12px 0 0;';

  const grid = el('div', { parent: overlay });
  grid.style.cssText = 'display:flex;flex-direction:column;gap:10px;min-width:300px;max-width:430px;width:100%;';
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  for (const d of DESKTOP_BUILDS) {
    const a = el('a', { parent: grid }) as HTMLAnchorElement;
    a.href = `${RELEASE_DL}/${d.file}`;
    a.className = 'menu-btn secondary';
    a.setAttribute('download', '');
    a.rel = 'noopener';
    a.style.cssText = 'text-decoration:none;display:flex;flex-direction:column;align-items:center;gap:2px;';
    el('span', { parent: a, text: `⬇ ${d.label}` });
    const note = el('span', { parent: a, text: d.sub });
    note.style.cssText = 'font-size:0.66rem;letter-spacing:0.08em;color:rgba(180,140,255,0.62);';
    if (d.ua.test(ua)) { a.style.borderColor = 'rgba(140,255,180,0.6)'; a.style.color = '#8cffb4'; }
  }

  const note = el('p', { parent: overlay, text: 'Desktop builds are unsigned. macOS: right-click → Open the first time (if it says “damaged”, run  xattr -cr /Applications/Pallasite.app  in Terminal once). Windows: “More info → Run anyway”.' });
  note.style.cssText = 'font-size:0.7rem;color:rgba(180,140,255,0.5);letter-spacing:0.03em;max-width:480px;text-align:center;line-height:1.5;margin:8px 0 0;';

  const back = el('button', { className: 'menu-btn secondary', parent: overlay, text: '◀ BACK' });
  back.style.cssText += 'margin-top:10px;';
  back.addEventListener('click', onBack);
}

export function renderTitle(state: GameState): void {
  refreshScoreRecovery(state);
  // Kiosk screen: collapse the whole title/menu surface to the stripped
  // booth lobby. This one gate also closes the post-game loop — anything that
  // would show the title shows the lobby instead.
  if (boothKiosk) { renderBoothLobby(state, boothKiosk); return; }
  clearOverlay();
  const overlay = el('div', { className: 'overlay mission-screen', parent: root });
  setupOverlayArrowNav(overlay);

  // Warm both leader-ghost cache slots: today's seed (for the daily-mode
  // race + chip) and the global top (for the title attract-loop and
  // non-daily races). Both go into the cache so whichever mode the player
  // chooses on IGNITE has a hot ghost. getActiveSeed isn't usable here —
  // the seed only locks on IGNITE — so we read the stored preference
  // directly to anticipate.
  //
  // Deferred off the page-load critical path: an immediate multi-relay fan-out
  // on first paint reads as beacon-like to network-reputation scanners, and the
  // ghost isn't needed until IGNITE (several seconds out). requestIdle keeps the
  // load itself network-quiet; the cache is warm well before a mode is picked.
  // Same idiom as deferProfileFetch.
  const warmGhostCache = (): void => prefetchTopGhost(getStoredDailyPref() ? todayUTC() : null);
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(warmGhostCache, { timeout: 2500 });
  } else {
    window.setTimeout(warmGhostCache, 1000);
  }

  // Pull skin unlocks the player earned on other devices. Fire-and-forget;
  // returned set has already been merged into local. The settings overlay
  // re-reads localStorage each time it opens so by the time the player
  // navigates there the merged set is visible.
  if (state.session) {
    void syncSkinUnlocksFromNostr(state.session.pubkey).catch(() => undefined);
  }

  // Wordmark image rendered with mix-blend-mode: screen so the baked-in
  // black starfield bg drops out and only the green lettering floats over
  // the cycling wave background. The text is preserved as alt for screen
  // readers and shows if the image fails to load.
  const titleLogo = el('img', { parent: overlay });
  titleLogo.className = 'title-logo';
  (titleLogo as HTMLImageElement).src = '/logo.webp';
  (titleLogo as HTMLImageElement).alt = 'PALLASITE';
  (titleLogo as HTMLImageElement).decoding = 'async';
  // Easter egg: long-press the logo to open the secret music player.
  bindLogoLongPress(titleLogo, () => renderMusicPlayer(state, () => renderTitle(state)));
  const tagline = el('p', { parent: overlay, text: 'SHOOT ROCKS · CLIMB THE BOARD' });
  tagline.style.cssText = 'font-size:1.2rem;color:var(--hud-yellow);letter-spacing:0.25em;text-shadow:0 0 8px rgba(255,216,74,0.5);margin-top:-12px;';
  el('p', { parent: overlay, text: 'Cosmic arcade · Nostr leaderboards' });

  const setup = el('div', { className: 'run-setup', parent: overlay });
  const launchRow = el('div', { className: 'menu-row launch-row', parent: setup });

  const sessionPanel = el('div', { className: 'session-panel', parent: overlay });
  renderSessionPanel(sessionPanel, state);

  // Mode picker — campaign / drift / bossrush / arena. Sits above difficulty
  // because it changes the SHAPE of the run; difficulty just tunes within.
  renderModeRow(setup, state);

  // Difficulty selector
  renderDifficultyRow(setup);

  // Daily / Free toggle
  renderDailyRow(setup);

  // Streak chip — only renders when the player has completed at least
  // one daily run. The number is the retention hook; once the player has
  // a streak to protect, the daily run pulls them back tomorrow.
  renderStreakChip(overlay);

  // Today's daily-seed leader — surfaces the standing top score on the
  // current daily seed so the title screen always has a visible "duel of
  // the day" hook. Empty state ("be the first") is itself motivating.
  renderDailyLeaderChip(overlay);

  const row = el('div', { className: 'menu-row', parent: overlay });
  // IGNITE label and behaviour both pivot on the stored mode: DUEL routes to
  // the /duel lobby instead of starting a solo run, so the button reflects
  // that. Refreshes on every renderTitle (called whenever Mode changes).
  const storedMode = getStoredMode();
  const startLabel = storedMode === 'coop-campaign'
    ? 'CO-OP LOBBY'
    : storedMode === 'deathmatch'
      ? 'DEATHMATCH LOBBY'
    : storedMode === 'duel'
      ? 'ENTER LOBBY'
      : 'IGNITE';
  const startBtn = el('button', { className: 'menu-btn', parent: launchRow, text: startLabel });
  el('span', { className: 'keyboard-hint', parent: startBtn, text: ' · PRESS ENTER' });
  startBtn.addEventListener('click', () => {
    void (async () => {
      void audio.unlockAudio();
      // Multiplayer-backed modes take the dedicated lobby route; everything else stays on the solo
      // path. The lobby reads the same broker the peer arena uses, so no
      // session needs to be created here — that happens inside the lobby
      // (HOST flow generates a session id; JOIN consumes one from the URL).
      const mode = getStoredMode();
      if (mode === 'duel' || mode === 'coop-campaign' || mode === 'deathmatch') {
        window.location.assign(mode === 'coop-campaign' ? '/duel?coop=1' : mode === 'deathmatch' ? '/duel?deathmatch=1' : '/duel');
        return;
      }
      // Fallback path for IGNITE without first typing a name: rather
      // than starting a session-less run that can't publish scores or
      // earn anything, provision an Anonymous guest identity inline.
      // The user can rename later from the title session panel.
      if (!state.session) {
        try {
          // Read whatever the title-screen arcade name picker has
          // currently typed. Falls back to 'Anonymous' for a freshly-
          // mounted picker the user hasn't touched.
          const typed = titleNamePickerGetName?.().trim() ?? '';
          state.session = await auth.createGuestSession(typed || 'Anonymous');
        } catch (err) {
          console.warn('[guest] inline create on IGNITE failed:', err);
        }
      }
      lockInDifficulty(getStoredDifficulty());
      gateBehindPreRun(() => onStartCb?.());
    })();
  });
  const howBtn = el('button', { className: 'menu-btn secondary', parent: row, text: 'HOW TO PLAY' });
  howBtn.addEventListener('click', () => renderHowToPlay(() => renderTitle(state)));
  const settingsBtn = el('button', { className: 'menu-btn secondary', parent: row, text: 'SETTINGS' });
  settingsBtn.addEventListener('click', () => {
    void audio.unlockAudio();
    renderSettings(() => renderTitle(state));
  });
  // Phone-as-controller — kicks off the pairing UI (QR code) and binds
  // accepted controller inputs into state.keys / state.targetHeading.
  // The host persists across the title→game transition; closing the
  // dialog with "KEEP CONNECTED" leaves the phone in charge.
  const phoneLabel = hasActiveControllerHost() ? '📱 PHONE · PAIRED' : '📱 USE PHONE';
  const phoneBtn = el('button', { className: 'menu-btn secondary', parent: row, text: phoneLabel });
  if (hasActiveControllerHost()) {
    phoneBtn.style.color = '#8cffb4';
    phoneBtn.style.borderColor = 'rgba(140,255,180,0.55)';
  }
  phoneBtn.addEventListener('click', () => {
    void audio.unlockAudio();
    renderControllerHostPairing(state, () => renderTitle(state));
  });
  // Music player — was behind a hidden logo long-press, surfaced as a
  // real button so players can find it without discovering the gesture.
  const musicBtn = el('button', { className: 'menu-btn secondary', parent: row, text: '🎵 MUSIC' });
  musicBtn.addEventListener('click', () => {
    void audio.unlockAudio();
    renderMusicPlayer(state, () => renderTitle(state));
  });
  // Desktop download — web only. A downloaded app shouldn't advertise the
  // download; desktop builds inject __PALLASITE_API_ORIGIN__, so gate on that.
  if (!(globalThis as { __PALLASITE_API_ORIGIN__?: unknown }).__PALLASITE_API_ORIGIN__) {
    const dlBtn = el('button', { className: 'menu-btn secondary', parent: row, text: '⬇ GET THE APP' });
    dlBtn.addEventListener('click', () => renderDownload(() => renderTitle(state)));
  }
  // DUEL used to live here as a separate action button; it now sits in the
  // Mode picker alongside CAMPAIGN / DRIFT / ARENA / SANCTUM so the choice
  // of "what kind of run am I about to start" is on one shelf. Selecting
  // DUEL flips the IGNITE button's label + behaviour above.

  // Defender used to be a big-yellow standalone button here; it now
  // lives in the Mode picker (above) as DEFENDER, same channel as
  // CAMPAIGN / SANCTUM / etc. The `?defender=1` URL flag still works
  // for dev / shareable links and is honoured by main.ts boot.

  // Show local high scores under the start button if any exist. Local entries
  // sometimes carry an eventId (set when the player published this run via the
  // faucet); when they do, the row is clickable and replays in the theatre.
  const selectedBoard: ScoreBoardId = getStoredMode() === 'coop-campaign' ? 'coop-campaign' : 'solo';
  const list = getLocalHighScores(selectedBoard);
  if (list.length > 0) {
    renderLeaderboardBlock(overlay, list, selectedBoard === 'coop-campaign' ? '— LOCAL CO-OP CAMPAIGN —' : '— LOCAL HIGH SCORES —', 5, () => renderTitle(state));
  }

  // Global leaderboard from kind 30762 events on relays. Rendered async so
  // the title screen never blocks on a network round-trip — show a placeholder
  // while it loads, swap in the real list when the relays answer.
  renderGlobalLeaderboard(overlay, state, selectedBoard);

  // Zap-us QR — passers-by / players can send sats anytime, not just after a
  // run. Static LNURL-pay; we never pay out for this.
  renderZapUsQR(overlay, state, { caption: 'ZAP US ⚡' });

  renderLegalFooter(overlay);

  // Keyboard cheatsheet removed — HOW TO PLAY button covers the same content
  // and the duplicate strip cramped the desktop layout against the high scores.
}

// ── Three-screen flow: attract → auth → mission select ──────────────────────
//
// Classic arcade structure. The title used to be a "settings dashboard"
// (logo + tagline + session panel + modes + difficulty + daily + streak +
// balance + pending claim + buttons + leaderboards all on one screen),
// which served the dev fine but read busy for new players. The new flow:
//
//   ATTRACT    Just logo + tagline + rotating panel + huge PLAY + footer.
//              Insert-coin energy — nothing to configure, nothing to learn,
//              one obvious thing to tap. Returning users skip auth and go
//              straight to mission select.
//
//   AUTH       Two-button chooser: SIGN IN WITH NOSTR (existing Signet flow)
//              or PLAY AS GUEST (arcade name picker → local keypair + kind 0
//              metadata + opt-out kind 3 follow of the Pallasite npub).
//
//   MISSION    renderTitle as it stands today — modes, difficulty, daily
//   SELECT     toggle, balance, pending claim, IGNITE, phone pairing,
//              leaderboards. Reached from attract via PLAY (after auth if
//              needed), or directly from game-over via "PLAY AGAIN".
//
// Rotation panels, kind 0 + kind 3 publish, game-over initials drop, and
// the universal claim destination flow are layered on top in follow-up
// commits. This commit is scaffolding only — visual redesign comes later.

/** Top-right utility chip strip on the attract screens. Always shows
 *  a SETTINGS chip. Conditionally shows INSTALL (Android Chrome with
 *  a captured beforeinstallprompt) or a Share-menu hint (iOS Safari).
 *  Once the page is running standalone, the install surfaces go away.
 *  `rerender` is the parent's render fn — called after a dismiss so
 *  the strip re-paints without the hint chip. */
function mountAttractUtilityChips(overlay: HTMLElement, rerender: () => void): void {
  const strip = el('div', { className: 'attract-utilities', parent: overlay });

  const chipStyle = [
    'font:bold 11px ui-monospace,monospace',
    'letter-spacing:0.16em',
    'padding:6px 12px',
    'background:rgba(20,12,36,0.78)',
    'border:1px solid rgba(255,216,74,0.5)',
    'color:#ffd84a',
    'border-radius:3px',
    'cursor:pointer',
    'text-decoration:none',
  ].join(';');

  // SETTINGS — always. Opens the standard panel; closing returns to
  // whichever attract called us via rerender (the panel restores the
  // overlay so we'd be wiped otherwise).
  const settingsChip = el('button', { parent: strip, text: '⚙ SETTINGS' });
  settingsChip.style.cssText = chipStyle;
  settingsChip.addEventListener('click', () => {
    void audio.unlockAudio();
    renderSettings(rerender);
  });

  // Install / fullscreen surfaces only matter on mobile and only when
  // we're not already in a standalone window.
  if (isMobileViewport() && !isStandalone()) {
    if (canInstallNow()) {
      // Android Chrome path — replay the captured beforeinstallprompt.
      const installChip = el('button', { parent: strip, text: '📲 INSTALL' });
      installChip.style.cssText = chipStyle;
      installChip.style.color = '#8cffb4';
      installChip.style.borderColor = 'rgba(140,255,180,0.55)';
      installChip.addEventListener('click', () => {
        void triggerInstall().then((accepted) => {
          if (accepted) rerender();
        });
      });
    } else if (isIosSafari() && !isInstallHintDismissed()) {
      // iOS Safari can't be triggered programmatically. Surface a
      // dismissable hint that points at the Share menu.
      const iosChip = el('button', { parent: strip, text: '🏠 ADD TO HOME · TAP ⎙' });
      iosChip.style.cssText = chipStyle;
      iosChip.style.color = '#8cffb4';
      iosChip.style.borderColor = 'rgba(140,255,180,0.55)';
      iosChip.title = 'Tap the Share icon in Safari, then "Add to Home Screen" for fullscreen.';
      iosChip.addEventListener('click', () => {
        dismissInstallHint();
        rerender();
      });
    } else if (hasFullscreenAPI()) {
      // Non-installable mobile (e.g. Android Chrome without an install
      // prompt yet, or iOS Chrome). Offer fullscreen as a fallback so
      // there's at least SOMETHING the player can tap for more screen.
      const fsChip = el('button', { parent: strip, text: '⛶ FULLSCREEN' });
      fsChip.style.cssText = chipStyle;
      fsChip.addEventListener('click', () => {
        tryEnterFullscreen();
      });
    }
  }
}

interface EventLobbyConfig {
  enabled: boolean;
  lobbyName: string;
  booth: number;
  boothCount: number;
}

const EVENT_LOBBY_KEY = 'pallasite:eventLobby';
const DEFAULT_EVENT_LOBBY: EventLobbyConfig = {
  enabled: false,
  lobbyName: 'PALLASITE',
  booth: 1,
  boothCount: 2,
};

function normaliseEventLobby(raw: unknown): EventLobbyConfig {
  const obj = raw && typeof raw === 'object' ? raw as Partial<EventLobbyConfig> & { eventName?: string } : {};
  const boothCountRaw = Math.floor(Number(obj.boothCount ?? DEFAULT_EVENT_LOBBY.boothCount));
  const boothCount = Math.max(1, Math.min(12, Number.isFinite(boothCountRaw) ? boothCountRaw : DEFAULT_EVENT_LOBBY.boothCount));
  const boothRaw = Math.floor(Number(obj.booth ?? DEFAULT_EVENT_LOBBY.booth));
  const lobbyName = obj.lobbyName ?? obj.eventName;
  return {
    enabled: obj.enabled === true,
    lobbyName: typeof lobbyName === 'string' && lobbyName.trim() ? lobbyName.trim().slice(0, 32) : DEFAULT_EVENT_LOBBY.lobbyName,
    booth: Math.max(1, Math.min(boothCount, Number.isFinite(boothRaw) ? boothRaw : DEFAULT_EVENT_LOBBY.booth)),
    boothCount,
  };
}

function getEventLobbyConfig(): EventLobbyConfig {
  try {
    const raw = localStorage.getItem(EVENT_LOBBY_KEY);
    if (!raw) return { ...DEFAULT_EVENT_LOBBY };
    return normaliseEventLobby(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_EVENT_LOBBY };
  }
}

function setEventLobbyConfig(next: Partial<EventLobbyConfig>): EventLobbyConfig {
  const cfg = normaliseEventLobby({ ...getEventLobbyConfig(), ...next });
  try { localStorage.setItem(EVENT_LOBBY_KEY, JSON.stringify(cfg)); } catch { /* ignore */ }
  return cfg;
}

function eventLobbyEnabled(): boolean {
  return getEventLobbyConfig().enabled;
}

function eventLobbyConfigForRoute(): EventLobbyConfig {
  const cfg = getEventLobbyConfig();
  try {
    const params = new URLSearchParams(window.location.search);
    const lobbyName = params.get('lobby') ?? params.get('event');
    const booth = params.get('booth') ?? params.get('station');
    const boothCount = params.get('booths') ?? params.get('stations');
    return normaliseEventLobby({
      ...cfg,
      ...(lobbyName ? { lobbyName } : {}),
      ...(booth ? { booth } : {}),
      ...(boothCount ? { boothCount } : {}),
    });
  } catch {
    return cfg;
  }
}

function renderEventLobbyAction(parent: HTMLElement, label: string, hint: string, onClick: () => void, emphasis: 'hero' | 'normal' | 'compact' = 'normal'): HTMLButtonElement {
  const hero = emphasis === 'hero', compact = emphasis === 'compact';
  const btn = el('button', { parent }) as HTMLButtonElement;
  btn.style.cssText = [
    'display:flex', 'flex-direction:column', 'align-items:flex-start', 'gap:4px',
    'width:min(520px, 92vw)',
    compact ? 'padding:10px 16px' : hero ? 'padding:22px 22px' : 'padding:14px 16px',
    'background:' + (hero ? 'rgba(40,30,8,0.82)' : 'rgba(20,12,36,0.72)'),
    'border:' + (hero ? '2.5px solid #ffd84a' : compact ? '1px solid rgba(180,140,255,0.4)' : '1.5px solid rgba(255,216,74,0.5)'),
    'border-radius:8px',
    'color:#fff5d8',
    'cursor:pointer',
    'text-align:left',
    hero ? 'box-shadow:0 0 26px rgba(255,216,74,0.32)' : '',
  ].filter(Boolean).join(';');
  const head = el('span', { parent: btn, text: label });
  head.style.cssText = "font-family:'VT323',ui-monospace,monospace;" + (hero ? 'font-size:2.3rem;' : compact ? 'font-size:1.05rem;' : 'font-size:1.35rem;') + 'letter-spacing:0.16em;color:#ffd84a;';
  const body = el('span', { parent: btn, text: hint });
  body.style.cssText = 'font-size:' + (compact ? '0.72rem' : '0.78rem') + ';letter-spacing:0.04em;color:rgba(220,210,255,' + (compact ? '0.6' : '0.72') + ');line-height:1.35;';
  btn.addEventListener('click', onClick);
  return btn;
}

export function renderEventLobby(state: GameState): void {
  const cfg = eventLobbyConfigForRoute();
  const stationLabel = cfg.boothCount > 1 ? `STATION ${cfg.booth}` : 'PRIVATE LOBBY';
  clearOverlay();
  const overlay = el('div', { className: 'overlay', parent: root });
  setupOverlayArrowNav(overlay);

  const utility = el('div', { parent: overlay });
  utility.style.cssText = [
    'position:absolute', 'top:max(14px, env(safe-area-inset-top))', 'right:14px',
    'display:flex', 'gap:8px', 'flex-wrap:wrap', 'justify-content:flex-end', 'z-index:5',
  ].join(';');
  const chipCss = 'font:bold 11px ui-monospace,monospace;letter-spacing:0.14em;padding:6px 12px;background:rgba(20,12,36,0.78);border:1px solid rgba(255,216,74,0.5);color:#ffd84a;border-radius:3px;cursor:pointer;';
  const settings = el('button', { parent: utility, text: 'SETTINGS' });
  settings.style.cssText = chipCss;
  settings.addEventListener('click', () => renderSettings(() => renderEventLobby(state)));
  const normal = el('button', { parent: utility, text: 'NORMAL MENU' });
  normal.style.cssText = chipCss;
  normal.addEventListener('click', () => {
    if (state.session) renderTitle(state);
    else renderAuth(state, () => renderTitle(state));
  });

  const titleLogo = el('img', { parent: overlay });
  titleLogo.className = 'title-logo';
  (titleLogo as HTMLImageElement).src = '/logo.webp';
  (titleLogo as HTMLImageElement).alt = 'PALLASITE';
  (titleLogo as HTMLImageElement).decoding = 'async';

  const kicker = el('p', { parent: overlay, text: `${cfg.lobbyName} · ${stationLabel}` });
  kicker.style.cssText = 'font-size:1rem;color:#8cffb4;letter-spacing:0.28em;text-shadow:0 0 8px rgba(140,255,180,0.4);margin:-12px 0 0;';
  const intro = el('p', { parent: overlay, text: cfg.boothCount > 1 ? 'Walk up, press one button, or scan the QR on this booth.' : 'Start a private lobby, or join one with a short code.' });
  intro.style.cssText = 'font-size:0.88rem;color:rgba(220,210,255,0.76);letter-spacing:0.08em;text-align:center;max-width:520px;line-height:1.45;margin:0;';

  const actions = el('div', { parent: overlay });
  actions.style.cssText = 'display:flex;flex-direction:column;align-items:center;gap:10px;margin:8px auto 0;width:100%;';

  renderEventLobbyAction(actions, 'PLAY SOLO', 'Fast kiosk start. Guest mode, free-run campaign, no menu setup.', () => {
    void (async () => {
      void audio.unlockAudio();
      tryEnterFullscreen();
      setStoredMode('campaign');
      setStoredDailyPref(false);
      if (!state.session) {
        try { state.session = await auth.createGuestSession(`${stationLabel} Pilot`); }
        catch (err) { console.warn('[event] guest create failed:', err); }
      }
      lockInDifficulty(getStoredDifficulty());
      gateBehindOnboarding(() => onStartCb?.());
    })();
  });

  renderEventLobbyAction(actions, '2 PLAYERS · 1 SCREEN', 'Two pilots share this TV — grab a pad each. Instant local co-op, no second device, no lag.', () => {
    // Local couch co-op on this machine: no peer, no lockstep input-delay
    // buffer. ?autostart=1 drops straight into a 2-player shared-screen run.
    window.location.assign(`/?couch=1&autostart=1&event=1&booth=${encodeURIComponent(String(cfg.booth))}`);
  });

  renderEventLobbyAction(actions, 'HOST DEATHMATCH', 'Create a booth match. Empty slots can be AI-filled until real pilots arrive.', () => {
    window.location.assign(`/duel?deathmatch=1&event=1&booth=${encodeURIComponent(String(cfg.booth))}`);
  });

  const join = el('div', { parent: overlay });
  join.style.cssText = 'display:flex;gap:8px;align-items:center;justify-content:center;flex-wrap:wrap;width:min(520px,92vw);margin-top:6px;';
  const code = el('input', { parent: join }) as HTMLInputElement;
  code.type = 'text';
  code.inputMode = 'text';
  code.autocapitalize = 'characters';
  code.autocomplete = 'off';
  code.placeholder = 'SESSION CODE';
  code.maxLength = 16;
  code.style.cssText = 'flex:1 1 180px;min-width:0;padding:12px 14px;font:0.95rem/1.4 ui-monospace,monospace;letter-spacing:0.16em;text-transform:uppercase;background:rgba(15,8,32,0.7);color:#ffd84a;border:1px solid rgba(255,216,74,0.45);border-radius:6px;outline:none;';
  const joinBtn = el('button', { className: 'menu-btn secondary', parent: join, text: 'JOIN CODE' });
  const status = el('p', { parent: overlay });
  status.style.cssText = 'min-height:1.1em;margin:0;font-size:0.74rem;color:rgba(255,138,168,0.9);letter-spacing:0.08em;text-align:center;';
  const joinCode = (): void => {
    const session = code.value.trim().replace(/\s+/g, '').toLowerCase();
    if (!/^[a-z0-9_-]{4,128}$/i.test(session)) {
      status.textContent = 'Enter the short code under the host QR.';
      return;
    }
    const url = buildDuelInviteUrl(session, 1, 2, undefined, 'coop-campaign');
    window.location.assign(url);
  };
  joinBtn.addEventListener('click', joinCode);
  code.addEventListener('keydown', (e) => { if (e.key === 'Enter') joinCode(); });

  if (cfg.boothCount > 1) {
    const boothStrip = el('p', { parent: overlay, text: Array.from({ length: cfg.boothCount }, (_, i) => i + 1 === cfg.booth ? `[STATION ${i + 1}]` : `STATION ${i + 1}`).join('   ') });
    boothStrip.style.cssText = 'font:0.68rem ui-monospace,monospace;color:rgba(180,140,255,0.58);letter-spacing:0.08em;text-align:center;margin:4px 0 0;';
  }
}

/** Render an avatar + name chip for a player identity into `parent`, repainting
 *  when the kind-0 profile lands. Reused on the booth join wizard's control-pick
 *  step and (later) the HUD / game-over. */
function renderIdentityChip(parent: HTMLElement, pubkey: string, fallbackName: string, label: string, colour: string): void {
  const chip = el('div', { parent });
  chip.style.cssText = 'display:flex;align-items:center;gap:10px;';
  let profile = getCachedProfile(pubkey);
  const paint = (): void => {
    chip.innerHTML = '';
    if (profile?.picture) {
      const img = document.createElement('img');
      img.src = profile.picture;
      img.alt = '';
      img.style.cssText = `width:40px;height:40px;border-radius:50%;border:2px solid ${colour};object-fit:cover;`;
      img.onerror = () => { img.style.display = 'none'; };
      chip.appendChild(img);
    }
    // No cached profile yet: prefer an explicit fallback name (e.g. a guest's
    // chosen name), else a short pubkey — NOT the literal "guest", which
    // mislabels a real signed-in identity (a My Signet natural-person carries no
    // displayName) until its kind-0 profile lands and we repaint with bestName.
    const name = profile ? bestName(profile, pubkey) : (fallbackName.trim() || bestName(null, pubkey));
    const t = document.createElement('div');
    t.style.cssText = 'text-align:left;';
    t.innerHTML = `<div style="font-size:0.62rem;letter-spacing:0.2em;color:${colour};">${escapeHtml(label)}</div>`
      + `<div style="font-size:0.92rem;color:#fff;font-weight:bold;">${escapeHtml(name)}</div>`;
    chip.appendChild(t);
  };
  paint();
  if (!profile) deferProfileFetch(pubkey, p => { profile = p; paint(); });
}

function deferProfileFetch(pubkey: string, onProfile: (profile: NostrProfile) => void): void {
  const refresh = () => {
    void fetchProfile(pubkey, { timeoutMs: 2500 }).then(p => { if (p) onProfile(p); });
  };
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(refresh, { timeout: 3000 });
  } else {
    window.setTimeout(refresh, 600);
  }
}

// ── Booth setup wizard (?p1/?p2) ──────────────────────────────────────────────
// Gamepad-first walk-up flow: press Ⓐ to join (how many pads press Ⓐ decides 1P
// vs 2P), then each joined pilot signs in (Nostr or guest) and picks their own
// flight scheme, then the run starts (solo, or local couch for two). The wizard
// owns the gamepads for its whole lifetime: it suppresses the main loop's pad
// routing and runs ONE rAF poll that — on the join screen — edge-detects Ⓐ/Start
// per pad, and — on a per-pilot setup step — drives just that pilot's pad through
// the menus via driveMenuFromPad. LINK BOOTHS (cross-booth) stays a separate path.
//
// A gamepad press is not a browser user-gesture, so audio.unlockAudio() /
// tryEnterFullscreen() are best-effort here — reliable via a real tap (touch
// booth) or the operator's initial page-load click. Launch the booth in kiosk
// fullscreen and give it one operator tap to unlock audio.

/** The three pad flight schemes, shared by the per-pilot control pick. */
const PAD_SCHEME_OPTS: ReadonlyArray<{ value: PadFlightMode; label: string; hint: string }> = [
  { value: 'flydirect', label: 'POINT & FLY',    hint: 'Pickup-and-go — push the left stick to aim and fly together' },
  { value: 'throttle',  label: 'AIM + THROTTLE', hint: 'Left stick aims, right trigger throttles — hold a firing line' },
  { value: 'classic',   label: 'CLASSIC',        hint: 'Arcade — d-pad turns, right trigger (or d-pad up) thrusts' },
];

interface BoothPilot { padIndex: number; padId?: string; slot: number; session: SignetSession | null; }

interface BoothWizard {
  active: boolean;
  booth: number;
  mode: 'join' | 'nav';
  /** gamepad.index of the pad driving the menus during a per-pilot setup step. */
  activePad: number | null;
  nav: MenuNavState;
  /** Per-pad (gamepad.index) edge memory for the join screen's Ⓐ / Start. */
  joinEdge: Map<number, { a: boolean; start: boolean }>;
  /** Per-pad edge memory for the operator settings cheat (see feedBoothCheat). */
  cheat: Map<number, { lastDir: 'up' | 'down' | 'left' | 'right' | null; prevY: boolean; seq: string[] }>;
  pilots: BoothPilot[];
  raf: number | null;
}

let boothWizard: BoothWizard | null = null;

/** True once a booth pilot has signed in (guest or Nostr) via the join wizard.
 *  main.ts's boot-time session restore reads this so a slow tryRestore() of the
 *  device's persisted personal session can't overwrite the pilot's chosen
 *  identity — which made a walk-up's run score under whoever last signed in on
 *  the device (e.g. a guest "DAZ" landing as the operator's TheCryptoDonkey). */
let boothPilotSessionClaimed = false;
export function boothPilotSessionWasClaimed(): boolean { return boothPilotSessionClaimed; }

function stopBoothWizard(): void {
  if (!boothWizard) return;
  if (boothWizard.raf !== null) cancelAnimationFrame(boothWizard.raf);
  boothWizard.active = false;
  boothWizard = null;
  setPadInputSuppressed(false);   // hand the pads back to the in-game loop
}

// One-time "TAP TO START" gate state. A gamepad press is NOT a browser
// user-gesture, so a pad-driven booth kiosk can't unlock its AudioContext and
// starts SILENT until a real tap. The gate forces one real gesture per page
// session; once primed, later cohorts skip straight to the join wizard.
let boothAudioPrimed = false;
let boothGateRaf: number | null = null;
let boothGateKeyup: ((e: KeyboardEvent) => void) | null = null;
function stopBoothGate(): void {
  if (boothGateRaf !== null) { cancelAnimationFrame(boothGateRaf); boothGateRaf = null; }
  if (boothGateKeyup) { window.removeEventListener('keyup', boothGateKeyup, true); boothGateKeyup = null; }
}

/** Entry for `?p1`/`?p2`. (Re)starts the wizard on a clean cohort: each new
 *  group joins + signs in from scratch (replacing the old SIGN OUT hand-over).
 *  state.session is left as-is (a redirect sign-in may have just set it) — the
 *  per-pilot auth step always overwrites it with this cohort's choice. */
export function renderBoothLobby(state: GameState, booth: number): void {
  stopBoothWizard();
  stopBoothGate();
  state.coopIdentity2 = null;
  clearPadFlightModeBySlot();
  clearBoothPadSlotBinding();
  // Booth default is CLASSIC (arcade rotate-and-thrust) — the most familiar
  // scheme for walk-ups and what the on-screen legend describes. Each pilot's
  // join-wizard picker still falls back to this, so they can switch per-slot.
  setPadFlightMode('classic');
  setPadAutoThrust(false);

  // A booth kiosk is gamepad-driven, but a gamepad press is NOT a browser
  // user-gesture — so audio.unlockAudio() from the pad flow is rejected and the
  // kiosk starts silent (no music/SFX) until a real tap. Gate the FIRST entry of
  // the page session behind a real "TAP TO START" gesture that unlocks the
  // AudioContext + enters fullscreen; once primed, later cohorts skip it.
  if (!boothAudioPrimed) {
    renderBoothStartGate(state, booth);
    return;
  }
  startBoothJoinWizard(state, booth);
}

/** The press-Ⓐ-to-join wizard proper, split out of renderBoothLobby so the
 *  one-time audio-unlock gate can run ahead of it. */
function startBoothJoinWizard(state: GameState, booth: number): void {
  // Booth music revival. The booth replaces the title screen with this lobby and
  // loops back here after every run — but a run ends in phase 'gameover', whose
  // resolved track is the one-shot `hull-breached` sting (loop:false). If we
  // re-enter the idle lobby still in that phase, music resolves to a sting that
  // already finished → silence ("not playing again"). Snap the phase back to
  // 'title' (the looping idle pool) and invalidate the track memo so the game
  // loop's next musicSetTrackForState tick crossfades the idle bed back in.
  state.phase = 'title';
  try { void audio.unlockAudio(); } catch { /* best-effort: already unlocked */ }
  musicForceRefresh();

  const w: BoothWizard = {
    active: true, booth, mode: 'join', activePad: null,
    nav: { menuDir: null, menuA: false, menuB: false },
    joinEdge: new Map(), cheat: new Map(), pilots: [], raf: null,
  };
  boothWizard = w;
  setPadInputSuppressed(true);   // the wizard owns the pads now
  const tick = (): void => {
    if (!w.active || boothWizard !== w) return;
    pollBoothWizard(state, w);
    w.raf = requestAnimationFrame(tick);
  };
  w.raf = requestAnimationFrame(tick);
  renderBoothJoin(state, booth);
}

/** One-time audio-unlock gate. A REAL trusted gesture (tap / click / any key)
 *  unlocks audio + fullscreen, then drops into the join wizard. A gamepad button
 *  is a SAFETY NET: it proceeds WITHOUT audio (a pad can't unlock the context)
 *  so a pad-only kiosk is never stuck — just silent until someone taps. The pad
 *  net is edge-detected off the buttons already held, so the Ⓐ that navigated
 *  here doesn't skip the gate instantly. */
function renderBoothStartGate(state: GameState, booth: number): void {
  clearOverlay();
  const overlay = el('div', { className: 'overlay', parent: root });
  setupOverlayArrowNav(overlay);
  document.body.dataset.surface = 'event';

  const titleLogo = el('img', { parent: overlay });
  titleLogo.className = 'title-logo';
  (titleLogo as HTMLImageElement).src = '/logo.webp';
  (titleLogo as HTMLImageElement).alt = 'PALLASITE';
  (titleLogo as HTMLImageElement).decoding = 'async';

  const kicker = el('p', { parent: overlay, text: `PALLASITE · STATION ${booth}` });
  kicker.style.cssText = 'font-size:1rem;color:#8cffb4;letter-spacing:0.28em;text-shadow:0 0 8px rgba(140,255,180,0.4);margin:-12px 0 0;';

  const btn = el('button', { className: 'menu-btn', parent: overlay, text: '▶ TAP TO START' }) as HTMLButtonElement;
  btn.style.cssText += 'font-size:1.7rem;padding:24px 60px;letter-spacing:0.2em;margin:26px auto;display:block;background:rgba(255,216,74,0.18);border-color:#ffd84a;color:#ffd84a;';

  const hint = el('p', { parent: overlay, text: 'Tap the screen once to turn on sound — then grab a pad and press Ⓐ to play.' });
  hint.style.cssText = 'font-size:0.86rem;color:rgba(220,210,255,0.76);letter-spacing:0.06em;text-align:center;max-width:520px;line-height:1.5;margin:0;';

  let done = false;
  let allReleased = false;
  const proceed = (withAudio: boolean): void => {
    if (done) return;
    done = true;
    boothAudioPrimed = true;
    stopBoothGate();
    if (withAudio) {
      try { void audio.unlockAudio(); } catch { /* best-effort */ }
      try { tryEnterFullscreen(); } catch { /* best-effort (e.g. headless / blocked) */ }
    }
    startBoothJoinWizard(state, booth);
  };

  // Real gesture → sound on.
  onTap(btn, () => proceed(true));
  boothGateKeyup = (e: KeyboardEvent): void => { if (e.isTrusted) proceed(true); };
  window.addEventListener('keyup', boothGateKeyup, true);
  setTimeout(() => tryFocusVisible(btn), 0);

  // Gamepad safety net — proceed silently on a FRESH press (all buttons released
  // first, then one goes down).
  const padTick = (): void => {
    if (done) return;
    let anyDown = false;
    const pads = (typeof navigator !== 'undefined' && navigator.getGamepads) ? navigator.getGamepads() : [];
    for (const p of pads) {
      if (!p || !p.connected) continue;
      for (let i = 0; i < p.buttons.length; i++) { if (padButtonDown(p, i)) { anyDown = true; break; } }
      if (anyDown) break;
    }
    if (!allReleased) { if (!anyDown) allReleased = true; }
    else if (anyDown) { proceed(false); return; }
    boothGateRaf = requestAnimationFrame(padTick);
  };
  boothGateRaf = requestAnimationFrame(padTick);
}

/** One rAF of wizard pad input. Join mode: a pad's Ⓐ joins the next free slot
 *  (or, if that pad already joined, begins); Start (9) begins. Nav mode: only the
 *  active pilot's pad walks the current overlay (sign-in / scheme pick). */
function pollBoothWizard(state: GameState, w: BoothWizard): void {
  if (typeof navigator === 'undefined' || typeof navigator.getGamepads !== 'function') return;
  const all = navigator.getGamepads();
  if (!all) return;
  if (w.mode === 'join') {
    for (const pad of all) {
      if (w.mode !== 'join') break;          // a join/begin flipped us to nav
      if (!pad || !pad.connected) continue;
      let edge = w.joinEdge.get(pad.index);
      if (!edge) { edge = { a: false, start: false }; w.joinEdge.set(pad.index, edge); }
      const a = padButtonDown(pad, 0);
      const start = padButtonDown(pad, 9);
      if (a && !edge.a) onBoothJoinPress(state, w, pad.index, pad.id);
      if (start && !edge.start && w.pilots.length > 0) beginBoothSetup(state, w);
      edge.a = a; edge.start = start;
      // Operator settings cheat: ▼ ▶ ◀ ◀ Ⓨ on any pad opens Settings.
      if (feedBoothCheat(state, w, pad)) return;
    }
    return;
  }
  if (w.activePad === null) return;
  const pad = all[w.activePad];
  if (pad && pad.connected) driveMenuFromPad(pad, w.nav);
}

/** Ⓐ on the join screen: an unjoined pad takes the next free slot; a pad that has
 *  already joined begins setup — so a lone pilot just taps Ⓐ twice to go solo. */
function onBoothJoinPress(state: GameState, w: BoothWizard, padIndex: number, padId?: string): void {
  if (w.pilots.some((p) => p.padIndex === padIndex)) { beginBoothSetup(state, w); return; }
  if (w.pilots.length >= 2) return;            // booth is two-up
  void audio.unlockAudio();
  // Record the pad's identity too — routing resolves by it so a reconnect that
  // shuffles gamepad.index can't hand this pilot's slot to a different pad.
  w.pilots.push({ padIndex, padId, slot: w.pilots.length, session: null });
  renderBoothJoin(state, w.booth);
}

// ── Booth operator settings cheat ───────────────────────────────────────────
// A booth kiosk has no keyboard, so Settings is otherwise unreachable once the
// join wizard owns the pads. On the join menu, the sequence ▼ ▶ ◀ ◀ Ⓨ on any
// pad opens Settings; the wizard's nav mode then drives the overlay (Ⓑ = back).
const BOOTH_SETTINGS_CHEAT: ReadonlyArray<string> = ['down', 'right', 'left', 'left', 'Y'];

/** Combined stick + d-pad direction, using the same 0.55 threshold as
 *  driveMenuFromPad so the cheat feels like normal menu nav. */
function padMenuDir(pad: Gamepad): 'up' | 'down' | 'left' | 'right' | null {
  const dx = (pad.axes[0] ?? 0) + (padButtonDown(pad, 15) ? 1 : 0) - (padButtonDown(pad, 14) ? 1 : 0);
  const dy = (pad.axes[1] ?? 0) + (padButtonDown(pad, 13) ? 1 : 0) - (padButtonDown(pad, 12) ? 1 : 0);
  if (Math.abs(dx) <= 0.55 && Math.abs(dy) <= 0.55) return null;
  return Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? 'right' : 'left') : (dy > 0 ? 'down' : 'up');
}

/** Feed one pad's join-screen input to the settings cheat. Records a token on
 *  each fresh directional press (so "left, left" needs a release between) and on
 *  each Ⓨ rising edge, keeping a rolling window. Returns true when the full
 *  sequence just completed (and Settings was opened). */
function feedBoothCheat(state: GameState, w: BoothWizard, pad: Gamepad): boolean {
  let cs = w.cheat.get(pad.index);
  if (!cs) { cs = { lastDir: null, prevY: false, seq: [] }; w.cheat.set(pad.index, cs); }
  const push = (tok: string): boolean => {
    cs!.seq.push(tok);
    if (cs!.seq.length > BOOTH_SETTINGS_CHEAT.length) cs!.seq.shift();
    return cs!.seq.length === BOOTH_SETTINGS_CHEAT.length
      && cs!.seq.every((t, i) => t === BOOTH_SETTINGS_CHEAT[i]);
  };
  let matched = false;
  const dir = padMenuDir(pad);
  if (dir && dir !== cs.lastDir && push(dir)) matched = true;
  cs.lastDir = dir;
  const y = padButtonDown(pad, 3);
  if (y && !cs.prevY && push('Y')) matched = true;
  cs.prevY = y;
  if (matched) { openBoothSettings(state, w, pad.index); return true; }
  return false;
}

/** Open Settings from the booth menu, reusing the wizard's nav mode so the same
 *  pad walks the overlay. Ⓑ (→ Escape → onBack) returns to the join menu. */
function openBoothSettings(state: GameState, w: BoothWizard, padIndex: number): void {
  w.cheat.clear();
  w.mode = 'nav';
  w.activePad = padIndex;
  w.nav = { menuDir: null, menuA: false, menuB: false };
  renderSettings(() => {
    if (boothWizard !== w || !w.active) return;
    w.mode = 'join';
    w.activePad = null;
    w.nav = { menuDir: null, menuA: false, menuB: false };
    w.cheat.clear();
    renderBoothJoin(state, w.booth);
  });
}

/** One-line control legend for the booth's CLASSIC pad scheme. Shown on the
 *  join lobby and the pause menu so a walk-up always knows the mapping without
 *  hunting through settings. Mirrors DEFAULT_BINDINGS (fire Ⓐ/LT, thrust RT,
 *  shield Ⓑ/RB, hyperspace Ⓧ/LB) and the classic d-pad rotate cluster. */
function renderBoothControlsLegend(parent: HTMLElement): HTMLElement {
  const legend = el('p', { parent, text: '🎮  D-pad ◀ ▶ turn  ·  RT (or D-pad ▲) thrust  ·  Ⓐ fire  ·  Ⓑ shield  ·  Ⓧ hyperspace  ·  right stick aims + auto-fires' });
  legend.style.cssText = 'font-size:0.74rem;color:rgba(140,255,180,0.66);letter-spacing:0.05em;text-align:center;margin:14px auto 0;max-width:660px;line-height:1.6;';
  return legend;
}

function renderBoothJoin(state: GameState, booth: number): void {
  const w = boothWizard;
  clearOverlay();
  const overlay = el('div', { className: 'overlay', parent: root });
  setupOverlayArrowNav(overlay);
  document.body.dataset.surface = 'event';

  const titleLogo = el('img', { parent: overlay });
  titleLogo.className = 'title-logo';
  (titleLogo as HTMLImageElement).src = '/logo.webp';
  (titleLogo as HTMLImageElement).alt = 'PALLASITE';
  (titleLogo as HTMLImageElement).decoding = 'async';

  const kicker = el('p', { parent: overlay, text: `PALLASITE · STATION ${booth}` });
  kicker.style.cssText = 'font-size:1rem;color:#8cffb4;letter-spacing:0.28em;text-shadow:0 0 8px rgba(140,255,180,0.4);margin:-12px 0 0;';
  const intro = el('p', { parent: overlay, text: 'Grab a pad and press Ⓐ to drop in. Two pilots welcome — one screen, one campaign.' });
  intro.style.cssText = 'font-size:0.88rem;color:rgba(220,210,255,0.76);letter-spacing:0.08em;text-align:center;max-width:520px;line-height:1.45;margin:0;';

  const joined = w?.pilots.length ?? 0;

  // Two slot cards. Each is clickable too (touch booths + headless tests): a
  // click joins that slot exactly as a pad's Ⓐ does.
  const cards = el('div', { parent: overlay });
  cards.style.cssText = 'display:flex;gap:18px;justify-content:center;flex-wrap:wrap;margin:18px 0 6px;';
  for (let slot = 0; slot < 2; slot++) {
    const taken = slot < joined;
    const colour = PLAYER_COLOURS[slot];
    const card = el('button', { parent: cards }) as HTMLButtonElement;
    card.style.cssText = [
      'display:flex', 'flex-direction:column', 'align-items:center', 'justify-content:center', 'gap:8px',
      'width:min(240px,42vw)', 'padding:26px 18px', 'border-radius:10px', 'cursor:pointer',
      'background:' + (taken ? 'rgba(40,40,60,0.55)' : 'rgba(20,12,36,0.72)'),
      'border:2.5px solid ' + (taken ? colour : 'rgba(255,216,74,0.45)'),
      taken ? `box-shadow:0 0 22px ${colour}55` : '',
    ].filter(Boolean).join(';');
    el('span', { parent: card, text: `PLAYER ${slot + 1}` })
      .style.cssText = `font-family:'VT323',ui-monospace,monospace;font-size:1.5rem;letter-spacing:0.18em;color:${taken ? colour : 'rgba(220,210,255,0.85)'};`;
    el('span', { parent: card, text: taken ? '✓ READY' : 'PRESS Ⓐ' })
      .style.cssText = `font-family:'VT323',ui-monospace,monospace;font-size:2rem;letter-spacing:0.16em;color:${taken ? colour : '#ffd84a'};`;
    card.addEventListener('click', () => {
      const cw = boothWizard; if (!cw || cw.mode !== 'join') return;
      void audio.unlockAudio();
      if (slot < cw.pilots.length) { beginBoothSetup(state, cw); return; }
      if (slot === cw.pilots.length) { cw.pilots.push({ padIndex: -1 - slot, slot, session: null }); renderBoothJoin(state, booth); }
    });
  }

  // START — once at least one pilot has joined. A joined pad's Ⓐ (or Start) does
  // the same; this is the focusable click/touch equivalent.
  const startRow = el('div', { className: 'menu-row', parent: overlay });
  if (joined > 0) {
    const startBtn = el('button', { className: 'menu-btn', parent: startRow, text: joined > 1 ? '▶ START · 2 PLAYERS' : '▶ START · SOLO' }) as HTMLButtonElement;
    startBtn.style.cssText += 'font-size:1.1rem;padding:12px 36px;letter-spacing:0.18em;background:rgba(255,216,74,0.18);border-color:#ffd84a;color:#ffd84a;';
    startBtn.addEventListener('click', () => { const cw = boothWizard; if (cw && cw.mode === 'join') beginBoothSetup(state, cw); });
    setTimeout(() => tryFocusVisible(startBtn), 0);
  } else {
    el('p', { parent: startRow, text: 'Waiting for a pilot…' })
      .style.cssText = 'font-size:0.8rem;color:rgba(180,140,255,0.6);letter-spacing:0.14em;';
  }

  // Control legend — booth default is CLASSIC arcade (each pilot can still
  // switch scheme in the next step). Shared with the pause menu.
  renderBoothControlsLegend(overlay);

  // LINK BOOTHS — a separate thing: cross-booth networked co-op. Kept small.
  const link = el('button', { parent: overlay, text: '🔗 LINK BOOTHS' });
  link.style.cssText = 'margin-top:14px;background:rgba(91,208,255,0.12);border:1px solid rgba(91,208,255,0.5);color:#5bd0ff;font:0.74rem ui-monospace,monospace;letter-spacing:0.14em;padding:7px 16px;border-radius:6px;cursor:pointer;';
  link.addEventListener('click', () => startLinkedBooth(booth));

  // Zap-us QR — persistent "send us sats" surface, same as attract / title.
  renderZapUsQR(overlay, state, { caption: 'ZAP US ⚡' });

  // Operator escape — long-press the logo to drop the kiosk flag → full menu.
  bindLogoLongPress(titleLogo, () => { stopBoothWizard(); window.location.assign('/?menu=1'); });
}

/** A pilot pressed begin → run each joined pilot through sign-in, then a control
 *  pick, in order; then start the run. */
function beginBoothSetup(state: GameState, w: BoothWizard): void {
  if (w.pilots.length === 0) return;
  void audio.unlockAudio();
  tryEnterFullscreen();
  setupBoothPilot(state, w, 0);
}

function setupBoothPilot(state: GameState, w: BoothWizard, i: number): void {
  if (i >= w.pilots.length) { startBoothRun(w); return; }
  const pilot = w.pilots[i];
  armBoothNav(w, pilot);
  const n = i + 1;
  // A fresh arcade launch already selected this pilot. Persisted personal
  // sessions and previous booth guests still take the explicit sign-in path.
  if (i === 0 && !boothPilotSessionClaimed && isArcadeHandoffSession(state.session)) {
    pilot.session = state.session;
    boothPilotSessionClaimed = true;
    renderBoothSchemePick(state, w, i);
    return;
  }
  if (i === 0 && boothPilotSessionClaimed && isArcadeHandoffSession(state.session)) {
    void state.session!.signer.close();
    state.session = null;
    state.profile = null;
  }
  // Sign in — Nostr or guest. P1 → state.session; P2 → state.coopIdentity2 (the
  // existing couch-in-place second-identity field).
  renderAuth(state, () => renderBoothSchemePick(state, w, i), {
    heading: `PLAYER ${n} — SIGN IN`,
    sub: w.pilots.length > 1
      ? `Player ${n} signs in with their own Nostr identity (or guest).`
      : 'Sign in with Nostr to carry your name + zaps everywhere — or play as a guest.',
    // Shared booth device: a guest sign-in here is a NEW walk-up each time, so
    // mint a fresh named identity rather than inheriting the stored record.
    freshGuest: true,
    onResolved: (s) => {
      // Lock out the boot-time restore: once a pilot explicitly signs in here,
      // the device's persisted personal session must not clobber this identity.
      boothPilotSessionClaimed = true;
      pilot.session = s;
      if (i === 0) {
        state.session = s;
        state.profile = getCachedProfile(s.pubkey);
        if (!state.profile) void fetchProfile(s.pubkey).then((p) => { if (p && state.session?.pubkey === s.pubkey) state.profile = p; });
      } else {
        state.coopIdentity2 = { pubkey: s.pubkey, displayName: s.displayName ?? '', profile: getCachedProfile(s.pubkey), session: s };
        if (!state.coopIdentity2.profile) void fetchProfile(s.pubkey).then((p) => { if (p && state.coopIdentity2) state.coopIdentity2.profile = p; });
      }
    },
  });
}

/** Hand the menus to one pilot's pad, debouncing the Ⓐ that's likely still held
 *  from the previous step so it doesn't instantly confirm the first focusable. */
function armBoothNav(w: BoothWizard, pilot: BoothPilot): void {
  w.mode = 'nav';
  w.activePad = pilot.padIndex >= 0 ? pilot.padIndex : null;
  w.nav = { menuDir: null, menuA: true, menuB: true };
}

function renderBoothSchemePick(state: GameState, w: BoothWizard, i: number): void {
  const pilot = w.pilots[i];
  armBoothNav(w, pilot);   // re-arm: the Ⓐ that confirmed sign-in is likely held
  clearOverlay();
  const overlay = el('div', { className: 'overlay', parent: root });
  setupOverlayArrowNav(overlay);
  const n = i + 1;
  el('h2', { parent: overlay, text: `PLAYER ${n} — CONTROLS` });

  if (pilot.session) {
    const who = el('div', { parent: overlay });
    who.style.cssText = 'display:flex;justify-content:center;margin:2px 0 12px;';
    renderIdentityChip(who, pilot.session.pubkey, pilot.session.displayName ?? '', `PLAYER ${n}`, PLAYER_COLOURS[pilot.slot]);
  }

  el('p', { parent: overlay, text: 'Pick how your pad flies. The right stick aims + auto-fires in every mode.' })
    .style.cssText = 'font-size:0.84rem;color:rgba(220,210,255,0.72);margin:0 auto 14px;max-width:520px;text-align:center;line-height:1.5;';

  const panel = el('div', { parent: overlay });
  panel.style.cssText = 'display:flex;gap:12px;align-items:stretch;justify-content:center;flex-wrap:wrap;';
  const cur = getPadFlightModeForSlot(pilot.slot);
  let focusBtn: HTMLButtonElement | null = null;
  for (const opt of PAD_SCHEME_OPTS) {
    const on = opt.value === cur;
    const b = el('button', { parent: panel }) as HTMLButtonElement;
    b.style.cssText = [
      'display:flex', 'flex-direction:column', 'gap:6px', 'width:min(220px,40vw)', 'padding:14px 16px', 'text-align:left',
      'background:' + (on ? 'rgba(88,255,88,0.16)' : 'rgba(20,12,36,0.72)'),
      'border:2px solid ' + (on ? '#58ff58' : 'rgba(180,140,255,0.4)'),
      'border-radius:8px', 'cursor:pointer',
    ].join(';');
    el('span', { parent: b, text: opt.label }).style.cssText = "font-family:'VT323',ui-monospace,monospace;font-size:1.3rem;letter-spacing:0.14em;color:#ffd84a;";
    el('span', { parent: b, text: opt.hint }).style.cssText = 'font-size:0.74rem;color:rgba(220,210,255,0.7);line-height:1.4;';
    b.addEventListener('click', () => { setPadFlightModeForSlot(pilot.slot, opt.value); setupBoothPilot(state, w, i + 1); });
    if (on) focusBtn = b;
  }
  if (focusBtn) { const fb = focusBtn; setTimeout(() => tryFocusVisible(fb), 0); }

  el('p', { parent: overlay, text: 'Press Ⓐ on your choice.' })
    .style.cssText = 'font-size:0.74rem;color:rgba(180,140,255,0.6);letter-spacing:0.14em;margin:14px 0 0;text-align:center;';
}

function startBoothRun(w: BoothWizard): void {
  const twoUp = w.pilots.length > 1;
  // Bind each real pad to its slot so join order = P1 / P2. Carry the pad's
  // identity (gamepad.id) so routing follows the physical controller across any
  // reconnect that reshuffles gamepad.index, not the bare index.
  setBoothPadSlotBinding(w.pilots.filter((p) => p.padIndex >= 0).map((p) => ({ slot: p.slot, padIndex: p.padIndex, padId: p.padId })));
  stopBoothWizard();   // re-enable the in-game pad loop
  setStoredMode('campaign');
  setStoredDailyPref(false);
  lockInDifficulty(getStoredDifficulty());
  gateBehindOnboarding(() => { if (twoUp) startCouchInPlace(); else onStartCb?.(); });
}

/** LINK BOOTHS — cross-booth networked co-op (a separate path from the local
 *  press-Ⓐ flow). One pilot per booth by default; ?pilots=N,M opts up to 2 each.
 *  Booth 1 → slot 0, Booth 2 → its offset; each booth owns its slot region so no
 *  slot is ever unowned. Rollback on by default; ?rollback=0 falls back to
 *  lockstep. Built directly because coop-campaign hard-codes 2 players. */
function startLinkedBooth(booth: number): void {
  void audio.unlockAudio();
  tryEnterFullscreen();
  const q = new URLSearchParams(window.location.search);
  const link = (q.get('link') || '').replace(/[^a-z0-9]/gi, '').slice(0, 32) || 'praguebooths';
  const counts = (q.get('pilots') || '1,1').split(',').map((nn) => Math.max(1, Math.min(2, parseInt(nn, 10) || 1)));
  const b1 = counts[0] ?? 1, b2 = counts[1] ?? 1;
  const offset = booth === 1 ? 0 : b1;
  const myCount = booth === 1 ? b1 : b2;
  const owned = Array.from({ length: myCount }, (_, i) => offset + i).join(',');
  const params = new URLSearchParams({
    peer: buildBrokerPeerUrl(link),
    session: link,
    slot: String(offset),
    localSlots: owned,
    players: String(b1 + b2),
    mode: 'coop-campaign',
    rollback: q.get('rollback') === '0' ? '0' : '1',
  });
  stopBoothWizard();
  window.location.assign(`${window.location.origin}/?${params.toString()}&p${booth}=1`);
}

export function renderAttract(state: GameState): void {
  refreshScoreRecovery(state);
  if (boothKiosk) { renderBoothLobby(state, boothKiosk); return; }
  // 600bn flavour gets its own bespoke attract screen — sacred number
  // wordmark, single PLAY to drop straight into the Sanctum, no auth
  // step in the way (claim flow at game-over handles sign-in if needed).
  if (getFlavour() === '600bn') {
    renderSanctumAttract(state);
    return;
  }
  if (eventLobbyEnabled()) {
    renderEventLobby(state);
    return;
  }
  clearOverlay();
  const overlay = el('div', { className: 'overlay attract-screen', parent: root });
  setupOverlayArrowNav(overlay);

  // Top utility chip strip — SETTINGS always, INSTALL when prompt
  // available (Android Chrome), iOS Safari Share-menu hint when not
  // already standalone. Kept in the layout above the logo; only renders on attract because that's where
  // first-time visitors land.
  mountAttractUtilityChips(overlay, () => renderAttract(state));

  // Logo — same wordmark as the previous title.
  const titleLogo = el('img', { parent: overlay });
  titleLogo.className = 'title-logo';
  (titleLogo as HTMLImageElement).src = '/logo.webp';
  (titleLogo as HTMLImageElement).alt = 'PALLASITE';
  (titleLogo as HTMLImageElement).decoding = 'async';
  bindLogoLongPress(titleLogo, () => renderMusicPlayer(state, () => renderAttract(state)));

  const tagline = el('p', { parent: overlay, text: 'SHOOT ROCKS · CLIMB THE BOARD' });
  tagline.style.cssText = 'font-size:1.2rem;color:var(--hud-yellow);letter-spacing:0.25em;text-shadow:0 0 8px rgba(255,216,74,0.5);margin-top:-12px;';
  el('p', { parent: overlay, text: 'Cosmic arcade · Nostr leaderboards' });

  // Rotating attract panel — three contents cycle every few seconds:
  //   GLOBAL HIGH SCORES TOP 5   (8s)  — live-updating leaderboard
  //   TOP RUN HIGHLIGHT           (8s) — placeholder for replay
  //                                      autoplay; full live render is
  //                                      a follow-up. Static "top run"
  //                                      stat panel for now.
  //   CREDITS                     (5s) — handcrafted by + zaps tip
  // 300ms crossfade between panels via opacity transition.
  //
  // Each render is fire-and-forget: it mounts a subtree into the panel
  // and tears down on next rotation tick (innerHTML='' is enough since
  // none of the panels open network sockets except the high-scores
  // panel, which checks container.isConnected for self-teardown).
  const attractPanel = el('div', { parent: overlay });
  attractPanel.style.cssText = 'min-height:240px;margin:24px auto;max-width:760px;width:100%;display:flex;align-items:center;justify-content:center;transition:opacity 300ms ease;';
  attractPanel.dataset.attractPanel = '1';

  type AttractPhase = 'scores' | 'replay' | 'credits';
  const PHASE_DURATIONS: Record<AttractPhase, number> = {
    scores: 8000,
    replay: 8000,
    credits: 5000,
  };
  const PHASE_ORDER: readonly AttractPhase[] = ['scores', 'replay', 'credits'];
  let phaseIdx = 0;
  let phaseTimer: number | null = null;

  const mountPhase = (phase: AttractPhase): void => {
    attractPanel.innerHTML = '';
    if (phase === 'scores') {
      // Wrap renderGlobalLeaderboard so we can scope the title to a
      // shorter ATTRACT-style label rather than the full "GLOBAL HIGH
      // SCORES" the mission-select uses.
      const wrap = el('div', { parent: attractPanel });
      wrap.style.cssText = 'width:100%;max-width:560px;text-align:center;';
      renderGlobalLeaderboard(wrap, state, 'solo');
    } else if (phase === 'replay') {
      // v1 placeholder — full live-frame autoplay of a top-zapped run
      // is the next commit. For now show a tasteful "highlight reel
      // loading" stand-in that doesn't promise more than we deliver.
      const wrap = el('div', { parent: attractPanel });
      wrap.style.cssText = 'width:100%;max-width:560px;text-align:center;display:flex;flex-direction:column;gap:8px;align-items:center;';
      const head = el('p', { parent: wrap, text: '⚡ TOP RUN HIGHLIGHTS' });
      head.style.cssText = 'font-size:0.95rem;color:#ffd84a;letter-spacing:0.18em;margin:0;';
      const body = el('p', { parent: wrap, text: 'Autoplay coming soon — open the WATCH page to see live runs and replay-worthy clips from across the relay set.' });
      body.style.cssText = 'font-size:0.82rem;color:rgba(220,210,255,0.65);max-width:480px;line-height:1.5;margin:0;';
      const watchBtn = el('a', { parent: wrap, text: 'OPEN WATCH PAGE ▶' }) as HTMLAnchorElement;
      watchBtn.href = 'https://watch.pallasite.app/';
      watchBtn.target = '_blank';
      watchBtn.rel = 'noopener';
      watchBtn.style.cssText = 'font-size:0.78rem;letter-spacing:0.16em;color:#8cffb4;text-decoration:none;border:1px solid rgba(140,255,180,0.55);padding:4px 14px;border-radius:3px;background:rgba(140,255,180,0.06);margin-top:4px;';
    } else {
      // Credits — minimal. Names + zap pointer; sources from
      // existing credits.ts module if there's more later, but for
      // attract a tight three-line block reads better.
      const wrap = el('div', { parent: attractPanel });
      wrap.style.cssText = 'width:100%;max-width:560px;text-align:center;display:flex;flex-direction:column;gap:6px;';
      el('p', { parent: wrap, text: '— CREDITS —' }).style.cssText = 'font-size:0.85rem;color:#ffd84a;letter-spacing:0.22em;margin:0 0 6px;';
      const line = el('p', { parent: wrap, text: 'Handcrafted by The Crypto Donkey' });
      line.style.cssText = 'font-size:0.92rem;color:#fff5d8;letter-spacing:0.06em;margin:0;';
      const tip = el('p', { parent: wrap, text: 'Built on Nostr · Lightning · open relays' });
      tip.style.cssText = 'font-size:0.74rem;color:rgba(220,210,255,0.6);letter-spacing:0.06em;margin:0;';
      const npub = el('p', { parent: wrap, text: 'npub1mgvlrnf5hm9yf0n5mf9nqmvarhvxkc6remu5ec3vf8r0txqkuk7su0e7q2' });
      npub.style.cssText = 'font-size:0.66rem;color:rgba(180,140,255,0.55);font-family:monospace;letter-spacing:0.04em;margin:4px 0 0;word-break:break-all;';
    }
  };

  const rotate = (): void => {
    if (!attractPanel.isConnected) {
      if (phaseTimer !== null) { window.clearTimeout(phaseTimer); phaseTimer = null; }
      return;
    }
    // Fade out, swap, fade in. setTimeout chain keeps the crossfade
    // visible without needing CSS @keyframes.
    attractPanel.style.opacity = '0';
    window.setTimeout(() => {
      if (!attractPanel.isConnected) return;
      phaseIdx = (phaseIdx + 1) % PHASE_ORDER.length;
      const next = PHASE_ORDER[phaseIdx];
      mountPhase(next);
      attractPanel.style.opacity = '1';
      phaseTimer = window.setTimeout(rotate, PHASE_DURATIONS[next]);
    }, 300);
  };
  // First panel — render immediately, no fade-in.
  mountPhase(PHASE_ORDER[0]);
  attractPanel.style.opacity = '1';
  phaseTimer = window.setTimeout(rotate, PHASE_DURATIONS[PHASE_ORDER[0]]);

  // The ONE thing on this screen — big PLAY button.
  const playRow = el('div', { className: 'menu-row', parent: overlay });
  const playBtn = el('button', { className: 'menu-btn', parent: playRow, text: 'PLAY ▶' }) as HTMLButtonElement;
  playBtn.style.cssText += 'font-size:1.4rem;padding:14px 48px;letter-spacing:0.28em;background:rgba(255,216,74,0.18);border-color:#ffd84a;color:#ffd84a;text-shadow:0 0 12px rgba(255,216,74,0.6);';
  overlay.insertBefore(playRow, attractPanel);
  playBtn.addEventListener('click', () => {
    void audio.unlockAudio();
    // Fullscreen MUST be requested in the same user-gesture tick. Sync
    // call (fire-and-forget) keeps the gesture chain alive. Silently
    // no-ops on iOS Safari which lacks the API.
    tryEnterFullscreen();
    if (state.session) {
      // Returning visitor with an identity — go straight to mission
      // select. The auth step is only for first-time visitors.
      renderTitle(state);
    } else {
      renderAuth(state, () => renderTitle(state));
    }
  });

  // Zap-us QR on attract — a walk-up at the booth can scan and send sats
  // before ever pressing PLAY (and the game-over recap auto-skips to title,
  // so this is the always-present place to catch them).
  renderZapUsQR(overlay, state, { caption: 'ZAP US ⚡' });

  // Footer — terms, privacy, version. Always visible on attract so the
  // legal surface is one tap away without cluttering the auth or
  // mission screens.
  renderLegalFooter(overlay);
}

/** On-screen QWERTY name entry. Built for booth walk-ups on a gamepad — the
 *  d-pad walks the key grid (setupOverlayArrowNav's geometric nav re-queries
 *  buttons live, so the keys are reachable) and Ⓐ presses a key — while a
 *  physical keyboard types straight in. SPACE / ⌫ BKSP / ✓ DONE on the bottom
 *  row, plus an optional BACK. Replaces the slow ▲▼ arcade cycler for guest
 *  sign-in. Returns getCurrentName() so callers can read the in-progress value. */
function renderQwertyName(
  parent: HTMLElement,
  opts: { onSubmit?: (name: string) => void; onBack?: () => void; maxLen?: number; initialValue?: string },
): { getCurrentName: () => string } {
  const MAX_LEN = opts.maxLen ?? 25;
  let name = (opts.initialValue ?? '').toUpperCase().slice(0, MAX_LEN);
  let submitted = false;

  const wrap = el('div', { parent });
  wrap.style.cssText = 'display:flex;flex-direction:column;align-items:center;gap:10px;width:100%;max-width:640px;margin:4px auto;';
  // Same marker renderArcadeName sets, so main.ts's Enter-restarts-game handler
  // stays out of the way while a name is being typed.
  wrap.dataset.arcadeInitials = 'open';

  const display = el('div', { parent: wrap });
  display.style.cssText = 'min-width:60%;min-height:1.8em;padding:8px 18px;font-family:monospace;font-size:1.6rem;letter-spacing:0.12em;color:#ffd84a;background:rgba(2,5,13,0.5);border:2px solid #ffd84a;border-radius:6px;text-align:center;text-shadow:0 0 10px rgba(255,216,74,0.45);';
  const renderName = (): void => { display.textContent = name.length ? name : '—'; };
  renderName();

  const getCurrentName = (): string => name.trim();
  const append = (ch: string): void => {
    if (name.length >= MAX_LEN) return;
    name += ch.toUpperCase();
    renderName();
    try { audio.initialCycle(); } catch { /* ignore */ }
  };
  const backspace = (): void => {
    if (!name.length) return;
    name = name.slice(0, -1);
    renderName();
    try { audio.initialBackspace(); } catch { /* ignore */ }
  };
  const commit = (): void => {
    if (submitted) return;
    submitted = true;
    window.removeEventListener('keydown', keyHandler, true);
    opts.onSubmit?.(getCurrentName());
  };
  const back = (): void => {
    window.removeEventListener('keydown', keyHandler, true);
    opts.onBack?.();
  };

  // pointerdown (touch, no 300ms delay) + click (mouse / focused-button Enter).
  const bindTap = (btn: HTMLElement, fn: () => void): void => {
    let handledPointer = false;
    btn.addEventListener('pointerdown', (e) => { e.preventDefault(); handledPointer = true; fn(); });
    btn.addEventListener('click', (e) => { e.preventDefault(); if (handledPointer) { handledPointer = false; return; } fn(); });
  };

  const keyBase = 'height:50px;min-width:46px;padding:0 6px;border-radius:7px;font-family:monospace;font-size:1.3rem;cursor:pointer;-webkit-tap-highlight-color:transparent;touch-action:manipulation;user-select:none;';
  const grid = el('div', { className: 'name-keyboard', parent: wrap });
  grid.style.cssText = 'display:flex;flex-direction:column;gap:7px;align-items:center;width:100%;';
  for (const row of ['1234567890', 'QWERTYUIOP', 'ASDFGHJKL', 'ZXCVBNM']) {
    const r = el('div', { parent: grid });
    r.style.cssText = 'display:flex;gap:7px;justify-content:center;';
    for (const ch of row) {
      const b = el('button', { parent: r, text: ch }) as HTMLButtonElement;
      b.type = 'button';
      b.style.cssText = keyBase + 'background:rgba(20,12,36,0.85);border:2px solid rgba(180,140,255,0.4);color:#dccfff;';
      bindTap(b, () => append(ch));
    }
  }

  const actions = el('div', { parent: wrap });
  actions.style.cssText = 'display:flex;gap:8px;justify-content:center;flex-wrap:wrap;margin-top:2px;';
  const spaceBtn = el('button', { parent: actions, text: 'SPACE' }) as HTMLButtonElement;
  spaceBtn.type = 'button';
  spaceBtn.style.cssText = keyBase + 'min-width:190px;background:rgba(20,12,36,0.85);border:2px solid rgba(180,140,255,0.4);color:#dccfff;letter-spacing:0.2em;font-size:0.95rem;';
  bindTap(spaceBtn, () => append(' '));
  const bkspBtn = el('button', { parent: actions, text: '⌫ BKSP' }) as HTMLButtonElement;
  bkspBtn.type = 'button';
  bkspBtn.style.cssText = keyBase + 'min-width:100px;background:rgba(255,120,120,0.14);border:2px solid rgba(255,120,120,0.5);color:#ff8a8a;letter-spacing:0.08em;font-size:0.95rem;';
  bindTap(bkspBtn, backspace);
  const doneBtn = el('button', { parent: actions, text: '✓ DONE' }) as HTMLButtonElement;
  doneBtn.type = 'button';
  doneBtn.style.cssText = keyBase + 'min-width:120px;background:rgba(88,255,88,0.16);border:2px solid #58ff58;color:#8cffb4;letter-spacing:0.14em;font-size:1rem;';
  bindTap(doneBtn, commit);
  if (opts.onBack) {
    const backBtn = el('button', { parent: actions, text: '◀ BACK' }) as HTMLButtonElement;
    backBtn.type = 'button';
    backBtn.style.cssText = keyBase + 'min-width:100px;background:rgba(140,140,255,0.10);border:2px solid rgba(140,140,255,0.35);color:#9b9bff;letter-spacing:0.08em;font-size:0.9rem;';
    bindTap(backBtn, back);
  }

  const hint = el('p', { parent: wrap, text: 'D-pad + Ⓐ to type · SPACE · ⌫ · ✓ DONE' });
  hint.style.cssText = 'font-size:0.7rem;color:rgba(180,140,255,0.55);letter-spacing:0.12em;margin:2px 0 0;';

  // Physical-keyboard fast path (capture phase + stopImmediatePropagation on
  // consumed keys, so neither the overlay nav nor main.ts's Enter-restart fires
  // mid-edit). Mirrors renderArcadeName: when a button is focused, Enter / Space
  // / arrows belong to overlay/pad navigation; otherwise letters type directly
  // and Enter submits.
  const keyHandler = (e: KeyboardEvent): void => {
    if (submitted) return;
    if (!document.body.contains(wrap)) { window.removeEventListener('keydown', keyHandler, true); return; }
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    const active = document.activeElement as Element | null;
    const activeIsButton = active instanceof HTMLButtonElement;
    const isNavKey = e.code === 'ArrowUp' || e.code === 'ArrowDown' || e.code === 'ArrowLeft' || e.code === 'ArrowRight' || e.code === 'Enter' || e.code === 'Space';
    if (isNavKey && activeIsButton) return;   // pad / arrow nav owns focused buttons
    if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')) return;
    if (e.code === 'ArrowUp' || e.code === 'ArrowDown' || e.code === 'ArrowLeft' || e.code === 'ArrowRight') return;
    let consumed = true;
    if (e.code === 'Backspace') backspace();
    else if (e.code === 'Enter') commit();
    else if (e.code === 'Space') append(' ');
    else if (e.key && e.key.length === 1 && /[A-Za-z0-9 ]/.test(e.key)) append(e.key);
    else consumed = false;
    if (consumed) { e.preventDefault(); e.stopImmediatePropagation(); }
  };
  window.addEventListener('keydown', keyHandler, true);

  return { getCurrentName };
}

export function renderAuth(state: GameState, onDone: () => void, opts?: { onResolved?: (s: SignetSession) => void; heading?: string; sub?: string; freshGuest?: boolean }): void {
  refreshScoreRecovery(state);
  clearOverlay();
  const overlay = el('div', { className: 'overlay auth-screen', parent: root });
  setupOverlayArrowNav(overlay);

  // When onResolved is supplied (capturing a SECOND player's identity at a
  // booth), it takes the resolved session instead of overwriting state.session.
  const resolve = (s: SignetSession): void => {
    if (opts?.onResolved) opts.onResolved(s);
    else { state.session = s; refreshScoreRecovery(state); }
  };

  // Two stacked views toggled by visibility (not re-render) so the follow
  // checkbox stays in the DOM for submitGuest to read: the chooser (Nostr /
  // Guest) and the guest-name QWERTY keyboard. setupOverlayArrowNav filters to
  // VISIBLE buttons, so the pad only ever navigates the showing view.
  const chooser = el('div', { parent: overlay });
  chooser.style.cssText = 'display:flex;flex-direction:column;align-items:center;width:100%;';
  const nameView = el('div', { parent: overlay });
  nameView.style.cssText = 'display:none;flex-direction:column;align-items:center;width:100%;';

  el('h2', { parent: chooser, text: opts?.heading ?? 'WHO ARE YOU?' });

  const sub = el('p', { parent: chooser, text: opts?.sub ?? 'Sign in with Nostr to take your name, your zaps, and your replays everywhere — or play as a guest and we\'ll create a local identity for you.' });
  sub.style.cssText = 'font-size:0.85rem;color:rgba(220,210,255,0.7);margin:6px auto 18px;max-width:560px;text-align:center;line-height:1.5;';

  // Option 1 — sign in with Nostr (existing Signet flow). The
  // SignInWithNostr button is the "advanced" path: NIP-07 extension,
  // NIP-46 bunker, or the SDK's QR-over-relay flow. We hand off to
  // auth.signIn() which manages the modal. Auto-highlighted (focused) below so
  // a booth pad lands here first; cursor-down reaches PLAY AS GUEST.
  const signInBtn = el('button', { className: 'menu-btn', parent: chooser, text: '⚡ SIGN IN WITH NOSTR' }) as HTMLButtonElement;
  signInBtn.style.cssText += 'font-size:1rem;padding:12px 32px;letter-spacing:0.18em;margin:6px auto;display:block;min-width:280px;';
  const signInStatus = el('p', { parent: chooser });
  signInStatus.style.cssText = 'font-size:0.78rem;color:rgba(180,140,255,0.85);min-height:1em;margin:0 0 12px;letter-spacing:0.04em;text-align:center;';
  let signing = false;
  signInBtn.addEventListener('click', () => {
    if (signing) return;
    signing = true;
    signInStatus.textContent = 'Connecting…';
    void (async () => {
      try {
        const session = await auth.signIn();
        if (session) {
          resolve(session);
          onDone();
          return;
        }
        signInStatus.textContent = 'No signer attached.';
        signInStatus.style.color = '#ff8a3a';
      } catch (err) {
        if (auth.isAuthOnlySignerError(err)) {
          signInStatus.textContent = 'Signer is not live. Choose a My Signet signing method.';
          signInStatus.style.color = '#ffd84a';
          renderSignerRecovery(state, { onResolved: resolve, onClose: onDone });
        } else {
          signInStatus.textContent = err instanceof auth.SignInTimeoutError
            ? `Timeout — ${err.message}`
            : `Sign-in failed: ${err instanceof Error ? err.message : String(err)}`;
          signInStatus.style.color = '#ff5050';
        }
      } finally {
        signing = false;
      }
    })();
  });

  // Visual separator between the two paths.
  const sep = el('div', { parent: chooser, text: '— OR —' });
  sep.style.cssText = 'font-family:monospace;color:rgba(220,210,255,0.4);letter-spacing:0.3em;margin:14px 0;text-align:center;';

  // Option 2 — guest. A button (cursor-down from Nostr) that opens the QWERTY
  // name keyboard, then mints a fresh local identity.
  const guestBtn = el('button', { className: 'menu-btn', parent: chooser, text: '🚀 PLAY AS GUEST' }) as HTMLButtonElement;
  guestBtn.style.cssText += 'font-size:1rem;padding:12px 32px;letter-spacing:0.18em;margin:6px auto;display:block;min-width:280px;background:rgba(88,255,88,0.12);border-color:#58ff58;color:#8cffb4;';

  const guestStatus = el('p', { parent: chooser });
  guestStatus.style.cssText = 'font-size:0.78rem;color:rgba(180,140,255,0.85);min-height:1em;margin:0;letter-spacing:0.04em;text-align:center;';

  let creating = false;
  const submitGuest = (raw: string): void => {
    if (creating) return;
    // Easter egg: "600B" (or the full 600,000,000,000) typed as a name drops
    // into 600bn Sanctum mode. getFlavour() is cached + branches the whole boot
    // path, so a query reload is the clean way in.
    const cheatNorm = raw.replace(/[\s,]/g, '').toUpperCase();
    if (cheatNorm === '600B' || cheatNorm === '600000000000') {
      try { void audio.unlockAudio(); } catch { /* ignore */ }
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('flavour', '600bn');
        // Solo Sanctum: drop the booth join-wizard / couch topology. ?p1 gates
        // run-start on the press-Ⓐ wizard, which the 600bn attract bypasses —
        // keeping it made the booth loop back to the attract instead of starting.
        // Dropping it boots the proven solo 600bn flow (pad 0 → the lone ship);
        // ?fullfx is preserved. Exiting via the arcade re-launches the AppImage
        // at its ?p1 boot query → back to the booth.
        url.searchParams.delete('p1');
        url.searchParams.delete('p2');
        url.searchParams.delete('couch');
        window.location.href = url.toString();
      } catch { /* ignore */ }
      return;
    }
    creating = true;
    guestStatus.textContent = '';
    try { void audio.unlockAudio(); } catch { /* ignore */ }
    void (async () => {
      try {
        const name = raw.trim() || 'Anonymous';
        // Read the opt-out checkbox state at submit time. Defaults
        // to true (pre-checked) so a user who just types and submits
        // gets the auto-follow.
        const followCheckEl = overlay.querySelector<HTMLInputElement>('input[data-follow-pallasite]');
        const followPallasite = followCheckEl?.checked ?? true;
        // Booth / kiosk: each walk-up is a different person, so mint a fresh
        // named identity (publishing their kind 0 + Pallasite follow) instead of
        // reusing the device's stored guest record from the previous player.
        resolve(await auth.createGuestSession(name, { followPallasite, fresh: opts?.freshGuest }));
        onDone();
      } catch (err) {
        guestStatus.textContent = `Couldn't create local identity: ${err instanceof Error ? err.message : String(err)}`;
        guestStatus.style.color = '#ff8a3a';
        creating = false;
        // Surface the error on the chooser, where guestStatus lives.
        chooser.style.display = 'flex';
        nameView.style.display = 'none';
      }
    })();
  };

  // Opt-out follow checkbox. Pre-checked so the default path publishes
  // a kind 3 contact list following the Pallasite game npub when the
  // guest identity is first created — disclosure is the point.
  const followRow = el('label', { parent: chooser });
  followRow.style.cssText = 'display:flex;align-items:center;justify-content:center;gap:8px;font-size:0.78rem;color:rgba(220,210,255,0.7);margin:6px auto 0;cursor:pointer;letter-spacing:0.04em;max-width:480px;';
  const followCheck = el('input', { parent: followRow }) as HTMLInputElement;
  followCheck.type = 'checkbox';
  followCheck.checked = true;
  followCheck.dataset.followPallasite = '1';
  followCheck.style.cssText = 'width:16px;height:16px;accent-color:#8cffb4;cursor:pointer;';
  el('span', { parent: followRow, text: 'Follow Pallasite on Nostr for daily seeds + run highlights' });

  // Back to attract.
  const backRow = el('div', { className: 'menu-row', parent: chooser });
  const backBtn = el('button', { className: 'menu-btn secondary', parent: backRow, text: '◀ BACK' }) as HTMLButtonElement;
  backBtn.style.cssText += 'font-size:0.78rem;padding:6px 18px;letter-spacing:0.16em;';
  backBtn.addEventListener('click', () => renderAttract(state));

  // Guest → name keyboard. Built once on first open; revealed by toggling
  // visibility so the follow checkbox above stays readable at submit time.
  let qwertyBuilt = false;
  const showNameView = (): void => {
    void audio.unlockAudio();
    chooser.style.display = 'none';
    nameView.style.display = 'flex';
    if (!qwertyBuilt) {
      qwertyBuilt = true;
      el('h2', { parent: nameView, text: 'ENTER YOUR NAME' });
      const q = renderQwertyName(nameView, {
        maxLen: 25,
        onSubmit: (name) => submitGuest(name),
        onBack: () => { nameView.style.display = 'none'; chooser.style.display = 'flex'; setTimeout(() => tryFocusVisible(guestBtn), 0); },
      });
      // Expose the in-progress name accessor (matches the title-screen pattern).
      titleNamePickerGetName = q.getCurrentName;
    }
    // Focus the first key so a booth pad can start walking the grid immediately.
    setTimeout(() => { const k = nameView.querySelector('button'); if (k) tryFocusVisible(k as HTMLButtonElement); }, 0);
  };
  guestBtn.addEventListener('click', showNameView);

  // Auto-highlight SIGN IN WITH NOSTR so it reads as the default; cursor-down
  // lands on PLAY AS GUEST.
  setTimeout(() => tryFocusVisible(signInBtn), 0);
}


/** Title/attract leaderboard read cap — see the deferral note in
 *  renderGlobalLeaderboard. Scores are replicated across every active relay
 *  (publishScore fans out to all), so the first few primaries surface the same
 *  global top without holding a socket open to all ~7. */
const LEADERBOARD_RELAY_CAP = 3;

function renderGlobalLeaderboard(parent: HTMLElement, state: GameState, board: ScoreBoardId = 'solo'): void {
  const container = el('div', { parent });
  const block = el('div', { className: 'leaderboard-block', parent: container });
  const titleText = board === 'coop-campaign' ? '— GLOBAL CO-OP CAMPAIGN —' : '— GLOBAL HIGH SCORES —';
  el('p', { className: 'leaderboard-title', parent: block, text: titleText });
  const status = el('p', { parent: block, text: 'Listening to relays…' });
  status.style.cssText = 'font-size:0.85rem;color:rgba(180,140,255,0.7);letter-spacing:0.06em;margin:0;';

  // Persistent subscription: scores stream in as relays propagate them and
  // the leaderboard reflects the new state without reloading the title. The
  // 30s cache that fetchGlobalHighScores used to apply is bypassed here on
  // purpose -- we want live updates, not polled snapshots.
  let renderToken = 0;
  let receivedAny = false;
  let cleanupTimer: number | null = null;
  let unsubscribe: (() => void) | null = null;
  let cancelled = false;

  const teardown = (): void => {
    cancelled = true;
    if (unsubscribe) { unsubscribe(); unsubscribe = null; }
    if (cleanupTimer !== null) { clearInterval(cleanupTimer); cleanupTimer = null; }
  };

  // Defer the relay subscription off the first-paint critical path. Subscribing
  // synchronously opens a socket to every active relay the instant the title
  // draws — a no-interaction, multi-host fan-out that reads as beacon-like to
  // network-reputation scanners. The "Listening to relays…" placeholder is
  // already on screen, so idle warm-up fills it a beat later. Reads a capped
  // primary subset too (see LEADERBOARD_RELAY_CAP). Same idiom as the ghost
  // prefetch + deferProfileFetch.
  const startSubscription = (): void => {
    if (cancelled || !container.isConnected) return;
    const relays = getActiveRelays().slice(0, LEADERBOARD_RELAY_CAP);
    unsubscribe = subscribeGlobalHighScores(async raw => {
      if (!container.isConnected) { teardown(); return; }
      receivedAny = true;
      if (raw.length === 0) {
        status.textContent = 'No global scores yet — be the first.';
        return;
      }
      const myToken = ++renderToken;
      const top = raw.slice(0, 5);
      const localHint = state.session?.displayName
        ? { pubkey: state.session.pubkey, displayName: state.session.displayName }
        : null;
      const entries = await Promise.all(top.map(e => resolveDisplayName(e, localHint)));
      if (!container.isConnected || myToken !== renderToken) return;
      container.innerHTML = '';
      renderLeaderboardBlock(container, entries.map(globalToLocal), titleText, 5, () => renderTitle(state));
    }, { board, relays });
  };
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(startSubscription, { timeout: 2500 });
  } else {
    window.setTimeout(startSubscription, 1000);
  }

  // Backstop probe: if the title screen unmounts during a long quiet period
  // the onUpdate-side disconnection check won't fire. Probe every 30s.
  cleanupTimer = window.setInterval(() => {
    if (!container.isConnected) teardown();
  }, 30_000);

  // Empty-state fallback if relays stay silent for 8s -- soften "Listening…"
  // into the same "be the first" hint fetchGlobalHighScores used to show.
  window.setTimeout(() => {
    if (!container.isConnected) return;
    if (!receivedAny && status.isConnected) {
      status.textContent = 'No global scores yet — be the first.';
    }
  }, 8000);
}

async function resolveDisplayName(
  entry: GlobalHighScore,
  localHint?: { pubkey: string; displayName: string } | null,
): Promise<GlobalHighScore & { displayName: string }> {
  const cached = getCachedProfile(entry.pubkey);
  const profile = cached ?? await fetchProfile(entry.pubkey).catch(() => null);
  const hasProfileName = !!(profile?.display_name?.trim() || profile?.name?.trim());
  // Persona parity with guests: if this row is the local player on a persona
  // that shares a handle but has no published kind-0 yet, show that handle
  // rather than a bare pubkey. A real kind-0 still wins (so it matches what
  // Gamestr/other clients resolve) — My Signet publishing a kind-0 per persona
  // is the cross-client fix.
  const localName = localHint && entry.pubkey.toLowerCase() === localHint.pubkey.toLowerCase()
    ? localHint.displayName.trim()
    : '';
  const displayName = hasProfileName ? bestName(profile, entry.pubkey) : (localName || bestName(null, entry.pubkey));
  return { ...entry, displayName };
}

function globalToLocal(entry: GlobalHighScore & { displayName: string }): ReturnType<typeof getLocalHighScores>[number] {
  return {
    name: entry.displayName,
    score: entry.score,
    sats: entry.sats,
    wave: entry.wave,
    at: entry.at,
    pubkey: entry.pubkey,
    eventId: entry.eventId,
  };
}

/**
 * Mode picker — campaign / co-op / drift / bossrush / arena. Shape of the run.
 * Selected mode highlights yellow; unfinished modes (bossrush, arena)
 * still render so the player knows they're coming, but tapping them
 * toasts COMING SOON and the selection reverts to the previous valid
 * mode.
 */
function leaveMultiplayerUrlSessionForMode(mode: RunMode): void {
  if (mode === 'duel' || mode === 'coop-campaign' || mode === 'deathmatch') return;
  try {
    (window as unknown as { __pallasiteExitMultiplayerSession?: () => void }).__pallasiteExitMultiplayerSession?.();
  } catch { /* ignore */ }

  try {
    const url = new URL(window.location.href);
    const params = url.searchParams;
    const multiplayerKeys = [
      'mode', 'coop', 'deathmatch', 'peer', 'session', 'slot', 'spectate',
      'players', 'deathmatchPlayers', 'peerBatch', 'batchFrames', 'aiFill',
      'humanSlots', 'inputDelay', 'wiretrace',
      'deathmatchTime', 'deathmatchKills', 'deathmatchRespawns', 'deathmatchAiSkill',
      'dmTime', 'dmKills', 'dmRespawns', 'dmAiSkill',
      'deathmatchAi', 'autoStart', 'autostart',
    ];
    let changed = false;
    for (const key of multiplayerKeys) {
      if (!params.has(key)) continue;
      params.delete(key);
      changed = true;
    }
    if (changed) {
      const search = params.toString();
      window.history.replaceState(null, '', `${url.pathname}${search ? `?${search}` : ''}${url.hash}`);
    }
  } catch { /* ignore */ }
}

function renderModeRow(parent: HTMLElement, state: GameState): void {
  const wrapper = el('div', { parent });
  wrapper.style.cssText = 'display:flex;flex-direction:column;gap:8px;align-items:center;';
  const label = el('p', { parent: wrapper, text: 'MODE' });
  label.style.cssText = 'font-size:0.8rem;color:rgba(180,140,255,0.85);letter-spacing:0.3em;margin:0;';

  const row = el('div', { parent: wrapper });
  row.style.cssText = 'display:flex;gap:6px;flex-wrap:wrap;justify-content:center;';

  const hint = el('p', { parent: wrapper });
  hint.style.cssText = 'font-size:0.78rem;color:rgba(180,140,255,0.7);letter-spacing:0.06em;margin:0;min-height:1em;text-align:center;';

  const buttons = new Map<RunMode, HTMLButtonElement>();
  const refresh = (): void => {
    const current = getStoredMode();
    for (const info of MODE_LIST) {
      const btn = buttons.get(info.id)!;
      const selected = info.id === current;
      btn.style.cssText = [
        'background:' + (selected ? 'rgba(255,216,74,0.18)' : 'transparent'),
        'border:2px solid ' + (selected ? '#ffd84a' : info.ready ? 'rgba(180,140,255,0.4)' : 'rgba(180,140,255,0.18)'),
        'color:' + (selected ? '#ffd84a' : info.ready ? 'rgba(220,210,255,0.85)' : 'rgba(180,140,255,0.45)'),
        "font-family:'VT323',ui-monospace,monospace",
        'font-size:0.95rem', 'padding:6px 14px',
        'letter-spacing:0.16em', 'cursor:pointer', 'border-radius:6px',
        'transition:all 0.12s ease',
        selected ? 'box-shadow:0 0 14px rgba(255,216,74,0.4);text-shadow:0 0 6px rgba(255,216,74,0.6)' : '',
      ].filter(Boolean).join(';');
    }
    const hintFor = MODE_LIST.find(m => m.id === current)?.hint ?? '';
    hint.textContent = hintFor;
  };

  for (const info of MODE_LIST) {
    const btn = el('button', { parent: row, text: info.label });
    buttons.set(info.id, btn);
    onTap(btn, () => {
      if (!info.ready) {
        hint.textContent = 'COMING SOON';
        return;
      }
      const prev = getStoredMode();
      setStoredMode(info.id);
      lockInMode(info.id);
      leaveMultiplayerUrlSessionForMode(info.id);
      // Re-fit so the renderMode picks up the new mode's camera /
      // background / radar settings (Defender uses landscape follow +
      // parallax bg + forced radar — without a refit those wouldn't
      // engage until the next browser resize event).
      try { (window as unknown as { __pallasiteFit?: () => void }).__pallasiteFit?.(); } catch { /* ignore */ }
      // Toggling in/out of lobby-backed modes changes the IGNITE button
      // label/behaviour and the leaderboard board, so re-render the title
      // rather than keep those separate DOM islands in sync by hand.
      if (prev === 'duel' || prev === 'coop-campaign' || prev === 'deathmatch' || info.id === 'duel' || info.id === 'coop-campaign' || info.id === 'deathmatch') {
        renderTitle(state);
      } else {
        refresh();
      }
    });
    btn.addEventListener('mouseenter', () => { hint.textContent = info.hint; });
    btn.addEventListener('mouseleave', () => {
      hint.textContent = MODE_LIST.find(m => m.id === getStoredMode())?.hint ?? '';
    });
  }
  refresh();
}

function renderDifficultyRow(parent: HTMLElement): void {
  const wrapper = el('div', { parent });
  wrapper.style.cssText = 'display:flex;flex-direction:column;gap:8px;align-items:center;';
  const label = el('p', { parent: wrapper, text: 'DIFFICULTY' });
  label.style.cssText = 'font-size:0.8rem;color:rgba(180,140,255,0.85);letter-spacing:0.3em;margin:0;';

  const row = el('div', { parent: wrapper });
  row.style.cssText = 'display:flex;gap:8px;flex-wrap:wrap;justify-content:center;';

  const opts: Array<{ value: Difficulty; label: string; hint: string }> = [
    { value: 'easy',   label: 'EASY',   hint: '5 lives · slow rocks · sloppy aim' },
    { value: 'normal', label: 'NORMAL', hint: '3 lives · the canonical run' },
    { value: 'hard',   label: 'HARD',   hint: '2 lives · fast rocks · sharp aim' },
  ];

  const hint = el('p', { parent: wrapper });
  hint.style.cssText = 'font-size:0.78rem;color:rgba(180,140,255,0.7);letter-spacing:0.06em;margin:0;min-height:1em;';

  const buttons = new Map<Difficulty, HTMLButtonElement>();
  const refresh = (): void => {
    const current = getStoredDifficulty();
    for (const [d, btn] of buttons) {
      const selected = d === current;
      btn.style.cssText = [
        'background:' + (selected ? 'rgba(255,216,74,0.18)' : 'transparent'),
        'border:2px solid ' + (selected ? '#ffd84a' : 'rgba(180,140,255,0.4)'),
        'color:' + (selected ? '#ffd84a' : 'rgba(220,210,255,0.85)'),
        "font-family:'VT323',ui-monospace,monospace",
        'font-size:1rem', 'padding:6px 16px',
        'letter-spacing:0.18em', 'cursor:pointer', 'border-radius:6px',
        'transition:all 0.12s ease',
        selected ? 'box-shadow:0 0 14px rgba(255,216,74,0.4);text-shadow:0 0 6px rgba(255,216,74,0.6)' : '',
      ].filter(Boolean).join(';');
    }
    const hintFor = opts.find(o => o.value === current)?.hint ?? '';
    hint.textContent = hintFor;
  };

  for (const opt of opts) {
    const btn = el('button', { parent: row, text: opt.label });
    buttons.set(opt.value, btn);
    btn.addEventListener('click', () => {
      setStoredDifficulty(opt.value);
      refresh();
    });
    btn.addEventListener('mouseenter', () => { hint.textContent = opt.hint; });
    btn.addEventListener('mouseleave', () => {
      const cur = opts.find(o => o.value === getStoredDifficulty())?.hint ?? '';
      hint.textContent = cur;
    });
  }
  refresh();
}

function renderDailyRow(parent: HTMLElement): void {
  const wrapper = el('div', { parent });
  wrapper.style.cssText = 'display:flex;flex-direction:column;gap:6px;align-items:center;margin-top:6px;';
  const label = el('p', { parent: wrapper, text: 'RUN MODE' });
  label.style.cssText = 'font-size:0.8rem;color:rgba(180,140,255,0.85);letter-spacing:0.3em;margin:0;';

  const row = el('div', { parent: wrapper });
  row.style.cssText = 'display:flex;gap:8px;flex-wrap:wrap;justify-content:center;';

  const opts: Array<{ value: boolean; label: string; hint: string }> = [
    { value: false, label: 'FREE',  hint: 'Random seeds. No daily lock.' },
    { value: true,  label: 'DAILY', hint: `Same layout for everyone today (${todayUTC()})` },
  ];

  const hint = el('p', { parent: wrapper });
  hint.style.cssText = 'font-size:0.78rem;color:rgba(180,140,255,0.7);letter-spacing:0.06em;margin:0;min-height:1em;';

  const buttons = new Map<boolean, HTMLButtonElement>();
  const refresh = (): void => {
    const current = getStoredDailyPref();
    for (const [v, btn] of buttons) {
      const selected = v === current;
      btn.style.cssText = [
        'background:' + (selected ? 'rgba(91,157,255,0.18)' : 'transparent'),
        'border:2px solid ' + (selected ? '#5b9dff' : 'rgba(180,140,255,0.4)'),
        'color:' + (selected ? '#5b9dff' : 'rgba(220,210,255,0.85)'),
        "font-family:'VT323',ui-monospace,monospace",
        'font-size:0.95rem', 'padding:5px 14px',
        'letter-spacing:0.18em', 'cursor:pointer', 'border-radius:6px',
        'transition:all 0.12s ease',
        selected ? 'box-shadow:0 0 14px rgba(91,157,255,0.4);text-shadow:0 0 6px rgba(91,157,255,0.6)' : '',
      ].filter(Boolean).join(';');
    }
    const hintFor = opts.find(o => o.value === current)?.hint ?? '';
    hint.textContent = hintFor;
  };

  for (const opt of opts) {
    const btn = el('button', { parent: row, text: opt.label });
    buttons.set(opt.value, btn);
    btn.addEventListener('click', () => {
      setStoredDailyPref(opt.value);
      refresh();
    });
    btn.addEventListener('mouseenter', () => { hint.textContent = opt.hint; });
    btn.addEventListener('mouseleave', () => {
      const cur = opts.find(o => o.value === getStoredDailyPref())?.hint ?? '';
      hint.textContent = cur;
    });
  }
  refresh();
  void getActiveSeed;  // keep import bound for cross-references
}






function renderStreakChip(parent: HTMLElement): void {
  const current = getStreak();
  if (current < 1) return;
  const best = getBestStreak();
  const wrap = el('div', { parent });
  wrap.style.cssText = [
    'display:inline-flex', 'gap:14px', 'align-items:center',
    'padding:6px 14px', 'border-radius:999px',
    'background:rgba(255,216,74,0.06)', 'border:1px solid rgba(255,216,74,0.35)',
    'font-size:0.78rem', 'letter-spacing:0.16em',
    'color:rgba(220,210,255,0.75)',
  ].join(';');
  const cur = el('span', { parent: wrap });
  cur.innerHTML = `STREAK <span style="color:#ffd84a;font-weight:bold;">${current}</span>`;
  if (best > current) {
    const bst = el('span', { parent: wrap });
    bst.innerHTML = `BEST <span style="color:rgba(255,216,74,0.6);">${best}</span>`;
  }
}

function renderDailyLeaderChip(parent: HTMLElement): void {
  const wrap = el('div', { parent });
  wrap.style.cssText = [
    'display:flex', 'flex-direction:column', 'align-items:center', 'gap:2px',
    'margin:8px 0 0', 'padding:6px 14px',
    'border:1px solid rgba(180,140,255,0.25)', 'border-radius:8px',
    'min-width:280px', 'text-align:center',
  ].join(';');

  const heading = el('p', { parent: wrap, text: `DAILY ${todayUTC()}` });
  heading.style.cssText = 'font-size:0.7rem;color:rgba(180,140,255,0.7);letter-spacing:0.28em;margin:0;';

  const body = el('p', { parent: wrap, text: 'TODAY IS FRESH · SET THE BAR' });
  body.style.cssText = "font-family:'VT323',ui-monospace,monospace;font-size:1.1rem;color:#ffd84a;letter-spacing:0.16em;margin:0;text-shadow:0 0 6px rgba(255,216,74,0.45);";

  let lastPaintedPubkey: string | null = null;

  function paint(): void {
    const seed = todayUTC();
    const run = getCachedGhost(seed);
    if (!run) return;
    if (run.pubkey === lastPaintedPubkey) return;
    lastPaintedPubkey = run.pubkey;

    // Optimistic stub render now; swap in the real display name async once
    // the kind 0 fetch lands (or hit cache for an instant swap).
    const stub = run.pubkey.slice(0, 8) + '…' + run.pubkey.slice(-4);
    const fmt = (name: string): string => `CHASING ${name} · ${run.score.toLocaleString()}`;
    body.textContent = fmt(stub);
    body.style.color = '#7fffb0';
    body.style.textShadow = '0 0 6px rgba(127,255,176,0.45)';

    const cached = getCachedProfile(run.pubkey);
    if (cached) {
      body.textContent = fmt(bestName(cached, run.pubkey));
    } else {
      void fetchProfile(run.pubkey).then(profile => {
        if (!document.body.contains(wrap)) return;
        if (lastPaintedPubkey !== run.pubkey) return;
        body.textContent = fmt(bestName(profile, run.pubkey));
      }).catch(() => undefined);
    }
  }
  paint();

  // Relay round-trip can land after this render. Poll the cache for ~5s
  // and update once the leader arrives. Bail when the chip leaves the DOM
  // (overlay swap) or after ticks burn.
  let ticks = 0;
  const interval = window.setInterval(() => {
    ticks += 1;
    paint();
    if (getCachedGhost(todayUTC()) || ticks > 12 || !document.body.contains(wrap)) {
      window.clearInterval(interval);
    }
  }, 400);
}

// ── Replay theatre ────────────────────────────────────────────────────────────

interface ReplayTheatreInput {
  scoreEventId: string;
  displayName: string;
  score: number;
  wave: number;
  sats: number;
  onClose: () => void;
}

/**
 * Watch a leaderboard run.
 *
 * Fetches the kind 30763 ghost referencing the supplied kind 30762 score
 * event, then plays it back in a small canvas with score + progress chip.
 * Ghost data carries ship pose (v2) or score-only (v1) -- the playback shows
 * how the player navigated and how their score climbed, not the asteroid
 * field they fought (the ghost stream doesn't carry that).
 *
 * Speed cycles 1x / 2x / 4x. Defaults to 2x. Space toggles looping at the
 * end of a run; Escape and the CLOSE button exit and call `onClose`.
 */
function renderReplayTheatre(input: ReplayTheatreInput): void {
  clearOverlay();
  const overlay = el('div', { className: 'overlay', parent: root });
  setupOverlayArrowNav(overlay);

  el('h2', { parent: overlay, text: 'WATCHING' });

  const nameEl = el('p', { parent: overlay, text: input.displayName.toUpperCase() });
  nameEl.style.cssText = 'margin:6px 0 4px;font-size:1.2rem;letter-spacing:0.18em;color:var(--hud-green);text-shadow:0 0 10px rgba(91,255,140,0.4);';

  const stat = el('p', { parent: overlay });
  stat.style.cssText = 'margin:0 0 14px;font-size:0.92rem;color:rgba(220,210,255,0.8);letter-spacing:0.1em;';
  stat.textContent = `WAVE ${input.wave} · ${input.score.toLocaleString()} SCORE${input.sats > 0 ? ` · ₿ ${input.sats}` : ''}`;

  const CANVAS_W = 600;
  const CANVAS_H = Math.round(CANVAS_W * PALL_WORLD_H / PALL_WORLD_W);
  const canvas = el('canvas', { parent: overlay, attrs: { width: String(CANVAS_W), height: String(CANVAS_H) } });
  canvas.style.cssText = 'border:1px solid rgba(91,157,255,0.4);border-radius:8px;background:#02050d;display:block;margin:0 auto;max-width:100%;';
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    el('p', { parent: overlay, text: 'Canvas unsupported in this browser.' });
    return;
  }

  const status = el('p', { parent: overlay, text: 'Fetching replay…' });
  status.style.cssText = 'margin:10px 0 4px;font-size:0.9rem;color:rgba(180,140,255,0.75);letter-spacing:0.08em;min-height:1.2em;';

  const liveScore = el('p', { parent: overlay });
  liveScore.style.cssText = 'margin:6px 0 0;font-size:1.4rem;letter-spacing:0.18em;color:var(--hud-yellow);text-shadow:0 0 12px rgba(255,216,74,0.5);min-height:1.4em;';

  const progressTrack = el('div', { parent: overlay });
  progressTrack.style.cssText = 'width:600px;max-width:90vw;height:4px;background:rgba(255,255,255,0.08);border-radius:2px;overflow:hidden;margin:10px auto 6px;';
  const progressFill = el('div', { parent: progressTrack });
  progressFill.style.cssText = 'height:100%;width:0%;background:#5b9dff;transition:width 80ms linear;';

  const timeLabel = el('p', { parent: overlay });
  timeLabel.style.cssText = 'margin:0 0 14px;font-size:0.78rem;color:rgba(180,140,255,0.7);letter-spacing:0.08em;min-height:1em;';

  const buttonRow = el('div', { className: 'menu-row', parent: overlay });
  let speed = 2;
  const speedBtn = el('button', { className: 'menu-btn secondary', parent: buttonRow, text: `SPEED ${speed}×` });
  const closeBtn = el('button', { className: 'menu-btn', parent: buttonRow, text: 'CLOSE · ESC' });

  // Stable starfield -- generated once so the background isn't re-randomised
  // every frame.
  const stars: { x: number; y: number; r: number }[] = [];
  for (let i = 0; i < 80; i++) {
    stars.push({ x: Math.random() * CANVAS_W, y: Math.random() * CANVAS_H, r: 0.4 + Math.random() * 1.0 });
  }

  let ghost: GhostRun | null = null;
  let playheadMs = 0;
  let lastFrame = 0;
  let rafId: number | null = null;
  let cancelled = false;
  let looped = false;

  const cleanup = (): void => {
    cancelled = true;
    if (rafId !== null) { cancelAnimationFrame(rafId); rafId = null; }
    window.removeEventListener('keydown', onKey);
  };

  const onKey = (e: KeyboardEvent): void => {
    if (e.code === 'Escape') {
      cleanup();
      input.onClose();
      return;
    }
    if (e.code === 'Space' && ghost) {
      e.preventDefault();
      looped = !looped;
      if (rafId === null) {
        playheadMs = 0;
        lastFrame = 0;
        status.textContent = looped ? 'Looping replay.' : '';
        rafId = requestAnimationFrame(tick);
      } else {
        status.textContent = looped ? 'Looping replay.' : 'Will stop at end.';
      }
    }
  };
  window.addEventListener('keydown', onKey);

  closeBtn.addEventListener('click', () => { cleanup(); input.onClose(); });

  speedBtn.addEventListener('click', () => {
    speed = speed === 1 ? 2 : speed === 2 ? 4 : 1;
    speedBtn.textContent = `SPEED ${speed}×`;
  });

  const formatTime = (ms: number): string => {
    const total = Math.max(0, Math.floor(ms / 1000));
    return `${Math.floor(total / 60)}:${(total % 60).toString().padStart(2, '0')}`;
  };

  const drawFrame = (): void => {
    if (cancelled || !ghost) return;

    ctx.fillStyle = '#02050d';
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
    ctx.fillStyle = 'rgba(180,140,255,0.55)';
    for (const star of stars) {
      ctx.beginPath();
      ctx.arc(star.x, star.y, star.r, 0, Math.PI * 2);
      ctx.fill();
    }

    const sx = CANVAS_W / PALL_WORLD_W;
    const sy = CANVAS_H / PALL_WORLD_H;
    const pose = ghostPoseAt(ghost, playheadMs);
    if (pose && pose.alive) {
      ctx.save();
      ctx.translate(pose.x * sx, pose.y * sy);
      ctx.rotate(pose.rot);
      ctx.scale(0.85, 0.85);
      ctx.lineWidth = 1.6;
      ctx.strokeStyle = '#8ee0ff';
      ctx.shadowColor = '#8ee0ff';
      ctx.shadowBlur = 10;
      ctx.beginPath();
      ctx.moveTo(14, 0);
      ctx.lineTo(-10, 8);
      ctx.lineTo(-6, 0);
      ctx.lineTo(-10, -8);
      ctx.closePath();
      ctx.stroke();
      if (pose.thrusting) {
        ctx.strokeStyle = '#cfeefb';
        ctx.shadowColor = '#cfeefb';
        ctx.beginPath();
        ctx.moveTo(-6, 4);
        ctx.lineTo(-14, 0);
        ctx.lineTo(-6, -4);
        ctx.stroke();
      }
      ctx.restore();
    } else if (pose && !pose.alive) {
      ctx.save();
      ctx.translate(pose.x * sx, pose.y * sy);
      ctx.strokeStyle = '#ff5050';
      ctx.shadowColor = '#ff5050';
      ctx.shadowBlur = 12;
      ctx.lineWidth = 1.4;
      ctx.beginPath();
      ctx.moveTo(-8, -8); ctx.lineTo(8, 8);
      ctx.moveTo(8, -8); ctx.lineTo(-8, 8);
      ctx.stroke();
      ctx.restore();
    }
    // pose === null on v1 (score-only) -- canvas stays starfield.

    liveScore.textContent = ghostScoreAt(ghost, playheadMs).toLocaleString();
    const pct = Math.min(100, Math.max(0, (playheadMs / Math.max(1, ghost.durationMs)) * 100));
    progressFill.style.width = `${pct}%`;
    timeLabel.textContent = `${formatTime(playheadMs)} / ${formatTime(ghost.durationMs)}`;
  };

  const tick = (now: number): void => {
    if (cancelled || !ghost) return;
    if (lastFrame === 0) lastFrame = now;
    const dt = now - lastFrame;
    lastFrame = now;
    playheadMs += dt * speed;
    if (playheadMs >= ghost.durationMs) {
      if (looped) {
        playheadMs = 0;
      } else {
        playheadMs = ghost.durationMs;
        drawFrame();
        status.textContent = 'End of replay. SPACE to loop, CLOSE to exit.';
        rafId = null;
        return;
      }
    }
    drawFrame();
    rafId = requestAnimationFrame(tick);
  };

  void (async () => {
    const result = await fetchGhostByScoreEventId(input.scoreEventId).catch(() => null);
    if (cancelled) return;
    if (!result) {
      status.textContent = 'No replay event found for this run.';
      return;
    }
    ghost = result;
    if (!ghost.poseSamples || ghost.poseSamples.length === 0) {
      status.textContent = 'Score-only replay (v1 ghost · no ship pose).';
    } else {
      const lengthSec = (ghost.durationMs / 1000).toFixed(0);
      status.textContent = `Live · ${lengthSec}s @ ${ghost.fps}Hz pose · SPACE loops, ${speed}× default`;
    }
    rafId = requestAnimationFrame(tick);
  })();
}

// ── Live theatre (NIP-53 kind 30311 stream → kind 22769 frames) ────────────
//
// Pairs with src/stream-session.ts. The watching browser:
//   1. Subscribes to kind 22769 events with #p=<master_pubkey>
//   2. Each arriving frame is parsed for ship pose (x, y, r) +
//      score + wave + frame_t (millisecond timestamp the player
//      captured the pose)
//   3. RAF loop linearly interpolates ship position between the two
//      most-recent frames in the buffer for smooth motion at 60fps
//      even though the wire frequency is ~2 Hz
//   4. HUD shows latest score + wave
//   5. CLOSE returns to the watch grid
//
// Trust model: v1 trusts the relay set to deliver only authentic
// frames (we read from relay.trotters.cc only, controlled by us).
// v2 will verify against the NIP-53 kind 30311 event — fetch the
// stream-key role's pubkey, accept only frames signed by it.

interface LiveTheatreInput {
  masterPubkey: string;
  displayName: string;
  initialScore: number;
  initialWave: number;
  /** Approximate run-start time (unix ms). Used as the `since` filter
   *  when polling for the player's kind 30763 ghost after STREAM ENDED
   *  so the re-watch button can find the right replay. */
  runStartedAtMs: number;
  onClose: () => void;
  /** Rich replay mode — when provided, skips the WS subscription, pre-
   *  seeds the frame buffer with these frames, and drives playback
   *  linearly through the whole timeline at 1x. Used by the kind 30764
   *  "WATCH FROM START" path so spectators see the full world (not just
   *  the pose-only kind 30763 ghost). */
  replaySource?: {
    frames: ReadonlyArray<ReplayFrameRaw>;
    durationMs: number;
    /** Header label shown above the canvas, e.g. "REPLAY · WATCHING". */
    headerLabel?: string;
    /** kind 30764 event id — used to build a `watch.pallasite.app/
     *  #replay=<id>` deep-link from the SHARE button. */
    eventId?: string;
  };
}

export interface LiveAsteroid {
  id: number;
  x: number;
  y: number;
  size: 'l' | 'm' | 's';
  type: 's' | 'i' | 'c' | 'p';
  rot: number;
}
export interface LiveUfo {
  id: number;
  x: number;
  y: number;
  type: 's' | 'p' | 't' | 'e' | 'c' | 'b';
  /** Current HP. Drives tank's bottom HP dots and boss's segmented HP
   *  bar. Defaults to 1 for old clients that don't ship the field. */
  hp: number;
}
export interface LiveMine { id: number; x: number; y: number; }
export interface LiveBullet { id: number; x: number; y: number; vx: number; vy: number; enemy: boolean; }
export interface LiveCoin { id: number; x: number; y: number; kind: 's' | 'd'; sourceType: 's' | 'i' | 'c' | 'p' | ''; }
export interface LivePowerup { id: number; x: number; y: number; type: 'r' | 'b' | 'n' | 't' | 'm'; }
export type LiveEventCode = 'ak' | 'uk' | 'md' | 'sh' | 'sb' | 'vc' | 'pu' | 'fi';
export interface LiveSfxEvent { code: LiveEventCode; x: number; y: number; }

export interface LiveFrame {
  /** When the player captured this frame (unix ms). */
  capturedAt: number;
  /** When this client received the frame from the relay (perf ms). */
  receivedAt: number;
  x: number;
  y: number;
  r: number;
  score: number;
  wave: number;
  lives: number;
  sats: number;
  thrust: boolean;
  alive: boolean;
  shielded: boolean;
  paused: boolean;
  /** Game phase — drives the watcher's incoming-wave banner during
   *  warp and similar overlays. Empty string for old wire payloads
   *  without the field. */
  phase: string;
  /** Wave the player is jumping to during 'warp' (0 when not warping). */
  nextWave: number;
  /** Ship skin code — drives the watcher ship palette. 'd' default,
   *  'i' ironclad orange, 'h' halo cyan. */
  skin: 'd' | 'i' | 'h';
  asteroids: LiveAsteroid[];
  ufos: LiveUfo[];
  mines: LiveMine[];
  bullets: LiveBullet[];
  coins: LiveCoin[];
  powerups: LivePowerup[];
  events: LiveSfxEvent[];
}

const KNOWN_EVENT_CODES: ReadonlySet<LiveEventCode> = new Set([
  'ak', 'uk', 'md', 'sh', 'sb', 'vc', 'pu', 'fi',
]);
function parseEvents(raw: unknown): LiveSfxEvent[] {
  if (!Array.isArray(raw)) return [];
  const out: LiveSfxEvent[] = [];
  for (const item of raw) {
    if (!Array.isArray(item) || item.length < 3) continue;
    const [code, x, y] = item;
    if (typeof code !== 'string' || typeof x !== 'number' || typeof y !== 'number') continue;
    if (!KNOWN_EVENT_CODES.has(code as LiveEventCode)) continue;
    out.push({ code: code as LiveEventCode, x, y });
  }
  return out;
}

function parseAsteroids(raw: unknown): LiveAsteroid[] {
  if (!Array.isArray(raw)) return [];
  const out: LiveAsteroid[] = [];
  for (const item of raw) {
    if (!Array.isArray(item) || item.length < 6) continue;
    const [id, x, y, size, type, rot] = item;
    if (typeof id !== 'number' || typeof x !== 'number' || typeof y !== 'number' || typeof rot !== 'number') continue;
    if (size !== 'l' && size !== 'm' && size !== 's') continue;
    if (type !== 's' && type !== 'i' && type !== 'c' && type !== 'p') continue;
    out.push({ id, x, y, size, type, rot });
  }
  return out;
}
function parseUfos(raw: unknown): LiveUfo[] {
  if (!Array.isArray(raw)) return [];
  const out: LiveUfo[] = [];
  for (const item of raw) {
    if (!Array.isArray(item) || item.length < 4) continue;
    const [id, x, y, type, hp] = item;
    if (typeof id !== 'number' || typeof x !== 'number' || typeof y !== 'number') continue;
    if (type !== 's' && type !== 'p' && type !== 't' && type !== 'e' && type !== 'c' && type !== 'b') continue;
    out.push({ id, x, y, type, hp: typeof hp === 'number' ? hp : 1 });
  }
  return out;
}
function parseMines(raw: unknown): LiveMine[] {
  if (!Array.isArray(raw)) return [];
  const out: LiveMine[] = [];
  for (const item of raw) {
    if (!Array.isArray(item) || item.length < 3) continue;
    const [id, x, y] = item;
    if (typeof id !== 'number' || typeof x !== 'number' || typeof y !== 'number') continue;
    out.push({ id, x, y });
  }
  return out;
}
function parseCoins(raw: unknown): LiveCoin[] {
  if (!Array.isArray(raw)) return [];
  const out: LiveCoin[] = [];
  for (const item of raw) {
    if (!Array.isArray(item) || item.length < 4) continue;
    const [id, x, y, kind, sourceType] = item;
    if (typeof id !== 'number' || typeof x !== 'number' || typeof y !== 'number') continue;
    if (kind !== 's' && kind !== 'd') continue;
    const src = (sourceType === 's' || sourceType === 'i' || sourceType === 'c' || sourceType === 'p') ? sourceType : '';
    out.push({ id, x, y, kind, sourceType: src as LiveCoin['sourceType'] });
  }
  return out;
}
function parsePowerups(raw: unknown): LivePowerup[] {
  if (!Array.isArray(raw)) return [];
  const out: LivePowerup[] = [];
  for (const item of raw) {
    if (!Array.isArray(item) || item.length < 4) continue;
    const [id, x, y, type] = item;
    if (typeof id !== 'number' || typeof x !== 'number' || typeof y !== 'number') continue;
    if (type !== 'r' && type !== 'b' && type !== 'n' && type !== 't' && type !== 'm') continue;
    out.push({ id, x, y, type });
  }
  return out;
}
function parseBullets(raw: unknown): LiveBullet[] {
  if (!Array.isArray(raw)) return [];
  const out: LiveBullet[] = [];
  for (const item of raw) {
    if (!Array.isArray(item) || item.length < 4) continue;
    // v2g wire shape: [id, x, y, vx, vy, enemy]. v2b/v2c/v2d/v2e/v2f used
    // [id, x, y, enemy] (no velocity). Accept both so a viewer can still
    // read older relayed events without crashing.
    let id: unknown, x: unknown, y: unknown, vx: unknown, vy: unknown, enemy: unknown;
    if (item.length >= 6) {
      [id, x, y, vx, vy, enemy] = item;
    } else {
      [id, x, y, enemy] = item;
      vx = 0;
      vy = 0;
    }
    if (typeof id !== 'number' || typeof x !== 'number' || typeof y !== 'number') continue;
    const vxn = typeof vx === 'number' ? vx : 0;
    const vyn = typeof vy === 'number' ? vy : 0;
    out.push({ id, x, y, vx: vxn, vy: vyn, enemy: enemy === 1 });
  }
  return out;
}

/** Convert a raw WS frame (the ReplayFrameRaw shape published by
 *  stream-session.ts → publishStreamFrameWs) into the parsed LiveFrame
 *  shape the renderer uses. The wire delivers the same world payload
 *  as kind 22769 used to — we just skip the Nostr envelope. */
function readWsFrame(obj: Record<string, unknown>): LiveFrame | null {
  const t = typeof obj.t === 'number' ? obj.t : null;
  const x = typeof obj.x === 'number' ? obj.x : null;
  const y = typeof obj.y === 'number' ? obj.y : null;
  const r = typeof obj.r === 'number' ? obj.r : null;
  if (t === null || x === null || y === null || r === null) return null;
  const world = (typeof obj.world === 'object' && obj.world) ? obj.world as Record<string, unknown> : {};
  return {
    capturedAt: t,
    receivedAt: performance.now(),
    x, y, r,
    score: typeof obj.score === 'number' ? obj.score : 0,
    wave: typeof obj.wave === 'number' ? obj.wave : 0,
    lives: typeof obj.lives === 'number' ? obj.lives : 0,
    sats: typeof obj.sats === 'number' ? obj.sats : 0,
    thrust: obj.thrust === true,
    alive: obj.alive !== false,
    shielded: obj.shielded === true,
    paused: obj.paused === true,
    phase: typeof world.ph === 'string' ? world.ph : '',
    nextWave: typeof world.nw === 'number' ? world.nw : 0,
    skin: (world.sk === 'i' || world.sk === 'h' ? world.sk : 'd') as 'd' | 'i' | 'h',
    asteroids: parseAsteroids(world.a),
    ufos: parseUfos(world.u),
    mines: parseMines(world.m),
    bullets: parseBullets(world.b),
    coins: parseCoins(world.c),
    powerups: parsePowerups(world.pu),
    events: parseEvents(world.e),
  };
}

export function renderLiveTheatre(input: LiveTheatreInput): void {
  clearOverlay();
  const overlay = el('div', { className: 'overlay', parent: root });
  setupOverlayArrowNav(overlay);

  // Opening the theatre counts as a user gesture (came from a click on
  // a watch-grid card or mini-tile), so we can safely unlock the audio
  // context here. Without this, a viewer who landed straight on the
  // watch page never hit the title-screen audio unlock and replay/live
  // SFX + music get silently dropped by the browser's autoplay policy.
  void audio.unlockAudio().catch(() => undefined);
  audio.resumePlayback();

  // Replay mode (kind 30764 full-world bundle) reuses this theatre but
  // skips the WS subscription, pre-seeds the frame buffer with the whole
  // run, and advances playbackT linearly across the whole timeline.
  // The watch origin keeps its own localStorage, so a first-time watcher
  // has no tier preference. Default them to the 3D showcase; a returning
  // watcher's explicit choice is kept (hasStoredVisualStyle gates this).
  if (!hasStoredVisualStyle()) setAllVisualStyles('mesh');

  const replayMode = !!input.replaySource;
  const headerLabel = input.replaySource?.headerLabel ?? (replayMode ? 'REPLAY · WATCHING' : 'LIVE · WATCHING');
  el('h2', { parent: overlay, text: headerLabel });

  const nameEl = el('p', { parent: overlay, text: input.displayName.toUpperCase() });
  nameEl.style.cssText = 'margin:6px 0 14px;font-size:1.2rem;letter-spacing:0.18em;color:#8cffb4;text-shadow:0 0 10px rgba(140,255,180,0.5);';
  // Profile-resolve the name asynchronously — the caller may have
  // passed in a shortPubkey fallback (e.g. when the theatre opens
  // before the watch card's own fetchProfile resolved). Replace with
  // the kind 0 display name once available so the header reads e.g.
  // 'THE CRYPTO DONKEY' instead of 'DA19F1CD…E5BD'.
  if (/^[0-9a-f]{64}$/i.test(input.masterPubkey)) {
    void (async () => {
      try {
        const profile = await fetchProfile(input.masterPubkey);
        const resolved = bestName(profile, input.masterPubkey);
        if (resolved && resolved !== input.masterPubkey) {
          nameEl.textContent = resolved.toUpperCase();
        }
      } catch { /* keep the shortPubkey fallback */ }
    })();
  }
  // The previous 'stat' line duplicated liveScore below — initialScore
  // was sticky from the entry's kind 30762 heartbeat (often wave=1
  // score=0 for a freshly-started run) while liveScore updates from
  // the frame stream. They'd disagree once the frames started arriving
  // (header shows '0' while the canvas HUD shows 11,705) which was
  // confusing. Removed; liveScore is canonical.

  // Sized to viewport. We pick the larger of "stay within the viewport
  // padding" and a min width so the canvas feels like a real watch
  // screen on desktop. Internal resolution scales with DPR so the ship
  // and HUD are crisp on retina displays (the v1 fixed 600×375
  // canvas displayed at 2x looked blurry).
  const cssWidthMax = Math.min(window.innerWidth - 32, 980);
  const cssWidth = Math.max(360, cssWidthMax);
  const cssHeight = Math.round(cssWidth * PALL_WORLD_H / PALL_WORLD_W);
  const dpr = Math.max(1, Math.min(3, window.devicePixelRatio || 1));
  // The theatre drives the shared render() in the game's retro mode. The
  // canvas backing store is the native game resolution scaled by dpr, and
  // CSS scales it to the viewport-fit display size. The canvas sits in a
  // positioned `stage` wrapper so the borrowed #game3d overlay can
  // composite over it for the mesh tier (see the render setup below).
  const CANVAS_W = Math.round(PALL_WORLD_W * dpr);
  const CANVAS_H = Math.round(PALL_WORLD_H * dpr);
  const stage = el('div', { parent: overlay });
  stage.style.cssText = `position:relative;width:${cssWidth}px;height:${cssHeight}px;margin:0 auto;max-width:100%;`;
  const canvas = el('canvas', { parent: stage, attrs: { width: String(CANVAS_W), height: String(CANVAS_H) } });
  canvas.style.cssText = `border:1px solid rgba(140,255,180,0.45);border-radius:8px;background:#02050d;display:block;width:${cssWidth}px;height:${cssHeight}px;max-width:100%;box-shadow:0 0 18px rgba(140,255,180,0.18);`;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    el('p', { parent: overlay, text: 'Canvas unsupported in this browser.' });
    return;
  }
  // Match the device pixel ratio so 1 game unit becomes one CSS pixel
  // after the implicit canvas → screen scale. The render loop also
  // multiplies world coordinates by sx/sy (canvas-internal/world)
  // which already accounts for both DPR and aspect.
  void ctx;

  // Visual-tier toggle — vector / shaded / 3D. render() reads the tier
  // live each frame, so a click switches the theatre on the next tick.
  // setAllVisualStyles persists the choice to the watch origin.
  const tierRow = el('div', { parent: overlay });
  tierRow.style.cssText = 'display:flex;justify-content:center;gap:6px;margin:10px auto 0;flex-wrap:wrap;';
  const TIERS: ReadonlyArray<readonly [VisualTier, string]> = [
    ['vector', 'VECTOR'], ['shaded', 'SHADED'], ['mesh', '3D'],
  ];
  const tierBtns: Array<[VisualTier, HTMLButtonElement]> = [];
  const syncTierBtns = (): void => {
    for (const [tier, b] of tierBtns) {
      const on = visualStyleIsUniform(tier);
      b.style.opacity = on ? '1' : '0.55';
      b.style.borderColor = on ? '#8cffb4' : 'rgba(140,255,180,0.3)';
    }
  };
  for (const [tier, label] of TIERS) {
    const b = el('button', { className: 'menu-btn secondary', parent: tierRow, text: label }) as HTMLButtonElement;
    b.style.cssText += 'flex:0 0 auto;padding:6px 16px;font-size:0.78rem;letter-spacing:0.1em;';
    b.addEventListener('click', () => { setAllVisualStyles(tier); syncTierBtns(); });
    tierBtns.push([tier, b]);
  }
  syncTierBtns();

  // Theme toggle — postfx presentation themes. applyPostFx in the tick
  // reads getTheme() live, so a click restyles the theatre next frame.
  // setTheme persists to the watch origin, same as the tier row.
  const themeRow = el('div', { parent: overlay });
  themeRow.style.cssText = 'display:flex;justify-content:center;gap:6px;margin:6px auto 0;flex-wrap:wrap;';
  const themeBtns: Array<[ThemeId, HTMLButtonElement]> = [];
  const syncThemeBtns = (): void => {
    const active = getTheme();
    for (const [id, b] of themeBtns) {
      const on = id === active;
      b.style.opacity = on ? '1' : '0.55';
      b.style.borderColor = on ? '#8cffb4' : 'rgba(140,255,180,0.3)';
    }
  };
  for (const themeInfo of THEMES) {
    const b = el('button', { className: 'menu-btn secondary', parent: themeRow, text: themeInfo.label }) as HTMLButtonElement;
    b.style.cssText += 'flex:0 0 auto;padding:6px 14px;font-size:0.72rem;letter-spacing:0.08em;';
    b.addEventListener('click', () => { setTheme(themeInfo.id); syncThemeBtns(); });
    themeBtns.push([themeInfo.id, b]);
  }
  syncThemeBtns();

  const status = el('p', { parent: overlay, text: 'Subscribing to relay…' });
  status.style.cssText = 'margin:10px 0 4px;font-size:0.9rem;color:rgba(140,255,180,0.8);letter-spacing:0.08em;min-height:1.2em;';

  const liveScore = el('p', { parent: overlay });
  liveScore.style.cssText = 'margin:6px 0 0;font-size:1.4rem;letter-spacing:0.18em;color:var(--hud-yellow);text-shadow:0 0 12px rgba(255,216,74,0.5);min-height:1.4em;';
  // Same "waiting on first frame" guard as the stat line so the HUD
  // doesn't shout "WAVE 0 · 0" before any data arrives.
  if (input.initialWave > 0 || input.initialScore > 0) {
    liveScore.textContent = `WAVE ${input.initialWave} · ${input.initialScore.toLocaleString()}`;
  } else {
    liveScore.textContent = '';
  }
  // Lives + sats sub-line — separate row so it can be styled smaller
  // and update independently of the big score readout.
  const liveStats = el('p', { parent: overlay });
  liveStats.style.cssText = 'margin:2px 0 0;font-size:0.95rem;letter-spacing:0.16em;color:rgba(220,210,255,0.78);min-height:1.2em;';
  liveStats.textContent = '';

  // Replay controls — scrub bar + play/pause + speed. Only visible in
  // replay mode; live mode hides this row entirely. State (speed,
  // paused) is captured by closure and read by the playback tick.
  let replaySpeed = 1;
  let replayPaused = false;
  const replayCtl = el('div', { parent: overlay });
  replayCtl.style.cssText = 'display:none;margin:10px auto 0;max-width:760px;width:100%;flex-direction:column;gap:6px;';
  if (replayMode) replayCtl.style.display = 'flex';

  const timeRow = el('div', { parent: replayCtl });
  timeRow.style.cssText = 'display:flex;align-items:center;gap:8px;font-family:monospace;font-size:0.78rem;color:rgba(220,210,255,0.78);';
  const playToggle = el('button', { className: 'menu-btn secondary', parent: timeRow, text: '⏸' }) as HTMLButtonElement;
  playToggle.style.cssText += 'flex:0 0 44px;padding:4px 0;';
  const restartBtn = el('button', { className: 'menu-btn secondary', parent: timeRow, text: '⏮' }) as HTMLButtonElement;
  restartBtn.style.cssText += 'flex:0 0 44px;padding:4px 0;';
  const timeLabel = el('span', { parent: timeRow, text: '0:00 / 0:00' });
  timeLabel.style.cssText = 'min-width:90px;text-align:center;color:#a8eecf;letter-spacing:0.06em;';
  const scrub = el('input', { parent: timeRow }) as HTMLInputElement;
  scrub.type = 'range';
  scrub.min = '0';
  scrub.max = '1000';
  scrub.step = '1';
  scrub.value = '0';
  scrub.style.cssText = 'flex:1;accent-color:#8cffb4;';

  // Playhead info — wave/score/lives/sats at the current frame. Updates
  // every tick as playback advances or the user scrubs. Sits between
  // the time row and speed row so it tracks naturally with the scrub
  // slider above it.
  const playInfo = el('div', { parent: replayCtl });
  playInfo.style.cssText = 'display:flex;justify-content:center;gap:14px;font-family:monospace;font-size:0.78rem;color:rgba(220,210,255,0.7);letter-spacing:0.08em;min-height:1.2em;';
  playInfo.textContent = '';

  const speedRow = el('div', { parent: replayCtl });
  speedRow.style.cssText = 'display:flex;justify-content:center;align-items:center;gap:4px;font-family:monospace;font-size:0.78rem;flex-wrap:wrap;';

  // JUMP TO DEATH — scan replay frames for the final 'sh' (ship-hit)
  // SFX event, seek to ~2s before it. Surfaced only in replay mode +
  // when at least one 'sh' event exists. Set up later (after `frames`
  // is seeded) since the seed loop runs further down.
  let deathSeekBtn: HTMLButtonElement | null = null;
  if (replayMode) {
    deathSeekBtn = el('button', { className: 'menu-btn secondary', parent: speedRow, text: '💀 LAST DEATH' }) as HTMLButtonElement;
    deathSeekBtn.style.cssText += 'flex:0 0 auto;padding:4px 12px;font-size:0.78rem;color:#ffb0b0;border-color:rgba(255,120,120,0.5);margin-left:14px;display:none;';
    deathSeekBtn.title = 'Seek to ~2s before the final ship-hit event.';
  }

  if (replayMode) {
    const help = el('div', { parent: replayCtl });
    help.style.cssText = 'text-align:center;font-family:monospace;font-size:0.7rem;color:rgba(220,210,255,0.45);letter-spacing:0.06em;';
    help.textContent = 'SPACE pause · J/L ±5% · ,/. step · [/] speed · 0-9 jump · HOME restart';
  }
  const SPEEDS = [0.5, 1, 2, 4];
  const speedBtns: HTMLButtonElement[] = [];
  for (const sp of SPEEDS) {
    const b = el('button', { className: 'menu-btn secondary', parent: speedRow, text: `${sp}×` }) as HTMLButtonElement;
    b.style.cssText += 'flex:0 0 60px;padding:4px 0;font-size:0.78rem;';
    if (sp === 1) {
      b.style.background = 'rgba(140,255,180,0.20)';
      b.style.color = '#8cffb4';
    }
    b.addEventListener('click', () => {
      replaySpeed = sp;
      for (let i = 0; i < SPEEDS.length; i++) {
        const active = SPEEDS[i] === sp;
        speedBtns[i].style.background = active ? 'rgba(140,255,180,0.20)' : '';
        speedBtns[i].style.color = active ? '#8cffb4' : '';
      }
    });
    speedBtns.push(b);
  }
  // SHARE — only meaningful in replay mode with a known event id.
  // Copies a `watch.pallasite.app/#replay=<id>` URL so the recipient
  // opens the same rich replay in one click (Nostr resolution happens
  // client-side; no server lookup).
  const shareEventId = input.replaySource?.eventId;
  if (replayMode && shareEventId) {
    const shareBtn = el('button', { className: 'menu-btn secondary', parent: speedRow, text: 'COPY LINK' }) as HTMLButtonElement;
    shareBtn.style.cssText += 'flex:0 0 110px;padding:4px 0;font-size:0.78rem;color:#cbb6ff;border-color:rgba(184,144,255,0.55);margin-left:14px;';
    shareBtn.addEventListener('click', () => {
      const url = `https://watch.pallasite.app/#replay=${shareEventId}`;
      const restore = (): void => { setTimeout(() => { shareBtn.textContent = 'COPY LINK'; }, 1400); };
      void (async () => {
        try {
          if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(url);
            shareBtn.textContent = 'COPIED';
            restore();
            return;
          }
        } catch { /* fall through to manual copy */ }
        // Fallback: select a hidden textarea so iOS Safari without
        // Clipboard permissions still surfaces the URL for the user
        // to long-press-copy. Cheap to ship; rarely hit on modern UAs.
        window.prompt('Copy the replay link:', url);
        shareBtn.textContent = 'COPIED';
        restore();
      })();
    });
  }

  const closeRow = el('div', { className: 'menu-row', parent: overlay });
  // "WATCH FROM START" — only shown once STREAM ENDED fires. Polls
  // for the player's kind 30763 ghost (published at claim time) and
  // hands off to renderReplayTheatre when found. The button sits in
  // the same row as CLOSE so spectators see a single clean choice
  // row after the stream ends.
  const replayBtn = el('button', { className: 'menu-btn', parent: closeRow, text: 'WATCH FROM START' }) as HTMLButtonElement;
  replayBtn.style.display = 'none';
  const closeBtn = el('button', { className: 'menu-btn secondary', parent: closeRow, text: 'CLOSE · ESC' });

  playToggle.addEventListener('click', () => {
    replayPaused = !replayPaused;
    playToggle.textContent = replayPaused ? '▶' : '⏸';
  });
  restartBtn.addEventListener('click', () => {
    if (frames.length === 0) return;
    playbackT = frames[0].capturedAt;
    // Re-queue all SFX events so the run replays end-to-end with sound.
    pendingEvents.length = 0;
    for (const f of frames) {
      for (const ev of f.events) pendingEvents.push({ code: ev.code, x: ev.x, y: ev.y, dueAt: f.capturedAt });
    }
    particles.length = 0;
    debris.length = 0;
  });
  // Scrub bar — slider value 0..1000 maps to playbackT across the run.
  // While dragging we update playbackT directly and freeze the scrub
  // updates the tick loop would otherwise do.
  let scrubbing = false;
  const seekTo = (frac: number): void => {
    if (frames.length === 0) return;
    const span = frames[frames.length - 1].capturedAt - frames[0].capturedAt;
    playbackT = frames[0].capturedAt + span * frac;
    // Drop SFX events past the playhead to avoid a burst if the user
    // skipped forward; collect future events again so audio resumes.
    pendingEvents.length = 0;
    for (const f of frames) {
      if (f.capturedAt < playbackT) continue;
      for (const ev of f.events) pendingEvents.push({ code: ev.code, x: ev.x, y: ev.y, dueAt: f.capturedAt });
    }
    particles.length = 0;
    debris.length = 0;
  };
  scrub.addEventListener('pointerdown', () => { scrubbing = true; });
  scrub.addEventListener('pointerup', () => { scrubbing = false; });
  scrub.addEventListener('input', () => { seekTo(parseInt(scrub.value, 10) / 1000); });
  const replayHint = el('p', { parent: overlay });
  replayHint.style.cssText = 'margin:6px 0 0;font-size:0.78rem;color:rgba(180,140,255,0.65);letter-spacing:0.08em;min-height:1em;text-align:center;';

  // Replay button state machine: idle → loading → (success | not_yet).
  // Auto-tries once on STREAM ENDED so a recently-claimed run replays
  // with no extra click. If first attempt fails, leaves a retry button.
  let replayState: 'hidden' | 'idle' | 'loading' | 'not_yet' | 'success' = 'hidden';
  let replayAutoTried = false;
  const setReplayState = (next: typeof replayState, hint = ''): void => {
    replayState = next;
    switch (next) {
      case 'hidden':
        replayBtn.style.display = 'none';
        replayHint.textContent = '';
        return;
      case 'idle':
        replayBtn.style.display = '';
        replayBtn.disabled = false;
        replayBtn.textContent = 'WATCH FROM START';
        replayHint.textContent = hint;
        return;
      case 'loading':
        replayBtn.style.display = '';
        replayBtn.disabled = true;
        replayBtn.textContent = 'LOADING…';
        replayHint.textContent = 'Fetching the ghost recording from relays…';
        return;
      case 'not_yet':
        replayBtn.style.display = '';
        replayBtn.disabled = false;
        replayBtn.textContent = 'TRY AGAIN';
        replayHint.textContent = 'No replay event found yet. Player may still be claiming, or their signer rejected the publish — check the player\'s console for [replay] lines.';
        return;
      case 'success':
        replayBtn.style.display = 'none';
        replayHint.textContent = '';
        return;
    }
  };
  const tryFetchReplay = async (): Promise<void> => {
    if (replayState === 'loading') return;
    setReplayState('loading');
    const sinceSec = Math.max(0, Math.floor(input.runStartedAtMs / 1000));
    console.log(`[replay] tryFetchReplay pubkey=${input.masterPubkey.slice(0, 8)}… since=${sinceSec}`);
    // 1) Rich kind 30764 replay — preferred. Carries the full world the
    // player saw (asteroids, UFOs, bullets, coins, SFX events). Played
    // back through this same theatre with replaySource set.
    let richReplay: Awaited<ReturnType<typeof findReplayByAuthor>> = null;
    try {
      richReplay = await findReplayByAuthor(input.masterPubkey, sinceSec);
    } catch (err) {
      console.warn('[replay] findReplayByAuthor threw:', err);
    }
    if (cancelled) return;
    if (richReplay && richReplay.frames.length >= 2) {
      setReplayState('success');
      cleanup();
      renderLiveTheatre({
        masterPubkey: input.masterPubkey,
        displayName: input.displayName,
        initialScore: richReplay.score,
        initialWave: richReplay.wave,
        runStartedAtMs: input.runStartedAtMs,
        onClose: input.onClose,
        replaySource: {
          frames: richReplay.frames,
          durationMs: richReplay.durationMs,
          headerLabel: 'REPLAY · WATCHING',
          eventId: richReplay.eventId,
        },
      });
      return;
    }
    // 2) Fallback: pose-only kind 30763 ghost. The legacy renderReplayTheatre
    // handles this — score chip + ship trajectory only, no world.
    let scoreEventId: string | null = null;
    try {
      scoreEventId = await findScoreIdForLatestGhost(input.masterPubkey, sinceSec);
    } catch { /* fall through to not_yet */ }
    if (cancelled) return;
    if (!scoreEventId) {
      setReplayState('not_yet');
      return;
    }
    setReplayState('success');
    const latest = frames[frames.length - 1];
    const finalScore = latest?.score ?? input.initialScore;
    const finalWave = latest?.wave ?? input.initialWave;
    cleanup();
    renderReplayTheatre({
      scoreEventId,
      displayName: input.displayName,
      score: finalScore,
      wave: finalWave,
      sats: 0,
      onClose: input.onClose,
    });
  };
  replayBtn.addEventListener('click', () => { void tryFetchReplay(); });

  // Wave assets — preload the most recent wave's background image so
  // the canvas can paint it once it's decoded. Music comes from the
  // shared music.ts pipeline, keyed on a synthetic playing-phase state
  // so musicSetTrackForState resolves the same wave-band track the
  // player is hearing.
  let currentWaveAsset = 0;
  const bgImage = new Image();
  bgImage.decoding = 'async';
  // Wave assets — switch the background image and music track when the
  // player crosses a wave boundary. The mid-screen INCOMING banner
  // (rendered above, during 'warp' phase) is the only wave-intro cue
  // the spectator gets; the previous duplicate top-banner that fired on
  // wave-increment was redundant and read as visual noise after the
  // INCOMING bookend.
  const applyWaveAssets = (wave: number): void => {
    if (wave === currentWaveAsset || wave < 1) return;
    currentWaveAsset = wave;
    preloadBackground(wave);
    bgImage.src = `/backgrounds/wave-${wave}.webp`;
    // Synthetic state for music — only the fields trackForState reads
    // need values. Keep this minimal so we don't accidentally trip
    // anything else through musicSetTrackForState.
    try {
      musicSetTrackForState({ phase: 'playing', wave } as unknown as GameState);
    } catch { /* ignore */ }
  };
  applyWaveAssets(Math.max(1, input.initialWave));

  // Stable starfield — generated once. Used as a layer ABOVE the
  // wave background image so the canvas still has motion-cues when
  // the bg is solid colour or hasn't loaded yet.
  const stars: { x: number; y: number; r: number }[] = [];
  for (let i = 0; i < 80; i++) {
    stars.push({ x: Math.random() * CANVAS_W, y: Math.random() * CANVAS_H, r: 0.6 + Math.random() * 1.4 });
  }

  // Ring buffer of recent frames. Keep ~6s worth so a slow update path
  // can still interpolate without skipping.
  const FRAME_BUFFER_MAX = 24;
  const frames: LiveFrame[] = [];
  let rafId: number | null = null;
  let cancelled = false;
  let autoReturnTimer: number | null = null;
  const sockets: WebSocket[] = [];

  const cleanup = (): void => {
    cancelled = true;
    if (rafId !== null) { cancelAnimationFrame(rafId); rafId = null; }
    if (watchdog !== null) { window.clearTimeout(watchdog); watchdog = null; }
    if (autoReturnTimer !== null) { window.clearTimeout(autoReturnTimer); autoReturnTimer = null; }
    for (const ws of sockets) { try { ws.close(); } catch { /* ignore */ } }
    window.removeEventListener('keydown', onKey);
    // Hand #game3d back to the game and let the watch-page loop resume.
    if (g3d && g3dHome) {
      g3d.width = g3dHome.w;
      g3d.height = g3dHome.h;
      g3d.style.cssText = g3dHome.css;
      if (g3dHome.next && g3dHome.parent) g3dHome.parent.insertBefore(g3d, g3dHome.next);
      else if (g3dHome.parent) g3dHome.parent.appendChild(g3d);
    }
    delete document.body.dataset.theatre;
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.code === 'Escape') { cleanup(); input.onClose(); return; }
    if (!replayMode) return;
    // Replay-mode keyboard shortcuts — modelled on YouTube + standard
    // media-player conventions. All edits go through the same scrub /
    // pause / speed paths the buttons drive, so behaviour stays
    // consistent regardless of input.
    const tag = (e.target as HTMLElement | null)?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA') return;  // don't hijack form fields
    const seekFracBy = (delta: number): void => {
      if (frames.length === 0) return;
      const span = frames[frames.length - 1].capturedAt - frames[0].capturedAt;
      if (span <= 0) return;
      const cur = (playbackT - frames[0].capturedAt) / span;
      const next = Math.max(0, Math.min(1, cur + delta));
      seekTo(next);
      scrub.value = String(Math.round(next * 1000));
    };
    const setSpeed = (sp: number): void => {
      replaySpeed = sp;
      for (let i = 0; i < SPEEDS.length; i++) {
        const active = SPEEDS[i] === sp;
        speedBtns[i].style.background = active ? 'rgba(140,255,180,0.20)' : '';
        speedBtns[i].style.color = active ? '#8cffb4' : '';
      }
    };
    switch (e.code) {
      case 'Space':
      case 'KeyK':
        e.preventDefault();
        replayPaused = !replayPaused;
        playToggle.textContent = replayPaused ? '▶' : '⏸';
        return;
      case 'KeyJ':
        e.preventDefault();
        seekFracBy(-0.05);  // ~5% back
        return;
      case 'KeyL':
        e.preventDefault();
        seekFracBy(0.05);
        return;
      case 'Comma':
        // Step back one wire frame (~16ms at 60Hz publish, ~33ms in
        // the subsampled buffer). Easiest is to find the frame just
        // before playbackT and seek to it.
        e.preventDefault();
        for (let i = frames.length - 1; i >= 0; i--) {
          if (frames[i].capturedAt < playbackT - 1) {
            seekTo((frames[i].capturedAt - frames[0].capturedAt) / Math.max(1, frames[frames.length - 1].capturedAt - frames[0].capturedAt));
            return;
          }
        }
        return;
      case 'Period':
        e.preventDefault();
        for (const f of frames) {
          if (f.capturedAt > playbackT + 1) {
            seekTo((f.capturedAt - frames[0].capturedAt) / Math.max(1, frames[frames.length - 1].capturedAt - frames[0].capturedAt));
            return;
          }
        }
        return;
      case 'BracketLeft': {
        e.preventDefault();
        const idx = SPEEDS.indexOf(replaySpeed);
        setSpeed(SPEEDS[Math.max(0, idx - 1)]);
        return;
      }
      case 'BracketRight': {
        e.preventDefault();
        const idx = SPEEDS.indexOf(replaySpeed);
        setSpeed(SPEEDS[Math.min(SPEEDS.length - 1, idx + 1)]);
        return;
      }
      case 'Home':
        e.preventDefault();
        if (frames.length > 0) seekTo(0);
        return;
    }
    // Digits 0-9 seek to 0%, 10%, … 90%.
    if (e.code.startsWith('Digit') && e.code.length === 6) {
      const n = parseInt(e.code[5], 10);
      if (Number.isFinite(n)) {
        e.preventDefault();
        seekTo(n / 10);
      }
    }
  };
  closeBtn.addEventListener('click', () => { cleanup(); input.onClose(); });
  window.addEventListener('keydown', onKey);

  const pushFrame = (frame: LiveFrame): void => {
    // De-dupe by capturedAt (relays can echo the same event).
    if (frames.length > 0 && frames[frames.length - 1].capturedAt === frame.capturedAt) return;
    // Out-of-order: discard older arrivals.
    if (frames.length > 0 && frame.capturedAt < frames[frames.length - 1].capturedAt) return;
    frames.push(frame);
    if (frames.length > FRAME_BUFFER_MAX) frames.shift();
    liveScore.textContent = `WAVE ${frame.wave} · ${frame.score.toLocaleString()}`;
    // Lives as ♥ glyphs; sats as ₿. Game caps lives at 3 typically so
    // the row stays compact. 0 lives shows an empty ♥♥♥ frame in red
    // so a watcher reads the "last life" tension.
    const heartsTotal = Math.max(0, frame.lives);
    const hearts = heartsTotal > 0
      ? '♥'.repeat(Math.min(5, heartsTotal))
      : 'NO LIVES';
    liveStats.textContent = `${hearts}  ·  ₿ ${frame.sats}`;
    liveStats.style.color = heartsTotal > 0 ? 'rgba(220,210,255,0.78)' : 'rgba(255,120,120,0.9)';
    // Music + bg follow the live wave the player is on — re-apply when
    // the wave changes so a spectator who joins mid-run still gets the
    // matching ambience as the player crosses wave boundaries.
    if (frame.wave > 0) applyWaveAssets(frame.wave);
    // Defer SFX bursts until playbackT reaches the event's captured
    // time. Earlier we fired them immediately on frame arrival, which
    // put explosions on screen ~350ms BEFORE the interpolated ship/
    // entity reached the death position. Buffering keeps the explosion
    // in lockstep with the ship the spectator is watching.
    for (const ev of frame.events) {
      pendingEvents.push({ code: ev.code, x: ev.x, y: ev.y, dueAt: frame.capturedAt });
    }
  };

  // Subscribe to the controller-ws relay as r=subscribe with the
  // player's master pubkey as the streamId. The relay broadcasts every
  // frame the player publishes to all subscribers. No Nostr envelope,
  // no per-event signature verification, ~3x lower latency than the
  // kind 22769 path.
  let frameCount = 0;
  let watchdog: number | null = null;
  const armWatchdog = (subOpen: () => void): void => {
    if (watchdog !== null) window.clearTimeout(watchdog);
    watchdog = window.setTimeout(() => {
      if (cancelled) return;
      if (frameCount > 0) return;
      for (const s of sockets) try { s.close(); } catch { /* ignore */ }
      sockets.length = 0;
      status.textContent = 'Reconnecting…';
      subOpen();
    }, 4000);
  };
  const openSubscriptions = (): void => {
    if (cancelled) return;
    const url = `wss://controller.pallasite.app/?s=${encodeURIComponent(input.masterPubkey)}&r=subscribe`;
    let ws: WebSocket;
    try { ws = new WebSocket(url); } catch { armWatchdog(openSubscriptions); return; }
    sockets.push(ws);
    ws.onopen = () => {
      status.textContent = 'Connected · waiting for first frame…';
    };
    ws.onmessage = (ev) => {
      const data = typeof ev.data === 'string' ? ev.data : '';
      if (!data) return;
      let parsed: unknown;
      try { parsed = JSON.parse(data); } catch { return; }
      if (!parsed || typeof parsed !== 'object') return;
      const obj = parsed as Record<string, unknown>;
      // Server control frames: {type: 'publisher-up' | 'publisher-down'}
      if (typeof obj.type === 'string') {
        if (obj.type === 'publisher-up') {
          status.textContent = 'Player connected — waiting for first frame…';
        } else if (obj.type === 'publisher-down') {
          status.textContent = 'Player disconnected.';
        }
        return;
      }
      // Otherwise it's a wire frame — same shape as ReplayFrameRaw.
      const frame = readWsFrame(obj);
      if (frame) {
        pushFrame(frame);
        frameCount += 1;
        // Surface end-to-end latency (player publish time → here) so
        // we can spot when the WS pipe is congested or the player is
        // backgrounded. capturedAt is the player's Date.now() at
        // publish; receivedAt is our local performance.now() so we
        // compute against a fresh Date.now() to keep the same clock
        // base. Difference > 500ms is unusual.
        const lagMs = Math.max(0, Math.round(Date.now() - frame.capturedAt));
        status.textContent = `Live · ${frameCount} frames · ${lagMs}ms`;
        if (watchdog !== null) { window.clearTimeout(watchdog); watchdog = null; }
      }
    };
    ws.onerror = () => { armWatchdog(openSubscriptions); };
    ws.onclose = () => { armWatchdog(openSubscriptions); };
    armWatchdog(openSubscriptions);
  };
  if (!replayMode) openSubscriptions();

  const c2d = canvas.getContext('2d')!;
  // Retro render mode: world coordinates map to device pixels through a
  // persistent dpr transform, exactly like the game's #game canvas, so
  // the shared render() fills this canvas. render() save/restores
  // internally, so this transform survives every frame.
  c2d.setTransform(dpr, 0, 0, dpr, 0, 0);
  const theatreRenderMode: RenderModeInfo = {
    kind: 'retro', vw: PALL_WORLD_W, vh: PALL_WORLD_H, dpr,
    scale: 1, tx: 0, ty: 0, insets: { top: 0, right: 0, bottom: 0, left: 0 },
  };
  // One persistent synthetic GameState, rewritten each tick from the
  // interpolated wire-frame pair and handed to render().
  const theatreState = createTheatreState();
  // Borrow the real #game3d WebGL overlay canvas. The overlay module is
  // hard-bound to that element, so moving it into `stage` is what lets
  // render()'s mesh tier composite 3D meshes over the 2D canvas. The
  // watch-page game loop pauses via the body dataset flag below so
  // nothing else draws to it. Saved refs restore it on theatre close.
  const g3d = document.getElementById('game3d') as HTMLCanvasElement | null;
  const g3dHome = g3d
    ? { parent: g3d.parentElement, next: g3d.nextSibling, css: g3d.style.cssText, w: g3d.width, h: g3d.height }
    : null;
  if (g3d) {
    stage.appendChild(g3d);
    // Match the 2D canvas backing store: the WebGL overlay sizes its
    // viewport as WORLD_W * scale * dpr and expects #game3d's backing to
    // match. Leaving it at the default resolution makes the overlay
    // overshoot, dropping most meshes off-canvas.
    g3d.width = CANVAS_W;
    g3d.height = CANVAS_H;
    // transform:none cancels the #game3d stylesheet centring rule
    // (translate(-50%,-50%)); without it the borrowed overlay is pulled
    // half its size off the theatre game box in retro display mode.
    g3d.style.cssText = `position:absolute;left:0;top:0;transform:none;width:${cssWidth}px;height:${cssHeight}px;display:block;pointer-events:none;`;
  }
  document.body.dataset.theatre = 'open';

  // Local particle system — purely viewer-side. Spawned on receipt of
  // SFX events (asteroid_kill, ufo_kill, mine_detonate, ship_destroyed,
  // shield_burst, vein_collapse). Wire pays nothing for these — the
  // events themselves are already on the wire, particles are just the
  // visual response. Bounded so a frenetic stream can't blow up frame
  // time on weaker devices.
  interface LiveParticle { x: number; y: number; vx: number; vy: number; ttl: number; ttlMax: number; colour: string; size: number; }
  // Debris — line-segment shards from ship-destroyed bursts. Matches
  // render.ts drawDebris look: tumbling angled segments fading to zero.
  interface LiveDebris { x: number; y: number; vx: number; vy: number; rot: number; rotV: number; ttl: number; ttlMax: number; length: number; colour: string; }
  // Pending SFX events — buffered with the frame's player-time capture
  // moment, drained in the tick when playbackT reaches them so the
  // explosion lines up with the interpolated ship/entity.
  interface PendingEvent { code: LiveEventCode; x: number; y: number; dueAt: number; }
  const particles: LiveParticle[] = [];
  const debris: LiveDebris[] = [];
  const pendingEvents: PendingEvent[] = [];

  // Replay mode: pre-seed the frame buffer with the entire replay, in
  // capture order. readWsFrame works directly on ReplayFrameRaw — same
  // shape as a WS payload (publishStreamFrameWs ships ReplayFrameRaw).
  // We push each frame manually rather than via pushFrame() so HUD
  // updates stay playback-driven (in the tick loop) instead of seeding
  // the HUD with the final-frame values up front.
  if (replayMode) {
    const replay = input.replaySource!;
    for (const raw of replay.frames) {
      const lf = readWsFrame(raw as unknown as Record<string, unknown>);
      if (!lf) continue;
      frames.push(lf);
      for (const ev of lf.events) {
        pendingEvents.push({ code: ev.code, x: ev.x, y: ev.y, dueAt: lf.capturedAt });
      }
    }
    status.textContent = `REPLAY · ${(replay.durationMs / 1000).toFixed(0)}s · ${replay.frames.length} frames`;
    // Replay starts at frame 0's wave so the spectator sees the run
    // unfold from the start. The tick-loop HUD update takes over after.
    const first = frames[0];
    if (first) {
      liveScore.textContent = `WAVE ${first.wave} · ${first.score.toLocaleString()}`;
      if (first.wave > 0) applyWaveAssets(first.wave);
    }
    // Scan for the latest 'sh' (ship-hit) SFX event — wire up the
    // JUMP TO DEATH button if one exists. Lead-in 2s before the
    // event so the spectator sees the lead-up + the hit.
    if (deathSeekBtn) {
      let deathT = -1;
      for (let i = frames.length - 1; i >= 0; i--) {
        const f = frames[i];
        for (const ev of f.events) {
          if (ev.code === 'sh') { deathT = f.capturedAt; break; }
        }
        if (deathT > 0) break;
      }
      if (deathT > 0 && frames.length > 0) {
        const oldest = frames[0].capturedAt;
        const newest = frames[frames.length - 1].capturedAt;
        const span = Math.max(1, newest - oldest);
        const seekTarget = (deathT - 2000 - oldest) / span;
        deathSeekBtn.style.display = '';
        deathSeekBtn.addEventListener('click', () => {
          seekTo(Math.max(0, Math.min(1, seekTarget)));
          scrub.value = String(Math.round(Math.max(0, Math.min(1, seekTarget)) * 1000));
        });
      }
    }
  }

  const MAX_PARTICLES = 240;
  const MAX_DEBRIS = 48;
  const spawnDebris = (cx: number, cy: number, count: number): void => {
    // Pure ship-green (#58ff58) — matches game.ts spawnShipDebris.
    // Game emits exactly 4 segments (the hull triangle's four edges)
    // with ttl 1500ms; we approximate with `count` randomly-oriented
    // segments since the SFX event carries no rotation. Sizes match
    // the hull edge lengths (8-24 world units).
    for (let i = 0; i < count && debris.length < MAX_DEBRIS; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = 70 + Math.random() * 80;
      debris.push({
        x: cx, y: cy,
        vx: Math.cos(a) * sp, vy: Math.sin(a) * sp,
        rot: Math.random() * Math.PI * 2,
        rotV: (Math.random() - 0.5) * 6,
        ttl: 1500,
        ttlMax: 1500,
        length: 8 + Math.random() * 16,
        colour: '#58ff58',
      });
    }
  };
  const spawnBurst = (cx: number, cy: number, count: number, colour: string, speed: number, ttl: number, size = 2): void => {
    for (let i = 0; i < count && particles.length < MAX_PARTICLES; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = speed * (0.6 + Math.random() * 0.8);
      particles.push({
        x: cx, y: cy,
        vx: Math.cos(a) * sp, vy: Math.sin(a) * sp,
        ttl, ttlMax: ttl, colour, size: size * (0.7 + Math.random() * 0.7),
      });
    }
  };

  // Playback clock — advances at 1× real time using performance.now()
  // so it's completely independent of player/viewer wall-clock drift.
  // Anchored to the player's Date.now() time-base on first frame, so
  // we can still match against frame.capturedAt for prev/next lookup.
  // The earlier renderAt = Date.now() - 600 approach was the source of
  // the asteroid jitter — any clock skew between player and viewer
  // dragged the interpolation window off the frame data.
  let playbackT = 0;
  let lastPerfMs = performance.now();
  // 30Hz wire → 33ms inter-frame. Lead of 0ms slams playback to the
  // latest received frame: prev = next = latest, render at its pose.
  // No interpolation between frames, just a 30fps slam-cut. Costs
  // some smoothness on slow networks (visible step instead of glide)
  // but eliminates the intentional 100ms lag the user was feeling as
  // "2 second delay" when playing phone→watch. With 30Hz wire,
  // 30fps slam-cut motion is plenty smooth for spectating.
  const PLAYBACK_LEAD_MS = 0;
  const PLAYBACK_LAG_LIMIT_MS = 2000;

  // Render loop — interpolate between adjacent frames for smooth motion
  // at 60fps even when the wire delivers 2 Hz.
  const tick = (): void => {
    if (cancelled) return;
    rafId = requestAnimationFrame(tick);
    const nowPerf = performance.now();
    const dtMs = nowPerf - lastPerfMs;
    lastPerfMs = nowPerf;

    // 1. Wave background (cover-fit, dimmed). Falls through to the
    // solid base colour while the image is still decoding.
    c2d.fillStyle = '#02050d';
    c2d.fillRect(0, 0, CANVAS_W, CANVAS_H);
    if (bgImage.complete && bgImage.naturalWidth > 0) {
      c2d.globalAlpha = 0.68;
      const ar = bgImage.naturalWidth / bgImage.naturalHeight;
      const canvasAr = CANVAS_W / CANVAS_H;
      let drawW = CANVAS_W, drawH = CANVAS_H;
      if (ar > canvasAr) drawW = CANVAS_H * ar;
      else drawH = CANVAS_W / ar;
      const drawX = (CANVAS_W - drawW) / 2;
      const drawY = (CANVAS_H - drawH) / 2;
      c2d.drawImage(bgImage, drawX, drawY, drawW, drawH);
      c2d.globalAlpha = 1;
    }
    // 2. Starfield overlay (subtle parallax look).
    c2d.fillStyle = 'rgba(220,210,255,0.55)';
    for (const s of stars) {
      c2d.beginPath();
      c2d.arc(s.x, s.y, s.r, 0, Math.PI * 2);
      c2d.fill();
    }

    if (frames.length === 0) {
      // No frames yet — wait.
      return;
    }
    // Advance the playback clock and clamp it to the frame buffer.
    const latestCap = frames[frames.length - 1].capturedAt;
    const oldestCap = frames[0].capturedAt;
    if (replayMode) {
      // Linear playback through the full timeline. Speed is governed
      // by replaySpeed (set by the 0.5/1/2/4× buttons); playback can
      // be paused via replayPaused.
      if (playbackT === 0) playbackT = oldestCap;
      if (!replayPaused) playbackT += dtMs * replaySpeed;
      if (playbackT >= latestCap) {
        playbackT = latestCap;
        status.textContent = 'REPLAY ENDED · CLOSE · ESC';
      }
      if (!scrubbing) {
        const span = latestCap - oldestCap;
        const frac = span > 0 ? (playbackT - oldestCap) / span : 0;
        scrub.value = String(Math.round(frac * 1000));
        const fmt = (ms: number): string => {
          const s = Math.max(0, Math.floor(ms / 1000));
          return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
        };
        timeLabel.textContent = `${fmt(playbackT - oldestCap)} / ${fmt(span)}`;
      }
    } else {
      if (playbackT === 0) playbackT = latestCap - PLAYBACK_LEAD_MS;
      playbackT += dtMs;
      // Pin to the live edge so the prev/next lookup always lands on
      // (last-frame, last-frame) and renders the freshest pose. With
      // PLAYBACK_LEAD_MS=0 this collapses to playbackT === latestCap.
      if (playbackT > latestCap - PLAYBACK_LEAD_MS) playbackT = latestCap - PLAYBACK_LEAD_MS;
      // Hard resync if we've fallen way behind (e.g. tab was backgrounded
      // for a while and frames piled up).
      if (playbackT < oldestCap || latestCap - playbackT > PLAYBACK_LAG_LIMIT_MS) {
        playbackT = latestCap - PLAYBACK_LEAD_MS;
      }
    }

    // Drain SFX events whose captured time playbackT has now passed.
    // Particle palette + counts mirror game.ts so the spectator sees
    // exactly the explosion the player saw:
    //   killShip:    42 green + 22 yellow + 18 white + line-segment debris
    //   destroyUfo:  26-36 red (averaging 30 since type isn't on the wire)
    //   asteroid break (small only):  ~14 of the asteroid's hue, derived
    //   from the source style. We don't know the type letter at SFX time
    //   either, so fall back to a chondrite-like cyan-violet mix that
    //   reads as a generic asteroid break.
    while (pendingEvents.length > 0 && pendingEvents[0].dueAt <= playbackT) {
      const ev = pendingEvents.shift()!;
      try {
        switch (ev.code) {
          case 'ak':
            audio.explosion(0.8);
            spawnBurst(ev.x, ev.y, 14, '#b48cff', 220, 500);
            break;
          case 'uk':
            // Same red as game.ts destroyUfo (#ff5050). Count 30 sits
            // between cruiser/elite/sniper (26) and tank (36).
            audio.explosion(1.0);
            spawnBurst(ev.x, ev.y, 30, '#ff5050', 220, 800);
            break;
          case 'md':
            audio.explosion(0.7);
            spawnBurst(ev.x, ev.y, 18, '#ff5050', 220, 600);
            break;
          case 'sh':
            // Match killShip layered explosion exactly: ship-green burst
            // + yellow flash + white sparks + line-segment debris.
            audio.explosion(1.4);
            spawnBurst(ev.x, ev.y, 42, '#58ff58', 280, 1100);
            spawnBurst(ev.x, ev.y, 22, '#ffd84a', 200,  700);
            spawnBurst(ev.x, ev.y, 18, '#ffffff', 380,  450);
            // Game emits 4 hull-edge segments — match the count, not 14.
            spawnDebris(ev.x, ev.y, 4);
            break;
          case 'sb':
            audio.shieldUp();
            spawnBurst(ev.x, ev.y, 10, '#5b9dff', 140, 380);
            break;
          case 'vc':
            audio.explosion(1.2);
            spawnBurst(ev.x, ev.y, 36, '#ffd84a', 320, 900);
            spawnBurst(ev.x, ev.y, 14, '#ff8ad6', 280, 700);
            break;
          case 'pu': audio.powerupPickup(); break;
          case 'fi': audio.fire(); break;
        }
      } catch { /* ignore audio errors */ }
    }
    // Guardrail: drop any pending events that are way behind us (a
    // playback resync just happened) so they don't fire all at once.
    while (pendingEvents.length > 0 && playbackT - pendingEvents[0].dueAt > 2_000) {
      pendingEvents.shift();
    }
    let prev = frames[0];
    let next = frames[0];
    for (let i = 0; i < frames.length; i++) {
      if (frames[i].capturedAt <= playbackT) prev = frames[i];
      if (frames[i].capturedAt >= playbackT) { next = frames[i]; break; }
    }
    const span = next.capturedAt - prev.capturedAt;
    const t = span > 0 ? Math.max(0, Math.min(1, (playbackT - prev.capturedAt) / span)) : 0;
    // Replay mode: drive the HUD off the playhead so score/wave/hearts
    // tick up as playback advances. Live mode updates HUD in pushFrame
    // when each new frame lands. playInfo lives in the controls row;
    // it duplicates the wave/score info next to the scrub slider so a
    // user dragging the bar sees the playhead state without looking up
    // at the main HUD.
    if (replayMode) {
      liveScore.textContent = `WAVE ${prev.wave} · ${prev.score.toLocaleString()}`;
      const heartsTotal = Math.max(0, prev.lives);
      const hearts = heartsTotal > 0 ? '♥'.repeat(Math.min(5, heartsTotal)) : 'NO LIVES';
      liveStats.textContent = `${hearts}  ·  ₿ ${prev.sats}`;
      liveStats.style.color = heartsTotal > 0 ? 'rgba(220,210,255,0.78)' : 'rgba(255,120,120,0.9)';
      if (prev.wave > 0) applyWaveAssets(prev.wave);
      playInfo.innerHTML = `<span style="color:#8cffb4;">WAVE ${prev.wave}</span>`
        + ` <span style="color:#ffe0a0;">SCORE ${prev.score.toLocaleString()}</span>`
        + ` <span style="color:${heartsTotal > 0 ? 'rgba(255,120,120,0.7)' : 'rgba(255,120,120,0.95)'};">${hearts}</span>`
        + ` <span style="color:#ffd84a;">₿ ${prev.sats}</span>`;
    }

    // 2b. Tick the viewer-side cosmetic pools (particles and debris
    // spawned by SFX events). render() draws them; their motion and fade
    // are stepped here so they stay independent of the wire cadence.
    const dt = 1 / 60;
    for (let i = particles.length - 1; i >= 0; i--) {
      const p = particles[i];
      p.ttl -= dt * 1000;
      if (p.ttl <= 0) { particles.splice(i, 1); continue; }
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vx *= 0.95;
      p.vy *= 0.95;
    }
    for (let i = debris.length - 1; i >= 0; i--) {
      const d = debris[i];
      d.ttl -= dt * 1000;
      if (d.ttl <= 0) { debris.splice(i, 1); continue; }
      d.x += d.vx * dt;
      d.y += d.vy * dt;
      d.vx *= 0.96;
      d.vy *= 0.96;
      d.rot += d.rotV * dt;
    }

    // 2c. Synthesise a GameState from the interpolated frame pair and
    // draw it through the shared game renderer, the same render() the
    // game uses, so the theatre inherits vector / shaded / mesh fidelity.
    populateTheatreState(theatreState, {
      prev, next, t, playbackT,
      particles: particles.map((p) => ({
        pos: { x: p.x, y: p.y }, vel: { x: p.vx, y: p.vy },
        ttl: p.ttl, maxTtl: p.ttlMax, colour: p.colour, size: p.size,
      })),
      debris: debris.map((d) => ({
        pos: { x: d.x, y: d.y }, vel: { x: d.vx, y: d.vy },
        rot: d.rot, rotVel: d.rotV, length: d.length,
        ttl: d.ttl, maxTtl: d.ttlMax, colour: d.colour,
      })),
    });
    setRenderMode(theatreRenderMode);
    render(canvas, theatreState, nowPerf);
    // Presentation theme — composite the borrowed 3D overlay in first so
    // the theme covers the meshes too, then post-process the frame.
    const theatreTheme = getTheme();
    if (theatreTheme !== 'none') {
      if (g3d && g3d.width > 0) {
        c2d.save();
        c2d.setTransform(1, 0, 0, 1, 0, 0);
        c2d.globalAlpha = 1;
        c2d.globalCompositeOperation = 'source-over';
        c2d.drawImage(g3d, 0, 0, g3d.width, g3d.height, 0, 0, canvas.width, canvas.height);
        c2d.restore();
        g3d.style.visibility = 'hidden';
      }
      applyPostFx(canvas, theatreTheme, nowPerf, { asciiCols: getAsciiCols(), bitDepth: getBitDepth(), bitColour: getBitColour() });
    } else if (g3d && g3d.style.visibility === 'hidden') {
      g3d.style.visibility = '';
    }
    // render() leaves the canvas in the game's world transform; the warp
    // banner and status overlays below draw in raw device pixels, so step
    // out of it here and restore at the end of the tick.
    c2d.save();
    c2d.setTransform(1, 0, 0, 1, 0, 0);

    // 3a. Warp-incoming banner — fires while the player is in 'warp'
    // phase (1.3s between waves). The host sends nextWave so we know
    // which wave is about to land; we render "WAVE N+1 INCOMING" as a
    // big centred banner over the frozen last-gameplay-frame canvas.
    // This replaces the 60Hz frame stream during warp (host throttles
    // to 1Hz heartbeats; banner gives the spectator something to look
    // at instead of a static last-frame).
    if (prev.phase === 'warp' && prev.nextWave > 0) {
      const incomingLore = WAVE_LORE[prev.nextWave - 1];
      if (incomingLore) {
        const bannerY = CANVAS_H * 0.5;
        c2d.save();
        c2d.globalAlpha = 0.78;
        c2d.fillStyle = '#02050d';
        c2d.fillRect(0, bannerY - 70 * dpr, CANVAS_W, 160 * dpr);
        c2d.globalAlpha = 1;
        c2d.textAlign = 'center';
        c2d.textBaseline = 'middle';
        c2d.fillStyle = 'rgba(140,255,180,0.9)';
        c2d.font = `${Math.round(16 * dpr)}px ui-monospace, monospace`;
        const blinkOn = Math.sin(nowPerf * 0.005) > 0;
        c2d.globalAlpha = blinkOn ? 1 : 0.35;
        c2d.fillText('INCOMING', CANVAS_W / 2, bannerY - 38 * dpr);
        c2d.globalAlpha = 1;
        c2d.fillStyle = '#ffd84a';
        c2d.shadowColor = 'rgba(255,216,74,0.7)';
        c2d.shadowBlur = 18 * dpr;
        c2d.font = `bold ${Math.round(34 * dpr)}px ui-monospace, monospace`;
        c2d.fillText(`WAVE ${prev.nextWave} · ${incomingLore.name}`, CANVAS_W / 2, bannerY - 4 * dpr);
        c2d.shadowBlur = 0;
        c2d.fillStyle = 'rgba(220,210,255,0.8)';
        c2d.font = `${Math.round(12 * dpr)}px ui-monospace, monospace`;
        c2d.fillText(incomingLore.subtitle, CANVAS_W / 2, bannerY + 26 * dpr);
        c2d.fillStyle = 'rgba(140,255,180,0.7)';
        c2d.font = `${Math.round(11 * dpr)}px ui-monospace, monospace`;
        c2d.fillText(incomingLore.tagline, CANVAS_W / 2, bannerY + 48 * dpr);
        c2d.textAlign = 'start';
        c2d.textBaseline = 'alphabetic';
        c2d.restore();
      }
    }

    // 4. Live indicator + age label + paused / ended overlays
    c2d.shadowBlur = 0;
    const latest = frames[frames.length - 1];
    // Age measured against viewer-local arrival time — capturedAt is
    // the player's Date.now() which can drift and gave false STALE
    // labels when the viewer's clock ran slightly behind the player's.
    // In replay mode the frame buffer is pre-seeded so `latest.receivedAt`
    // would always show massively stale — short-circuit the freshness
    // checks instead.
    const ageMs = !replayMode && latest ? performance.now() - latest.receivedAt : 0;
    const stale = !replayMode && ageMs > 3_000;
    const veryStale = !replayMode && ageMs > 6_000;
    // PAUSED only sticks while frames keep arriving (the player is
    // genuinely paused, frames keep flowing every 500ms with paused=1).
    // If frames go stale during a paused frame, the player has quit
    // or backgrounded — drop the PAUSED overlay so the watcher doesn't
    // sit on PAUSED forever waiting for the very-stale threshold.
    const paused = !replayMode && next.paused === true && !stale;
    // RUN ENDED only fires when frames stop arriving. Ship being dead
    // during deathreplay (alive=false in a fresh frame) is just a brief
    // gap between lives — the run is still in progress and frames keep
    // flowing every 500ms. When the run truly ends, the player stops
    // publishing entirely and the stream goes stale; 6s feels like a
    // human "they've gone" rather than a network hiccup.
    const ended = veryStale;

    // First STREAM ENDED transition — auto-try fetching the ghost so a
    // recently-claimed run replays without an extra click. If first
    // attempt fails (player hasn't claimed yet), leaves the button in
    // 'not_yet' state for manual retry. Skipped in replay mode (we're
    // already in the replay).
    if (!replayMode) {
      if (ended && !replayAutoTried) {
        replayAutoTried = true;
        void tryFetchReplay();
        // Bounce back to the watch landing a few seconds after the run
        // ends. tryFetchReplay's own cleanup() clears this if it rolls
        // into a replay; a manual close clears it via cleanup() too.
        autoReturnTimer = window.setTimeout(() => {
          autoReturnTimer = null;
          cleanup();
          input.onClose();
        }, 6000);
      } else if (!ended && replayState !== 'hidden' && replayState !== 'loading') {
        // Stream came back to life (rare relay reconnect): hide the
        // button so we don't strand a stale "TRY AGAIN" alongside live
        // frames, and cancel the pending bounce-to-watch.
        setReplayState('hidden');
        if (autoReturnTimer !== null) { window.clearTimeout(autoReturnTimer); autoReturnTimer = null; }
      }
    }

    // Live / paused / ended pill in the top-left
    let pillColour = 'rgba(140,255,180,0.85)';
    let pillLabel = 'LIVE';
    if (ended) {
      pillColour = 'rgba(255,120,120,0.9)';
      pillLabel = 'STREAM ENDED';
    } else if (paused) {
      pillColour = 'rgba(255,216,74,0.9)';
      pillLabel = 'PAUSED';
    } else if (stale) {
      pillColour = 'rgba(255,150,150,0.85)';
      pillLabel = `STALE · ${(ageMs / 1000).toFixed(0)}s`;
    }
    c2d.fillStyle = pillColour;
    c2d.beginPath();
    c2d.arc(14 * dpr, 14 * dpr, 4 * dpr, 0, Math.PI * 2);
    c2d.fill();
    c2d.font = `${Math.round(11 * dpr)}px ui-monospace, monospace`;
    c2d.fillStyle = pillColour;
    c2d.fillText(pillLabel, 24 * dpr, 18 * dpr);

    // Centre overlay for PAUSED / RUN ENDED. Keeps the canvas drawn
    // underneath so spectators see the frozen game world, with a
    // clear status banner on top.
    if (paused || ended) {
      c2d.save();
      c2d.fillStyle = 'rgba(2,5,13,0.55)';
      c2d.fillRect(0, 0, CANVAS_W, CANVAS_H);
      c2d.textAlign = 'center';
      c2d.textBaseline = 'middle';
      const cx = CANVAS_W / 2;
      const cy = CANVAS_H / 2;
      if (paused) {
        c2d.shadowColor = 'rgba(255,216,74,0.7)';
        c2d.shadowBlur = 16 * dpr;
        c2d.fillStyle = '#ffd84a';
        c2d.font = `bold ${Math.round(38 * dpr)}px ui-monospace, monospace`;
        c2d.fillText('⏸  PAUSED', cx, cy);
        c2d.shadowBlur = 0;
        c2d.fillStyle = 'rgba(255,216,74,0.85)';
        c2d.font = `${Math.round(13 * dpr)}px ui-monospace, monospace`;
        c2d.fillText(`Wave ${latest.wave} · ${latest.score.toLocaleString()}`, cx, cy + 30 * dpr);
      } else {
        c2d.shadowColor = 'rgba(255,120,120,0.7)';
        c2d.shadowBlur = 16 * dpr;
        c2d.fillStyle = '#ff8a8a';
        c2d.font = `bold ${Math.round(36 * dpr)}px ui-monospace, monospace`;
        c2d.fillText('STREAM ENDED', cx, cy - 14 * dpr);
        c2d.shadowBlur = 0;
        c2d.fillStyle = 'rgba(255,200,200,0.85)';
        c2d.font = `${Math.round(15 * dpr)}px ui-monospace, monospace`;
        c2d.fillText(`Last wave ${latest.wave} · ${latest.score.toLocaleString()}`, cx, cy + 14 * dpr);
        c2d.font = `${Math.round(11 * dpr)}px ui-monospace, monospace`;
        c2d.fillStyle = 'rgba(220,210,255,0.7)';
        c2d.fillText('Replay via the kind 30763 ghost once the player claims', cx, cy + 36 * dpr);
      }
      c2d.textAlign = 'start';
      c2d.textBaseline = 'alphabetic';
      c2d.restore();
    }
    // Balance the save() before the device-pixel overlays above, so the
    // next tick's render() starts from the world transform again.
    c2d.restore();
  };
  rafId = requestAnimationFrame(tick);
}

// ── Admin panel ───────────────────────────────────────────────────────────────
//
// Layer 1 visual review surface. Reached via ?admin=1 in the URL; the operator
// supplies the bearer token configured as ADMIN_TOKEN on the faucet. Lists
// currently-flagged players together with the run whose telemetry tripped
// the heuristic, with WATCH buttons that open the existing replay theatre.
// Pairs with the Layer 0 fingerprinter in pallasite-faucet/src/heuristics.ts.

const ADMIN_TOKEN_KEY = 'pallasite-admin-token';

function shortPubkey(hex: string): string {
  if (hex.length < 12) return hex;
  return `${hex.slice(0, 8)}…${hex.slice(-4)}`;
}

function formatFlaggedAt(ms: number | null): string {
  if (!ms) return '—';
  const d = new Date(ms);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}Z`;
}

// ── Phone-as-controller ──────────────────────────────────────────────────────
//
// Two surfaces live in this section:
//   1. renderControllerHostPairing — big-screen overlay shown when the
//      player taps "USE PHONE AS CONTROLLER" on the title screen. Shows
//      QR + short pubkey, latches once the mobile paired.
//   2. renderControllerPage — the mobile page itself, served from the
//      same SPA at /controller?h=...&s=...&r=... . Joystick + buttons.
//
// The host is held in a module-level slot so it survives the pairing
// dialog being closed — the user can pair, dismiss the dialog, then
// IGNITE and the phone keeps driving the ship. Explicit DISCONNECT
// (or window close) tears it down.

let activeControllerHost: import('./controller-host.js').ControllerHost | null = null;

export function hasActiveControllerHost(): boolean {
  return activeControllerHost !== null;
}

export function disconnectActiveControllerHost(): void {
  activeControllerHost?.close();
  activeControllerHost = null;
}

export function renderControllerHostPairing(state: GameState, onClose: () => void): void {
  clearOverlay();
  const overlay = el('div', { className: 'overlay', parent: root });
  setupOverlayArrowNav(overlay);

  el('h2', { parent: overlay, text: 'USE PHONE AS CONTROLLER' });

  const desc = el('p', { parent: overlay });
  desc.style.cssText = 'margin:8px 0 14px;font-size:0.95rem;color:rgba(220,210,255,0.85);max-width:520px;line-height:1.55;';
  desc.textContent = 'Open the QR on your phone to drive this screen. Pairing key is one-shot — it lives for this session only.';

  // Bigger QR slot for cleaner phone-camera detection at desk distance.
  // 200px was tight on a 1080p+ host monitor — a phone aimed from 30cm
  // away saw the QR fill <5% of its frame, which most scanners struggle
  // with. 280px nearly doubles the visual area and reliably latches
  // even on mid-range phone cameras.
  const qrSlot = el('div', { parent: overlay });
  qrSlot.style.cssText = 'width:280px;height:280px;background:#fff;border-radius:8px;padding:12px;margin:0 auto;box-shadow:0 0 20px rgba(140,255,180,0.25);';

  const codeP = el('p', { parent: overlay });
  codeP.style.cssText = 'margin:14px 0 6px;font-size:1.1rem;letter-spacing:0.2em;color:var(--hud-yellow);text-shadow:0 0 8px rgba(255,216,74,0.45);';

  const status = el('p', { parent: overlay });
  status.style.cssText = 'margin:8px 0 4px;font-size:0.95rem;color:rgba(140,255,180,0.85);min-height:1.4em;letter-spacing:0.08em;';
  status.textContent = 'Generating session key…';

  const hint = el('p', { parent: overlay });
  hint.style.cssText = 'margin:6px 0 18px;font-size:0.8rem;color:rgba(180,140,255,0.7);max-width:520px;line-height:1.4em;';
  hint.textContent = '';

  const row = el('div', { className: 'menu-row', parent: overlay });
  // Two action buttons: KEEP CONNECTED leaves the host alive so the
  // phone can drive the game after the dialog closes; DISCONNECT tears
  // the host down. Labels swap based on pair state.
  const primaryBtn = el('button', { className: 'menu-btn', parent: row, text: 'CANCEL' });
  const secondaryBtn = el('button', { className: 'menu-btn secondary', parent: row, text: '' });
  secondaryBtn.style.display = 'none';

  let paired = false;
  const startOrReuseHost = async (): Promise<void> => {
    const mod = await import('./controller-host.js');
    // Reuse an existing host if one's already running — saves a key
    // re-roll and lets the user re-open the QR mid-session.
    if (!activeControllerHost) {
      // The relay carries inputs only: the phone drives the ship and never
      // signs for it, so the screen keeps its own session.
      activeControllerHost = mod.startControllerHost(state, { ws: CONTROLLER_WS_ENDPOINT_DEFAULT });
    } else {
      // Already paired, jump to the paired view.
      paired = true;
    }
    const host = activeControllerHost;
    void renderQRInto(qrSlot, host.pairingUrl);
    codeP.textContent = host.sessionId.slice(0, 4).toUpperCase() + '·' + host.sessionId.slice(4, 8).toUpperCase();
    const renderPairedState = (): void => {
      status.textContent = 'Phone connected.';
      status.style.color = 'rgba(91,255,140,0.95)';
      hint.textContent = 'Close this dialog and IGNITE — your phone will keep driving the ship.';
      primaryBtn.textContent = 'KEEP CONNECTED · ESC';
      secondaryBtn.textContent = 'DISCONNECT';
      secondaryBtn.style.display = '';
    };
    if (paired) {
      renderPairedState();
    } else {
      status.textContent = 'Waiting for phone to scan…';
      hint.textContent = `Or visit ${host.pairingUrl} on your phone.`;
    }
    host.onStatus((s) => {
      if (s.kind === 'paired') {
        paired = true;
        renderPairedState();
      } else if (s.kind === 'waiting') {
        // Either the initial waiting state or the phone disconnected
        // after a successful pair. If we'd previously latched paired,
        // surface it so the dialog reflects the dropped peer.
        if (paired) {
          paired = false;
          status.textContent = 'Phone disconnected — re-scan to reconnect.';
          status.style.color = 'rgba(255,216,74,0.85)';
          primaryBtn.textContent = 'CANCEL';
          secondaryBtn.style.display = 'none';
        }
      } else if (s.kind === 'closed') {
        status.textContent = 'Disconnected.';
        primaryBtn.textContent = 'BACK';
        secondaryBtn.style.display = 'none';
      }
    });
  };
  void startOrReuseHost();

  const closeDialog = (alsoDisconnect: boolean): void => {
    window.removeEventListener('keydown', onKey);
    if (alsoDisconnect) {
      activeControllerHost?.close();
      activeControllerHost = null;
    }
    onClose();
  };
  primaryBtn.addEventListener('click', () => {
    // Primary action depends on pair state:
    //  unpaired → CANCEL (close + tear down)
    //  paired   → KEEP CONNECTED (close, host persists)
    closeDialog(!paired);
  });
  secondaryBtn.addEventListener('click', () => closeDialog(true));
  const onKey = (e: KeyboardEvent): void => {
    if (e.code === 'Escape') closeDialog(!paired);
  };
  window.addEventListener('keydown', onKey);
}

// Controller mobile page — renders a touch UI that publishes input
// events. Self-contained: doesn't touch the game state at all, only
// reads the URL token and uses controller-mobile to publish.
//
// Joystick semantics mirror touch.ts → attachJoystick exactly: drag
// angle drives target heading, deflection past THRUST_THRESHOLD turns
// thrust on. Quick tap-and-release fires one shot. Action buttons
// (fire-hold, hyperspace, shield, pause) sit on the right thumb side.

/**
 * Identity card shown at the top of the controller PWA home page.
 *
 * Step 1 of the phone-as-signer plan: the controller is just an
 * identity carrier here — pair frames don't yet propagate the signed-in
 * pubkey to a host. That's step 2. For now this card lets a player
 * sign in (or stay anonymous) on the pad itself, surfaces the pubkey,
 * and offers SIGN OUT.
 *
 * Method ordering is deliberate: bunker / nsec / extension first, then
 * Signet, then guest. Privacy-conscious nostr users often do not want
 * a Signet redirect as the headline option; they want to
 * paste their bunker URI from nsec.app or paste an nsec directly.
 */
function renderControllerIdentityCard(parent: HTMLElement, state: GameState): void {
  const card = el('div', { parent });
  card.style.cssText = 'border:1px solid rgba(255,216,74,0.32);border-radius:14px;padding:18px;background:rgba(255,216,74,0.05);display:flex;flex-direction:column;gap:12px;';

  const refresh = (): void => {
    card.replaceChildren();
    renderControllerIdentityCardInner(card, state, refresh);
  };
  renderControllerIdentityCardInner(card, state, refresh);
}

function renderControllerIdentityCardInner(
  card: HTMLElement,
  state: GameState,
  refresh: () => void,
): void {
  const label = el('div', { parent: card, text: 'IDENTITY' });
  label.style.cssText = 'font-size:0.78rem;letter-spacing:0.22em;color:rgba(255,216,74,0.85);text-align:center;';

  const session = state.session;
  if (session) {
    // Signed-in summary: name / npub / method / SIGN OUT.
    const guest = getGuestRecord();
    const isGuest = guest?.pubkey === session.pubkey;
    const profile = getCachedProfile(session.pubkey);
    // Prefer the profile, then fall back to SignetSession.displayName
    // before the 8-hex sentinel
    // so a freshly-paired bunker session shows the bunker's display
    // name immediately instead of "ABCD1234".
    const profileName = bestName(profile, session.pubkey);
    const profileLooksLikeFallback = /^[0-9A-F]{8}$/.test(profileName);
    const name = isGuest && guest
      ? guest.name
      : (profileLooksLikeFallback && session.displayName ? session.displayName : profileName);

    const nameRow = el('div', { parent: card, text: name });
    nameRow.style.cssText = 'font-size:1.05rem;color:#fff5d8;letter-spacing:0.08em;text-align:center;text-shadow:0 0 10px rgba(255,216,74,0.35);';

    let npubShort: string;
    try {
      const full = encodeNpub(session.pubkey);
      npubShort = full.length > 24 ? `${full.slice(0, 14)}…${full.slice(-6)}` : full;
    } catch {
      npubShort = `${session.pubkey.slice(0, 8)}…${session.pubkey.slice(-4)}`;
    }
    const npubRow = el('div', { parent: card, text: npubShort });
    npubRow.style.cssText = 'font-size:0.72rem;color:rgba(220,210,255,0.6);text-align:center;letter-spacing:0.06em;word-break:break-all;';

    const methodLabel = isGuest ? 'LOCAL GUEST' : describeMethod(session);
    const methodRow = el('div', { parent: card, text: methodLabel });
    methodRow.style.cssText = 'font-size:0.65rem;color:rgba(140,255,180,0.7);letter-spacing:0.22em;text-align:center;';

    const actions = el('div', { parent: card });
    actions.style.cssText = 'display:flex;gap:8px;justify-content:center;flex-wrap:wrap;margin-top:4px;';

    if (isGuest && guest) {
      const exportBtn = el('button', { parent: actions, text: 'EXPORT KEYS' }) as HTMLButtonElement;
      exportBtn.style.cssText = 'padding:10px 16px;background:rgba(140,255,180,0.14);border:1px solid rgba(140,255,180,0.55);border-radius:8px;color:#8cffb4;font-family:ui-monospace,monospace;font-size:0.78rem;letter-spacing:0.16em;cursor:pointer;';
      exportBtn.addEventListener('click', () => {
        const priv = getGuestPrivkeyHex();
        if (priv) openGuestKeyExport(guest.pubkey, priv);
      });
    }

    const signOutBtn = el('button', { parent: actions, text: 'SIGN OUT' }) as HTMLButtonElement;
    signOutBtn.style.cssText = 'padding:10px 16px;background:rgba(255,120,120,0.14);border:1px solid rgba(255,120,120,0.55);border-radius:8px;color:#ff8a8a;font-family:ui-monospace,monospace;font-size:0.78rem;letter-spacing:0.16em;cursor:pointer;';
    signOutBtn.addEventListener('click', () => {
      void (async () => {
        signOutBtn.disabled = true;
        try { await auth.signOut(state.session); } catch { /* ignore */ }
        // Guest identities live in our localStorage, not Signet's — wipe
        // those too so SIGN OUT is a clean slate regardless of method.
        if (isGuest) clearGuestIdentity();
        state.session = null;
        refresh();
      })();
    });

    // Once paired, the host swaps its local session for a remote
    // signer backed by this phone. Every game-side signEvent
    // (heartbeat, score, replay, claim) round-trips back here and
    // the phone's local signer fulfils it. The big screen never
    // sees your nsec.
    const gap = el('p', { parent: card });
    gap.style.cssText = 'margin:8px 0 0;font-size:0.68rem;color:rgba(140,255,180,0.55);text-align:center;letter-spacing:0.04em;line-height:1.5;';
    gap.textContent = 'Pair with a game — the big screen will sign through this phone. Your key never leaves the device.';
    return;
  }

  // Anonymous: explain what signing in buys, list methods in a
  // self-custody-first order (bunker → nsec → extension → Signet → guest).
  const blurb = el('p', { parent: card });
  blurb.style.cssText = 'margin:0;font-size:0.78rem;color:rgba(220,210,255,0.65);line-height:1.5;text-align:center;';
  blurb.textContent = 'Sign in on the pad so your phone holds your keys. Optional — you can pair as a plain joystick and let the host sign.';

  const methods = el('div', { parent: card });
  methods.style.cssText = 'display:flex;flex-direction:column;gap:8px;margin-top:4px;';

  type MethodSpec = {
    label: string;
    sub: string;
    onClick: () => void;
    accent: string;
  };
  const status = el('div', { parent: card });
  status.style.cssText = 'min-height:1.2em;font-size:0.78rem;color:rgba(220,210,255,0.7);text-align:center;letter-spacing:0.04em;';

  let busy = false;
  const runSignIn = async (method: auth.SignInMethod, label: string): Promise<void> => {
    if (busy) return;
    busy = true;
    status.textContent = `Opening ${label}…`;
    status.style.color = 'rgba(180,140,255,0.85)';
    try {
      const sess = await auth.signInWith(method);
      if (sess) {
        state.session = sess;
        // Profile lookup is fire-and-forget; the card renders from
        // session.pubkey + cached profile so we don't gate on the fetch.
        void import('./profile.js').then(({ fetchProfile }) => {
          void fetchProfile(sess.pubkey).then(p => {
            if (p && state.session?.pubkey === sess.pubkey) {
              state.profile = p;
              refresh();
            }
          });
        });
        refresh();
      } else {
        status.textContent = 'No signer attached.';
        status.style.color = '#ff8a3a';
        busy = false;
      }
    } catch (err) {
      status.textContent = err instanceof auth.SignInTimeoutError
        ? err.message
        : `Failed: ${err instanceof Error ? err.message : String(err)}`;
      status.style.color = '#ff5050';
      busy = false;
    }
  };

  // Ordered MOST secure → LEAST secure. The bunker path keeps your
  // nsec on a separate device entirely; the extension/Amber paths
  // keep it OS- or browser-isolated; Signet trusts an operator; nsec
  // paste and guest both land an nsec in this device's localStorage
  // (nsec paste is worse blast-radius because it's likely your main
  // identity, vs. a guest that's a fresh sacrifice key).
  const hasNip07 = typeof window !== 'undefined' && 'nostr' in window && Boolean((window as { nostr?: unknown }).nostr);
  const isAndroid = typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);
  const specs: MethodSpec[] = [];
  specs.push({
    label: '🔗 PASTE BUNKER URI',
    sub: 'NIP-46 · key stays on your remote signer (nsec.app, Alby, your own bunker)',
    accent: 'rgba(140,255,180,0.55)',
    onClick: () => { void runSignIn('bunker', 'bunker'); },
  });
  if (hasNip07) {
    specs.push({
      label: '🔌 BROWSER EXTENSION',
      sub: 'NIP-07 · key isolated in the browser extension',
      accent: 'rgba(140,200,255,0.55)',
      onClick: () => { void runSignIn('nip07', 'extension'); },
    });
  }
  if (isAndroid) {
    specs.push({
      label: '🤖 SIGN VIA AMBER',
      sub: 'Android-only · key stays in the Amber signing app',
      accent: 'rgba(255,200,120,0.55)',
      onClick: () => { void runSignIn('amber', 'Amber'); },
    });
  }
  specs.push({
    label: '📱 SIGNET ON ANOTHER DEVICE',
    sub: 'QR relay sign-in · keep My Signet open so this pad can request signatures',
    accent: 'rgba(255,216,74,0.55)',
    onClick: () => { void runSignIn('qr', 'Signet QR'); },
  });
  specs.push({
    label: '🔑 PASTE NSEC',
    sub: 'Raw key in this browser · only do this on a phone YOU control',
    accent: 'rgba(184,144,255,0.55)',
    onClick: () => { void runSignIn('nsec', 'nsec paste'); },
  });
  specs.push({
    label: '🎮 PLAY AS GUEST',
    sub: 'Fresh local keypair · stored in this browser, exportable later',
    accent: 'rgba(255,140,200,0.55)',
    onClick: () => { void startControllerGuestFlow(state, refresh); },
  });

  for (const spec of specs) {
    const btn = el('button', { parent: methods }) as HTMLButtonElement;
    btn.style.cssText = `display:flex;flex-direction:column;align-items:flex-start;gap:2px;padding:12px 14px;background:rgba(2,5,13,0.55);border:1.5px solid ${spec.accent};border-radius:10px;color:#fff5d8;font-family:ui-monospace,monospace;font-size:0.92rem;letter-spacing:0.12em;cursor:pointer;text-align:left;`;
    const top = el('div', { parent: btn, text: spec.label });
    top.style.cssText = 'font-weight:bold;';
    const sub = el('div', { parent: btn, text: spec.sub });
    sub.style.cssText = 'font-size:0.7rem;color:rgba(220,210,255,0.6);letter-spacing:0.06em;font-weight:normal;';
    btn.addEventListener('click', spec.onClick);
  }
}

/** Describe a SignetSession's signing method for the IDENTITY card. */
function describeMethod(session: { signer?: { method?: string } | null } | null): string {
  const m = session?.signer?.method;
  if (!m) return 'NOSTR';
  if (m === 'nip07') return 'BROWSER EXTENSION';
  if (m === 'bunker') return 'BUNKER (NIP-46)';
  if (m === 'nsec') return 'PASTED NSEC';
  if (m === 'amber') return 'AMBER';
  if (m === 'redirect' || m === 'signet') return 'SIGNET';
  return m.toUpperCase();
}

/**
 * Guest sign-in flow for the controller PWA: full-screen overlay with
 * the arcade name picker. On submit, provisions a local keypair via
 * loadOrCreateGuest and sets state.session. Pre-checked "follow
 * Pallasite" stays as the default, mirroring the host flow.
 */
function startControllerGuestFlow(state: GameState, refresh: () => void): void {
  const modal = el('div', { parent: root });
  modal.style.cssText = 'position:fixed;inset:0;background:rgba(2,5,13,0.95);display:flex;align-items:center;justify-content:center;z-index:1000;padding:18px;overflow-y:auto;';
  const inner = el('div', { parent: modal });
  inner.style.cssText = 'background:#02050d;border:1px solid rgba(255,140,200,0.45);border-radius:14px;padding:22px;max-width:520px;width:100%;display:flex;flex-direction:column;gap:14px;font-family:ui-monospace,monospace;box-shadow:0 0 36px rgba(255,140,200,0.18);';

  const title = el('h2', { parent: inner, text: 'PLAY AS GUEST' });
  title.style.cssText = 'margin:0;font-size:1rem;letter-spacing:0.2em;color:#ffacd5;text-align:center;';
  const sub = el('p', { parent: inner, text: 'A fresh local keypair lives only on this phone. Back it up later via EXPORT KEYS.' });
  sub.style.cssText = 'margin:0;font-size:0.78rem;color:rgba(220,210,255,0.7);line-height:1.5;text-align:center;';

  const close = (): void => { modal.remove(); window.removeEventListener('keydown', onKey); };
  const onKey = (e: KeyboardEvent): void => { if (e.code === 'Escape') close(); };
  window.addEventListener('keydown', onKey);
  // Backdrop dismiss — click + non-mouse pointerup (body touch-action:none
  // suppresses tap→click on mobile; this modal is appended to root).
  const dismissBackdrop = (e: Event): void => { if (e.target === modal) close(); };
  modal.addEventListener('click', dismissBackdrop);
  modal.addEventListener('pointerup', (e) => { if (e.pointerType !== 'mouse') dismissBackdrop(e); });

  const status = el('div', { parent: inner });
  status.style.cssText = 'min-height:1.2em;font-size:0.78rem;color:rgba(220,210,255,0.7);text-align:center;';

  let followPallasite = true;
  const followRow = el('label', { parent: inner });
  followRow.style.cssText = 'display:flex;align-items:center;gap:8px;font-size:0.78rem;color:rgba(220,210,255,0.75);justify-content:center;cursor:pointer;';
  const followBox = el('input', { parent: followRow, attrs: { type: 'checkbox' } }) as HTMLInputElement;
  followBox.checked = true;
  followBox.addEventListener('change', () => { followPallasite = followBox.checked; });
  el('span', { parent: followRow, text: 'Follow Pallasite (kind 3) on signup' });

  let creating = false;
  const submit = async (name: string): Promise<void> => {
    if (creating) return;
    creating = true;
    status.textContent = 'Creating your identity…';
    status.style.color = 'rgba(180,140,255,0.85)';
    try {
      const sess = await auth.createGuestSession(name.trim() || 'Anonymous', { followPallasite });
      state.session = sess;
      close();
      refresh();
    } catch (err) {
      status.textContent = `Failed: ${err instanceof Error ? err.message : String(err)}`;
      status.style.color = '#ff5050';
      creating = false;
    }
  };

  renderArcadeName(inner, {
    maxLen: 25,
    onSubmit: (name) => { void submit(name); },
  });

  const cancelRow = el('div', { parent: inner });
  cancelRow.style.cssText = 'display:flex;justify-content:center;margin-top:6px;';
  const cancelBtn = el('button', { parent: cancelRow, text: 'CANCEL' }) as HTMLButtonElement;
  cancelBtn.style.cssText = 'padding:8px 18px;background:transparent;border:1px solid rgba(220,210,255,0.3);color:rgba(220,210,255,0.75);border-radius:6px;font-family:ui-monospace,monospace;font-size:0.78rem;letter-spacing:0.14em;cursor:pointer;';
  onTap(cancelBtn, close);
}

/** Home page shown on mobile.pallasite.app when there's no pairing
 *  token in the URL. Lets the user scan a QR (BarcodeDetector when
 *  available, falls back to manual code entry) or type the 8-character
 *  pairing code from the big screen. On success, navigates to
 *  /?s=<code> which the route handler bounces back into
 *  renderControllerPage as the gamepad. */
function renderControllerHomePage(state: GameState): void {
  clearOverlay();
  // Portrait OR landscape — the home page is just a card; we don't
  // need the rotate-device lockdown here.
  document.documentElement.style.height = '';
  document.documentElement.style.overflow = '';
  // Pad against the iPhone notch + side-rails. max() guarantees a
  // minimum padding so the content still has breathing room when there
  // ARE no safe-area insets (regular browsers). Portrait notch lives
  // top; landscape notch lives left or right depending on orientation.
  document.body.style.cssText = 'background:#02050d;color:rgba(220,210,255,0.9);font-family:ui-monospace,monospace;margin:0;min-height:100vh;display:flex;flex-direction:column;align-items:center;padding-top:max(24px, env(safe-area-inset-top));padding-bottom:max(24px, env(safe-area-inset-bottom));padding-left:max(16px, env(safe-area-inset-left));padding-right:max(16px, env(safe-area-inset-right));';
  let vp = document.querySelector('meta[name="viewport"]');
  if (!vp) {
    vp = document.createElement('meta');
    vp.setAttribute('name', 'viewport');
    document.head.appendChild(vp);
  }
  vp.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover');

  const gameCanvas = document.getElementById('game');
  if (gameCanvas) gameCanvas.style.display = 'none';
  const touchCtl = document.getElementById('touch-controls');
  if (touchCtl) touchCtl.style.display = 'none';

  const overlay = el('div', { className: 'overlay', parent: root });
  overlay.style.cssText = 'position:static;padding:0;margin:0;max-width:520px;width:100%;display:flex;flex-direction:column;align-items:stretch;gap:18px;';

  // Header
  const header = el('div', { parent: overlay });
  header.style.cssText = 'text-align:center;margin-bottom:6px;';
  const title = el('h1', { parent: header, text: 'PALLASITE · CONTROLLER' });
  title.style.cssText = 'margin:0 0 6px;font-size:1.25rem;letter-spacing:0.18em;color:#8cffb4;text-shadow:0 0 12px rgba(140,255,180,0.4);';
  const subtitle = el('p', { parent: header, text: 'Pair with a game to start playing' });
  subtitle.style.cssText = 'margin:0;font-size:0.85rem;color:rgba(220,210,255,0.7);letter-spacing:0.08em;';

  // ── Identity card ──────────────────────────────────────────────────
  // Phone-as-signer step 1: surface a sign-in entry point on the
  // controller home page so the pad can carry the user's identity
  // independent of any specific game. Method-first ordering keeps
  // bunker / nsec / extension first; Signet sits as one option among
  // others, not the headline.
  renderControllerIdentityCard(overlay, state);

  // ── Code entry — single big license-plate input ────────────────────
  // Big, centred, uppercase via CSS. No mid-typing reformatting — that
  // fights the user's cursor and the IME on mobile. Only normalise on
  // submit. Accepts any combination of hex chars + punctuation (e.g.
  // "ABCD 1234", "abcd-1234", "abcd1234"); strip non-hex on submit.
  const codeCard = el('div', { parent: overlay });
  codeCard.style.cssText = 'border:1px solid rgba(140,255,180,0.35);border-radius:14px;padding:20px;background:rgba(60,200,140,0.06);display:flex;flex-direction:column;gap:14px;';
  const codeLabel = el('label', { parent: codeCard, text: 'ENTER CODE FROM GAME' });
  codeLabel.style.cssText = 'font-size:0.85rem;letter-spacing:0.18em;color:rgba(140,255,180,0.9);text-align:center;';
  const codeInput = el('input', { parent: codeCard, attrs: { type: 'text', inputmode: 'text', autocapitalize: 'characters', autocomplete: 'off', spellcheck: 'false', placeholder: 'ABCD 1234' } }) as HTMLInputElement;
  codeInput.style.cssText = 'background:rgba(2,5,13,0.7);border:2px solid rgba(140,255,180,0.5);border-radius:12px;padding:18px 14px;font-size:2rem;letter-spacing:0.18em;color:#ffd84a;text-align:center;font-family:ui-monospace,monospace;outline:none;text-transform:uppercase;width:100%;box-sizing:border-box;';
  const goBtn = el('button', { parent: codeCard, text: 'PAIR' }) as HTMLButtonElement;
  goBtn.style.cssText = 'background:rgba(140,255,180,0.22);border:2px solid rgba(140,255,180,0.7);color:#8cffb4;border-radius:12px;padding:18px;font-size:1.1rem;letter-spacing:0.22em;font-weight:bold;cursor:pointer;font-family:ui-monospace,monospace;';
  const codeHint = el('p', { parent: codeCard });
  codeHint.style.cssText = 'margin:0;font-size:0.78rem;color:rgba(180,140,255,0.7);line-height:1.5;text-align:center;';
  const HINT_DEFAULT = 'Find the 8-character code under the QR on the game screen.';
  codeHint.textContent = HINT_DEFAULT;

  const normalise = (raw: string): string | null => {
    const stripped = raw.replace(/[^a-fA-F0-9]/g, '').toLowerCase();
    return /^[a-f0-9]{8}$/.test(stripped) ? stripped : null;
  };
  const setError = (msg: string | null): void => {
    if (msg) {
      codeHint.textContent = msg;
      codeHint.style.color = 'rgba(255,120,120,0.9)';
      codeInput.style.borderColor = 'rgba(255,120,120,0.75)';
    } else {
      codeHint.textContent = HINT_DEFAULT;
      codeHint.style.color = 'rgba(180,140,255,0.7)';
      codeInput.style.borderColor = 'rgba(140,255,180,0.5)';
    }
  };
  const submitCode = (): void => {
    const code = normalise(codeInput.value);
    if (!code) {
      setError('Code must be 8 hex characters (0-9, A-F).');
      return;
    }
    codeInput.blur();
    window.location.assign(`/?s=${code}`);
  };
  goBtn.addEventListener('click', submitCode);
  codeInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); submitCode(); }
  });
  // Only clear errors on input — no auto-formatting that fights the
  // user. They type whatever, we strip on submit.
  codeInput.addEventListener('input', () => setError(null));

  // ── QR scanner — works on every browser via jsQR (pure JS decoder).
  //    Live video on cameras that support getUserMedia, file-upload
  //    fallback (snap a photo, decode the image) where camera access
  //    is denied or unsupported.
  const tryParse = (raw: string): string | null => {
    try {
      const u = new URL(raw);
      const s = u.searchParams.get('s');
      if (s) return normalise(s);
    } catch { /* not a URL — try as plain code */ }
    return normalise(raw);
  };

  const scanCard = el('div', { parent: overlay });
  scanCard.style.cssText = 'border:1px solid rgba(184,144,255,0.35);border-radius:12px;padding:18px;background:rgba(120,90,200,0.06);display:flex;flex-direction:column;gap:12px;align-items:stretch;';
  const scanLabel = el('div', { parent: scanCard, text: 'OR SCAN QR CODE' });
  scanLabel.style.cssText = 'font-size:0.85rem;letter-spacing:0.18em;color:rgba(184,144,255,0.9);text-align:center;';
  const scanRow = el('div', { parent: scanCard });
  scanRow.style.cssText = 'display:flex;gap:10px;flex-wrap:wrap;';
  const scanBtn = el('button', { parent: scanRow, text: '📷  CAMERA' }) as HTMLButtonElement;
  scanBtn.style.cssText = 'flex:1;min-width:140px;background:rgba(184,144,255,0.18);border:2px solid rgba(184,144,255,0.6);color:#cbb6ff;border-radius:10px;padding:16px;font-size:1rem;letter-spacing:0.16em;font-weight:bold;cursor:pointer;font-family:ui-monospace,monospace;';
  // File-upload fallback — picks/takes a photo, we decode with jsQR.
  // Works on every browser (including older iOS) and doesn't require
  // an always-on camera stream.
  const fileBtn = el('button', { parent: scanRow, text: '🖼  FILE' }) as HTMLButtonElement;
  fileBtn.style.cssText = 'flex:1;min-width:140px;background:rgba(220,210,255,0.08);border:2px solid rgba(184,144,255,0.4);color:rgba(220,210,255,0.85);border-radius:10px;padding:16px;font-size:0.95rem;letter-spacing:0.14em;cursor:pointer;font-family:ui-monospace,monospace;';
  const fileInput = el('input', { parent: scanCard, attrs: { type: 'file', accept: 'image/*' } }) as HTMLInputElement;
  fileInput.setAttribute('capture', 'environment');
  fileInput.style.display = 'none';

  const scanHost = el('div', { parent: scanCard });
  scanHost.style.cssText = 'display:none;flex-direction:column;gap:10px;align-items:stretch;';
  const video = el('video', { parent: scanHost }) as HTMLVideoElement;
  video.style.cssText = 'width:100%;max-height:48vh;border-radius:8px;background:#000;object-fit:cover;';
  video.playsInline = true;
  video.muted = true;
  const scanStatus = el('p', { parent: scanHost });
  scanStatus.style.cssText = 'margin:0;font-size:0.8rem;color:rgba(220,210,255,0.75);text-align:center;letter-spacing:0.08em;';
  scanStatus.textContent = 'Point at the QR on the big screen';
  const stopBtn = el('button', { parent: scanHost, text: 'CANCEL' }) as HTMLButtonElement;
  stopBtn.style.cssText = 'background:rgba(255,255,255,0.06);border:1px solid rgba(220,210,255,0.3);color:rgba(220,210,255,0.85);border-radius:8px;padding:10px 12px;font-size:0.85rem;letter-spacing:0.12em;cursor:pointer;font-family:ui-monospace,monospace;';

  let stream: MediaStream | null = null;
  let scanRaf: number | null = null;
  let jsQRLib: typeof import('jsqr').default | null = null;

  const loadJsQR = async (): Promise<typeof import('jsqr').default> => {
    if (jsQRLib) return jsQRLib;
    const mod = await import('jsqr');
    jsQRLib = mod.default;
    return jsQRLib;
  };

  const stopScan = (): void => {
    if (scanRaf !== null) { cancelAnimationFrame(scanRaf); scanRaf = null; }
    if (stream) { for (const t of stream.getTracks()) t.stop(); stream = null; }
    // Belt-and-braces release the video element so iOS Safari doesn't
    // keep the camera indicator hot — track.stop() is the contractual
    // cure but iOS occasionally needs srcObject=null + pause() to
    // actually drop the MediaStream reference.
    try { video.pause(); } catch { /* ignore */ }
    try { video.srcObject = null; } catch { /* ignore */ }
    scanHost.style.display = 'none';
    scanRow.style.display = 'flex';
    scanBtn.disabled = false;
    scanStatus.textContent = 'Point at the QR on the big screen';
    scanStatus.style.color = 'rgba(220,210,255,0.75)';
  };

  const handleResult = (raw: string): boolean => {
    const code = tryParse(raw);
    if (!code) return false;
    scanStatus.textContent = 'Got it — pairing…';
    stopScan();
    window.location.assign(`/?s=${code}`);
    return true;
  };

  const startCameraScan = async (): Promise<void> => {
    scanBtn.disabled = true;
    setError(null);
    let jsQR: typeof import('jsqr').default;
    try { jsQR = await loadJsQR(); } catch {
      scanBtn.disabled = false;
      setError('QR library failed to load — use FILE or type the code.');
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      scanBtn.disabled = false;
      setError('This browser has no camera API — use FILE or type the code.');
      return;
    }
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false,
      });
    } catch (err) {
      scanBtn.disabled = false;
      setError(err instanceof Error && err.name === 'NotAllowedError'
        ? 'Camera permission denied — use FILE or type the code.'
        : 'Could not open the camera — use FILE or type the code.');
      return;
    }
    scanRow.style.display = 'none';
    scanHost.style.display = 'flex';
    video.srcObject = stream;
    try { await video.play(); } catch { /* ignore */ }
    // Downscale phone-camera frames to ~640px wide before running
    // jsQR. Modern phones deliver 1080p+ video which decodes ~5x
    // slower per frame than the 640px scale that gives reliable QR
    // detection. Cuts per-tick CPU from ~80ms to ~16ms on a mid-range
    // Android, which is the difference between "scans instantly" and
    // "video is on but seems stuck".
    const SCAN_TARGET_W = 640;
    const canvas = document.createElement('canvas');
    const cctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!cctx) return;
    const scanStart = Date.now();
    let lastStatusTick = scanStart;
    const tick = (): void => {
      if (!stream) return;
      if (video.readyState >= 2 && video.videoWidth > 0) {
        // Maintain aspect ratio so QRs near the frame edge aren't
        // squished. Scale to target width; height follows.
        const aspect = video.videoHeight / video.videoWidth;
        const w = Math.min(SCAN_TARGET_W, video.videoWidth);
        const h = Math.round(w * aspect);
        if (canvas.width !== w || canvas.height !== h) {
          canvas.width = w;
          canvas.height = h;
        }
        cctx.drawImage(video, 0, 0, w, h);
        try {
          const data = cctx.getImageData(0, 0, w, h);
          // attemptBoth covers light-on-dark QRs (e.g. an inverted
          // screen, a dark-mode browser rendering the QR) on top of
          // the standard dark-on-light path. Roughly 2x per-frame
          // cost vs dontInvert, but the downscale above absorbs it.
          const found = jsQR(data.data, data.width, data.height, { inversionAttempts: 'attemptBoth' });
          if (found && handleResult(found.data)) return;
        } catch { /* keep scanning */ }
      }
      // After 8s of unsuccessful scanning, soften the status text to
      // a hint pointing at the type-the-code fallback. Avoids the
      // dead "Point at the QR" line lingering forever when the
      // camera is on but the QR isn't readable.
      const now = Date.now();
      if (now - scanStart > 8_000 && now - lastStatusTick > 1_000) {
        scanStatus.textContent = 'Still scanning… if it won\'t latch, tap CANCEL and type the 8-character code below.';
        scanStatus.style.color = 'rgba(255,216,74,0.85)';
        lastStatusTick = now;
      }
      scanRaf = requestAnimationFrame(tick);
    };
    tick();
  };

  const handleFile = async (file: File): Promise<void> => {
    setError(null);
    let jsQR: typeof import('jsqr').default;
    try { jsQR = await loadJsQR(); } catch {
      setError('QR library failed to load.');
      return;
    }
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.src = url;
    try { await img.decode(); } catch {
      URL.revokeObjectURL(url);
      setError('Could not read that image.');
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const cctx = canvas.getContext('2d');
    if (!cctx) { URL.revokeObjectURL(url); return; }
    cctx.drawImage(img, 0, 0);
    URL.revokeObjectURL(url);
    let data: ImageData;
    try { data = cctx.getImageData(0, 0, canvas.width, canvas.height); }
    catch { setError('Could not read image data.'); return; }
    const found = jsQR(data.data, data.width, data.height);
    if (!found || !handleResult(found.data)) {
      setError('No QR code found in that photo — try again or type the code.');
    }
  };

  scanBtn.addEventListener('click', () => { void startCameraScan(); });
  fileBtn.addEventListener('click', () => fileInput.click());
  fileInput.addEventListener('change', () => {
    const file = fileInput.files?.[0];
    if (file) void handleFile(file);
    fileInput.value = '';
  });
  stopBtn.addEventListener('click', stopScan);

  // ── Add-to-Home-Screen install ──────────────────────────────────────
  // iOS has no programmatic install — show a banner with manual
  // instructions on first visit. Android Chrome fires
  // beforeinstallprompt — capture + expose an INSTALL button.
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches
    || (window.navigator as unknown as { standalone?: boolean }).standalone === true;
  if (!isStandalone) {
    const seenKey = 'pall:a2hs:seen';
    const seen = localStorage.getItem(seenKey) === '1';
    const ua = navigator.userAgent;
    const isIOS = /iPhone|iPad|iPod/.test(ua) && !/Android/.test(ua);
    const a2hsCard = el('div', { parent: overlay });
    a2hsCard.style.cssText = `border:1px solid rgba(140,255,180,0.3);border-radius:10px;padding:12px 14px;background:rgba(60,200,140,0.05);display:${seen ? 'none' : 'flex'};flex-direction:column;gap:8px;font-size:0.8rem;color:rgba(220,210,255,0.85);line-height:1.5;`;
    const a2hsTitle = el('div', { parent: a2hsCard, text: '📲 INSTALL AS APP' });
    a2hsTitle.style.cssText = 'font-size:0.78rem;letter-spacing:0.16em;color:rgba(140,255,180,0.95);';
    const a2hsBody = el('div', { parent: a2hsCard });
    a2hsBody.style.fontSize = '0.78rem';
    a2hsBody.innerHTML = isIOS
      ? 'Tap the <strong>share icon</strong> in Safari and choose <strong>Add to Home Screen</strong> — opens straight into the controller.'
      : 'Tap the <strong>⋮ menu</strong> in your browser and choose <strong>Install app</strong> or <strong>Add to Home screen</strong>.';
    const a2hsRow = el('div', { parent: a2hsCard });
    a2hsRow.style.cssText = 'display:flex;gap:8px;justify-content:flex-end;margin-top:4px;';
    let installPromptDeferred: { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> } | null = null;
    const installBtn = el('button', { parent: a2hsRow, text: 'INSTALL' }) as HTMLButtonElement;
    installBtn.style.cssText = 'background:rgba(140,255,180,0.18);border:1px solid rgba(140,255,180,0.5);color:#8cffb4;border-radius:6px;padding:8px 14px;font-size:0.78rem;letter-spacing:0.12em;cursor:pointer;font-family:ui-monospace,monospace;display:none;';
    installBtn.addEventListener('click', () => {
      if (!installPromptDeferred) return;
      installBtn.disabled = true;
      void installPromptDeferred.prompt();
      void installPromptDeferred.userChoice.then(() => { installPromptDeferred = null; });
    });
    const dismissBtn = el('button', { parent: a2hsRow, text: 'DISMISS' }) as HTMLButtonElement;
    dismissBtn.style.cssText = 'background:transparent;border:1px solid rgba(220,210,255,0.25);color:rgba(220,210,255,0.65);border-radius:6px;padding:8px 14px;font-size:0.78rem;letter-spacing:0.12em;cursor:pointer;font-family:ui-monospace,monospace;';
    dismissBtn.addEventListener('click', () => {
      localStorage.setItem(seenKey, '1');
      a2hsCard.style.display = 'none';
    });
    // Capture Android Chrome's beforeinstallprompt so we can fire it
    // from the INSTALL button (the spec requires user-gesture trigger).
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      installPromptDeferred = e as unknown as typeof installPromptDeferred;
      if (installPromptDeferred) installBtn.style.display = '';
    });
  }
}

export function renderControllerPage(state: GameState): void {
  // If the URL doesn't carry a pairing token, render the home page
  // instead (scan QR / enter code). Once the user pairs we navigate
  // to ?s=<code> and re-enter this function, this time taking the
  // gamepad branch below.
  const url = new URL(window.location.href);
  if (!url.searchParams.get('s')) {
    renderControllerHomePage(state);
    return;
  }
  clearOverlay();
  // Lock the page chrome — controller is full-screen landscape only.
  document.documentElement.style.height = '100%';
  document.documentElement.style.overflow = 'hidden';
  document.body.style.cssText = 'background:#02050d;overflow:hidden;touch-action:none;position:fixed;inset:0;width:100vw;height:100vh;margin:0;padding:0;overscroll-behavior:none;';
  let vp = document.querySelector('meta[name="viewport"]');
  if (!vp) {
    vp = document.createElement('meta');
    vp.setAttribute('name', 'viewport');
    document.head.appendChild(vp);
  }
  vp.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover');

  const gameCanvas = document.getElementById('game');
  if (gameCanvas) gameCanvas.style.display = 'none';
  const touchCtl = document.getElementById('touch-controls');
  if (touchCtl) touchCtl.style.display = 'none';

  void (async () => {
    try {
      const orientation = screen.orientation as ScreenOrientation & { lock?: (o: string) => Promise<void> };
      if (orientation?.lock) await orientation.lock('landscape');
    } catch { /* not supported in non-PWA contexts */ }
  })();

  const overlay = el('div', { className: 'overlay', parent: root });
  // Safe-area-inset padding so the joystick + face cluster sit inside
  // the iPhone notch / home indicator zone in landscape (the notch is
  // on the LEFT or RIGHT depending on orientation — env(safe-area-
  // inset-left) tracks whichever is active). box-sizing:border-box
  // lets the padding eat into the 100vw/100vh box.
  overlay.style.cssText = 'padding-top:max(8px, env(safe-area-inset-top));padding-bottom:max(8px, env(safe-area-inset-bottom));padding-left:max(8px, env(safe-area-inset-left));padding-right:max(8px, env(safe-area-inset-right));margin:0;max-width:none;width:100vw;height:100vh;display:flex;flex-direction:column;overflow:hidden;position:relative;box-sizing:border-box;';

  // ── Rotate-device card (portrait fallback) ─────────────────────────
  const rotateCard = el('div', { parent: overlay });
  rotateCard.style.cssText = 'position:absolute;inset:0;display:none;align-items:center;justify-content:center;flex-direction:column;background:#02050d;color:rgba(220,210,255,0.85);text-align:center;padding:20px;z-index:30;';
  const rotateIcon = el('div', { parent: rotateCard, text: '📱↺' });
  rotateIcon.style.cssText = 'font-size:4rem;margin-bottom:18px;filter:drop-shadow(0 0 10px rgba(140,255,180,0.5));';
  el('h2', { parent: rotateCard, text: 'ROTATE TO LANDSCAPE' }).style.cssText = 'margin:0 0 10px;letter-spacing:0.18em;color:#8cffb4;';
  el('p', { parent: rotateCard, text: 'The controller is designed for two-thumb landscape play.' }).style.cssText = 'margin:0 0 16px;font-size:0.95rem;max-width:400px;line-height:1.5;';
  const pwaHint = el('p', { parent: rotateCard });
  pwaHint.style.cssText = 'margin:8px 0 0;font-size:0.8rem;color:rgba(180,140,255,0.7);max-width:420px;line-height:1.5;';
  pwaHint.innerHTML = 'Tip: tap the share icon → <strong>Add to Home Screen</strong> to install this as a controller PWA — opens straight into the joystick.';
  const isPortrait = (): boolean => window.innerHeight > window.innerWidth;
  const applyOrientation = (): void => {
    rotateCard.style.display = isPortrait() ? 'flex' : 'none';
  };
  applyOrientation();
  window.addEventListener('resize', applyOrientation);
  window.addEventListener('orientationchange', applyOrientation);

  // ── Top status strip ───────────────────────────────────────────────
  const titleBar = el('div', { parent: overlay });
  titleBar.style.cssText = 'display:flex;align-items:center;justify-content:space-between;padding:4px 10px;font-size:0.78rem;color:rgba(220,210,255,0.85);letter-spacing:0.14em;height:22px;flex-shrink:0;z-index:5;gap:10px;';
  const gameNameChip = el('span', { parent: titleBar, text: 'CONTROLLER' });
  // Right cluster: status + battery + network. Status takes precedence
  // for colour (paired green vs reconnecting orange); battery + net are
  // muted-grey so they sit under the status visually.
  const rightCluster = el('span', { parent: titleBar });
  rightCluster.style.cssText = 'display:flex;align-items:center;gap:10px;';
  const batteryChip = el('span', { parent: rightCluster });
  batteryChip.style.cssText = 'color:rgba(220,210,255,0.55);font-size:0.7rem;font-family:monospace;';
  batteryChip.textContent = '';
  const netChip = el('span', { parent: rightCluster });
  netChip.style.cssText = 'color:rgba(180,140,255,0.55);font-size:0.7rem;font-family:monospace;';
  netChip.textContent = '';
  const statusChip = el('span', { parent: rightCluster });
  statusChip.style.cssText = 'color:rgba(255,216,74,0.85);font-size:0.7rem;';
  statusChip.textContent = '…';

  // Battery API — Android Chrome only; Safari + Firefox have removed
  // it. Updates on level/charging changes. Silently no-ops elsewhere.
  void (async () => {
    type BatteryManager = { level: number; charging: boolean; addEventListener: (ev: string, cb: () => void) => void };
    type WithBattery = { getBattery?: () => Promise<BatteryManager> };
    const nav = navigator as unknown as WithBattery;
    if (typeof nav.getBattery !== 'function') return;
    let bm: BatteryManager;
    try { bm = await nav.getBattery(); } catch { return; }
    const paint = (): void => {
      const pct = Math.round(bm.level * 100);
      batteryChip.textContent = `${bm.charging ? '⚡' : '🔋'} ${pct}%`;
      if (pct <= 15 && !bm.charging) batteryChip.style.color = 'rgba(255,120,120,0.85)';
      else batteryChip.style.color = 'rgba(220,210,255,0.55)';
    };
    paint();
    bm.addEventListener('levelchange', paint);
    bm.addEventListener('chargingchange', paint);
  })();

  // Network Information API — Chromium-only. Surfaces effectiveType
  // ('4g' / '3g' / 'slow-2g') + downlink so users can see when a
  // congested link explains laggy input.
  void (() => {
    type NetInfo = { effectiveType?: string; downlink?: number; addEventListener?: (ev: string, cb: () => void) => void };
    type WithConnection = { connection?: NetInfo };
    const nav = navigator as unknown as WithConnection;
    const info = nav.connection;
    if (!info) return;
    const paint = (): void => {
      const et = info.effectiveType ?? '';
      const dl = info.downlink ? `${info.downlink}Mb` : '';
      netChip.textContent = et ? `${et.toUpperCase()}${dl ? ' · ' + dl : ''}` : '';
    };
    paint();
    info.addEventListener?.('change', paint);
  })();

  // Screen Wake Lock — keep the phone screen on while paired so the
  // controller doesn't suspend mid-run. Re-acquires on visibility
  // change because the lock auto-releases when the tab backgrounds.
  void (async () => {
    type WakeLockSentinel = { release: () => Promise<void> };
    type WithWakeLock = { wakeLock?: { request: (kind: 'screen') => Promise<WakeLockSentinel> } };
    const nav = navigator as unknown as WithWakeLock;
    if (!nav.wakeLock) return;
    let sentinel: WakeLockSentinel | null = null;
    const acquire = async (): Promise<void> => {
      try { sentinel = await nav.wakeLock!.request('screen'); } catch { /* ignore */ }
    };
    await acquire();
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && !sentinel) void acquire();
    });
  })();

  // ── Gamepad surface — a single positioned canvas so shoulders sit
  //    at the screen corners regardless of where the joystick + face
  //    button clusters land. Joystick on the left thumb, face buttons
  //    diamond on the right thumb, shoulders along the top edge.
  const surface = el('div', { parent: overlay });
  surface.style.cssText = 'flex:1;position:relative;min-height:0;';

  // Buttons not in the spec stay hidden. We pre-build every standard
  // slot at fixed positions and toggle visibility based on the spec.
  const slotEls = new Map<string, HTMLElement>();
  const slotLabels = new Map<string, HTMLElement>();

  const makeButton = (slot: string, css: string, defaultColour = 'rgba(220,210,255,0.7)'): HTMLElement => {
    const btn = el('div', { parent: surface });
    // border-radius comes from the caller's css string (face buttons
    // want 50%, shoulders want 14px). Same for width/height/position.
    btn.style.cssText = `position:absolute;display:none;align-items:center;justify-content:center;background:rgba(255,255,255,0.04);border:2px solid ${defaultColour}55;user-select:none;-webkit-tap-highlight-color:transparent;touch-action:none;color:${defaultColour};text-shadow:0 0 12px ${defaultColour}88;font-weight:bold;${css}`;
    slotEls.set(slot, btn);
    const inner = el('div', { parent: btn });
    inner.style.cssText = 'display:flex;flex-direction:column;align-items:center;justify-content:center;gap:2px;line-height:1;pointer-events:none;';
    slotLabels.set(slot, inner);
    return btn;
  };

  // Ergonomic two-thumb layout. Thumbs rest at the bottom corners of
  // a phone held in landscape, so:
  //   - Joystick sits bottom-left, centred ~thumb-reach from corner
  //   - Face-button diamond sits bottom-right, mirroring it
  //   - Shoulders ride the top-left + top-right corners (index fingers
  //     or thumb-stretches)
  //   - start + select small at the top centre (rarely-pressed pause-y
  //     things)
  // Sizes use clamp() so a tiny phone and a large tablet both work.

  // Mirrors the in-game touch.ts cluster the user said feels right:
  // FIRE is a big horizontal rounded-rect across the bottom-right,
  // WARP + SHIELD sit as smaller pills above it side-by-side, PAUSE
  // tucks into the top-right corner. Joystick anchors bottom-left
  // (smaller than the face cluster — player preference: dominant
  // FIRE button, lean joystick).
  const SHOULDER_W = 'clamp(74px, 22vh, 110px)';
  const SHOULDER_H = 'clamp(48px, 14vh, 70px)';
  // FIRE (A) — the big wide one. Bottom-right edge.
  const FIRE_W = 'clamp(220px, 28vw, 320px)';
  const FIRE_H = 'clamp(88px, 24vh, 116px)';
  // SHIELD / WARP — the smaller pills above FIRE.
  const SEC_W  = 'clamp(110px, 16vw, 168px)';
  const SEC_H  = 'clamp(64px, 18vh, 92px)';
  // PAUSE (Y) — tucks in the top-right; small and easy to tap.
  const SYS_W = 'clamp(76px, 14vh, 96px)';
  const SYS_H = 'clamp(40px, 10vh, 54px)';

  // Shoulders + start/select stay in the spec for non-Pallasite games
  // but Pallasite hides them. Positions kept tight to the corners so
  // they don't clash with the face cluster when a game does use them.
  makeButton('L1', `top:8px;left:8px;width:${SHOULDER_W};height:${SHOULDER_H};border-radius:14px;font-size:1rem;`);
  makeButton('L2', `top:8px;left:calc(16px + ${SHOULDER_W});width:${SHOULDER_W};height:${SHOULDER_H};border-radius:14px;font-size:1rem;`);
  makeButton('R2', `top:calc(16px + ${SYS_H});right:8px;width:${SHOULDER_W};height:${SHOULDER_H};border-radius:14px;font-size:1rem;`);
  makeButton('R1', `top:calc(16px + ${SYS_H} + 12px + ${SHOULDER_H});right:8px;width:${SHOULDER_W};height:${SHOULDER_H};border-radius:14px;font-size:1rem;`);
  makeButton('select', `top:14px;left:calc(50% - ${SYS_W} - 4px);width:${SYS_W};height:${SYS_H};border-radius:14px;font-size:0.78rem;`);

  // Face cluster — vertically centred on the right thumb edge. Phone
  // cases eat the bottom 30-50px of the screen so a bottom-anchored
  // cluster forces thumb-reach down past the case lip. Centring on
  // top:50% puts the cluster squarely in the thumb's natural sweep.
  //
  //   [Y]                ← PAUSE (small, top of the column)
  //   [X] [B]            ← WARP + SHIELD pills
  //   [    A    ]        ← FIRE wide pill
  //
  // Wrapped in a flex column anchored to right:14px with translateY
  // to perfectly centre. Buttons are flex children (position:static)
  // so the column auto-sizes around them.
  const faceWrap = el('div', { parent: surface });
  faceWrap.style.cssText = 'position:absolute;top:50%;right:14px;transform:translateY(-50%);display:flex;flex-direction:column;align-items:flex-end;gap:10px;pointer-events:none;';
  const secRow = el('div', { parent: faceWrap });
  secRow.style.cssText = 'display:flex;gap:10px;justify-content:flex-end;';

  const reparent = (slot: string, parent: HTMLElement): void => {
    const btn = slotEls.get(slot);
    if (btn) parent.appendChild(btn);  // moves from `surface` to the flex parent
  };
  makeButton('Y', `position:static;width:${SYS_W};height:${SYS_H};border-radius:14px;font-size:0.85rem;pointer-events:auto;`);
  makeButton('X', `position:static;width:${SEC_W};height:${SEC_H};border-radius:calc(${SEC_H} / 2);font-size:1rem;pointer-events:auto;`);
  makeButton('B', `position:static;width:${SEC_W};height:${SEC_H};border-radius:calc(${SEC_H} / 2);font-size:1rem;pointer-events:auto;`);
  makeButton('A', `position:static;width:${FIRE_W};height:${FIRE_H};border-radius:calc(${FIRE_H} / 2);font-size:1.1rem;pointer-events:auto;`);
  reparent('Y', faceWrap);
  reparent('X', secRow);
  reparent('B', secRow);
  reparent('A', faceWrap);
  // `start` stays in the top centre — not used by Pallasite but
  // available to other specs that want a system button surface.
  makeButton('start', `top:8px;right:calc(50% - ${SYS_W} / 2);width:${SYS_W};height:${SYS_H};border-radius:14px;font-size:0.78rem;`);

  // ── Joystick (left thumb) — anchored bottom-left, sized to match
  //    the face cluster so left and right thumb work in symmetry.
  // Joystick responsiveness — lower deadzone (was 0.18, now 0.10) so a
  // small thumb deflection already starts steering. Heading sample
  // rate up from 20Hz to 33Hz for less perceived lag. Pad radius
  // reaches max at 60% of pad-pixels (was 70%), so smaller drags hit
  // full deflection.
  const JOY_HEADING_DEADZONE = 0.10;
  const JOY_THRUST_THRESHOLD = 0.45;
  const JOY_TAP_TIME_MS = 220;
  const JOY_TAP_MOVE_PX = 8;
  const HEADING_SAMPLE_MS = 30;
  const JOY_RADIUS_SCALE = 0.6;
  // Joystick is smaller than face cluster — feedback was that the
  // joystick dominated the screen. Capped tighter and hugged closer
  // to the left edge.
  const JOY_SIZE = 'clamp(170px, 48vh, 240px)';

  const pad = el('div', { parent: surface });
  // Vertically centred on the left thumb edge — bottom-anchored
  // joysticks were unreachable past the phone case lip on most phones.
  pad.style.cssText = `position:absolute;left:14px;top:50%;transform:translateY(-50%);width:${JOY_SIZE};height:${JOY_SIZE};border-radius:50%;background:radial-gradient(circle, rgba(140,255,180,0.10) 0%, rgba(140,255,180,0.04) 60%, rgba(140,255,180,0) 100%);border:2px solid rgba(140,255,180,0.35);touch-action:none;-webkit-tap-highlight-color:transparent;display:none;`;
  const knob = el('div', { parent: pad });
  // No always-on transition; drag updates are per-frame and need to be instant.
  // The snap-back transition is applied at release time and cleared after.
  // will-change keeps the knob on its own compositor layer for smooth translate.
  knob.style.cssText = 'position:absolute;left:50%;top:50%;width:38%;height:38%;margin:-19% 0 0 -19%;border-radius:50%;background:radial-gradient(circle, rgba(140,255,180,0.45) 0%, rgba(91,255,140,0.18) 70%);border:2px solid rgba(140,255,180,0.75);box-shadow:0 0 18px rgba(140,255,180,0.4);transform:translate(0,0);will-change:transform;';
  slotEls.set('joyL', pad);

  // ── D-pad (left thumb, menu mode) — replaces the joystick when the
  //    host signals a menu/initials phase. Cross-shaped, sized to fit
  //    inside the joystick's footprint so swapping in/out doesn't
  //    reflow the left thumb area.
  const DPAD_BTN = 'clamp(58px, 13vh, 84px)';
  // Offset from the joystick centre — buttons sit 1.05× their own
  // width away so the cross gap is ~5% of a button.
  const DPAD_OFFSET = 'clamp(64px, 14vh, 90px)';
  const DPAD_CENTRE_X = `calc(14px + ${JOY_SIZE} / 2)`;
  // dpad arrows are big — keep label hidden, lean on the icon.
  makeButton('dpadU', `left:calc(${DPAD_CENTRE_X} - ${DPAD_BTN} / 2);top:calc(50% - ${DPAD_OFFSET} - ${DPAD_BTN} / 2);width:${DPAD_BTN};height:${DPAD_BTN};border-radius:18px;font-size:1.8rem;`);
  makeButton('dpadD', `left:calc(${DPAD_CENTRE_X} - ${DPAD_BTN} / 2);top:calc(50% + ${DPAD_OFFSET} - ${DPAD_BTN} / 2);width:${DPAD_BTN};height:${DPAD_BTN};border-radius:18px;font-size:1.8rem;`);
  makeButton('dpadL', `left:calc(${DPAD_CENTRE_X} - ${DPAD_OFFSET} - ${DPAD_BTN} / 2);top:calc(50% - ${DPAD_BTN} / 2);width:${DPAD_BTN};height:${DPAD_BTN};border-radius:18px;font-size:1.8rem;`);
  makeButton('dpadR', `left:calc(${DPAD_CENTRE_X} + ${DPAD_OFFSET} - ${DPAD_BTN} / 2);top:calc(50% - ${DPAD_BTN} / 2);width:${DPAD_BTN};height:${DPAD_BTN};border-radius:18px;font-size:1.8rem;`);

  // ── Waiting-for-game card (centre overlay, hidden once spec lands)
  const waitCard = el('div', { parent: surface });
  waitCard.style.cssText = 'position:absolute;inset:0;display:flex;align-items:center;justify-content:center;flex-direction:column;color:rgba(220,210,255,0.7);text-align:center;padding:20px;pointer-events:none;';
  const waitIcon = el('div', { parent: waitCard, text: '⏳' });
  waitIcon.style.cssText = 'font-size:3rem;margin-bottom:14px;opacity:0.85;';
  const waitTitle = el('h3', { parent: waitCard, text: 'WAITING FOR GAME…' });
  waitTitle.style.cssText = 'margin:0 0 6px;letter-spacing:0.15em;color:#ffd84a;font-size:1rem;';
  const waitBody = el('p', { parent: waitCard, text: 'Scan a game QR code or share a pairing link to start controlling.' });
  waitBody.style.cssText = 'margin:0;font-size:0.85rem;max-width:480px;line-height:1.5;';

  // ── Wire setup ──────────────────────────────────────────────────────
  const importMobile = import('./controller-mobile.js');
  const importHost = import('./controller-host.js');
  let client: import('./controller-mobile.js').ControllerClient | null = null;

  const setSlotConfig = (slot: string, cfg: import('./controller-types.js').SlotConfig): void => {
    const btnEl = slotEls.get(slot);
    if (!btnEl) return;
    btnEl.style.display = slot === 'joyL' ? 'block' : 'flex';
    const colour = cfg.colour ?? '#8cffb4';
    btnEl.style.color = colour;
    btnEl.style.borderColor = colour + '88';
    btnEl.style.textShadow = `0 0 12px ${colour}88`;
    if (slot !== 'joyL') {
      const inner = slotLabels.get(slot);
      if (inner) {
        inner.innerHTML = '';
        // Per-slot sizing — FIRE (A) is the dominant horizontal pill,
        // SHIELD/WARP (B/X) are smaller pills above it, PAUSE (Y) is
        // a top-corner pill. Shoulders + system buttons stay neutral.
        let iconSize: string;
        let labelSize: string;
        let layout: 'col' | 'row' = 'col';
        switch (slot) {
          case 'A':                iconSize = '1.9rem'; labelSize = '0.9rem'; layout = 'row'; break;
          case 'B': case 'X':      iconSize = '1.5rem'; labelSize = '0.72rem'; layout = 'row'; break;
          case 'Y':                iconSize = '1.05rem'; labelSize = '0.62rem'; layout = 'row'; break;
          case 'start': case 'select': iconSize = '1rem'; labelSize = '0.6rem'; break;
          default:                 iconSize = '1.3rem'; labelSize = '0.65rem'; break;
        }
        inner.style.flexDirection = layout === 'row' ? 'row' : 'column';
        inner.style.gap = layout === 'row' ? '10px' : '2px';
        if (cfg.icon) {
          const ic = el('span', { parent: inner, text: cfg.icon });
          ic.style.cssText = `font-size:${iconSize};line-height:1;`;
        }
        if (cfg.label) {
          const lb = el('span', { parent: inner, text: cfg.label });
          lb.style.cssText = `font-size:${labelSize};letter-spacing:0.14em;color:${colour};font-weight:bold;`;
        }
      }
    }
  };

  const applySpec = (spec: import('./controller-types.js').ControllerSpec): void => {
    gameNameChip.textContent = (spec.name ?? 'GAME').toUpperCase() + ' · CONTROLLER';
    // Reset all slots to hidden, then enable the ones the spec lists.
    for (const slotEl of slotEls.values()) slotEl.style.display = 'none';
    for (const [slot, cfg] of Object.entries(spec.slots ?? {})) {
      if (!cfg) continue;
      setSlotConfig(slot, cfg);
    }
    waitCard.style.display = 'none';
  };

  void Promise.all([importMobile, importHost]).then(([m, h]) => {
    const token = h.decodePairingUrl(window.location.href);
    if (!token) {
      statusChip.textContent = 'NEED PAIRING';
      statusChip.style.color = 'rgba(255,120,120,0.9)';
      waitTitle.textContent = 'NOT PAIRED';
      waitBody.textContent = 'Open the QR scanner on the home page or scan a fresh game code.';
      return;
    }
    statusChip.textContent = 'CONNECTING…';
    client = m.startControllerClient(token);
    client.onStatus((s) => {
      switch (s.kind) {
        case 'connecting':    statusChip.textContent = 'CONNECTING…'; statusChip.style.color = 'rgba(255,216,74,0.85)'; break;
        case 'waiting':       statusChip.textContent = 'WAITING FOR GAME'; statusChip.style.color = 'rgba(255,216,74,0.85)'; break;
        case 'paired':        statusChip.textContent = 'PAIRED · LIVE'; statusChip.style.color = 'rgba(91,255,140,0.95)'; break;
        case 'reconnecting':  statusChip.textContent = 'RECONNECTING…'; statusChip.style.color = 'rgba(255,150,80,0.9)'; break;
        case 'closed':        statusChip.textContent = 'CLOSED'; statusChip.style.color = 'rgba(255,120,120,0.9)'; break;
      }
    });
    client.onSpec((spec) => applySpec(spec));
  });

  // ── Joystick interaction (always built; visible iff joyL in spec) ──
  // Floating-origin stick (nipplejs `dynamic` mode): the touchdown point IS
  // the stick centre for that gesture, not the CSS-anchored pad centre, so
  // thumb position doesn't have to land on the pad bullseye.
  let joyActive = false;
  let joyActiveId: number | null = null;
  let joyOriginX = 0, joyOriginY = 0;
  let joyPadCx = 0, joyPadCy = 0;     // pad CSS centre, cached on pointerdown
  let joyMaxRadius = 100;
  let joyPressedAt = 0;
  let joyMaxDrift = 0;
  let joyDidEngage = false;
  let lastHeadingSendAt = 0;
  let thrustingNow = false;
  let joyTapSlot: string | null = null;
  const JOY_SNAP_BACK_MS = 140;       // CSS transition 120ms + margin to clear transition style

  const sendThrust = (on: boolean): void => {
    if (thrustingNow === on) return;
    thrustingNow = on;
    // Soft thump when thrust engages — gives the player tactile
    // confirmation past the deadzone threshold. Skipping the
    // disengagement vibration keeps the buzz from chattering on
    // the deadzone boundary.
    if (on && typeof navigator.vibrate === 'function') {
      try { navigator.vibrate(10); } catch { /* ignore */ }
    }
    client?.sendInput('joyL-thrust', on ? '1' : '0');
  };

  pad.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    joyActive = true;
    joyActiveId = e.pointerId;
    const rect = pad.getBoundingClientRect();
    joyPadCx = rect.left + rect.width / 2;
    joyPadCy = rect.top + rect.height / 2;
    joyOriginX = e.clientX;
    joyOriginY = e.clientY;
    joyMaxRadius = (rect.width / 2) * JOY_RADIUS_SCALE;
    joyPressedAt = performance.now();
    joyMaxDrift = 0;
    joyDidEngage = false;
    joyTapSlot = client?.spec?.slots?.joyL?.tapAction ?? null;
    pad.setPointerCapture(e.pointerId);
    // Snap the knob under the finger immediately, no transition.
    knob.style.transition = 'none';
    knob.style.transform = `translate(${(joyOriginX - joyPadCx).toFixed(1)}px, ${(joyOriginY - joyPadCy).toFixed(1)}px)`;
  });
  pad.addEventListener('pointermove', (e) => {
    if (!joyActive || e.pointerId !== joyActiveId) return;
    const dx = e.clientX - joyOriginX;
    const dy = e.clientY - joyOriginY;
    const dist = Math.hypot(dx, dy);
    if (dist > joyMaxDrift) joyMaxDrift = dist;
    const clipped = Math.min(dist || 1, joyMaxRadius);
    const clipDx = (dx / (dist || 1)) * clipped;
    const clipDy = (dy / (dist || 1)) * clipped;
    // Knob translate is relative to its CSS home (pad centre); compose the
    // floating-origin offset with the clipped drag delta.
    const kx = (joyOriginX - joyPadCx) + clipDx;
    const ky = (joyOriginY - joyPadCy) + clipDy;
    knob.style.transform = `translate(${kx.toFixed(1)}px, ${ky.toFixed(1)}px)`;
    const magnitude = clipped / joyMaxRadius;
    if (magnitude > JOY_HEADING_DEADZONE) {
      joyDidEngage = true;
      const angle = Math.atan2(dy, dx);
      const now = performance.now();
      if (now - lastHeadingSendAt >= HEADING_SAMPLE_MS) {
        lastHeadingSendAt = now;
        const norm = ((angle % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
        client?.sendInput('joyL', String(Math.round(norm * 1000)));
      }
      sendThrust(magnitude > JOY_THRUST_THRESHOLD);
    } else {
      sendThrust(false);
    }
  });
  const joyRelease = (e?: PointerEvent): void => {
    if (!joyActive) return;
    if (e && e.pointerId !== joyActiveId) return;
    joyActive = false;
    joyActiveId = null;
    const heldMs = performance.now() - joyPressedAt;
    // Animated snap-back to centre. Transition is cleared after the animation
    // so the next gesture's instant per-frame translate isn't interpolated.
    knob.style.transition = 'transform 120ms cubic-bezier(0.2, 0.9, 0.3, 1)';
    knob.style.transform = 'translate(0px, 0px)';
    window.setTimeout(() => { knob.style.transition = 'none'; }, JOY_SNAP_BACK_MS);
    sendThrust(false);
    client?.sendInput('joyL-end', '1');
    if (!joyDidEngage && heldMs < JOY_TAP_TIME_MS && joyMaxDrift < JOY_TAP_MOVE_PX) {
      client?.sendInput('joyL-tap', '1');
      // Tap-fire haptic — mirrors the A/FIRE button's 18ms thump so a
      // quick joystick tap feels the same as pressing FIRE.
      if (typeof navigator.vibrate === 'function') {
        try { navigator.vibrate(18); } catch { /* ignore */ }
      }
      // Also emit the tapAction slot if the spec wired one — gives the
      // host a slot-keyed press event to feed straight into its slot map.
      if (joyTapSlot) {
        const target = joyTapSlot;
        client?.sendInput(target, '1');
        window.setTimeout(() => client?.sendInput(target, '0'), 60);
      }
    }
  };
  pad.addEventListener('pointerup', joyRelease);
  pad.addEventListener('pointercancel', joyRelease);

  // PWA backgrounded mid-press: synthesise a release so the knob isn't
  // stuck and the host isn't told the user is still thrusting while
  // they take a call or accept a notification.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden' && joyActive) {
      joyRelease();
    }
  });

  // ── Bind press/release on every standard button slot ───────────────
  // Each slot is hold-style by default (presses send '1', releases '0').
  // One-shot semantics are the host's job — the slot map can ignore
  // releases for action buttons like SHIELD that should fire once per
  // press.
  // Vibration intensity per slot type. navigator.vibrate is widely
  // supported on Android Chrome / Firefox; iOS Safari silently no-ops,
  // which is fine — the buttons still work, just without haptics.
  const VIBE_MS: Record<string, number> = {
    A: 18,  // FIRE — primary, slightly heavier so the player feels each shot
    B: 28,  // SHIELD — defensive, distinct heavier thump
    X: 22,  // WARP — escape, mid-thump
    Y: 8,   // PAUSE — light tick
    dpadU: 6, dpadD: 6, dpadL: 6, dpadR: 6,  // d-pad — barely-there ticks for menu nav
  };
  const bindButton = (slot: string): void => {
    const btn = slotEls.get(slot);
    if (!btn || slot === 'joyL') return;
    const press = (e: Event): void => {
      e.preventDefault();
      btn.style.background = 'rgba(255,255,255,0.18)';
      const ms = VIBE_MS[slot];
      if (ms && typeof navigator.vibrate === 'function') {
        try { navigator.vibrate(ms); } catch { /* iOS / unsupported — ignore */ }
      }
      client?.sendInput(slot, '1');
    };
    const release = (): void => {
      btn.style.background = 'rgba(255,255,255,0.04)';
      client?.sendInput(slot, '0');
    };
    btn.addEventListener('pointerdown', press);
    btn.addEventListener('pointerup', release);
    btn.addEventListener('pointercancel', release);
    btn.addEventListener('pointerleave', release);
  };
  for (const slot of ['A', 'B', 'X', 'Y', 'L1', 'L2', 'R1', 'R2', 'start', 'select', 'dpadU', 'dpadD', 'dpadL', 'dpadR']) bindButton(slot);
}

export function renderAdminPanel(): void {
  clearOverlay();
  const overlay = el('div', { className: 'overlay', parent: root });
  setupOverlayArrowNav(overlay);

  el('h2', { parent: overlay, text: 'ADMIN · LAYER 1 REVIEW' });
  const intro = el('p', { parent: overlay });
  intro.style.cssText = 'margin:4px 0 18px;font-size:0.85rem;color:rgba(220,210,255,0.7);letter-spacing:0.08em;max-width:640px;';
  intro.textContent = 'Heuristic-flagged runs awaiting visual review. WATCH opens the kind 30763 ghost in the theatre.';

  const cached = (() => {
    try { return sessionStorage.getItem(ADMIN_TOKEN_KEY); } catch { return null; }
  })();

  if (!cached) {
    renderAdminTokenPrompt(overlay);
    return;
  }

  renderAdminFlaggedList(overlay, cached);
}

function renderAdminTokenPrompt(overlay: HTMLElement): void {
  const form = el('div', { parent: overlay });
  form.style.cssText = 'display:flex;flex-direction:column;gap:10px;align-items:center;margin:18px auto;max-width:420px;';

  el('p', { parent: form, text: 'Paste your faucet ADMIN_TOKEN to view flagged runs.' })
    .style.cssText = 'margin:0;font-size:0.88rem;color:rgba(220,210,255,0.85);';

  const input = el('input', { parent: form });
  input.setAttribute('type', 'password');
  input.setAttribute('autocomplete', 'off');
  input.setAttribute('placeholder', 'bearer token');
  input.style.cssText = 'width:100%;padding:10px 12px;background:rgba(255,255,255,0.06);border:1px solid rgba(255,255,255,0.18);border-radius:6px;color:#fff;font-family:monospace;letter-spacing:0.05em;';

  const row = el('div', { className: 'menu-row', parent: form });
  const enterBtn = el('button', { className: 'menu-btn', parent: row, text: 'ENTER' });
  const closeBtn = el('button', { className: 'menu-btn secondary', parent: row, text: 'BACK' });

  const submit = (): void => {
    const token = (input as HTMLInputElement).value.trim();
    if (!token) return;
    try { sessionStorage.setItem(ADMIN_TOKEN_KEY, token); } catch { /* sessionStorage blocked — keep in-memory only */ }
    renderAdminPanel();
  };

  input.addEventListener('keydown', (e) => {
    if ((e as KeyboardEvent).key === 'Enter') submit();
  });
  enterBtn.addEventListener('click', submit);
  closeBtn.addEventListener('click', exitAdmin);

  setTimeout(() => (input as HTMLInputElement).focus(), 0);
}

function renderAdminFlaggedList(overlay: HTMLElement, token: string): void {
  const status = el('p', { parent: overlay, text: 'Loading flagged runs…' });
  status.style.cssText = 'margin:0 0 12px;font-size:0.88rem;color:rgba(180,140,255,0.7);letter-spacing:0.08em;min-height:1.2em;';

  const list = el('div', { parent: overlay });
  list.style.cssText = 'display:flex;flex-direction:column;gap:10px;max-width:760px;width:100%;margin:0 auto;';

  const footer = el('div', { className: 'menu-row', parent: overlay });
  const refreshBtn = el('button', { className: 'menu-btn secondary', parent: footer, text: 'REFRESH' });
  const logoutBtn = el('button', { className: 'menu-btn secondary', parent: footer, text: 'CLEAR TOKEN' });
  const closeBtn = el('button', { className: 'menu-btn', parent: footer, text: 'BACK' });

  refreshBtn.addEventListener('click', () => renderAdminPanel());
  logoutBtn.addEventListener('click', () => {
    try { sessionStorage.removeItem(ADMIN_TOKEN_KEY); } catch { /* ignore */ }
    renderAdminPanel();
  });
  closeBtn.addEventListener('click', exitAdmin);

  void (async () => {
    const result = await fetchFlagged(token);
    if (!result.ok) {
      if (result.error === 'unauthorized') {
        try { sessionStorage.removeItem(ADMIN_TOKEN_KEY); } catch { /* ignore */ }
        status.textContent = 'Token rejected. Re-enter to retry.';
        setTimeout(() => renderAdminPanel(), 1200);
        return;
      }
      status.textContent = `Failed: ${result.error}${result.status ? ` (HTTP ${result.status})` : ''}`;
      return;
    }
    if (result.flagged.length === 0) {
      status.textContent = 'No flagged runs. Either the heuristics haven\'t tripped, or you\'ve cleared everything.';
      return;
    }
    status.textContent = `${result.flagged.length} flagged ${result.flagged.length === 1 ? 'player' : 'players'}.`;
    for (const entry of result.flagged) {
      list.appendChild(renderFlaggedRow(entry, token));
    }
  })();
}

function renderFlaggedRow(entry: FlaggedEntry, token: string): HTMLElement {
  const row = el('div');
  row.style.cssText = 'border:1px solid rgba(255,80,80,0.35);border-radius:8px;padding:10px 14px;background:rgba(255,80,80,0.06);display:flex;flex-direction:column;gap:6px;';

  const head = el('div', { parent: row });
  head.style.cssText = 'display:flex;justify-content:space-between;align-items:baseline;gap:12px;flex-wrap:wrap;';

  const pk = el('div', { parent: head, text: shortPubkey(entry.pubkey) });
  pk.style.cssText = 'font-family:monospace;font-size:0.95rem;color:#ffb0b0;letter-spacing:0.06em;';

  const when = el('div', { parent: head, text: formatFlaggedAt(entry.flagged_at) });
  when.style.cssText = 'font-size:0.78rem;color:rgba(220,210,255,0.6);letter-spacing:0.06em;';

  const reasonText = entry.flag_reason ?? '(no reason recorded)';
  const reason = el('div', { parent: row, text: reasonText });
  reason.style.cssText = 'font-size:0.82rem;color:rgba(255,200,200,0.85);font-family:monospace;word-break:break-word;';

  const meta = el('div', { parent: row });
  meta.style.cssText = 'display:flex;gap:14px;font-size:0.82rem;color:rgba(220,210,255,0.75);letter-spacing:0.06em;flex-wrap:wrap;';
  if (entry.claim) {
    el('span', { parent: meta, text: `WAVE ${entry.claim.wave}` });
    el('span', { parent: meta, text: `SCORE ${entry.claim.score?.toLocaleString() ?? '—'}` });
    if (entry.claim.seed) el('span', { parent: meta, text: `SEED ${entry.claim.seed}` });
  } else {
    el('span', { parent: meta, text: 'No credited claim recorded.' });
  }

  const actions = el('div', { parent: row });
  actions.style.cssText = 'display:flex;gap:8px;margin-top:4px;';
  const watch = el('button', { className: 'menu-btn', parent: actions, text: 'WATCH' });
  if (!entry.claim?.score_event_id) {
    watch.setAttribute('disabled', 'true');
    watch.style.opacity = '0.4';
    watch.title = 'No kind 30762 score event id stored — cannot fetch ghost.';
  } else {
    const claim = entry.claim;
    watch.addEventListener('click', () => {
      renderReplayTheatre({
        scoreEventId: claim.score_event_id!,
        displayName: shortPubkey(entry.pubkey),
        score: claim.score ?? 0,
        wave: claim.wave ?? 0,
        sats: 0,
        onClose: () => renderAdminPanel(),
      });
    });
  }
  const copyPk = el('button', { className: 'menu-btn secondary', parent: actions, text: 'COPY PUBKEY' });
  copyPk.addEventListener('click', () => {
    void navigator.clipboard?.writeText(entry.pubkey).then(() => {
      copyPk.textContent = 'COPIED';
      setTimeout(() => (copyPk.textContent = 'COPY PUBKEY'), 1200);
    });
  });
  // DELETE — publishes a NIP-09 kind 5 deletion from the game pubkey
  // referencing the kind 30762 score event id (and any associated
  // ghost/replay/case events the faucet has on file), and clears the
  // flag from the faucet DB. Only enabled when we have a score event
  // id to delete by — anonymous flags or claim-less rows don't have a
  // canonical thing to point the NIP-09 at.
  const scoreId = entry.claim?.score_event_id ?? null;
  const del = el('button', { className: 'menu-btn secondary', parent: actions, text: 'DELETE' }) as HTMLButtonElement;
  del.style.cssText += 'color:#ffb0b0;border-color:rgba(255,120,120,0.5);';
  if (!scoreId) {
    del.disabled = true;
    del.style.opacity = '0.4';
    del.title = 'No score event id stored — no canonical event to NIP-09 against.';
  } else {
    del.addEventListener('click', () => {
      if (!window.confirm(`Delete this flag and publish NIP-09 kind 5 referencing ${scoreId.slice(0, 12)}…?\n\nThis broadcasts a deletion to all relays. Honest relays will drop the score + ghost + replay events. Cannot be undone.`)) return;
      void (async () => {
        del.disabled = true;
        del.textContent = 'DELETING…';
        const result = await requestDeleteFlag(token, { scoreEventId: scoreId });
        if (result.ok) {
          row.style.opacity = '0.4';
          row.style.pointerEvents = 'none';
          del.textContent = result.deletionEventId
            ? `DELETED · ${result.deletionEventId.slice(0, 8)}…`
            : 'DELETED';
        } else {
          del.disabled = false;
          del.textContent = `FAILED · ${result.error}`;
          setTimeout(() => (del.textContent = 'DELETE'), 2400);
        }
      })();
    });
  }
  return row;
}

function exitAdmin(): void {
  // Drop ?admin from the URL so a refresh goes back to the normal title.
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete('admin');
    window.history.replaceState({}, '', url.toString());
  } catch { /* ignore */ }
  window.location.reload();
}

// ── Jury page ─────────────────────────────────────────────────────────────────
//
// Public cheat-review surface at /jury. Anyone can land here — no sign-in
// required to spectate. Signed-in players can set up an anonymous jury
// identity (kind 30765 delegation) so the faucet can include them in
// future case circles, enabling LSAG ballot signing in a later phase.
//
// Pairs with src/jury.ts (data layer) and the faucet's review.ts +
// delegations.ts (case publication + delegation watcher).

function exitJury(): void {
  // Path-based route: send the user back to "/" so a normal refresh shows
  // the title screen. Using location.assign so the SPA reboots cleanly
  // rather than trying to re-render the title from the jury overlay.
  try {
    window.location.assign('/');
  } catch {
    window.location.reload();
  }
}

export function renderJuryPage(state: GameState): void {
  clearOverlay();
  const overlay = el('div', { className: 'overlay', parent: root });
  setupOverlayArrowNav(overlay);

  const header = el('div', { parent: overlay });
  header.style.cssText = 'display:flex;align-items:baseline;gap:12px;flex-wrap:wrap;justify-content:center;margin-bottom:6px;';
  el('h2', { parent: header, text: 'PALLASITE · JURY' });
  const tag = el('span', { parent: header, text: 'PUBLIC · ANONYMOUS REVIEW' });
  tag.style.cssText = 'font-size:0.72rem;color:rgba(180,140,255,0.7);letter-spacing:0.18em;font-family:monospace;';

  const intro = el('p', { parent: overlay });
  intro.style.cssText = 'margin:4px auto 18px;font-size:0.85rem;color:rgba(220,210,255,0.75);max-width:680px;line-height:1.5;text-align:center;';
  intro.innerHTML =
    'The faucet flags runs whose telemetry looks impossible (bot-tier hit ratios, score inflation, etc.). ' +
    'Each flag publishes a kind 31764 case to relays. Anyone can watch the ghost replay. ' +
    'Jurors verified by NIP-58 badges cast anonymous trust scores via <span style="color:#b890ff;font-family:monospace;">nostr-veil</span> — ' +
    'the ring signature hides who voted while the verdict (median rank) is publicly auditable.';

  // Setup banner — depends on session + identity state. Re-rendered on
  // state changes (sign-in completes, identity created, delegation
  // published, etc.).
  const setupSection = el('div', { parent: overlay });
  setupSection.style.cssText = 'margin:0 auto 18px;max-width:680px;width:100%;';
  renderJurySetupBanner(setupSection, state);

  const status = el('p', { parent: overlay });
  status.style.cssText = 'margin:0 0 12px;font-size:0.88rem;color:rgba(180,140,255,0.7);letter-spacing:0.08em;min-height:1.2em;text-align:center;';
  status.textContent = 'Loading open cases…';

  const list = el('div', { parent: overlay });
  list.style.cssText = 'display:flex;flex-direction:column;gap:10px;max-width:760px;width:100%;margin:0 auto;';

  const footer = el('div', { className: 'menu-row', parent: overlay });
  const refreshBtn = el('button', { className: 'menu-btn secondary', parent: footer, text: 'REFRESH' });
  const backBtn = el('button', { className: 'menu-btn', parent: footer, text: 'BACK' });
  refreshBtn.addEventListener('click', () => renderJuryPage(state));
  backBtn.addEventListener('click', exitJury);

  void (async () => {
    const cases = await fetchReviewCases();
    if (cases.length === 0) {
      status.textContent = 'No open cases on relays right now. Either the heuristics have not tripped, or the relay set is unreachable.';
      return;
    }
    const word = cases.length === 1 ? 'case' : 'cases';
    status.textContent = `${cases.length} open ${word}.`;
    const identity = getStoredJuryIdentity();
    for (const c of cases) {
      list.appendChild(renderJuryCaseCard(c, identity, state));
    }
  })();
}

function renderJurySetupBanner(parent: HTMLElement, state: GameState): void {
  parent.innerHTML = '';
  const session = state.session;
  const identity = getStoredJuryIdentity();

  if (!session) {
    const card = el('div', { parent });
    card.style.cssText = 'border:1px solid rgba(184,144,255,0.35);border-radius:8px;padding:12px 16px;background:rgba(120,90,200,0.08);display:flex;flex-direction:column;gap:8px;';
    const title = el('div', { parent: card, text: 'Spectator mode' });
    title.style.cssText = 'font-size:0.92rem;color:#cbb6ff;letter-spacing:0.12em;text-transform:uppercase;';
    el('div', { parent: card, text: 'Anyone can watch the ghosts and read the verdict trail. Sign in if you want to set up an anonymous jury identity and vote on future cases.' })
      .style.cssText = 'font-size:0.85rem;color:rgba(220,210,255,0.8);line-height:1.5;';
    const row = el('div', { parent: card });
    row.style.cssText = 'display:flex;gap:8px;flex-wrap:wrap;';
    const signInBtn = el('button', { className: 'menu-btn', parent: row, text: 'SIGN IN' });
    signInBtn.addEventListener('click', () => { void onJurySignIn(state, parent); });
    return;
  }

  if (!identity) {
    const card = el('div', { parent });
    card.style.cssText = 'border:1px solid rgba(184,144,255,0.45);border-radius:8px;padding:12px 16px;background:rgba(120,90,200,0.12);display:flex;flex-direction:column;gap:8px;';
    el('div', { parent: card, text: 'You\'re signed in. Set up a jury identity to enable voting.' })
      .style.cssText = 'font-size:0.95rem;color:#ddc8ff;letter-spacing:0.04em;';
    el('div', { parent: card, text: 'A fresh keypair is generated locally; your master signs a delegation that links it to your Nostr identity. The master never leaves your signer. The jury private key stays in your browser.' })
      .style.cssText = 'font-size:0.82rem;color:rgba(220,210,255,0.75);line-height:1.5;';
    const row = el('div', { parent: card });
    row.style.cssText = 'display:flex;gap:8px;flex-wrap:wrap;';
    const setupBtn = el('button', { className: 'menu-btn', parent: row, text: 'SET UP JURY IDENTITY' });
    setupBtn.addEventListener('click', () => { void onJurySetupStart(state, parent); });
    return;
  }

  // Has identity — assume delegation was published when it was set up.
  // Future improvement: query relays for the kind 30765 event and show
  // its publish status / event id. For phase 2a it's enough to surface
  // that the identity is configured.
  const card = el('div', { parent });
  card.style.cssText = 'border:1px solid rgba(120,200,150,0.45);border-radius:8px;padding:12px 16px;background:rgba(80,200,150,0.08);display:flex;flex-direction:column;gap:8px;';
  const head = el('div', { parent: card });
  head.style.cssText = 'display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;';
  el('div', { parent: head, text: 'Jury identity active' })
    .style.cssText = 'font-size:0.95rem;color:#a8eecf;letter-spacing:0.04em;';
  const pk = el('div', { parent: head, text: shortPubkey(identity.pubkey) });
  pk.style.cssText = 'font-family:monospace;font-size:0.78rem;color:rgba(220,210,255,0.7);';
  el('div', { parent: card, text: 'Your master pubkey has delegated voting authority to this key. When a case lands and your jury pubkey appears in its circle, you can cast an anonymous LSAG ballot — coming in the next deploy.' })
    .style.cssText = 'font-size:0.82rem;color:rgba(220,210,255,0.75);line-height:1.5;';
  const row = el('div', { parent: card });
  row.style.cssText = 'display:flex;gap:8px;flex-wrap:wrap;';
  const republishBtn = el('button', { className: 'menu-btn secondary', parent: row, text: 'REPUBLISH DELEGATION' });
  republishBtn.addEventListener('click', () => { void onJuryRepublish(state, identity); });
  const clearBtn = el('button', { className: 'menu-btn secondary', parent: row, text: 'CLEAR JURY KEY' });
  clearBtn.addEventListener('click', () => {
    if (!window.confirm('Clear your jury identity? You\'ll need to set up a new one to vote again. The previously published delegation remains on relays and is still tied to your master — to fully rotate, publish a fresh delegation with a new key.')) return;
    clearStoredJuryIdentity();
    renderJurySetupBanner(parent, state);
  });
}

async function onJurySignIn(state: GameState, banner: HTMLElement): Promise<void> {
  try {
    const session = await auth.signIn();
    if (session) {
      state.session = session;
      renderJurySetupBanner(banner, state);
    }
  } catch (err) {
    console.warn('[jury] sign-in failed:', err);
    window.alert(
      err instanceof auth.SignInTimeoutError
        ? `Timeout — ${err.message}`
        : `Sign-in failed: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
}

async function onJurySetupStart(state: GameState, banner: HTMLElement): Promise<void> {
  const session = state.session;
  if (!session) return;
  const identity = generateJuryIdentity();
  // Render an in-progress card so the user sees what's happening — sign +
  // publish can take a few seconds and feedback matters here.
  banner.innerHTML = '';
  const card = el('div', { parent: banner });
  card.style.cssText = 'border:1px solid rgba(184,144,255,0.55);border-radius:8px;padding:12px 16px;background:rgba(120,90,200,0.15);display:flex;flex-direction:column;gap:8px;';
  el('div', { parent: card, text: 'Publishing your jury delegation…' })
    .style.cssText = 'font-size:0.95rem;color:#ddc8ff;letter-spacing:0.04em;';
  const statusLine = el('div', { parent: card });
  statusLine.style.cssText = 'font-size:0.82rem;color:rgba(220,210,255,0.75);line-height:1.5;';
  statusLine.textContent = 'Requesting signature from your Nostr signer…';

  const result = await publishDelegation(session, identity.pubkey);
  if (!result.ok) {
    statusLine.textContent = `Failed: ${result.error ?? 'no relays accepted the event'}.`;
    statusLine.style.color = '#ff8a8a';
    const retryRow = el('div', { parent: card });
    retryRow.style.cssText = 'display:flex;gap:8px;margin-top:6px;';
    const retry = el('button', { className: 'menu-btn', parent: retryRow, text: 'RETRY' });
    retry.addEventListener('click', () => { void onJurySetupStart(state, banner); });
    const cancel = el('button', { className: 'menu-btn secondary', parent: retryRow, text: 'CANCEL' });
    cancel.addEventListener('click', () => renderJurySetupBanner(banner, state));
    return;
  }
  // Only persist the privkey after the delegation has been signed and
  // accepted by at least one relay — a partial failure shouldn't leave a
  // locally-stored key that nobody else knows about.
  setStoredJuryIdentity(identity);
  statusLine.innerHTML =
    `Published to <strong>${result.publishedTo.length}</strong> of <strong>${result.publishedTo.length + result.failed.length}</strong> relays. ` +
    `Jury identity stored locally. The faucet's watcher will pick it up on its next pass.`;
  statusLine.style.color = '#a8eecf';
  const ok = el('button', { className: 'menu-btn', parent: card, text: 'DONE' });
  ok.style.alignSelf = 'flex-start';
  ok.addEventListener('click', () => renderJurySetupBanner(banner, state));
}

async function onJuryRepublish(state: GameState, identity: StoredJuryIdentity): Promise<void> {
  const session = state.session;
  if (!session) return;
  const result = await publishDelegation(session, identity.pubkey);
  if (!result.ok) {
    window.alert(`Republish failed: ${result.error ?? 'no relays accepted the event'}.`);
    return;
  }
  window.alert(`Republished to ${result.publishedTo.length} of ${result.publishedTo.length + result.failed.length} relays.`);
}

/** Render the rank slider + submit / already-voted panel for a juror
 *  who's eligible to vote on this case. State machine:
 *    idle     → slider + SUBMIT VOTE (default rank 50)
 *    sending  → slider locked + "SUBMITTING…" button
 *    sent     → read-only "✓ VOTE SUBMITTED · rank N" line
 *    failed   → slider unlocked + error text under the button
 *  Already-voted state (from localStorage) skips the slider and renders
 *  the sent panel directly.
 *
 *  Rank semantics: 100 = clearly honest, 50 = uncertain, 0 = clearly
 *  cheating. submitVote enforces 0..100 server-side; the slider mirrors. */
function renderJuryVotePanel(parent: HTMLElement, c: ReviewCase, identity: StoredJuryIdentity): void {
  const panel = el('div', { parent });
  panel.style.cssText = 'border-top:1px dashed rgba(184,144,255,0.25);margin-top:4px;padding-top:8px;display:flex;flex-direction:column;gap:8px;';

  // Already-voted from localStorage — render read-only state, skip slider.
  if (hasVotedOnCase(c.eventId)) {
    const done = el('div', { parent: panel });
    done.style.cssText = 'font-size:0.85rem;color:#a8eecf;letter-spacing:0.06em;display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;';
    el('span', { parent: done, text: '✓ VOTE SUBMITTED' });
    const sub = el('span', { parent: done, text: 'Your LSAG ballot is on relays. Verdict aggregates when the case closes.' });
    sub.style.cssText = 'font-size:0.78rem;color:rgba(220,210,255,0.6);letter-spacing:0.04em;';
    return;
  }

  const label = el('div', { parent: panel });
  label.style.cssText = 'font-size:0.85rem;color:#cbb6ff;letter-spacing:0.08em;';
  label.textContent = '✓ YOU ARE IN THIS CIRCLE — CAST AN ANONYMOUS RANK';

  // Slider row: 0 ── (rank value bubble) ── 100 with cheat ↔ honest hints.
  const sliderRow = el('div', { parent: panel });
  sliderRow.style.cssText = 'display:flex;align-items:center;gap:10px;font-family:monospace;font-size:0.78rem;color:rgba(220,210,255,0.7);';
  const minHint = el('span', { parent: sliderRow, text: '0 · CHEAT' });
  minHint.style.cssText = 'color:#ffb0b0;min-width:80px;';
  const slider = el('input', { parent: sliderRow }) as HTMLInputElement;
  slider.type = 'range';
  slider.min = '0';
  slider.max = '100';
  slider.step = '1';
  slider.value = '50';
  slider.style.cssText = 'flex:1;accent-color:#b890ff;';
  const maxHint = el('span', { parent: sliderRow, text: 'HONEST · 100' });
  maxHint.style.cssText = 'color:#a8eecf;min-width:90px;text-align:right;';

  const liveBubble = el('div', { parent: panel });
  liveBubble.style.cssText = 'font-size:0.8rem;color:rgba(220,210,255,0.85);text-align:center;letter-spacing:0.06em;';
  const renderBubble = (): void => {
    const v = parseInt(slider.value, 10);
    let verdict: string;
    if (v >= 80) verdict = 'CLEARLY HONEST';
    else if (v >= 60) verdict = 'PROBABLY HONEST';
    else if (v >= 40) verdict = 'UNCERTAIN';
    else if (v >= 20) verdict = 'PROBABLY CHEATING';
    else verdict = 'CLEARLY CHEATING';
    liveBubble.textContent = `RANK ${v} · ${verdict}`;
  };
  renderBubble();
  slider.addEventListener('input', renderBubble);

  const actions = el('div', { parent: panel });
  actions.style.cssText = 'display:flex;gap:8px;align-items:center;';
  const submitBtn = el('button', { className: 'menu-btn', parent: actions, text: 'SUBMIT VOTE' }) as HTMLButtonElement;
  submitBtn.style.cssText += 'flex:0 1 200px;';
  const statusLine = el('div', { parent: actions });
  statusLine.style.cssText = 'font-size:0.78rem;color:rgba(220,210,255,0.6);letter-spacing:0.04em;flex:1;min-height:1.2em;';

  submitBtn.addEventListener('click', () => {
    void (async () => {
      const rank = parseInt(slider.value, 10);
      submitBtn.disabled = true;
      slider.disabled = true;
      submitBtn.textContent = 'SUBMITTING…';
      statusLine.textContent = 'Signing LSAG ballot + publishing to relays…';
      statusLine.style.color = 'rgba(255,216,74,0.85)';
      let result: VoteSubmitResult;
      try {
        result = await submitVote({ reviewCase: c, identity, rank });
      } catch (err) {
        result = { ok: false, publishedTo: [], failed: [], error: err instanceof Error ? err.message : 'unknown' };
      }
      if (result.ok) {
        // Replace the whole panel with the sent state.
        panel.innerHTML = '';
        const done = el('div', { parent: panel });
        done.style.cssText = 'font-size:0.85rem;color:#a8eecf;letter-spacing:0.06em;display:flex;flex-direction:column;gap:4px;';
        const head = el('div', { parent: done });
        head.style.cssText = 'display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;';
        el('span', { parent: head, text: '✓ VOTE SUBMITTED' });
        const rankTag = el('span', { parent: head, text: `RANK ${rank}` });
        rankTag.style.cssText = 'font-family:monospace;color:#ffd84a;letter-spacing:0.06em;';
        const meta = el('div', { parent: done });
        meta.style.cssText = 'font-size:0.74rem;color:rgba(220,210,255,0.55);letter-spacing:0.04em;font-family:monospace;';
        const pubCount = result.publishedTo.length;
        const failCount = result.failed.length;
        meta.textContent = `Published to ${pubCount}/${pubCount + failCount} relays · key-image ${(result.keyImage ?? '').slice(0, 12)}…`;
      } else {
        submitBtn.disabled = false;
        slider.disabled = false;
        submitBtn.textContent = 'TRY AGAIN';
        statusLine.style.color = 'rgba(255,120,120,0.9)';
        statusLine.textContent = `Vote failed: ${result.error ?? 'unknown'}`;
      }
    })();
  });
}

function renderJuryCaseCard(c: ReviewCase, identity: StoredJuryIdentity | null, state: GameState): HTMLElement {
  const row = el('div');
  row.style.cssText = 'border:1px solid rgba(255,80,80,0.35);border-radius:8px;padding:10px 14px;background:rgba(255,80,80,0.06);display:flex;flex-direction:column;gap:6px;';

  const head = el('div', { parent: row });
  head.style.cssText = 'display:flex;justify-content:space-between;align-items:baseline;gap:12px;flex-wrap:wrap;';

  const pk = el('div', { parent: head, text: shortPubkey(c.flaggedPubkey) });
  pk.style.cssText = 'font-family:monospace;font-size:0.95rem;color:#ffb0b0;letter-spacing:0.06em;';

  const when = el('div', { parent: head, text: formatFlaggedAt(c.createdAt * 1000) });
  when.style.cssText = 'font-size:0.78rem;color:rgba(220,210,255,0.6);letter-spacing:0.06em;';

  const reason = el('div', { parent: row, text: c.flagReason });
  reason.style.cssText = 'font-size:0.82rem;color:rgba(255,200,200,0.85);font-family:monospace;word-break:break-word;';

  const meta = el('div', { parent: row });
  meta.style.cssText = 'display:flex;gap:14px;font-size:0.82rem;color:rgba(220,210,255,0.75);letter-spacing:0.06em;flex-wrap:wrap;';
  el('span', { parent: meta, text: `WAVE ${c.wave}` });
  el('span', { parent: meta, text: `SCORE ${c.score.toLocaleString()}` });
  if (c.seed) el('span', { parent: meta, text: `SEED ${c.seed}` });
  const circleLabel = c.underQuorum ? `${c.circleSize} JURORS · UNDER QUORUM` : `${c.circleSize} JURORS`;
  const circleBadge = el('span', { parent: meta, text: circleLabel });
  circleBadge.style.color = c.underQuorum ? 'rgba(255,184,120,0.85)' : 'rgba(180,255,200,0.85)';

  // Eligibility + voting UI for the signed-in juror.
  if (identity) {
    const inCircle = c.circleMembers.includes(identity.pubkey);
    if (!inCircle) {
      const elig = el('div', { parent: row });
      elig.style.cssText = 'font-size:0.78rem;letter-spacing:0.08em;color:rgba(220,210,255,0.45);';
      elig.textContent = '— YOU ARE NOT IN THIS CIRCLE';
    } else {
      renderJuryVotePanel(row, c, identity);
    }
  }

  const actions = el('div', { parent: row });
  actions.style.cssText = 'display:flex;gap:8px;margin-top:4px;';
  const watch = el('button', { className: 'menu-btn', parent: actions, text: 'WATCH' });
  watch.addEventListener('click', () => {
    renderReplayTheatre({
      scoreEventId: c.scoreEventId,
      displayName: shortPubkey(c.flaggedPubkey),
      score: c.score,
      wave: c.wave,
      sats: 0,
      onClose: () => renderJuryPage(state),
    });
  });
  const copyPk = el('button', { className: 'menu-btn secondary', parent: actions, text: 'COPY PUBKEY' });
  copyPk.addEventListener('click', () => {
    void navigator.clipboard?.writeText(c.flaggedPubkey).then(() => {
      copyPk.textContent = 'COPIED';
      setTimeout(() => (copyPk.textContent = 'COPY PUBKEY'), 1200);
    });
  });
  return row;
}

// ── Watch page (watch.pallasite.app) ──────────────────────────────────────────
//
// Public live spectator surface. Subscribes to kind 30762 score events from
// the faucet game pubkey and shows one card per recently-active player.
// Visitors can WATCH the ghost replay or ZAP the player. v1 surfaces final
// runs (newest first); a later faucet change emitting state='active' on a
// heartbeat will let this same surface show in-progress runs in real time.

const WATCH_ZAP_PRESETS_SATS = [50, 200, 1000] as const;

let watchActiveUnsubscribe: (() => void) | null = null;
/** Module-scoped teardown for the top-3 live mini-tile WS subscriptions.
 *  Tracked here (not in renderWatchPage's closure) so a re-entry into
 *  the watch page or a route-away tears down the previous tiles' WS
 *  connections — otherwise each renderWatchPage call would leak 3+
 *  sockets per visit. */
let watchActiveMiniTeardown: (() => void) | null = null;
/** Module-scoped teardown for the kind 9735 zap aggregator. Same
 *  rationale as watchActiveMiniTeardown — separate so the page can
 *  re-open the zap subscription with a refreshed pubkey set without
 *  tearing down the score-event subscription. */
let watchActiveZapTeardown: (() => void) | null = null;

export function renderWatchPage(state: GameState): void {
  clearOverlay();
  // Tear down any prior subscription before we open a new one — re-entering
  // the page (e.g. via BACK from the theatre) would otherwise leak sockets.
  watchActiveUnsubscribe?.();
  watchActiveUnsubscribe = null;
  watchActiveMiniTeardown?.();
  watchActiveMiniTeardown = null;
  watchActiveZapTeardown?.();
  watchActiveZapTeardown = null;

  // Deep-link router: two URL forms
  //   /#replay=<kind-30764-event-id>  → fetch the rich replay directly
  //   /#score=<kind-30762-score-id>   → fetch the matching kind 30764 via
  //                                     its #e tag, then open the theatre.
  // The score-id form is the SHARE-from-card link: card knows the score
  // event id from the kind 30762 it subscribes to, doesn't need to fetch
  // 30764 before building the URL. Hash is cleared after the theatre
  // opens so a BACK from the theatre lands on the normal grid instead
  // of re-firing the deep-link.
  const scoreMatch = /^#score=([0-9a-f]{64})$/i.exec(window.location.hash);
  const hashMatch = /^#replay=([0-9a-f]{64})$/i.exec(window.location.hash);
  if (scoreMatch) {
    const scoreEventId = scoreMatch[1].toLowerCase();
    try { history.replaceState(null, '', window.location.pathname + window.location.search); } catch { /* ignore */ }
    const overlay = el('div', { className: 'overlay', parent: root });
    setupOverlayArrowNav(overlay);
    el('h2', { parent: overlay, text: 'OPENING REPLAY…' });
    const stat = el('p', { parent: overlay, text: `Fetching kind 30764 via #e=${scoreEventId.slice(0, 8)}…` });
    stat.style.cssText = 'margin:12px auto;font-size:0.85rem;color:rgba(220,210,255,0.7);max-width:560px;text-align:center;';
    void (async () => {
      const rich = await fetchReplayByScoreEventId(scoreEventId).catch(() => null);
      if (rich && rich.frames.length >= 2) {
        renderLiveTheatre({
          masterPubkey: rich.pubkey,
          displayName: shortPubkey(rich.pubkey),
          initialScore: rich.score,
          initialWave: rich.wave,
          runStartedAtMs: Date.now() - rich.durationMs,
          onClose: () => renderWatchPage(state),
          replaySource: {
            frames: rich.frames,
            durationMs: rich.durationMs,
            headerLabel: 'REPLAY · SHARED LINK',
            eventId: rich.eventId,
          },
        });
        return;
      }
      stat.textContent = 'Could not find a kind 30764 replay e-tagged to this score event. Player may not have published yet, or NIP-09 deleted it.';
      stat.style.color = 'rgba(255,120,120,0.85)';
      const back = el('button', { className: 'menu-btn', parent: overlay, text: 'CONTINUE TO WATCH PAGE' });
      back.style.cssText += 'margin:14px auto;display:block;';
      back.addEventListener('click', () => renderWatchPage(state));
    })();
    return;
  }
  if (hashMatch) {
    const replayEventId = hashMatch[1].toLowerCase();
    try { history.replaceState(null, '', window.location.pathname + window.location.search); } catch { /* ignore */ }
    const overlay = el('div', { className: 'overlay', parent: root });
    setupOverlayArrowNav(overlay);
    el('h2', { parent: overlay, text: 'OPENING REPLAY…' });
    const stat = el('p', { parent: overlay, text: `Fetching kind 30764 ${replayEventId.slice(0, 8)}… from relays.` });
    stat.style.cssText = 'margin:12px auto;font-size:0.85rem;color:rgba(220,210,255,0.7);max-width:560px;text-align:center;';
    void (async () => {
      const rich = await fetchReplayByEventId(replayEventId).catch(() => null);
      if (rich && rich.frames.length >= 2) {
        renderLiveTheatre({
          masterPubkey: rich.pubkey,
          displayName: shortPubkey(rich.pubkey),
          initialScore: rich.score,
          initialWave: rich.wave,
          runStartedAtMs: Date.now() - rich.durationMs,
          onClose: () => renderWatchPage(state),
          replaySource: {
            frames: rich.frames,
            durationMs: rich.durationMs,
            headerLabel: 'REPLAY · SHARED LINK',
            eventId: rich.eventId,
          },
        });
        return;
      }
      stat.textContent = 'Could not find this replay on the relay set. The author may not have published, or it has been deleted via NIP-09.';
      stat.style.color = 'rgba(255,120,120,0.85)';
      const back = el('button', { className: 'menu-btn', parent: overlay, text: 'CONTINUE TO WATCH PAGE' });
      back.style.cssText += 'margin:14px auto;display:block;';
      back.addEventListener('click', () => renderWatchPage(state));
    })();
    return;
  }
  // When the user lands fresh on the watch page (initial navigation, not
  const overlay = el('div', { className: 'overlay', parent: root });
  setupOverlayArrowNav(overlay);

  const header = el('div', { parent: overlay });
  header.style.cssText = 'display:flex;align-items:baseline;gap:12px;flex-wrap:wrap;justify-content:center;margin-bottom:6px;';
  el('h2', { parent: header, text: 'PALLASITE · WATCH' });
  const live = el('span', { parent: header, text: 'LIVE · RECENT RUNS' });
  live.style.cssText = 'font-size:0.72rem;color:rgba(255,216,74,0.85);letter-spacing:0.18em;font-family:monospace;';
  // Live count chip — pulses ship-green when ≥1 player is live so the
  // tab title bar reads "3 LIVE" at a glance.
  const liveCount = el('span', { parent: header, text: '' });
  liveCount.style.cssText = 'display:none;font-size:0.72rem;letter-spacing:0.16em;color:#8cffb4;border:1px solid rgba(140,255,180,0.55);padding:2px 8px;border-radius:3px;font-family:monospace;animation:pallasite-live-pulse 1.6s ease-in-out infinite;';
  const updateLiveCount = (n: number): void => {
    if (n > 0) {
      liveCount.style.display = 'inline-block';
      liveCount.textContent = `${n} LIVE`;
    } else {
      liveCount.style.display = 'none';
    }
  };

  // Tighter intro — the hero tiles now sit directly under the header so
  // the explanation can shrink to a single line. The verbose
  // "Every claimed run lands here as a kind 30762…" copy moved to a
  // <title> tooltip on the page header for the curious.
  const intro = el('p', { parent: overlay });
  intro.style.cssText = 'margin:4px auto 12px;font-size:0.78rem;color:rgba(220,210,255,0.6);max-width:680px;line-height:1.4;text-align:center;letter-spacing:0.04em;';
  intro.innerHTML = 'Click a tile to <strong>WATCH</strong>. ⚡ ZAP the player from any card below.';
  header.title = 'Every claimed run lands as a kind 30762 score event signed by the faucet. Ghosts (kind 30763) and full-world replays (kind 30764) are fetched per-card.';

  const status = el('p', { parent: overlay });
  status.style.cssText = 'margin:0 0 8px;font-size:0.78rem;color:rgba(255,216,74,0.65);letter-spacing:0.08em;min-height:1.2em;text-align:center;';
  status.textContent = 'Connecting to relays…';

  // Per-relay status pills — one chip per relay we tried, coloured by
  // state: green=settled with events, yellow=settled+empty, red=errored,
  // grey=connecting. Event count appears next to the URL host so a
  // spectator can see at a glance which relay actually has data.
  const relayPills = el('div', { parent: overlay });
  relayPills.style.cssText = 'display:flex;flex-wrap:wrap;justify-content:center;gap:6px;margin:0 auto 14px;max-width:760px;font-family:monospace;font-size:0.68rem;letter-spacing:0.06em;';
  const renderRelayPills = (per: ReadonlyArray<{ url: string; settled: boolean; errored: boolean; events: number }>): void => {
    relayPills.innerHTML = '';
    for (const r of per) {
      const host = (() => { try { return new URL(r.url).host; } catch { return r.url; } })();
      const pill = el('span', { parent: relayPills, text: '' });
      let colour = 'rgba(220,210,255,0.4)';  // connecting
      let glyph = '○';
      if (r.errored) { colour = 'rgba(255,120,120,0.85)'; glyph = '✗'; }
      else if (r.settled && r.events > 0) { colour = '#8cffb4'; glyph = '✓'; }
      else if (r.settled) { colour = 'rgba(255,216,74,0.75)'; glyph = '·'; }
      pill.style.cssText = `border:1px solid ${colour}55;border-radius:3px;padding:2px 7px;color:${colour};background:transparent;`;
      pill.textContent = `${glyph} ${host}${r.events > 0 ? ` ${r.events}` : ''}`;
      pill.title = r.url + (r.errored ? ' — connection error/close' : r.settled ? ' — settled' : ' — connecting…');
    }
  };

  // Filter tabs — LIVE / TODAY / ZAPPED / ALL (+ MINE if signed in).
  // Toggles card visibility client-side based on data-* attributes set
  // in renderEntry. Tabs always render so the user knows the dimension
  // exists; counts update when entries land. ZAPPED also re-sorts the
  // grid by aggregate sats received instead of newest-first.
  type FilterKind = 'live' | 'today' | 'arena' | 'mine' | 'zapped' | 'all';
  let activeFilter: FilterKind = 'all';
  const filterRow = el('div', { parent: overlay });
  filterRow.style.cssText = 'display:flex;justify-content:center;gap:6px;margin:0 auto 12px;max-width:760px;width:100%;flex-wrap:wrap;';
  const filterCounts: Record<FilterKind, HTMLSpanElement> = { live: el('span'), today: el('span'), arena: el('span'), mine: el('span'), zapped: el('span'), all: el('span') };
  const filterBtns: Record<FilterKind, HTMLButtonElement> = {} as Record<FilterKind, HTMLButtonElement>;
  const FILTER_DEFS: Array<{ k: FilterKind; label: string; visible: boolean }> = [
    { k: 'live', label: 'LIVE', visible: true },
    { k: 'today', label: 'TODAY', visible: true },
    { k: 'arena', label: 'ARENA', visible: true },
    { k: 'zapped', label: '⚡ ZAPPED', visible: true },
    { k: 'mine', label: 'MINE', visible: !!state.session },
    { k: 'all', label: 'ALL', visible: true },
  ];
  const FILTER_COLOUR: Record<FilterKind, string> = {
    live: '#8cffb4',
    today: '#ffd84a',
    arena: '#5cd0e8',
    zapped: '#ff8a3a',
    mine: '#cbb6ff',
    all: 'rgba(220,210,255,0.85)',
  };
  for (const def of FILTER_DEFS) {
    if (!def.visible) continue;
    const btn = el('button', { className: 'menu-btn secondary', parent: filterRow }) as HTMLButtonElement;
    btn.style.cssText += 'flex:0 0 auto;padding:4px 14px;font-size:0.78rem;letter-spacing:0.16em;';
    btn.innerHTML = '';
    const labelEl = el('span', { parent: btn, text: def.label });
    const countEl = filterCounts[def.k];
    countEl.textContent = '0';
    countEl.style.cssText = 'margin-left:6px;font-size:0.7rem;color:rgba(220,210,255,0.55);font-family:monospace;';
    btn.appendChild(countEl);
    void labelEl;
    btn.addEventListener('click', () => setFilter(def.k));
    filterBtns[def.k] = btn;
  }

  // PERSON filter — narrows the visible cards to a single pubkey (or
  // matching prefix). Accepts:
  //   • a full npub1... bech32 string (decoded to 64-char hex)
  //   • a hex pubkey (full 64 chars or shorter prefix substring)
  // Lives alongside the LIVE/TODAY/MINE/ALL tabs as an orthogonal axis:
  // tabs still gate visibility, person narrows within. Empty input
  // clears the constraint. The clear (✕) chip nulls it without
  // requiring the user to manually empty the field.
  let personFilter = '';  // lowercase hex prefix, possibly empty
  const personRow = el('div', { parent: overlay });
  personRow.style.cssText = 'display:flex;justify-content:center;align-items:center;gap:6px;margin:0 auto 12px;max-width:760px;width:100%;flex-wrap:wrap;';
  const personInput = el('input', { parent: personRow }) as HTMLInputElement;
  personInput.type = 'text';
  personInput.placeholder = 'FILTER BY NPUB OR HEX PUBKEY…';
  personInput.autocomplete = 'off';
  personInput.spellcheck = false;
  personInput.style.cssText = 'flex:1 1 320px;max-width:520px;padding:6px 10px;border:1px solid rgba(220,210,255,0.25);border-radius:4px;background:rgba(2,5,13,0.6);color:rgba(220,210,255,0.92);font-family:monospace;font-size:0.82rem;letter-spacing:0.04em;';
  const personHint = el('span', { parent: personRow, text: '' });
  personHint.style.cssText = 'font-family:monospace;font-size:0.7rem;letter-spacing:0.08em;color:rgba(220,210,255,0.55);min-width:6em;';
  const personClear = el('button', { className: 'menu-btn secondary', parent: personRow, text: '✕' }) as HTMLButtonElement;
  personClear.style.cssText += 'flex:0 0 auto;padding:4px 10px;font-size:0.78rem;display:none;';
  personClear.title = 'Clear person filter';
  const setPersonFilter = (raw: string): void => {
    const trimmed = raw.trim();
    if (!trimmed) {
      personFilter = '';
      personHint.textContent = '';
      personClear.style.display = 'none';
      applyFilter();
      return;
    }
    const npub = decodeNpub(trimmed);
    if (npub) {
      personFilter = npub;
      personHint.textContent = `→ ${npub.slice(0, 8)}…`;
      personHint.style.color = '#8cffb4';
    } else {
      // Hex prefix — accept anything that's plausibly the start of a
      // 64-char hex pubkey. Sanitise to lowercase hex only so a paste of
      // "0xAB..." or a stray space doesn't break the prefix match.
      const hex = trimmed.toLowerCase().replace(/[^0-9a-f]/g, '');
      personFilter = hex;
      if (hex.length === 0) {
        personHint.textContent = 'INVALID';
        personHint.style.color = 'rgba(255,120,120,0.85)';
      } else if (hex.length === 64) {
        personHint.textContent = 'EXACT';
        personHint.style.color = '#8cffb4';
      } else {
        personHint.textContent = `PREFIX ${hex.length}/64`;
        personHint.style.color = 'rgba(255,216,74,0.85)';
      }
    }
    personClear.style.display = '';
    applyFilter();
  };
  personInput.addEventListener('input', () => setPersonFilter(personInput.value));
  personClear.addEventListener('click', () => {
    personInput.value = '';
    setPersonFilter('');
    personInput.focus();
  });

  // Top live tiles — up to 3 mini canvases of currently-live players,
  // one per row of the grid below. Click expands into the full live
  // theatre via makeMiniLiveTile's own click handler. Kept above the
  // grid so a glance at the watch page surfaces who's actually playing
  // right now without having to scan the score list.
  const liveTiles = el('div', { parent: overlay });
  // Hero row — wider tiles (min 320px) so the canvas reads as a real
  // game view, not a thumbnail. Bigger gap. Sits high in the page DOM
  // (moved above the relay/filter clutter via insertBefore below) so
  // a fresh visitor sees actual play before any chrome.
  liveTiles.style.cssText = 'display:grid;grid-template-columns:repeat(auto-fit, minmax(300px, 340px));justify-content:center;gap:22px;max-width:1100px;width:100%;margin:6px auto 22px;position:relative;';
  liveTiles.style.display = 'none';  // shown once at least one live tile lands
  // Per-pubkey tracker so we can keep stable tiles across update emits
  // (rebuilding from scratch every emit would flicker the canvas and
  // re-subscribe the WS, killing the smooth-frame illusion).
  const miniTiles = new Map<string, { el: HTMLElement; unsubscribe: () => void }>();
  watchActiveMiniTeardown = () => {
    for (const t of miniTiles.values()) t.unsubscribe();
    miniTiles.clear();
  };
  // Move the hero row up to sit directly below the page header. The
  // intro / status / relay pills / filter rows are all chrome around
  // the actual live play; pulling the tiles above them makes the page
  // read as "here's who's playing right now" first.
  overlay.insertBefore(liveTiles, intro);

  const grid = el('div', { parent: overlay });
  grid.style.cssText = 'display:flex;flex-direction:column;gap:10px;max-width:760px;width:100%;margin:0 auto;';

  const footer = el('div', { className: 'menu-row', parent: overlay });
  const backBtn = el('button', { className: 'menu-btn', parent: footer, text: 'BACK TO PALLASITE.APP' });
  backBtn.addEventListener('click', () => {
    watchActiveUnsubscribe?.();
    watchActiveUnsubscribe = null;
    watchActiveZapTeardown?.();
    watchActiveZapTeardown = null;
    for (const t of miniTiles.values()) t.unsubscribe();
    miniTiles.clear();
    try { window.location.assign('https://pallasite.app/'); }
    catch { window.location.assign('/'); }
  });

  // Build / SW version + update-check chip, so a stale-cache suspicion on
  // the watch surface is answerable at a glance. Clicking it re-checks.
  renderLegalFooter(overlay);

  // Card rendering — keyed by pubkey so update emits can patch existing
  // cards rather than tear down and rebuild the grid (avoids layout jank
  // and keeps any open zap popovers anchored to the right element).
  // Declared BEFORE setFilter/applyFilter because the initial
  // setFilter('all') call walks the empty map at boot — declaring this
  // after that call hit TDZ on a fresh visit and froze the watch page
  // on 'Connecting to relays…'.
  const cardByPubkey = new Map<string, HTMLElement>();

  // Initial highlight on ALL — done before any clicks.
  const setFilter = (k: FilterKind): void => {
    activeFilter = k;
    for (const def of FILTER_DEFS) {
      if (!def.visible) continue;
      const active = def.k === k;
      const c = FILTER_COLOUR[def.k];
      const btn = filterBtns[def.k];
      btn.style.background = active ? `${c}22` : '';
      btn.style.color = active ? c : '';
      btn.style.borderColor = active ? c : '';
    }
    applyFilter();
  };
  const applyFilter = (): void => {
    for (const [pubkey, card] of cardByPubkey) {
      const isLive = card.dataset.live === '1';
      const isMine = card.dataset.mine === '1';
      const at = parseInt(card.dataset.createdAt ?? '0', 10);
      const isToday = at > 0 && (Date.now() - at * 1000) < 24 * 60 * 60_000;
      const zapSats = parseInt(card.dataset.zapSats ?? '0', 10) || 0;
      let show = true;
      if (activeFilter === 'live') show = isLive;
      else if (activeFilter === 'today') show = isToday;
      else if (activeFilter === 'mine') show = isMine;
      else if (activeFilter === 'zapped') show = zapSats > 0;
      else if (activeFilter === 'arena') show = card.dataset.mode === 'arena';
      // Person filter — prefix match against the card's pubkey. Empty
      // filter means "no constraint". A short prefix matches several
      // cards; the 64-char form pins it to exactly one.
      if (show && personFilter) show = pubkey.startsWith(personFilter);
      card.style.display = show ? '' : 'none';
    }
    // ZAPPED tab re-orders cards by aggregate sats descending — playing
    // a "leaderboard of generosity" angle rather than chronology. Cards
    // with zero zaps drop out via the show=false branch above, so the
    // sort only touches the survivors.
    if (activeFilter === 'zapped') {
      const visible: Array<[number, HTMLElement]> = [];
      for (const card of cardByPubkey.values()) {
        if (card.style.display === 'none') continue;
        const zapSats = parseInt(card.dataset.zapSats ?? '0', 10) || 0;
        visible.push([zapSats, card]);
      }
      visible.sort((a, b) => b[0] - a[0]);
      for (const [, card] of visible) grid.appendChild(card);
    }
  };
  setFilter('all');

  const setCardLiveState = (card: HTMLElement, isLive: boolean): void => {
    card.dataset.live = isLive ? '1' : '0';
    card.style.borderColor = isLive ? 'rgba(140,255,180,0.7)' : 'rgba(255,216,74,0.35)';
    card.style.background = isLive ? 'rgba(60,200,140,0.10)' : 'rgba(255,216,74,0.04)';
    card.style.boxShadow = isLive ? '0 0 14px rgba(140,255,180,0.25)' : 'none';
    const pill = card.querySelector('[data-watch-live-pill]') as HTMLElement | null;
    if (pill) pill.style.display = isLive ? 'inline-block' : 'none';
  };

  const renderEntry = (entry: WatchEntry): void => {
    const isMine = !!(state.session && state.session.pubkey === entry.pubkey);
    const existing = cardByPubkey.get(entry.pubkey);
    if (existing) {
      const scorePill = existing.querySelector('[data-watch-score]');
      const wavePill = existing.querySelector('[data-watch-wave]');
      const whenPill = existing.querySelector('[data-watch-when]');
      if (scorePill) scorePill.textContent = entry.score.toLocaleString();
      if (wavePill) wavePill.textContent = `WAVE ${entry.wave}`;
      if (whenPill) whenPill.textContent = entry.isLive ? 'LIVE' : timeAgo(entry.createdAt);
      existing.dataset.createdAt = String(entry.createdAt);
      existing.dataset.mine = isMine ? '1' : '0';
      existing.dataset.mode = entry.mode;
      setCardLiveState(existing, entry.isLive);
      return;
    }
    const card = renderWatchCard(entry, state);
    card.dataset.mine = isMine ? '1' : '0';
    card.dataset.mode = entry.mode;
    cardByPubkey.set(entry.pubkey, card);
    setCardLiveState(card, entry.isLive);
    grid.appendChild(card);
  };

  const refreshFilterCounts = (): void => {
    let live = 0, today = 0, arena = 0, mine = 0, zapped = 0;
    const now = Date.now();
    for (const card of cardByPubkey.values()) {
      if (card.dataset.live === '1') live += 1;
      if (card.dataset.mine === '1') mine += 1;
      if (card.dataset.mode === 'arena') arena += 1;
      const at = parseInt(card.dataset.createdAt ?? '0', 10);
      if (at > 0 && (now - at * 1000) < 24 * 60 * 60_000) today += 1;
      const zapSats = parseInt(card.dataset.zapSats ?? '0', 10) || 0;
      if (zapSats > 0) zapped += 1;
    }
    filterCounts.live.textContent = String(live);
    filterCounts.today.textContent = String(today);
    filterCounts.arena.textContent = String(arena);
    filterCounts.mine.textContent = String(mine);
    filterCounts.zapped.textContent = String(zapped);
    filterCounts.all.textContent = String(cardByPubkey.size);
  };

  const reorderGrid = (entries: WatchEntry[]): void => {
    // LIVE entries first, then everything else newest-first.
    const sorted = [...entries].sort(
      (a, b) =>
        Number(b.isLive) - Number(a.isLive) ||
        b.createdAt - a.createdAt,
    );
    for (const e of sorted) {
      const card = cardByPubkey.get(e.pubkey);
      if (card) grid.appendChild(card);
    }
  };

  // Zap-total state — apply to cards on every entries OR zap emit, so a
  // card that arrives AFTER the zap aggregator already saw its receipts
  // still gets decorated, and a late-arriving zap on a known card
  // updates the chip without re-rendering. Keyed by lowercase hex.
  const zapTotalsByPubkey = new Map<string, { sats: number; count: number }>();
  // The pubkey set the zap subscription was opened with — used to know
  // when the set has grown enough that re-subscribing is worth the
  // socket churn. We tolerate stale subscriptions for a few new players
  // before re-opening: each re-subscribe drops the historical totals
  // we've accumulated client-side and re-fetches them.
  let lastSubscribedPubkeys: ReadonlySet<string> = new Set();
  const applyZapToCard = (card: HTMLElement, pubkey: string): void => {
    const totals = zapTotalsByPubkey.get(pubkey);
    const chip = card.querySelector('[data-watch-zap]') as HTMLElement | null;
    if (!chip) return;
    if (!totals || totals.sats <= 0) {
      chip.style.display = 'none';
      card.dataset.zapSats = '0';
      return;
    }
    card.dataset.zapSats = String(totals.sats);
    chip.style.display = '';
    chip.textContent = `⚡ ${totals.sats.toLocaleString()} sat${totals.sats === 1 ? '' : 's'}`;
    chip.title = `${totals.count} zap${totals.count === 1 ? '' : 's'} totalling ${totals.sats} sat${totals.sats === 1 ? '' : 's'} via kind 9735 receipts`;
  };
  const onZapTotals = (totals: ZapTotalsByPubkey): void => {
    // Copy into the local map — totals is a ReadonlyMap that may be the
    // aggregator's internal store, and we keep a stable reference here
    // so a card rendered after this emit can still look up its totals.
    zapTotalsByPubkey.clear();
    for (const [pk, t] of totals) zapTotalsByPubkey.set(pk, { sats: t.sats, count: t.count });
    for (const [pk, card] of cardByPubkey) applyZapToCard(card, pk);
    refreshFilterCounts();
    if (activeFilter === 'zapped') applyFilter();
  };
  const refreshZapSubscription = (pubkeys: ReadonlySet<string>): void => {
    // Only re-open the subscription when at least 4 new pubkeys appear
    // (or the set shrinks). Each re-subscribe drops the accumulated
    // client-side totals + reopens 6 WebSockets, so we want to batch.
    if (pubkeys.size === lastSubscribedPubkeys.size) {
      let identical = true;
      for (const pk of pubkeys) if (!lastSubscribedPubkeys.has(pk)) { identical = false; break; }
      if (identical) return;
    }
    const grown = pubkeys.size - lastSubscribedPubkeys.size;
    if (lastSubscribedPubkeys.size > 0 && grown < 4) return;
    watchActiveZapTeardown?.();
    lastSubscribedPubkeys = new Set(pubkeys);
    if (pubkeys.size === 0) { watchActiveZapTeardown = null; return; }
    watchActiveZapTeardown = subscribeZapTotals(
      Array.from(pubkeys),
      onZapTotals,
    );
  };

  watchActiveUnsubscribe = subscribeRecentRuns(
    (entries) => {
      // Drop locally-dismissed event ids; user removed them via the DISMISS
      // button so they shouldn't re-appear on update emits.
      const dismissed = getDismissedWatchEntries();
      const visible = entries.filter((e) => !dismissed.has(e.eventId));
      if (visible.length === 0) return; // status copy handled in onStatus
      const count = visible.length;
      const liveN = visible.reduce((acc, e) => acc + (e.isLive ? 1 : 0), 0);
      status.textContent = `${count} ${count === 1 ? 'player' : 'players'} surfaced from the last batch.`;
      updateLiveCount(liveN);
      // Drop the empty-state CTA now that we have entries to show.
      grid.querySelector('[data-watch-empty-cta]')?.remove();
      for (const e of visible) renderEntry(e);
      reorderGrid(visible);
      // Decorate any freshly-rendered card with whatever totals we
      // already know about — covers the "zap aggregator saw the receipt
      // before the score event arrived" case.
      for (const [pk, card] of cardByPubkey) applyZapToCard(card, pk);
      refreshFilterCounts();
      applyFilter();
      // Refresh the zap subscription against the latest pubkey set.
      refreshZapSubscription(new Set(visible.map((e) => e.pubkey)));
      // Sync top-3 live mini-canvas tiles. Live entries with the most
      // recent heartbeat win the slots. Add new ones, remove tiles whose
      // entry dropped out of the live set, leave matching ones in place.
      const liveTop = visible.filter((e) => e.isLive).slice(0, 3);
      const wanted = new Set(liveTop.map((e) => e.pubkey));
      for (const [pk, t] of miniTiles) {
        if (!wanted.has(pk)) { t.unsubscribe(); t.el.remove(); miniTiles.delete(pk); }
      }
      for (let i = 0; i < liveTop.length; i++) {
        const e = liveTop[i];
        if (miniTiles.has(e.pubkey)) continue;
        const t = makeMiniLiveTile(
          e.pubkey,
          shortPubkey(e.pubkey),
          e.score,
          e.wave,
          e.sats,
          e.createdAt * 1000,
          state,
          (i + 1) as 1 | 2 | 3,
        );
        liveTiles.appendChild(t.el);
        miniTiles.set(e.pubkey, t);
      }
      liveTiles.style.display = miniTiles.size > 0 ? 'grid' : 'none';
    },
    {
      onStatus: (s) => {
        renderRelayPills(s.perRelay);
        // Only mutate copy while the grid is still empty — once cards land,
        // the entry-count line above takes precedence.
        if (cardByPubkey.size > 0) return;
        if (s.relaysAttempted === 0) {
          status.textContent = 'Connecting to relays…';
        } else if (s.relaysSettled === 0) {
          status.textContent = `Connecting to ${s.relaysAttempted} relays…`;
        } else if (s.emptyConfirmed) {
          status.textContent = `No runs on relays yet (${s.relaysSettled}/${s.relaysAttempted} settled).`;
          // First-run empty state — render a one-button CTA into the
          // grid that sends the visitor over to pallasite.app/play to
          // be the first published run. Renders inline once; further
          // status emits don't re-add the CTA.
          if (!grid.querySelector('[data-watch-empty-cta]')) {
            const cta = el('div', { parent: grid });
            cta.setAttribute('data-watch-empty-cta', '1');
            cta.style.cssText = 'border:1px dashed rgba(255,216,74,0.35);border-radius:8px;padding:18px 14px;background:rgba(255,216,74,0.03);display:flex;flex-direction:column;align-items:center;gap:10px;text-align:center;';
            const icon = el('div', { parent: cta, text: '🛸' });
            icon.style.cssText = 'font-size:2rem;opacity:0.85;';
            const headLine = el('div', { parent: cta, text: 'BE THE FIRST PLAYER ON THE WATCH PAGE' });
            headLine.style.cssText = 'font-size:0.95rem;color:#ffe0a0;letter-spacing:0.14em;';
            const sub = el('div', { parent: cta, text: 'No claimed runs on relays yet. Play a game on pallasite.app and your score lands here as a kind 30762 event.' });
            sub.style.cssText = 'font-size:0.82rem;color:rgba(220,210,255,0.75);max-width:520px;line-height:1.5;';
            const playBtn = el('button', { className: 'menu-btn', parent: cta, text: 'PLAY NOW · PALLASITE.APP' });
            playBtn.addEventListener('click', () => {
              try { window.location.assign('https://pallasite.app/'); }
              catch { window.location.assign('/'); }
            });
          }
        } else {
          status.textContent = `Listening to ${s.relaysSettled}/${s.relaysAttempted} relays…`;
        }
      },
    },
  );

  // Lightweight "X minutes ago" updater so cards age visibly without a
  // full re-subscribe. Also re-evaluates liveness: a LIVE card whose
  // most recent heartbeat is now older than LIVE_FRESHNESS_MS demotes
  // to a regular recent-run card.
  const ageTimer = window.setInterval(() => {
    const now = Date.now();
    let anyDemoted = false;
    for (const card of cardByPubkey.values()) {
      const at = parseInt(card.dataset.createdAt ?? '0', 10);
      if (!at) continue;
      const stillLive = card.dataset.live === '1' && now - at * 1000 < LIVE_FRESHNESS_MS;
      const wasLive = card.dataset.live === '1';
      const whenPill = card.querySelector('[data-watch-when]');
      if (whenPill) whenPill.textContent = stillLive ? 'LIVE' : timeAgo(at);
      if (wasLive && !stillLive) {
        setCardLiveState(card, false);
        anyDemoted = true;
      }
    }
    // If we demoted a card, the LIVE-first sort may now be wrong — push
    // ex-live cards down. Cheap: snapshot order, re-sort by dataset.live then createdAt.
    if (anyDemoted) {
      const cards = Array.from(cardByPubkey.values()).sort((a, b) => {
        const liveDelta = Number(b.dataset.live === '1') - Number(a.dataset.live === '1');
        if (liveDelta) return liveDelta;
        return parseInt(b.dataset.createdAt ?? '0', 10) - parseInt(a.dataset.createdAt ?? '0', 10);
      });
      for (const c of cards) grid.appendChild(c);
    }
  }, 10_000);
  // When user navigates away, stop the timer too. Hook into the unsubscribe.
  const baseUnsub = watchActiveUnsubscribe;
  watchActiveUnsubscribe = (): void => {
    window.clearInterval(ageTimer);
    baseUnsub?.();
  };
}

/** Mini live preview canvas for a player. Subscribes to the same WS
 *  endpoint the full theatre uses but renders a stripped-down view:
 *  ship triangle + asteroid dots + UFO dots, no particles, no skins,
 *  no shield/thrust effects. Optimised for ≤200px-wide tiles so 3-4
 *  can run in parallel at 60fps without burning the main thread.
 *
 *  Returns an unsubscribe — call when the tile is removed. The caller
 *  can also call .onClick to wire up expand-to-full-theatre. */
export function makeMiniLiveTile(
  pubkey: string,
  displayName: string,
  initialScore: number,
  initialWave: number,
  initialSats: number,
  runStartedAtMs: number,
  state: GameState,
  rank: 1 | 2 | 3,
): { el: HTMLElement; unsubscribe: () => void } {
  // A live preview window: a wave / score / sats strip on top, the game
  // rendered live in vector below it, player name + icon underneath.
  // Rank colour tints the border so 1ST / 2ND / 3RD reads at a glance.
  const rankColours: Record<1 | 2 | 3, string> = { 1: '#ffd84a', 2: '#cbd5e0', 3: '#cd7f32' };
  const rankGlows: Record<1 | 2 | 3, string> = {
    1: '0 0 22px rgba(255,216,74,0.42)',
    2: '0 0 18px rgba(203,213,224,0.30)',
    3: '0 0 18px rgba(205,127,50,0.30)',
  };
  const rankLabels: Record<1 | 2 | 3, string> = { 1: '1ST', 2: '2ND', 3: '3RD' };
  const tint = rankColours[rank];

  const card = el('div');
  card.style.cssText = [
    'position:relative',
    `border:2px solid ${tint}88`,
    'border-radius:10px',
    'background:#02050d',
    `box-shadow:${rankGlows[rank]}`,
    'overflow:hidden',
    'cursor:pointer',
    '-webkit-tap-highlight-color:transparent',
    'display:flex',
    'flex-direction:column',
    'transition:transform 120ms ease, box-shadow 120ms ease',
  ].join(';');
  card.addEventListener('mouseenter', () => {
    card.style.transform = 'translateY(-2px)';
    card.style.boxShadow = rankGlows[rank].replace(/0\.\d+/g, (m) => String(Math.min(0.9, parseFloat(m) + 0.18)));
  });
  card.addEventListener('mouseleave', () => {
    card.style.transform = '';
    card.style.boxShadow = rankGlows[rank];
  });

  // Top strip: WAVE / SCORE / sats.
  const topStrip = el('div', { parent: card });
  topStrip.style.cssText = 'display:flex;align-items:baseline;justify-content:space-between;gap:8px;padding:7px 11px;font-family:monospace;background:rgba(2,5,13,0.92);border-bottom:1px solid rgba(220,210,255,0.10);';
  const waveEl = el('span', { parent: topStrip, text: `WAVE ${initialWave}` });
  waveEl.style.cssText = 'color:#8cffb4;letter-spacing:0.12em;font-size:0.74rem;';
  const scoreEl = el('span', { parent: topStrip, text: initialScore.toLocaleString() });
  scoreEl.style.cssText = 'color:#ffd84a;letter-spacing:0.05em;font-size:1.04rem;font-weight:bold;text-shadow:0 0 8px rgba(255,216,74,0.45);';
  const satsEl = el('span', { parent: topStrip, text: `₿ ${initialSats}` });
  satsEl.style.cssText = 'color:#ffd84a;letter-spacing:0.06em;font-size:0.78rem;';

  // Canvas: the live vector render. Wrapped so the rank and LIVE chips
  // can overlay it without colliding with the strips.
  const canvasWrap = el('div', { parent: card });
  canvasWrap.style.cssText = 'position:relative;line-height:0;';
  const dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
  const canvas = el('canvas', {
    parent: canvasWrap,
    attrs: {
      width: String(Math.round(PALL_WORLD_W * dpr)),
      height: String(Math.round(PALL_WORLD_H * dpr)),
    },
  }) as HTMLCanvasElement;
  canvas.style.cssText = `display:block;width:100%;height:auto;aspect-ratio:${PALL_WORLD_W} / ${PALL_WORLD_H};background:#02050d;`;
  const ctx = canvas.getContext('2d');
  if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

  const rankChip = el('span', { parent: canvasWrap, text: rankLabels[rank] });
  rankChip.style.cssText = `position:absolute;top:7px;left:7px;font-size:0.64rem;font-weight:bold;letter-spacing:0.18em;color:${tint};border:1.5px solid ${tint};padding:2px 8px;border-radius:4px;font-family:monospace;background:rgba(2,5,13,0.82);text-shadow:0 0 8px ${tint}88;`;
  const pill = el('span', { parent: canvasWrap, text: '● LIVE' });
  pill.style.cssText = 'position:absolute;top:7px;right:7px;font-size:0.6rem;font-weight:bold;letter-spacing:0.16em;color:#8cffb4;border:1.5px solid rgba(140,255,180,0.7);padding:2px 8px;border-radius:4px;font-family:monospace;background:rgba(2,5,13,0.82);animation:pallasite-live-pulse 1.6s ease-in-out infinite;';

  // Bottom strip: avatar + player name.
  const botStrip = el('div', { parent: card });
  botStrip.style.cssText = 'display:flex;align-items:center;gap:8px;padding:8px 10px;background:rgba(2,5,13,0.92);border-top:1px solid rgba(220,210,255,0.10);';
  const avatarEl = el('div', { parent: botStrip });
  avatarEl.style.cssText = `flex:0 0 26px;width:26px;height:26px;border-radius:50%;background:linear-gradient(135deg, ${tint}33, rgba(140,255,180,0.18));border:1px solid ${tint}66;background-size:cover;background-position:center;`;
  const nameEl = el('span', { parent: botStrip, text: displayName });
  nameEl.style.cssText = 'flex:1;color:#fff5d8;letter-spacing:0.08em;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-family:monospace;font-size:0.85rem;';

  // Resolve a profile name + avatar in the background.
  void (async () => {
    try {
      const profile = await fetchProfile(pubkey);
      const display = bestName(profile, pubkey);
      if (display && display !== pubkey) nameEl.textContent = display;
      if (profile?.picture) avatarEl.style.backgroundImage = `url('${profile.picture.replace(/'/g, '%27')}')`;
    } catch { /* ignore */ }
  })();

  // Click opens the full live theatre.
  card.addEventListener('click', () => {
    unsubscribe();
    renderLiveTheatre({
      masterPubkey: pubkey,
      displayName: nameEl.textContent ?? shortPubkey(pubkey),
      initialScore: parseInt((scoreEl.textContent ?? '0').replace(/,/g, ''), 10) || 0,
      initialWave: parseInt((waveEl.textContent ?? '0').replace(/\D/g, ''), 10) || 0,
      runStartedAtMs,
      onClose: () => renderWatchPage(state),
    });
  });

  // WS subscription: full LiveFrame via readWsFrame, the same wire the
  // theatre consumes.
  let lastFrame: LiveFrame | null = null;
  let ws: WebSocket | null = null;
  let closed = false;
  let lastActivity = Date.now();
  const open = (): void => {
    if (closed) return;
    try {
      ws = new WebSocket(`wss://controller.pallasite.app/?s=${encodeURIComponent(pubkey)}&r=subscribe`);
    } catch { return; }
    ws.onmessage = (ev) => {
      try {
        const obj = JSON.parse(typeof ev.data === 'string' ? ev.data : '');
        if (!obj || typeof obj.type === 'string') return;  // ignore peer-up/down
        const lf = readWsFrame(obj as Record<string, unknown>);
        if (!lf) return;
        lastFrame = lf;
        lastActivity = Date.now();
        scoreEl.textContent = lf.score.toLocaleString();
        waveEl.textContent = `WAVE ${lf.wave}`;
        satsEl.textContent = `₿ ${lf.sats}`;
      } catch { /* ignore */ }
    };
    ws.onclose = () => { if (!closed) window.setTimeout(open, 1500); };
    ws.onerror = () => { try { ws?.close(); } catch { /* ignore */ } };
  };
  open();

  // Render loop: synthesise a GameState from the latest frame and draw
  // it through the shared render() in a forced vector tier with the HUD
  // hidden (score / wave / sats live in the strip above). Both overrides
  // are set only across the synchronous render() call, so they never
  // race the game loop or another tile.
  const tileState = createTheatreState();
  const tileRenderMode: RenderModeInfo = {
    kind: 'retro', vw: PALL_WORLD_W, vh: PALL_WORLD_H, dpr,
    scale: 1, tx: 0, ty: 0, insets: { top: 0, right: 0, bottom: 0, left: 0 },
  };
  let raf = 0;
  const draw = (): void => {
    if (closed) return;
    raf = requestAnimationFrame(draw);
    if (!ctx) return;
    if (!lastFrame) {
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.fillStyle = '#02050d';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = 'rgba(140,255,180,0.18)';
      ctx.fillRect(0, canvas.height / 2, canvas.width, 1);
      ctx.restore();
      return;
    }
    populateTheatreState(tileState, {
      prev: lastFrame, next: lastFrame, t: 0,
      playbackT: lastFrame.capturedAt, particles: [], debris: [],
    });
    setRenderMode(tileRenderMode);
    setForcedVisualTier('vector');
    setHudHidden(true);
    try {
      render(canvas, tileState, performance.now());
    } finally {
      setForcedVisualTier(null);
      setHudHidden(false);
    }
    if (Date.now() - lastActivity > 6000) {
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.fillStyle = 'rgba(2,5,13,0.55)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = 'rgba(255,120,120,0.85)';
      ctx.font = `bold ${Math.round(11 * dpr)}px ui-monospace, monospace`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('STALE', canvas.width / 2, canvas.height / 2);
      ctx.restore();
    }
  };
  raf = requestAnimationFrame(draw);

  const unsubscribe = (): void => {
    closed = true;
    cancelAnimationFrame(raf);
    try { ws?.close(); } catch { /* ignore */ }
  };

  return { el: card, unsubscribe };
}

function renderWatchCard(entry: WatchEntry, state: GameState): HTMLElement {
  const card = el('div');
  card.dataset.pubkey = entry.pubkey;
  card.dataset.createdAt = String(entry.createdAt);
  card.style.cssText = 'border:1px solid rgba(255,216,74,0.35);border-radius:8px;padding:10px 14px;background:rgba(255,216,74,0.04);display:flex;flex-direction:column;gap:6px;';

  const head = el('div', { parent: card });
  head.style.cssText = 'display:flex;justify-content:space-between;align-items:baseline;gap:12px;flex-wrap:wrap;';

  const name = el('div', { parent: head, text: shortPubkey(entry.pubkey) });
  name.style.cssText = 'font-family:monospace;font-size:0.95rem;color:#ffe0a0;letter-spacing:0.06em;';
  // Resolve display name from kind 0 in the background — replaces the
  // short pubkey when (and if) we have a profile.
  void (async () => {
    try {
      const profile = await fetchProfile(entry.pubkey);
      const display = bestName(profile, entry.pubkey);
      if (display && display !== entry.pubkey) name.textContent = display;
    } catch { /* ignore */ }
  })();

  const meta = el('div', { parent: head });
  meta.style.cssText = 'display:flex;gap:8px;align-items:baseline;';
  // "MINE" badge — surfaces signed-in player's own runs so they can
  // verify their score landed on relays + open their own replay.
  // Tinted purple so it sits off the LIVE-green / when-grey scale.
  if (state.session && state.session.pubkey === entry.pubkey) {
    const mine = el('span', { parent: meta, text: 'MINE' });
    mine.style.cssText = 'font-size:0.68rem;letter-spacing:0.16em;color:#cbb6ff;border:1px solid rgba(184,144,255,0.55);padding:1px 6px;border-radius:3px;font-family:monospace;background:rgba(120,90,200,0.10);';
  }
  const livePill = el('span', { parent: meta, text: 'LIVE' });
  livePill.setAttribute('data-watch-live-pill', '1');
  livePill.style.cssText = 'display:none;font-size:0.68rem;letter-spacing:0.16em;color:#8cffb4;border:1px solid rgba(140,255,180,0.55);padding:1px 6px;border-radius:3px;font-family:monospace;animation:pallasite-live-pulse 1.6s ease-in-out infinite;';
  const when = el('div', { parent: meta, text: entry.isLive ? 'LIVE' : timeAgo(entry.createdAt) });
  when.setAttribute('data-watch-when', '1');
  when.style.cssText = 'font-size:0.78rem;color:rgba(220,210,255,0.6);letter-spacing:0.06em;';

  const stats = el('div', { parent: card });
  stats.style.cssText = 'display:flex;gap:14px;font-size:0.88rem;color:rgba(220,210,255,0.85);letter-spacing:0.06em;flex-wrap:wrap;align-items:baseline;';
  const score = el('span', { parent: stats, text: entry.score.toLocaleString() });
  score.setAttribute('data-watch-score', '1');
  score.style.cssText = 'color:#ffe0a0;font-size:1.05rem;font-family:monospace;';
  const wave = el('span', { parent: stats, text: `WAVE ${entry.wave}` });
  wave.setAttribute('data-watch-wave', '1');
  if (entry.sats > 0) el('span', { parent: stats, text: `₿ ${entry.sats}` })
    .style.cssText = 'color:#ffd84a;';
  if (entry.seed) el('span', { parent: stats, text: `SEED ${entry.seed}` })
    .style.cssText = 'color:rgba(180,140,255,0.7);font-family:monospace;';
  // Zap-total chip — kept invisible until the kind 9735 aggregator
  // reports >0 sats for this pubkey. The orange ⚡ visually separates
  // it from the ₿ daily-claim sats already on the card, and reads as a
  // "this player got zapped" signal at a glance.
  const zapChip = el('span', { parent: stats, text: '' });
  zapChip.setAttribute('data-watch-zap', '1');
  zapChip.style.cssText = 'display:none;color:#ff8a3a;font-family:monospace;letter-spacing:0.06em;';

  const actions = el('div', { parent: card });
  actions.style.cssText = 'display:flex;gap:8px;margin-top:4px;flex-wrap:wrap;';
  const watch = el('button', { className: 'menu-btn', parent: actions, text: 'WATCH' });
  // LIVE actives open the live theatre (kind 22769 frame subscription).
  // Finals open the replay theatre (kind 30763 ghost from gamestr-spec).
  // Stale/orphan actives that aren't fresh enough to be LIVE get a
  // disabled label since their kind 30763 ghost was never published
  // either. Non-live / non-final entries still get a clickable button:
  // we attempt the ghost fetch on demand — if the player has since
  // claimed, the replay opens; if not, we surface a friendly retry.
  const updateWatchButton = (live: boolean, st: WatchEntry['state']): void => {
    if (live) {
      watch.disabled = false;
      watch.style.opacity = '1';
      watch.style.cursor = 'pointer';
      watch.textContent = '👁  WATCH LIVE';
      watch.title = 'Live spectate — kind 22769 frame stream rendered in lockstep.';
      return;
    }
    const isFinal = st === 'final';
    watch.disabled = false;
    watch.style.opacity = '1';
    watch.style.cursor = 'pointer';
    watch.textContent = isFinal ? 'WATCH' : 'TRY REPLAY';
    watch.title = isFinal
      ? 'Open the kind 30763 ghost in the replay theatre.'
      : 'The replay (kind 30763 ghost) is published when the run ends. We\'ll check the relay — if the player has claimed since, it will open.';
  };
  updateWatchButton(entry.isLive, entry.state);
  watch.setAttribute('data-watch-button', '1');
  watch.addEventListener('click', () => {
    if (watch.disabled) return;
    // Audio prep MUST happen synchronously inside the click gesture.
    // iOS Safari unlocks audio per-element on the first in-gesture
    // .play(); musicWarmUpAll does that priming dance for every track,
    // skipping whatever is currently playing so we don't bounce the
    // title music. The async replay fetch below runs OUTSIDE the
    // gesture window, so any element first-played there is permanently
    // muted. resumePlayback also handles the post-pageshow case where
    // the AudioContext was suspended by silenceAll().
    void audio.unlockAudio().catch(() => undefined);
    audio.resumePlayback();
    musicWarmUpAll(currentTrackId() ?? undefined);
    if (entry.isLive) {
      renderLiveTheatre({
        masterPubkey: entry.pubkey,
        displayName: name.textContent ?? shortPubkey(entry.pubkey),
        initialScore: entry.score,
        initialWave: entry.wave,
        // WatchEntry.createdAt is the most-recent heartbeat (within a
        // few seconds of run start in practice) — good enough as the
        // ghost-fetch `since` filter when the spectator clicks
        // "WATCH FROM START" after the run ends.
        runStartedAtMs: entry.createdAt * 1000,
        onClose: () => renderWatchPage(state),
      });
      return;
    }
    // Try rich kind 30764 first (full-world replay) — falls back to the
    // pose-only kind 30763 path if no 30764 is found. Works for both
    // 'active' (player hasn't claimed) and 'final' entries because
    // findReplayByAuthor / fetchReplayByScoreEventId both publish
    // alongside the ghost on claim.
    watch.disabled = true;
    const original = watch.textContent;
    watch.textContent = 'LOADING…';
    void (async () => {
      const sinceSec = Math.max(0, entry.createdAt - 30);
      // Final entries: try score-id lookup first (#e match — only works
      // if the player passed scoreEventId at publishReplay time; at
      // game-over they don't have it yet because the score event is
      // signed by the faucet AFTER the claim). Then fall back to
      // author+since which finds any kind 30764 from that pubkey.
      let rich: Awaited<ReturnType<typeof findReplayByAuthor>> = null;
      try {
        if (entry.state === 'final') {
          rich = await fetchReplayByScoreEventId(entry.eventId);
          if (!rich) {
            console.log(`[replay] #e lookup empty for ${entry.eventId.slice(0, 8)}…, falling back to findReplayByAuthor`);
            rich = await findReplayByAuthor(entry.pubkey, sinceSec);
          }
        } else {
          rich = await findReplayByAuthor(entry.pubkey, sinceSec);
        }
      } catch { /* fall through to ghost */ }
      if (rich && rich.frames.length >= 2) {
        renderLiveTheatre({
          masterPubkey: entry.pubkey,
          displayName: name.textContent ?? shortPubkey(entry.pubkey),
          initialScore: rich.score,
          initialWave: rich.wave,
          runStartedAtMs: entry.createdAt * 1000,
          onClose: () => renderWatchPage(state),
          replaySource: {
            frames: rich.frames,
            durationMs: rich.durationMs,
            headerLabel: 'REPLAY · WATCHING',
            eventId: rich.eventId,
          },
        });
        return;
      }
      // Fallback: pose-only kind 30763 ghost.
      const scoreEventId = entry.state === 'final'
        ? entry.eventId
        : await findScoreIdForLatestGhost(entry.pubkey, sinceSec).catch(() => null);
      if (!scoreEventId) {
        watch.textContent = 'NO REPLAY YET';
        watch.title = 'Player has not claimed yet — the kind 30763 / 30764 events publish at claim. Try again later.';
        setTimeout(() => { watch.disabled = false; watch.textContent = original ?? 'TRY REPLAY'; }, 2_500);
        return;
      }
      renderReplayTheatre({
        scoreEventId,
        displayName: name.textContent ?? shortPubkey(entry.pubkey),
        score: entry.score,
        wave: entry.wave,
        sats: entry.sats,
        onClose: () => renderWatchPage(state),
      });
    })();
  });

  // SHARE — copies a /#score=<eventId> deep-link. Single click, no
  // pre-fetch needed — recipient's browser resolves the kind 30764
  // via its #e tag on page load.
  const share = el('button', { className: 'menu-btn secondary', parent: actions, text: 'SHARE' }) as HTMLButtonElement;
  share.style.cssText += 'color:#cbb6ff;border-color:rgba(184,144,255,0.55);';
  share.addEventListener('click', () => {
    const url = `https://watch.pallasite.app/#score=${entry.eventId}`;
    const restore = (): void => { setTimeout(() => { share.textContent = 'SHARE'; }, 1400); };
    void (async () => {
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(url);
          share.textContent = 'COPIED';
          restore();
          return;
        }
      } catch { /* fall through */ }
      window.prompt('Copy the replay link:', url);
      share.textContent = 'COPIED';
      restore();
    })();
  });
  const zap = el('button', { className: 'menu-btn secondary', parent: actions, text: '⚡ ZAP' });
  zap.style.cssText = 'color:#ffd84a;border-color:rgba(255,216,74,0.45);';
  zap.addEventListener('click', () => { void onWatchZapClick(entry, name.textContent ?? shortPubkey(entry.pubkey), zap, actions, state); });

  const dismiss = el('button', { className: 'menu-btn secondary', parent: actions, text: 'DISMISS' });
  dismiss.style.cssText = 'color:rgba(220,210,255,0.6);border-color:rgba(220,210,255,0.25);font-size:0.78rem;';
  dismiss.title = 'Hide this entry from your view. Local-only — does not publish a NIP-09 deletion.';
  dismiss.addEventListener('click', () => {
    dismissWatchEntry(entry.eventId);
    card.remove();
  });

  return card;
}

async function onWatchZapClick(
  entry: WatchEntry,
  displayName: string,
  zapBtn: HTMLButtonElement,
  actionsRow: HTMLElement,
  state: GameState,
): Promise<void> {
  zapBtn.disabled = true;
  zapBtn.textContent = '⚡ …';
  let lud16: string | null;
  try {
    const profile = await fetchProfile(entry.pubkey);
    lud16 = profile?.lud16 ?? null;
  } catch {
    lud16 = null;
  }
  if (!lud16) {
    zapBtn.disabled = false;
    zapBtn.textContent = '⚡ NO LUD16';
    zapBtn.title = 'This player has no lightning address in their Nostr profile.';
    setTimeout(() => { zapBtn.textContent = '⚡ ZAP'; zapBtn.title = ''; }, 2400);
    return;
  }
  zapBtn.disabled = false;
  zapBtn.textContent = '⚡ ZAP';

  // Replace the actions row with an inline amount picker — anchored under
  // the same card so the user doesn't lose context. CANCEL puts the
  // original buttons back.
  const original = Array.from(actionsRow.children);
  actionsRow.innerHTML = '';
  const label = el('span', { parent: actionsRow, text: `ZAP ${displayName}:` });
  label.style.cssText = 'font-size:0.82rem;color:rgba(255,216,74,0.85);letter-spacing:0.06em;align-self:center;';
  for (const sats of WATCH_ZAP_PRESETS_SATS) {
    const btn = el('button', { className: 'menu-btn secondary', parent: actionsRow, text: `${sats}` });
    btn.style.cssText = 'color:#ffd84a;border-color:rgba(255,216,74,0.55);min-width:64px;';
    btn.addEventListener('click', () => {
      const recipient: ZapRecipient = { pubkey: entry.pubkey, lightningAddress: lud16! };
      void quickZapToRecipient(state, recipient, displayName, sats, btn);
    });
  }
  const cancel = el('button', { className: 'menu-btn secondary', parent: actionsRow, text: 'CANCEL' });
  cancel.addEventListener('click', () => {
    actionsRow.innerHTML = '';
    for (const child of original) actionsRow.appendChild(child);
  });
}

/** Mirror of quickZap (for the dev) but addressing an arbitrary recipient. */
async function quickZapToRecipient(
  state: GameState,
  recipient: ZapRecipient,
  displayName: string,
  amountSats: number,
  btn: HTMLButtonElement,
): Promise<void> {
  const originalHtml = btn.innerHTML;
  const restore = (): void => { btn.innerHTML = originalHtml; btn.disabled = false; btn.style.opacity = '1'; };
  btn.disabled = true;
  btn.style.opacity = '0.6';
  btn.innerHTML = `⚡ …`;
  const pop = createZapPopover(amountSats);
  // Customise the popover heading so the user knows who's being zapped.
  const headings = pop.querySelectorAll('p');
  if (headings[0]) headings[0].textContent = `ZAP ${displayName} · ${amountSats} SATS`;

  try {
    const res = await requestZapTo({
      recipient,
      session: state.session,
      amountSats,
      comment: `Pallasite watch zap — wave done well`,
    });
    populateZapPopover(pop, res.invoice, amountSats, res.isZap);
    restore();
    if (hasWebLN()) {
      try {
        await payViaWebLN(res.invoice);
        markPopoverPaid(pop, amountSats);
        return;
      } catch { /* WebLN refused — leave the QR up */ }
    }
  } catch (err) {
    failPopover(pop, err instanceof Error ? err.message : String(err));
    btn.innerHTML = '✗ FAIL';
    btn.style.borderColor = '#ff5050';
    btn.style.color = '#ff5050';
    setTimeout(() => {
      btn.style.borderColor = 'rgba(255,216,74,0.55)';
      btn.style.color = '#ffd84a';
      restore();
    }, 2500);
  }
}

function renderLeaderboardBlock(parent: HTMLElement, list: ReturnType<typeof getLocalHighScores>, title: string, max = 5, onReplayClose?: () => void): void {
  const block = el('div', { className: 'leaderboard-block', parent });
  el('p', { className: 'leaderboard-title', parent: block, text: title });
  const table = el('div', { className: 'leaderboard-table', parent: block });
  list.slice(0, max).forEach((entry, i) => {
    // When the entry has a published kind 30762 score event id, the row is
    // clickable -- click anywhere on it to fetch the matching ghost (kind
    // 30763) and play it back in the replay theatre. Local-only entries
    // (no eventId) stay non-interactive.
    const replayable = !!entry.eventId;
    const cls = (base: string): string => replayable ? `${base} replay-row` : base;
    const rank = el('div', { className: cls('rank'), parent: table, text: String(i + 1).padStart(2, '0') });
    const name = el('div', { className: cls('name'), parent: table, text: entry.name });
    const score = el('div', { className: cls('score'), parent: table, text: String(entry.score).padStart(6, '0') });
    const sats = el('div', { className: cls('sats'), parent: table, text: entry.sats > 0 ? `₿ ${entry.sats}` : '' });
    if (replayable && entry.eventId && onReplayClose) {
      const eventId = entry.eventId;
      const onClick = (): void => {
        renderReplayTheatre({
          scoreEventId: eventId,
          displayName: entry.name,
          score: entry.score,
          wave: entry.wave,
          sats: entry.sats,
          onClose: onReplayClose,
        });
      };
      for (const cell of [rank, name, score, sats]) {
        cell.setAttribute('data-event-id', eventId);
        cell.addEventListener('click', onClick);
      }
    }
  });
}


/** Accessor for the live in-progress name in the title-screen arcade
 *  picker. Set by renderSessionPanel when the no-session branch mounts
 *  the picker; cleared when the panel re-renders into the signed-in
 *  branch. The IGNITE button on the title reads this so a player who
 *  hammers IGNITE without pressing DONE still gets whatever they
 *  cycled in, falling back to 'Anonymous' for an untouched picker. */
let titleNamePickerGetName: (() => string) | null = null;

function renderSessionPanel(parent: HTMLElement, state: GameState): void {
  parent.innerHTML = '';
  // Reset the title-screen name-picker accessor on every panel render —
  // a re-render usually means the picker is being unmounted, so a stale
  // closure would point at a detached DOM widget.
  titleNamePickerGetName = null;
  if (state.session && isGuestSession(state.session)) {
    // Subtle disclosure path — the player is on a locally-generated
    // Nostr identity. We surface the name (editable) and a quiet
    // npub line + upgrade affordance, but skip the "locked to X via
    // bunker" copy that's appropriate for "real" signers. Goal:
    // feels like a normal name display, not a crypto-key panel.
    const session = state.session;
    const guestName = session.displayName ?? 'Anonymous';
    const identity = el('div', { parent });
    identity.style.cssText = 'display:flex;flex-direction:column;align-items:center;gap:6px;';
    const nameLine = el('div', { parent: identity });
    nameLine.style.cssText = 'display:flex;align-items:center;gap:8px;';
    el('span', { parent: nameLine, text: 'Signed in as' }).style.cssText = 'color:rgba(220,210,255,0.7);letter-spacing:0.08em;';
    const nameBtn = el('button', { parent: nameLine, text: guestName }) as HTMLButtonElement;
    nameBtn.style.cssText = 'background:transparent;border:none;color:var(--hud-yellow);font-weight:bold;font-size:1.05rem;letter-spacing:0.06em;cursor:pointer;padding:2px 6px;border-bottom:1px dashed rgba(255,216,74,0.4);';
    nameBtn.title = 'Tap to rename';
    nameBtn.addEventListener('click', () => {
      // Inline-swap the panel into rename mode using the same arcade
      // picker the first-time signup uses. window.prompt was a quick
      // start but is unreachable from the controller PWA d-pad (no
      // alphanumerics on the joystick) and breaks the seamless feel
      // on mobile (modal blocks the page).
      parent.innerHTML = '';
      titleNamePickerGetName = null;
      const banner = el('p', { parent, text: 'RENAME' });
      banner.style.cssText = 'font-size:0.95rem;color:#ffd84a;letter-spacing:0.16em;margin:0 0 6px;';
      const commit = (raw: string): void => {
        const result = setGuestName(raw);
        if (result.ok && result.name && state.session) {
          // Mutate displayName in place so subsequent reads of
          // state.session see the new name without rebuilding the
          // whole SignetSession object.
          (state.session as { displayName: string }).displayName = result.name;
        }
        renderSessionPanel(parent, state);
      };
      renderArcadeName(parent, {
        maxLen: 25,
        initialValue: guestName,
        onSubmit: (name) => commit(name.trim() || guestName),
      });
      const cancelRow = el('div', { parent });
      cancelRow.style.cssText = 'display:flex;justify-content:center;margin-top:6px;';
      const cancel = el('button', { className: 'menu-btn secondary', parent: cancelRow, text: 'CANCEL' }) as HTMLButtonElement;
      cancel.style.cssText += 'font-size:0.72rem;padding:4px 12px;letter-spacing:0.14em;';
      cancel.addEventListener('click', () => renderSessionPanel(parent, state));
    });
    // Identity disclosure — small "your Nostr identity" line with the
    // truncated npub. Click-to-copy so a curious user can paste it
    // into another Nostr client and confirm it's a real key.
    const idLine = el('div', { parent: identity });
    idLine.style.cssText = 'font-size:0.7rem;color:rgba(180,140,255,0.65);letter-spacing:0.06em;font-family:monospace;cursor:pointer;';
    const shortPk = shortPubkey(session.pubkey);
    idLine.textContent = `local identity · ${shortPk} · tap to copy`;
    idLine.addEventListener('click', () => {
      void navigator.clipboard?.writeText(session.pubkey).then(
        () => { idLine.textContent = `copied! · ${shortPk}`; window.setTimeout(() => { idLine.textContent = `local identity · ${shortPk} · tap to copy`; }, 1400); },
        () => { /* clipboard refused — silent, the user can find it again on the settings panel */ },
      );
    });
    const row = el('div', { className: 'menu-row', parent });
    const upgrade = el('button', { className: 'menu-btn secondary', parent: row, text: 'UPGRADE TO NOSTR' }) as HTMLButtonElement;
    upgrade.style.cssText += 'font-size:0.72rem;padding:4px 12px;letter-spacing:0.14em;';
    upgrade.title = 'Sign in with a NIP-07 extension or bunker URI. Replaces this local identity with a portable Nostr account that works across devices.';
    upgrade.addEventListener('click', () => {
      void (async () => {
        try { void audio.unlockAudio(); } catch { /* ignore */ }
        try {
          const signedIn = await auth.signIn();
          if (signedIn) {
            // Real Nostr signer wins over the guest. Don't wipe the
            // guest record here — leave it on disk so an EJECT
            // returns the player to their guest identity rather than
            // a blank "what's your name?" prompt. Settings has a
            // separate "clear local identity" affordance for hard
            // wipe.
            state.session = signedIn;
            renderSessionPanel(parent, state);
          }
        } catch (err) {
          if (auth.isAuthOnlySignerError(err)) renderSignerRecovery(state);
          // Other errors are already surfaced by the SDK modal.
        }
      })();
    });
    const exportBtn = el('button', { className: 'menu-btn secondary', parent: row, text: 'EXPORT KEY' }) as HTMLButtonElement;
    exportBtn.style.cssText += 'font-size:0.72rem;padding:4px 12px;letter-spacing:0.14em;';
    exportBtn.title = 'Show your npub (public) and nsec (secret key) so you can back this identity up in a Nostr client.';
    exportBtn.addEventListener('click', () => {
      const privHex = getGuestPrivkeyHex();
      if (!privHex) return;
      openGuestKeyExport(session.pubkey, privHex);
    });
    const wipe = el('button', { className: 'menu-btn secondary', parent: row, text: 'SIGN OUT' }) as HTMLButtonElement;
    wipe.style.cssText += 'font-size:0.72rem;padding:4px 12px;letter-spacing:0.14em;color:rgba(255,120,120,0.85);border-color:rgba(255,120,120,0.4);';
    wipe.title = 'Forget this local identity. You\'ll be asked for your name again next time you visit.';
    const confirmBox = el('div', { parent });
    confirmBox.style.cssText = 'display:none;flex-direction:column;align-items:center;gap:8px;margin-top:6px;padding:10px 12px;border:1px solid rgba(255,120,120,0.35);border-radius:8px;background:rgba(255,80,80,0.08);';
    const confirmText = el('p', { parent: confirmBox, text: 'Forget this local identity? Your scores stay on relays. This device will ask for a new name next time.' });
    confirmText.style.cssText = 'margin:0;max-width:420px;text-align:center;font-size:0.76rem;line-height:1.45;color:rgba(255,190,190,0.9);letter-spacing:0.04em;';
    const confirmRow = el('div', { className: 'menu-row', parent: confirmBox });
    const keepBtn = el('button', { className: 'menu-btn secondary', parent: confirmRow, text: 'KEEP PLAYING' }) as HTMLButtonElement;
    keepBtn.style.cssText += 'font-size:0.72rem;padding:4px 12px;letter-spacing:0.14em;';
    const confirmBtn = el('button', { className: 'menu-btn secondary', parent: confirmRow, text: 'SIGN OUT' }) as HTMLButtonElement;
    confirmBtn.style.cssText += 'font-size:0.72rem;padding:4px 12px;letter-spacing:0.14em;color:rgba(255,120,120,0.95);border-color:rgba(255,120,120,0.55);';
    onTap(wipe, () => {
      row.style.display = 'none';
      confirmBox.style.display = 'flex';
      setTimeout(() => tryFocusVisible(confirmBtn), 0);
    });
    onTap(keepBtn, () => {
      confirmBox.style.display = 'none';
      row.style.display = '';
      setTimeout(() => tryFocusVisible(wipe), 0);
    });
    onTap(confirmBtn, () => {
      clearGuestIdentity();
      state.session = null;
      state.profile = null;
      // After wipe we're a fresh visitor — the attract screen is the
      // right home, not the in-place no-session panel (which would
      // show the arcade picker mid-mission-select-context).
      renderAttract(state);
    });
    return;
  }
  if (state.session) {
    const pubkey = state.session.pubkey;
    const method = state.session.method;
    // Use cached profile immediately if present, then asynchronously refresh
    const cached = state.profile ?? getCachedProfile(pubkey);
    if (cached && !state.profile) state.profile = cached;
    const renderName = (): void => {
      const name = bestName(state.profile, pubkey);
      const identityRow = parent.querySelector<HTMLElement>('.session-identity');
      if (identityRow) {
        identityRow.innerHTML = '';
        if (state.profile?.picture) {
          const img = document.createElement('img');
          img.src = state.profile.picture;
          img.alt = '';
          img.style.cssText = 'width:32px;height:32px;border-radius:50%;border:1px solid rgba(255,216,74,0.5);object-fit:cover;';
          img.onerror = () => { img.style.display = 'none'; };
          identityRow.appendChild(img);
        }
        const text = document.createElement('div');
        text.innerHTML = `Signed in as <span style="color:var(--hud-yellow);font-weight:bold;">${escapeHtml(name)}</span> <span style="color:rgba(180,180,200,0.55);font-size:0.78em;">· ${method}</span>`;
        identityRow.appendChild(text);
      }
    };
    const identity = el('div', { parent });
    identity.className = 'session-identity';
    identity.style.cssText = 'display:flex;gap:10px;align-items:center;justify-content:center;';
    renderName();
    if (!state.profile) {
      void fetchProfile(pubkey).then(p => {
        if (p) { state.profile = p; renderName(); }
      });
    }
    if (!state.session.signer.capabilities.canSignEvents) {
      const wrap = el('div', { parent });
      wrap.style.cssText = 'display:flex;flex-direction:column;align-items:center;gap:4px;margin:4px 0';
      const note = el('p', {
        parent: wrap,
        text: 'Auth-only · reconnecting signer…',
      });
      note.style.cssText = 'font-size:0.82rem;color:rgba(255,200,100,0.85);margin:0';
      // Manual nudge — if the silent watcher hasn't picked up the signer
      // after a few seconds (extension hibernation, browser policy), the
      // player can force a fresh restore. Quicker than logging out and
      // back in.
      const reconnectBtn = el('button', {
        className: 'menu-btn secondary',
        parent: wrap,
        text: 'RECONNECT SIGNER',
      }) as HTMLButtonElement;
      reconnectBtn.style.cssText = 'padding:4px 10px;font-size:0.72rem;cursor:pointer';
      onTap(reconnectBtn, () => {
        void (async () => {
          reconnectBtn.disabled = true;
          note.textContent = 'Reconnecting…';
          try {
            const upgraded = await auth.tryRestore();
            if (upgraded?.signer.capabilities.canSignEvents
                && state.session
                && upgraded.pubkey === state.session.pubkey) {
              state.session = upgraded;
              renderTitle(state);
              return;
            }
            note.textContent = 'Signer still unreachable. Unlock your extension and tap again.';
            reconnectBtn.disabled = false;
          } catch (err) {
            note.textContent = err instanceof Error ? err.message : 'Reconnect failed.';
            reconnectBtn.disabled = false;
          }
        })();
      });
    }
    const row = el('div', { className: 'menu-row', parent });
    // ADMIN button — only visible when the signed-in pubkey matches
    // the server's ADMIN_PUBKEY_HEX. Server still enforces the
    // allowlist on every action, so a tampered client that forced
    // this button to appear would just bounce off the /api/admin/v2
    // 403 wall. Useful so the admin doesn't have to remember the
    // /admin URL.
    if (isAdminSession(pubkey)) {
      const adminBtn = el('button', { className: 'menu-btn', parent: row, text: '⚙ ADMIN' }) as HTMLButtonElement;
      adminBtn.style.cssText += 'border-color:rgba(255,216,74,0.7);color:#ffd84a;letter-spacing:0.14em';
      adminBtn.title = 'Operator panel — tune caps, presets, settings, player overrides.';
      adminBtn.addEventListener('click', () => { window.location.assign('/admin'); });
    } else {
      // GameInfo (which carries admin_pubkey) is fetched lazily — if
      // the cache hasn't landed yet, kick off the fetch and re-render
      // when it resolves so the ADMIN button surfaces without
      // requiring the user to navigate away and back.
      void import('./faucet.js').then(async ({ fetchGameInfo, isAdminSession: isAdmin }) => {
        await fetchGameInfo();
        if (isAdmin(pubkey) && parent.isConnected) {
          renderSessionPanel(parent, state);
        }
      });
    }
    const out = el('button', { className: 'menu-btn secondary', parent: row, text: 'SIGN OUT' });
    let ejecting = false;
    const doEject = async (): Promise<void> => {
      if (ejecting) return;
      ejecting = true;
      try {
        await auth.signOut(state.session);
      } catch { /* ignore */ }
      state.session = null;
      state.profile = null;
      ejecting = false;
      // EJECT returns to attract — a session-less mission select would
      // surface the inline arcade picker (back-compat branch in
      // renderSessionPanel) but that's confusing in the new flow.
      renderAttract(state);
    };
    out.addEventListener('click', () => { void doEject(); });
    out.addEventListener('pointerup', e => {
      if (e.pointerType !== 'mouse') void doEject();
    });
  } else {
    // Seamless guest identity — every visitor gets a local Nostr
    // keypair the first time they ignite. The user-visible primitive is
    // a name field, not "create an account": we ask "what's your name?"
    // and silently provision the keypair behind it. Returning visitors
    // skip this branch entirely because auth.tryRestore resurrects the
    // stored guest record into a SignetSession on boot.
    const greet = el('p', { parent, text: 'WHAT\'S YOUR NAME?' });
    greet.style.cssText = 'font-size:0.95rem;color:#ffd84a;letter-spacing:0.16em;margin:0 0 6px;';
    const status = el('p', { parent });
    status.style.cssText = 'font-size:0.78rem;color:rgba(180,140,255,0.85);min-height:1em;margin:0;letter-spacing:0.04em;';
    let creating = false;
    const submitWithName = (raw: string): void => {
      if (creating) return;
      creating = true;
      status.textContent = '';
      // Audio unlock under the click gesture — same reason as the
      // watch page WATCH-button handler: any audio play() after an
      // async hop gets rejected by iOS Safari.
      try { void audio.unlockAudio(); } catch { /* ignore */ }
      void (async () => {
        try {
          const name = raw.trim() || 'Anonymous';
          const session = await auth.createGuestSession(name);
          state.session = session;
          renderSessionPanel(parent, state);
        } catch (err) {
          status.textContent = `Couldn\'t create local identity: ${err instanceof Error ? err.message : String(err)}`;
          status.style.color = '#ff8a3a';
          creating = false;
        }
      })();
    };
    // Arcade-style 25-char name picker. Three input modes all work:
    //   • Desktop kb: ▲▼ arrows or type letters directly.
    //   • Mobile touch: tap ▲▼/◀▶/BKSP/DONE buttons.
    //   • Controller PWA in d-pad mode: setupOverlayArrowNav cycles
    //     focus through the picker's <button> elements; ENTER (A) on
    //     ▲ cycles char, on ◀▶ moves cursor, on DONE submits.
    // The plain <input> the panel had before was unusable on the
    // controller PWA — d-pad emits arrow keys, not alphanumerics.
    const picker = renderArcadeName(parent, {
      maxLen: 25,
      onSubmit: (name) => { submitWithName(name); },
    });
    titleNamePickerGetName = picker.getCurrentName;
    // "I have a Nostr account already" — fold the existing Signet flow
    // into a smaller secondary path so the primary action stays "type
    // name and play". A user with NIP-07 / bunker / signet QR just
    // taps this to skip the guest path entirely.
    const advRow = el('div', { parent });
    advRow.style.cssText = 'display:flex;justify-content:center;margin-top:4px;';
    const inBtn = el('button', { className: 'menu-btn secondary', parent: advRow, text: 'I HAVE A NOSTR ACCOUNT' }) as HTMLButtonElement;
    inBtn.style.cssText += 'font-size:0.72rem;padding:4px 12px;letter-spacing:0.14em;';
    let signing = false;
    const doSignIn = async (): Promise<void> => {
      if (signing) return;
      signing = true;
      status.textContent = 'Connecting…';
      status.style.color = 'rgba(180,140,255,0.85)';
      try { void audio.unlockAudio(); } catch { /* ignore */ }
      let elapsed = 0;
      const ticker = window.setInterval(() => {
        elapsed += 1;
        status.textContent = `Connecting to your signer (${elapsed}s)…`;
      }, 1000);
      try {
        const session = await auth.signIn();
        if (session) {
          status.textContent = '';
          state.session = session;
          renderSessionPanel(parent, state);
        } else {
          status.textContent = 'No signer attached. Try a NIP-07 extension or your bunker URI.';
          status.style.color = '#ff8a3a';
        }
      } catch (err) {
        if (auth.isAuthOnlySignerError(err)) {
          status.textContent = 'Signer is not live. Choose a My Signet signing method.';
          status.style.color = '#ffd84a';
          renderSignerRecovery(state);
        } else {
          status.textContent = err instanceof auth.SignInTimeoutError
            ? `Timeout — ${err.message}`
            : `Sign-in failed: ${err instanceof Error ? err.message : String(err)}`;
          status.style.color = '#ff5050';
        }
      } finally {
        window.clearInterval(ticker);
        signing = false;
      }
    };
    inBtn.addEventListener('click', () => { void doSignIn(); });
    // Touch fallback. body's `touch-action: none` cascade plus the overlay's
    // `pan-y` can occasionally swallow click events on iOS/Android when a
    // gesture starts as a tap and the browser reclassifies it mid-flight.
    // pointerup fires from the same gesture and is more reliable. The
    // `signing` flag dedupes the (rare) case where both events land.
    inBtn.addEventListener('pointerup', e => {
      if (e.pointerType !== 'mouse') void doSignIn();
    });
    // Autofocus the first interactive control inside the arcade name
    // picker on desktop so a kb user can start typing letters straight
    // away (the picker has a keydown shortcut for letter / digit keys
    // that types into the active slot). Skip on coarse-pointer devices
    // since we don't want focus jumping into a hidden control before
    // the user has decided to play.
    if (!matchMedia('(pointer: coarse)').matches) {
      window.setTimeout(() => {
        const firstBtn = parent.querySelector<HTMLButtonElement>('[data-arcade-initials="open"] button');
        try { firstBtn?.focus(); } catch { /* ignore */ }
      }, 60);
    }
  }
}

// ── Music player (secret) ────────────────────────────────────────────────────

/**
 * Hidden music-player easter egg. Reached by long-pressing the title logo.
 * Lists every campaign track with a play button that crossfades to it.
 * The currently-playing track shows a pulsing ▶ glyph. BACK restores the
 * normal title music via musicForceRefresh — the game loop's next tick
 * resolves 'pallasite-idle' from the title phase and crossfades back.
 */
function renderMusicPlayer(state: GameState, onBack: () => void): void {
  void state;
  clearOverlay();
  // Freeplay: full songs from the top, album plays through (vs gameplay's
  // good-minute clips). Reset on BACK.
  musicSetFreeplay(true);
  const overlay = el('div', { className: 'overlay', parent: root });
  setupOverlayArrowNav(overlay);
  el('h2', { parent: overlay, text: 'SOUNDTRACK' });
  const sub = el('p', { parent: overlay, text: 'Two albums. Tune the dial.' });
  sub.style.cssText = 'font-size:0.95rem;letter-spacing:0.2em;color:var(--hud-yellow);margin:-12px 0 6px;';

  // OutRun-style car-stereo tuner — the album selector. Mounted here so it sits
  // directly above the EQ analyser (which reads as the radio's graphic EQ).
  // Populated at the foot of this function, once buildList()/shownAlbum exist.
  const radioMount = el('div', { parent: overlay });
  radioMount.style.cssText = 'width:100%;max-width:480px;margin:0 0 6px;';

  // Spectral analyser, sticky at the top of the overlay so it stays in
  // view while the user scrolls through the track list. Log-scale bands
  // give bass and treble equal visual weight, gradient fill (green→yellow
  // →red) plus peak-hold caps with decay echo classic VU meters, strong
  // bloom on bar tops makes the bass kicks read.
  //
  // Lightweight mode on mobile: the underlying game canvas + WebGL
  // overlay are already drawing at 60fps beneath the music player.
  // Stacking a third 60fps canvas with ~200 shadowBlur calls per frame
  // plus sparks plus the analyser tap on top of the AudioContext
  // decoding music chokes mobile browsers badly enough that audio drops
  // out and touch events stop firing. The lightweight mode drops shadowBlur
  // entirely (the most expensive Safari op), skips sparks, halves the bar
  // count, and throttles the RAF loop to ~20fps.
  const lightweightViz = mobileRuntimeActive() || (isIosSafari() && isStandalone());
  const vizSticky = el('div', { parent: overlay });
  vizSticky.style.cssText = [
    'position:sticky', 'top:-8px',  // -8px so the rounded corners overlap the overlay padding
    'z-index:5',
    'width:100%', 'max-width:480px',
    'padding:8px 0',
    'background:linear-gradient(180deg, rgba(0,0,0,0.85), rgba(0,0,0,0.55))',
    'backdrop-filter:blur(6px)',
    'border-radius:10px',
    'display:flex', 'justify-content:center',
  ].join(';');
  const canvas = el('canvas', { parent: vizSticky, attrs: { width: '960', height: '360' } }) as HTMLCanvasElement;
  // Shrink the viz on phones so the 20-row track list isn't pushed
  // below the fold. 110px is enough for the bar pattern to read; the
  // 172px desktop version stays via the min() ceiling.
  canvas.style.cssText = 'width:100%;max-width:460px;height:min(172px, 22vh);border-radius:6px;background:radial-gradient(ellipse at 50% 100%, rgba(180,140,255,0.18), rgba(0,0,0,0.7));';

  const BAR_COUNT = lightweightViz ? 32 : 96;
  const peaks = new Float32Array(BAR_COUNT);
  const smoothed = new Float32Array(BAR_COUNT);  // smoothed bar values, fluid rise/fall
  const PEAK_DECAY = 0.014;
  const FALL_RATE = 0.045;     // how fast bars fall when freq drops (gravity)
  let bgPulse = 0;
  let flashAmp = 0;            // bass-kick driven full-canvas flash
  let prevBass = 0;
  type Spark = { x: number; y: number; vx: number; vy: number; life: number; hue: number };
  const sparks: Spark[] = [];

  const drawViz = (): void => {
    if (!document.body.contains(canvas)) return;
    const analyser = getMusicAnalyser();
    const cctx = canvas.getContext('2d');
    if (!cctx) return;
    const bins = analyser.frequencyBinCount;
    const freq = new Uint8Array(bins);
    const time = new Uint8Array(analyser.fftSize);
    analyser.getByteFrequencyData(freq);
    analyser.getByteTimeDomainData(time);
    const w = canvas.width;
    const h = canvas.height;
    const baseline = h * 0.78;  // bars stand on this line; mirror below

    // Bass envelope. Detect kicks via positive delta to drive the flash.
    let bassAcc = 0;
    for (let i = 0; i < 6; i++) bassAcc += freq[i];
    const bass = bassAcc / 6 / 255;
    const bassDelta = Math.max(0, bass - prevBass);
    prevBass = bass;
    if (bassDelta > 0.06) flashAmp = Math.min(1, flashAmp + bassDelta * 1.6);
    flashAmp *= 0.86;
    bgPulse = bgPulse * 0.83 + bass * 0.17;

    cctx.clearRect(0, 0, w, h);

    // Background — radial gradient that pulses with bass; topped by a
    // brief full-canvas flash on each kick.
    const bgGrad = cctx.createRadialGradient(w * 0.5, baseline, h * 0.05, w * 0.5, baseline, h * (0.7 + bgPulse * 0.6));
    bgGrad.addColorStop(0, `rgba(255, 100, 200, ${0.07 + bgPulse * 0.22})`);
    bgGrad.addColorStop(0.55, `rgba(120, 90, 255, ${0.05 + bgPulse * 0.12})`);
    bgGrad.addColorStop(1, 'rgba(0,0,0,0)');
    cctx.fillStyle = bgGrad;
    cctx.fillRect(0, 0, w, h);
    if (flashAmp > 0.02) {
      cctx.fillStyle = `rgba(255, 255, 255, ${flashAmp * 0.18})`;
      cctx.fillRect(0, 0, w, h);
    }

    // Vertical bar gradient — green→yellow→red top-up.
    const barGrad = cctx.createLinearGradient(0, baseline, 0, 0);
    barGrad.addColorStop(0.00, '#3afc7c');
    barGrad.addColorStop(0.45, '#ffd84a');
    barGrad.addColorStop(0.78, '#ff8a3a');
    barGrad.addColorStop(1.00, '#ff4858');

    const gap = 2;
    const barW = (w - gap * (BAR_COUNT + 1)) / BAR_COUNT;
    const usableBins = Math.floor(bins * 0.82);

    for (let i = 0; i < BAR_COUNT; i++) {
      // Log-scale band mapping for equal-weight bass/mid/treble.
      const t0 = i / BAR_COUNT;
      const t1 = (i + 1) / BAR_COUNT;
      const lo = Math.floor(Math.pow(t0, 2.2) * usableBins);
      const hi = Math.max(lo + 1, Math.floor(Math.pow(t1, 2.2) * usableBins));
      let acc = 0;
      for (let j = lo; j < hi; j++) acc += freq[j];
      const raw = (acc / (hi - lo)) / 255;
      const eased = Math.pow(raw, 0.78);

      // Smooth fall — bars rise instantly to new peaks, decay back at FALL_RATE.
      if (eased > smoothed[i]) smoothed[i] = eased;
      else smoothed[i] = Math.max(0, smoothed[i] - FALL_RATE);
      const v = smoothed[i];

      // Peak hold cap.
      if (v > peaks[i]) peaks[i] = v;
      else peaks[i] = Math.max(0, peaks[i] - PEAK_DECAY);

      const x = gap + i * (barW + gap);
      const barH = v * (baseline - 6);
      const y = baseline - barH;

      // Main bar. shadowBlur is the costliest Canvas2D op on Safari —
      // a single overlay with ~200 blurred fills per frame chokes iOS,
      // so the lightweight path zeroes it.
      cctx.shadowColor = '#ff8a3a';
      cctx.shadowBlur = lightweightViz ? 0 : (14 + v * 26);
      cctx.fillStyle = barGrad;
      cctx.fillRect(x, y, barW, barH);

      // Mirror reflection — same gradient flipped, alpha fades to 0.
      const refH = barH * 0.55;
      const refGrad = cctx.createLinearGradient(0, baseline, 0, baseline + refH);
      refGrad.addColorStop(0, 'rgba(255, 138, 58, 0.45)');
      refGrad.addColorStop(1, 'rgba(255, 138, 58, 0)');
      cctx.shadowBlur = 0;
      cctx.fillStyle = refGrad;
      cctx.fillRect(x, baseline + 1, barW, refH);

      // Peak-hold cap — bright thin bar that lingers above as the bar falls.
      const peakY = baseline - peaks[i] * (baseline - 6) - 2;
      cctx.shadowBlur = lightweightViz ? 0 : 14;
      cctx.shadowColor = '#fff5d8';
      cctx.fillStyle = '#fff5d8';
      cctx.fillRect(x, peakY, barW, 2);

      // Spark burst on a fresh peak (raw amplitude high AND newly so).
      // Skipped on lightweight: ~200 sparks/sec each with shadowBlur is
      // the second-heaviest cost after the bar blurs.
      if (!lightweightViz && raw > 0.78 && Math.random() < 0.18) {
        const cx = x + barW / 2;
        const cy = peakY;
        for (let k = 0; k < 2; k++) {
          sparks.push({
            x: cx, y: cy,
            vx: (Math.random() - 0.5) * 2.4,
            vy: -1.5 - Math.random() * 1.8,
            life: 1,
            hue: 30 + Math.random() * 30,
          });
        }
      }
    }
    cctx.shadowBlur = 0;

    // Sparks — tiny dots flying upward on peak bursts.
    for (const s of sparks) {
      s.x += s.vx;
      s.y += s.vy;
      s.vy += 0.04;            // light gravity
      s.life -= 0.025;
    }
    for (let i = sparks.length - 1; i >= 0; i--) {
      if (sparks[i].life <= 0) sparks.splice(i, 1);
    }
    for (const s of sparks) {
      cctx.fillStyle = `hsla(${s.hue}, 100%, 70%, ${s.life})`;
      cctx.shadowColor = `hsla(${s.hue}, 100%, 80%, ${s.life})`;
      cctx.shadowBlur = 8;
      cctx.fillRect(s.x - 1.5, s.y - 1.5, 3, 3);
    }
    cctx.shadowBlur = 0;

    // Time-domain waveform — bright cyan line scrolls across at the
    // baseline, distorted by bass. Reads as the music's heartbeat.
    cctx.lineWidth = 1.6;
    cctx.strokeStyle = `rgba(120, 240, 255, ${0.55 + bass * 0.45})`;
    cctx.shadowColor = '#7ff0ff';
    cctx.shadowBlur = lightweightViz ? 0 : (12 + bass * 18);
    cctx.beginPath();
    const tStride = Math.max(1, Math.floor(time.length / w));
    for (let x = 0; x < w; x++) {
      const idx = Math.min(time.length - 1, x * tStride);
      const sample = (time[idx] - 128) / 128;
      const y = baseline + sample * (10 + bass * 28);
      if (x === 0) cctx.moveTo(x, y);
      else cctx.lineTo(x, y);
    }
    cctx.stroke();
    cctx.shadowBlur = 0;

    // Baseline rule — anchors the bars + mirror.
    cctx.fillStyle = 'rgba(255, 255, 255, 0.10)';
    cctx.fillRect(0, baseline, w, 1);

    // Throttle to ~20fps on iOS PWA so the underlying canvases get
    // breathing room. Desktop / Android keep the smooth 60fps path.
    if (lightweightViz) {
      window.setTimeout(() => requestAnimationFrame(drawViz), 50);
    } else {
      requestAnimationFrame(drawViz);
    }
  };
  requestAnimationFrame(drawViz);

  // Buttons go ABOVE the list so STOP + BACK are reachable without
  // scrolling past 20+ rows on a phone. Previously they sat at the
  // bottom of the overlay; with the expanded track set they were
  // off-screen on iPhone SE-class displays.
  const buttons = el('div', { className: 'menu-row', parent: overlay });
  const stop = el('button', { className: 'menu-btn secondary', parent: buttons, text: 'STOP' });
  const back = el('button', { className: 'menu-btn', parent: buttons, text: 'BACK' });

  // Albums are swappable soundtracks. The OutRun tuner (mounted above the EQ,
  // built at the foot of this function) BOTH browses an album's setlist AND
  // sets the album the game plays.
  let shownAlbum = getActiveAlbumId();

  const list = el('div', { parent: overlay });
  list.style.cssText = 'display:flex;flex-direction:column;gap:8px;width:100%;max-width:460px;';

  const renderHeader = (text: string): void => {
    const h = el('div', { parent: list, text });
    h.style.cssText = 'margin:8px 0 2px;font-size:0.78rem;letter-spacing:0.22em;color:rgba(184,144,255,0.75);text-align:left;font-family:monospace;';
  };

  const rows: Array<{ id: string; el: HTMLElement; glyph: HTMLElement }> = [];
  const paint = (): void => {
    const active = currentTrackId();
    for (const r of rows) {
      const isActive = r.id === active;
      r.el.style.borderColor = isActive ? 'rgba(255,216,74,0.8)' : 'rgba(180,140,255,0.3)';
      r.el.style.background = isActive ? 'rgba(255,216,74,0.08)' : 'rgba(180,140,255,0.04)';
      r.glyph.textContent = isActive ? '▶' : '·';
      r.glyph.style.color = isActive ? '#ffd84a' : 'rgba(180,140,255,0.6)';
    }
  };

  const addRow = (t: import('./music.js').TrackInfo): void => {
    const row = el('div', { parent: list, attrs: { role: 'group' } });
    row.style.cssText = [
      'display:flex', 'align-items:center', 'gap:10px',
      'padding:10px 12px', 'border-radius:8px',
      'border:1px solid rgba(180,140,255,0.3)',
      'background:rgba(180,140,255,0.04)',
      'cursor:pointer', '-webkit-tap-highlight-color:transparent',
      'touch-action:manipulation',
    ].join(';');

    const glyph = el('span', { parent: row, text: '·' });
    glyph.style.cssText = 'font-size:1.1rem;width:1.4rem;text-align:center;color:rgba(180,140,255,0.6);flex:0 0 1.4rem;';

    // Prominent wave-number chip on the left for wave tracks. Stings
    // get a small dim chip so the columns align visually.
    const waveChip = el('span', { parent: row });
    waveChip.style.cssText = [
      'flex:0 0 56px',
      'font-family:monospace', 'font-size:0.95rem', 'letter-spacing:0.06em',
      'text-align:center', 'padding:3px 0',
      'border-radius:4px',
      t.wave !== null
        ? 'background:rgba(255,216,74,0.12);color:#ffd84a;border:1px solid rgba(255,216,74,0.35);'
        : 'background:rgba(184,144,255,0.08);color:rgba(184,144,255,0.55);border:1px solid rgba(184,144,255,0.25);',
    ].join(';');
    waveChip.textContent = t.wave !== null ? `W${t.wave}` : '·';

    const text = el('div', { parent: row });
    text.style.cssText = 'flex:1;display:flex;flex-direction:column;gap:1px;text-align:left;min-width:0;';
    const label = el('span', { parent: text, text: t.label });
    label.style.cssText = "font-family:'VT323',ui-monospace,monospace;font-size:1.05rem;letter-spacing:0.16em;color:#fff5d8;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;";
    const hint = el('span', { parent: text, text: t.hint });
    hint.style.cssText = 'font-size:0.72rem;letter-spacing:0.06em;color:rgba(220,210,255,0.6);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;';

    onTap(row, () => {
      void audio.unlockAudio();
      musicPreviewPlay(t.id);
      paint();
    });

    rows.push({ id: t.id, el: row, glyph });
  };

  // (Re)build the track list for the currently-shown album. Stings/system
  // (title + cinematic), bonus levels (off-rail detours), then the wave
  // setlist. Called on first render and whenever the album chip changes.
  const buildList = (): void => {
    list.replaceChildren();
    rows.length = 0;
    const tracks = listTracks().filter((t) => (t.album ?? 'pallasite') === shownAlbum);
    const stings = tracks.filter((t) => t.category === 'sting');
    const bonusTracks = tracks.filter((t) => t.category === 'bonus');
    const waveTracks = tracks.filter((t) => t.category === 'wave');
    if (stings.length) {
      renderHeader(shownAlbum === 'pallasite' ? 'STINGS · SYSTEM' : 'TITLE · CREDITS');
      for (const t of stings) addRow(t);
    }
    if (bonusTracks.length) {
      renderHeader('BONUS LEVELS');
      for (const t of bonusTracks) addRow(t);
    }
    if (waveTracks.length) {
      renderHeader('WAVE TRACKS (1 → 25)');
      for (const t of waveTracks) addRow(t);
    }
    paint();
  };

  // ── OutRun car-stereo tuner ──────────────────────────────────────
  // Albums are radio "stations": ◄ TUNE ► (or tapping a preset) flips the
  // in-game album, the LCD seeks to the station's frequency, and the list
  // below re-builds. The sticky EQ analyser above doubles as the radio's
  // graphic EQ, completing the head-unit look.
  const tunerAlbums = listAlbums();
  const FREQ: Record<string, string> = { pallasite: '88.5', relay: '99.3' };
  const freqOf = (id: string): string => FREQ[id] ?? (87.5 + Math.max(0, tunerAlbums.findIndex((a) => a.id === id)) * 5.7).toFixed(1);
  const labelOf = (id: string): string => tunerAlbums.find((a) => a.id === id)?.label ?? id;

  const bezel = el('div', { parent: radioMount });
  bezel.style.cssText = [
    'position:relative', 'border-radius:12px', 'padding:10px 12px 11px',
    'background:linear-gradient(180deg,#1a1024 0%,#0a0612 70%,#140a1e 100%)',
    'border:1px solid rgba(255,170,90,0.35)',
    'box-shadow:0 0 0 1px rgba(0,0,0,0.6) inset,0 0 18px rgba(255,120,180,0.18),0 6px 18px rgba(0,0,0,0.5)',
  ].join(';');

  // Top control row: ◄ TUNE · F M · TUNE ►
  const ctrlRow = el('div', { parent: bezel });
  ctrlRow.style.cssText = 'display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:8px;';
  const mkTuneBtn = (txt: string): HTMLButtonElement => {
    const b = el('button', { parent: ctrlRow, text: txt }) as HTMLButtonElement;
    b.style.cssText = [
      'font-family:monospace', 'font-size:0.8rem', 'letter-spacing:0.16em', 'padding:6px 12px',
      'border-radius:7px', 'color:#ffd0a0',
      'background:linear-gradient(180deg,rgba(60,40,30,0.9),rgba(30,18,14,0.9))',
      'border:1px solid rgba(255,170,90,0.5)', 'box-shadow:0 1px 0 rgba(255,210,170,0.25) inset',
      'cursor:pointer', '-webkit-tap-highlight-color:transparent', 'touch-action:manipulation',
    ].join(';');
    return b;
  };
  const tuneL = mkTuneBtn('◄ TUNE');
  const fmBadge = el('span', { parent: ctrlRow, text: 'F M' });
  fmBadge.style.cssText = "font-family:monospace;font-size:0.7rem;letter-spacing:0.3em;color:#ff6fae;text-shadow:0 0 8px rgba(255,111,174,0.8);";
  const tuneR = mkTuneBtn('TUNE ►');

  // LCD station display: frequency + album name.
  const lcd = el('div', { parent: bezel });
  lcd.style.cssText = [
    'display:flex', 'align-items:baseline', 'gap:10px', 'justify-content:center', 'flex-wrap:wrap',
    'padding:8px 12px', 'border-radius:8px',
    'background:radial-gradient(ellipse at 50% 40%,rgba(10,30,30,0.95),rgba(2,8,10,0.98))',
    'border:1px solid rgba(120,240,255,0.25)', 'box-shadow:0 0 14px rgba(0,0,0,0.7) inset',
  ].join(';');
  const freqEl = el('span', { parent: lcd });
  freqEl.style.cssText = "font-family:'VT323',ui-monospace,monospace;font-size:1.5rem;letter-spacing:0.08em;color:#8ef0ff;text-shadow:0 0 10px rgba(142,240,255,0.85);min-width:4ch;text-align:right;";
  const mhz = el('span', { parent: lcd, text: 'MHz' });
  mhz.style.cssText = 'font-family:monospace;font-size:0.6rem;letter-spacing:0.16em;color:rgba(142,240,255,0.6);';
  const nameEl = el('span', { parent: lcd });
  nameEl.style.cssText = "font-family:'VT323',ui-monospace,monospace;font-size:1.5rem;letter-spacing:0.22em;color:#ffd84a;text-shadow:0 0 10px rgba(255,216,74,0.7);white-space:nowrap;";

  // Preset dots.
  const dotsRow = el('div', { parent: bezel });
  dotsRow.style.cssText = 'display:flex;align-items:center;justify-content:center;gap:18px;margin-top:8px;';
  const dotEls: Array<{ id: string; dot: HTMLElement; lab: HTMLElement; cell: HTMLElement }> = [];
  for (const a of tunerAlbums) {
    const cell = el('div', { parent: dotsRow });
    cell.style.cssText = 'display:flex;align-items:center;gap:6px;cursor:pointer;-webkit-tap-highlight-color:transparent;touch-action:manipulation;';
    const dot = el('span', { parent: cell, text: '◦' });
    dot.style.cssText = 'font-size:0.9rem;';
    const lab = el('span', { parent: cell, text: a.label });
    lab.style.cssText = 'font-family:monospace;font-size:0.66rem;letter-spacing:0.14em;';
    dotEls.push({ id: a.id, dot, lab, cell });
  }

  let sweepTimer: number | null = null;
  const paintRadio = (): void => {
    nameEl.textContent = labelOf(shownAlbum);
    if (sweepTimer === null) freqEl.textContent = freqOf(shownAlbum);
    for (const d of dotEls) {
      const on = d.id === shownAlbum;
      d.dot.textContent = on ? '●' : '◦';
      d.dot.style.color = on ? '#ff6fae' : 'rgba(180,140,255,0.5)';
      d.dot.style.textShadow = on ? '0 0 8px rgba(255,111,174,0.9)' : 'none';
      d.lab.style.color = on ? '#fff5d8' : 'rgba(180,140,255,0.55)';
    }
  };
  // Dial-seek flourish: scramble the frequency digits, then settle on target.
  const sweepToFreq = (target: string): void => {
    if (sweepTimer !== null) { clearInterval(sweepTimer); sweepTimer = null; }
    let ticks = 0;
    sweepTimer = window.setInterval(() => {
      if (!document.body.contains(freqEl)) { if (sweepTimer !== null) clearInterval(sweepTimer); sweepTimer = null; return; }
      ticks += 1;
      if (ticks >= 10) {
        freqEl.textContent = target;
        if (sweepTimer !== null) clearInterval(sweepTimer);
        sweepTimer = null;
        return;
      }
      freqEl.textContent = (87.5 + Math.random() * 20).toFixed(1);
    }, 35);
  };
  const selectAlbum = (id: string): void => {
    void audio.unlockAudio();
    const changed = id !== shownAlbum;
    shownAlbum = id;
    setActiveAlbum(id);   // the station you tune to is the one the game plays
    if (changed) sweepToFreq(freqOf(id));
    paintRadio();
    buildList();
  };
  const tune = (dir: 1 | -1): void => {
    const i = Math.max(0, tunerAlbums.findIndex((a) => a.id === shownAlbum));
    selectAlbum(tunerAlbums[(i + dir + tunerAlbums.length) % tunerAlbums.length].id);
  };
  onTap(tuneL, () => tune(-1));
  onTap(tuneR, () => tune(1));
  for (const d of dotEls) onTap(d.cell, () => selectAlbum(d.id));

  paintRadio();
  buildList();

  // Follow the jukebox: when freeplay auto-advances to the next album track,
  // move the active-row highlight with it. Cheap per-frame id check; stops
  // itself once the overlay is gone.
  let lastActiveRow = currentTrackId();
  const syncActiveRow = (): void => {
    if (!document.body.contains(list)) return;
    const a = currentTrackId();
    if (a !== lastActiveRow) { lastActiveRow = a; paint(); }
    requestAnimationFrame(syncActiveRow);
  };
  requestAnimationFrame(syncActiveRow);

  onTap(stop, () => { musicStop(250); paint(); });
  onTap(back, () => {
    // Leave freeplay so gameplay music resumes its good-minute behaviour.
    musicSetFreeplay(false);
    // Restore state-driven music. Force-refresh invalidates the memo so the
    // next musicSetTrackForState tick (the game loop runs every frame) will
    // re-resolve the current phase and crossfade back to pallasite-idle.
    musicForceRefresh();
    onBack();
  });

  // ── Audio diagnostic + RESET ──────────────────────────────────────────
  // iOS PWA-only: SFX work but music goes silent occasionally. The diag
  // strip surfaces enough of the audio state to triage without
  // ?dbg=audio, and the RESET button performs the same recovery dance
  // firstUnlock does (resume ctx → dispose pre-gesture elements → warm
  // up under THIS gesture → re-play the current phase's track). One-tap
  // self-serve fix when music is dead.
  const diagWrap = el('div', { parent: overlay });
  diagWrap.style.cssText = 'margin:14px auto 0;max-width:480px;width:100%;display:flex;flex-direction:column;gap:8px;align-items:center;';
  const diagText = el('pre', { parent: diagWrap });
  diagText.style.cssText = 'margin:0;font:0.72rem/1.4 ui-monospace,monospace;color:rgba(220,210,255,0.65);text-align:center;background:rgba(15,8,32,0.6);padding:8px 10px;border-radius:6px;border:1px solid rgba(184,144,255,0.25);white-space:pre-wrap;';
  const refreshDiag = (): void => {
    const snap = getMusicDebugSnapshot();
    const ctxState = audio.getAudioContextState();
    diagText.textContent =
      `ctx:${ctxState} · vol:${(audio.getMusicVolume() * 100).toFixed(0)}% · muted:${audio.isMuted() ? 'Y' : 'N'}\n` +
      `track:${snap.currentId ?? '-'} · paused:${snap.paused ?? '-'} · ready:${snap.readyState ?? '-'} · loaded:${snap.loadedCount}` +
      (snap.errorCode != null ? `\nerr ${snap.errorCode}: ${snap.errorMsg ?? ''}` : '');
  };
  refreshDiag();
  const diagTimer = window.setInterval(refreshDiag, 1000);
  // Stop the poll when the overlay is rebuilt or torn down.
  const observer = new MutationObserver(() => {
    if (!document.body.contains(diagText)) {
      clearInterval(diagTimer);
      observer.disconnect();
    }
  });
  observer.observe(root, { childList: true, subtree: true });
  const diagRow = el('div', { parent: diagWrap });
  diagRow.style.cssText = 'display:flex;gap:8px;flex-wrap:wrap;justify-content:center;';
  const resetBtn = el('button', { className: 'menu-btn secondary', parent: diagRow, text: '🔧 RESET AUDIO' });
  onTap(resetBtn, () => {
    const resumeId = currentTrackId();
    void audio.unlockAudio();
    musicResetElements();
    musicWarmUpAll(resumeId ?? undefined);
    if (resumeId) musicPreviewPlay(resumeId);
    musicForceRefresh();
    refreshDiag();
  });
}

// ── Pause overlay ─────────────────────────────────────────────────────────────

export function renderPause(state?: GameState): void {
  if (state) refreshScoreRecovery(state);
  clearOverlay();
  const overlay = el('div', { className: 'overlay', parent: root });
  setupOverlayArrowNav(overlay);
  el('h2', { parent: overlay, text: 'PAUSED' });
  const row = el('div', { className: 'menu-row', parent: overlay });
  const resume = el('button', { className: 'menu-btn', parent: row, text: 'RESUME' });
  resume.addEventListener('click', () => onResumeCb?.());
  // Focus RESUME so a single Enter press (or controller-pad A) un-pauses
  // without first hunting via the d-pad. setTimeout(0) defers past the
  // keydown that opened the pause menu — otherwise the focus call races
  // the same event and the button doesn't take focus. tryFocusVisible
  // makes the focus ring light up loudly so the player sees what's
  // about to fire.
  setTimeout(() => tryFocusVisible(resume), 0);
  // Settings is operator-only at a booth kiosk — walk-ups never reach remap /
  // presets / display / clear-scores. The operator gets there via the hidden
  // logo long-press on the lobby (→ full menu → Settings).
  if (!boothKiosk) {
    const settings = el('button', { className: 'menu-btn secondary', parent: row, text: 'SETTINGS' });
    settings.addEventListener('click', () => renderSettings(() => renderPause(state)));
  }
  if (state) {
    const quit = el('button', { className: 'menu-btn secondary', parent: row, text: boothKiosk ? 'END RUN' : 'QUIT TO TITLE' });
    quit.addEventListener('click', () => {
      // Abandon the run — drop straight to title without going through gameover.
      audio.thrustOff();
      audio.ufoSirenStop();
      audio.stopHeartbeat();
      audio.stopAmbient();
      audio.setMusicDuck(1);
      // Wipe entities — leftover asteroids/debris/particles otherwise
      // bleed through the title overlay's translucent backdrop.
      clearEntitiesForTitle(state);
      state.phase = 'title';
      state.phaseStart = state.elapsed;
      renderTitle(state);
    });
  }

  // Booth walk-ups are on pads, not a keyboard — show the pad control legend
  // here too so a paused player can re-check the mapping. Desktop keeps the
  // keyboard hint strip.
  if (boothKiosk) {
    renderBoothControlsLegend(overlay);
  } else {
    const hint = el('div', { className: 'kbhint', parent: root });
    hint.innerHTML = '<kbd>P</kbd> resume &nbsp;·&nbsp; <kbd>ESC</kbd> resume &nbsp;·&nbsp; <kbd>M</kbd> mute';
  }
}

// ── How to Play overlay ──────────────────────────────────────────────────────

/**
 * Player-facing instructions. Voice-guide compliant: physics + verbs, no
 * marketing fluff, British English. Cheats deliberately omitted (they're
 * easter eggs that lose meaning if telegraphed).
 */
export function renderHowToPlay(onBack: () => void): void {
  clearOverlay();
  const overlay = el('div', { className: 'overlay', parent: root });
  setupOverlayArrowNav(overlay);
  el('h2', { parent: overlay, text: 'HOW TO PLAY' });

  const tagline = el('p', { parent: overlay, text: 'Drift the orbit. Shoot rocks. Survive 25 waves.' });
  tagline.style.cssText = 'font-size:1rem;color:var(--hud-yellow);letter-spacing:0.18em;text-shadow:0 0 8px rgba(255,216,74,0.45);margin:0 0 6px;';

  const panel = el('div', { parent: overlay });
  panel.style.cssText = 'display:flex;flex-direction:column;gap:18px;align-items:stretch;max-width:540px;text-align:left;';

  type Row = readonly [string, string] | readonly [string, string, HTMLCanvasElement];
  function section(title: string, lines: ReadonlyArray<Row>): void {
    const block = el('div', { parent: panel });
    block.style.cssText = 'display:flex;flex-direction:column;gap:6px;';
    const h = el('p', { parent: block, text: title });
    h.style.cssText = 'font-size:0.78rem;letter-spacing:0.32em;color:rgba(180,140,255,0.95);margin:0;';
    const grid = el('div', { parent: block });
    // Three columns now: preview slot (44px, kept consistent across all rows
    // so text aligns even when most rows have no preview) | key | value.
    grid.style.cssText = 'display:grid;grid-template-columns:44px max-content 1fr;gap:6px 14px;font-size:0.92rem;color:rgba(220,210,255,0.92);align-items:center;';
    for (const row of lines) {
      const previewCell = el('div', { parent: grid });
      previewCell.style.cssText = 'display:flex;align-items:center;justify-content:center;';
      if (row.length === 3) previewCell.appendChild(row[2]);
      const key = el('span', { parent: grid, text: row[0] });
      key.style.cssText = 'color:#5b9dff;letter-spacing:0.08em;font-weight:bold;';
      el('span', { parent: grid, text: row[1] });
    }
  }

  section('CONTROLS', [
    ['← →', 'Rotate ship'],
    ['↑', 'Thrust forward'],
    ['Space', 'Fire'],
    ['↓', 'Shield (timed burst)'],
    ['↓ ↓ or Shift / H', 'Hyperspace — risky'],
    ['P / Esc', 'Pause'],
    ['M', 'Mute audio'],
  ]);

  section('THE GOAL', [
    ['Waves 1-24', 'Each named after a real pallasite meteorite'],
    ['Wave 25', 'Event Horizon — the boss arena'],
    ['Dust shards', 'Tinted to the source rock · score reward', dustPreview('iron')],
    ['Sat coins', '₿ — adds to your in-game sats counter', satCoinPreview()],
    ['Lives', 'Lose all and the run ends · extra life every 10,000 score'],
  ]);

  section('WHAT TO WATCH FOR', [
    ['Combo chain', 'Quick consecutive kills stack a multiplier — watch the chip'],
    ['Hyperspace', 'Re-emerges anywhere, but ~6% chance the warp goes wrong'],
    ['Mines', 'From wave 8 — gravity wells; takes a few hits', minePreview()],
    ['Snipers', 'From wave 10 — slow, accurate, lethal', sniperPreview()],
    ['Stony rocks', 'The baseline silicate — 1 hit, modest payout', asteroidPreview('stony')],
    ['Iron rocks', 'Two hits to crack — orange shards, 1.6× score', asteroidPreview('iron')],
    ['Chondrite rocks', 'Fragile — splits into three on break', asteroidPreview('chondrite')],
    ['Pallasite rocks', 'Rare jackpot — gold shards, 2× score, sat-guaranteed', asteroidPreview('pallasite')],
  ]);

  section('POWERUPS — drop from UFO kills', [
    ['RAPID', 'Faster fire cadence for 8 seconds', powerupPreview('rapid')],
    ['×2 SATS', 'Doubles sat value of dropped coins for 12 seconds', powerupPreview('satboost')],
    ['TRIDENT', 'Three-bullet fan instead of single shot · 6 seconds', powerupPreview('trident')],
    ['MAGNET', 'Pulls coins + dust to your ship across the screen · 8 seconds', powerupPreview('magnet')],
    ['NOVA', 'One-shot — destroys every asteroid on screen · score-only', powerupPreview('nova')],
  ]);

  section('SIGN IN WITH NOSTR', [
    ['Optional', 'Guests play for score only · sign in to publish to leaderboards'],
    ['Daily run', 'Same seed for everyone today · clean leaderboard'],
  ]);

  const row = el('div', { className: 'menu-row', parent: overlay });
  const back = el('button', { className: 'menu-btn', parent: row, text: 'BACK' });
  back.addEventListener('click', onBack);
}

// ── Settings overlay ─────────────────────────────────────────────────────────

/**
 * Audio mix panel — master / music / SFX sliders + mute. Reachable from title
 * and pause; `onBack` re-renders whichever screen opened it.
 */
/**
 * Renders a small ship preview into a fixed-size canvas. Used by the skins
 * panel so each skin card shows the actual silhouette + thrust palette the
 * player will see in-game, not a coloured swatch.
 */
function paintShipPreview(canvas: HTMLCanvasElement, skin: SkinDef, locked: boolean): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const w = canvas.width;
  const h = canvas.height;
  ctx.clearRect(0, 0, w, h);
  ctx.save();
  ctx.translate(w / 2, h / 2);
  ctx.scale(1.4, 1.4);
  ctx.globalAlpha = locked ? 0.35 : 1;
  ctx.lineWidth = 1.6;
  ctx.strokeStyle = skin.palette.ship;
  ctx.shadowColor = skin.palette.shipShadow;
  ctx.shadowBlur = 12;
  ctx.beginPath();
  ctx.moveTo(14, 0);
  ctx.lineTo(-10, 8);
  ctx.lineTo(-6, 0);
  ctx.lineTo(-10, -8);
  ctx.closePath();
  ctx.stroke();
  // Static thrust flame so the player sees the full palette.
  ctx.strokeStyle = skin.palette.thrust;
  ctx.shadowColor = skin.palette.thrustShadow;
  ctx.shadowBlur = 10;
  ctx.beginPath();
  ctx.moveTo(-6, 4);
  ctx.lineTo(-14, 0);
  ctx.lineTo(-6, -4);
  ctx.stroke();
  ctx.restore();
}

/**
 * Skins section in the settings overlay. Shows every catalogue entry as a
 * card with a live ship preview, label, description, and either an
 * "ACTIVE" badge (unlocked + selected), a "SELECT" button (unlocked +
 * not selected), or the unlock hint (still locked).
 */
function renderSkinsPanel(parent: HTMLElement): void {
  const heading = el('p', { parent, text: 'SHIPS' });
  heading.style.cssText = 'font-size:0.78rem;letter-spacing:0.4em;color:rgba(180,140,255,0.85);margin:8px 0 -10px;';

  const grid = el('div', { parent });
  grid.style.cssText = 'display:flex;gap:14px;flex-wrap:wrap;justify-content:center;align-items:stretch;';

  const cards: Array<{ skin: SkinDef; el: HTMLElement; preview: HTMLCanvasElement; cta: HTMLElement }> = [];

  for (const skin of SKINS) {
    const card = el('div', { parent: grid });
    card.style.cssText = [
      'display:flex', 'flex-direction:column', 'align-items:center', 'gap:6px',
      'padding:12px 14px', 'border-radius:10px', 'min-width:160px',
      'background:rgba(180,140,255,0.04)', 'border:1px solid rgba(180,140,255,0.25)',
    ].join(';');

    const preview = el('canvas', { parent: card, attrs: { width: '110', height: '60' } });
    preview.style.cssText = 'background:#02050d;border-radius:6px;width:110px;height:60px;';

    const label = el('p', { parent: card, text: skin.label });
    label.style.cssText = "font-family:'VT323',ui-monospace,monospace;font-size:1.1rem;letter-spacing:0.18em;color:#fff5d8;margin:0;";

    const desc = el('p', { parent: card, text: skin.description });
    desc.style.cssText = 'font-size:0.7rem;letter-spacing:0.04em;color:rgba(220,210,255,0.75);margin:0;text-align:center;line-height:1.4;max-width:160px;';

    const cta = el('div', { parent: card });
    cta.style.cssText = 'min-height:30px;display:flex;align-items:center;justify-content:center;';

    cards.push({ skin, el: card, preview, cta });
  }

  function paintAll(): void {
    const activeId = getActiveSkinId();
    for (const { skin, el: card, preview, cta } of cards) {
      const unlocked = isSkinUnlocked(skin.id);
      paintShipPreview(preview, skin, !unlocked);

      cta.innerHTML = '';
      if (!unlocked) {
        card.style.borderColor = 'rgba(180,140,255,0.18)';
        const hint = el('p', { parent: cta, text: skin.unlockHint });
        hint.style.cssText = 'font-size:0.72rem;color:rgba(180,140,255,0.65);letter-spacing:0.06em;margin:0;text-align:center;';
      } else if (skin.id === activeId) {
        card.style.borderColor = 'rgba(255,216,74,0.65)';
        const badge = el('p', { parent: cta, text: 'ACTIVE' });
        badge.style.cssText = "font-family:'VT323',ui-monospace,monospace;font-size:0.95rem;letter-spacing:0.18em;color:#ffd84a;margin:0;text-shadow:0 0 6px rgba(255,216,74,0.45);";
      } else {
        card.style.borderColor = 'rgba(91,255,140,0.45)';
        const btn = el('button', { parent: cta, text: 'SELECT' });
        btn.style.cssText = [
          'background:transparent', 'border:1px solid rgba(91,255,140,0.6)',
          'color:#7fffb0', "font-family:'VT323',ui-monospace,monospace",
          'font-size:0.85rem', 'letter-spacing:0.12em', 'padding:4px 14px',
          'cursor:pointer', 'border-radius:6px',
        ].join(';');
        btn.addEventListener('click', () => {
          if (setActiveSkinId(skin.id)) paintAll();
        });
      }
    }
  }
  paintAll();
}

export function renderSettings(onBack: () => void): void {
  clearOverlay();
  const overlay = el('div', { className: 'overlay settings-screen', parent: root });
  setupOverlayArrowNav(overlay, onBack);
  el('h2', { parent: overlay, text: 'SETTINGS' });
  const sub = el('p', { parent: overlay, text: 'AUDIO' });
  sub.style.cssText = 'font-size:0.78rem;letter-spacing:0.4em;color:rgba(180,140,255,0.85);margin:0 0 -8px;';

  const panel = el('div', { parent: overlay });
  panel.style.cssText = 'display:flex;flex-direction:column;gap:18px;align-items:stretch;min-width:0;width:100%;max-width:480px;margin-top:6px;';

  function row(label: string, get: () => number, set: (v: number) => void): { input: HTMLInputElement; readout: HTMLElement } {
    const wrap = el('div', { parent: panel });
    wrap.style.cssText = 'display:grid;grid-template-columns:76px minmax(0,1fr) 36px;gap:10px;align-items:center;';
    const lab = el('label', { parent: wrap, text: label });
    lab.style.cssText = 'font-size:0.95rem;color:rgba(180,140,255,0.95);letter-spacing:0.22em;';
    const input = el('input', { parent: wrap, attrs: { type: 'range', min: '0', max: '100', step: '1' } }) as HTMLInputElement;
    input.value = String(Math.round(get() * 100));
    input.style.cssText = 'accent-color:#ffd84a;width:100%;min-width:0;min-height:44px;';
    const readout = el('div', { parent: wrap, text: input.value });
    readout.style.cssText = "font-family:'VT323',ui-monospace,monospace;font-size:1.1rem;color:#ffd84a;text-align:right;letter-spacing:0.08em;";
    input.addEventListener('input', () => {
      const v = parseInt(input.value, 10) / 100;
      set(v);
      readout.textContent = input.value;
    });
    return { input, readout };
  }

  row('MASTER', audio.getMasterVolume, audio.setMasterVolume);
  const musicRow = row('MUSIC',  audio.getMusicVolume,  audio.setMusicVolume);
  row('SFX',    audio.getSfxVolume,    audio.setSfxVolume);

  // Mute toggle row
  const muteWrap = el('div', { parent: panel });
  muteWrap.style.cssText = 'display:flex;justify-content:space-between;align-items:center;gap:14px;border-top:1px solid rgba(180,140,255,0.2);padding-top:14px;';
  const muteLab = el('span', { parent: muteWrap, text: 'MUTE ALL' });
  muteLab.style.cssText = 'font-size:0.95rem;color:rgba(180,140,255,0.95);letter-spacing:0.22em;';
  const muteBtn = el('button', { parent: muteWrap, text: audio.isMuted() ? 'ON' : 'OFF' });
  function paintMute(): void {
    const on = audio.isMuted();
    muteBtn.textContent = on ? 'ON' : 'OFF';
    muteBtn.style.cssText = [
      'background:' + (on ? 'rgba(255,80,80,0.18)' : 'transparent'),
      'border:2px solid ' + (on ? '#ff5050' : 'rgba(180,140,255,0.4)'),
      'color:' + (on ? '#ff5050' : 'rgba(220,210,255,0.85)'),
      "font-family:'VT323',ui-monospace,monospace",
      'font-size:1rem', 'padding:6px 18px', 'letter-spacing:0.18em',
      'cursor:pointer', 'border-radius:6px', 'min-width:64px',
    ].join(';');
  }
  paintMute();
  muteBtn.addEventListener('click', () => {
    audio.setMuted(!audio.isMuted());
    paintMute();
  });

  // Quick reference for the in-game mute key
  const note = el('p', { parent: panel, text: 'Tap M in-game to toggle mute. Music ducks while paused.' });
  note.style.cssText = 'font-size:0.78rem;color:rgba(180,140,255,0.7);letter-spacing:0.06em;margin:0;text-align:center;';

  // ── SOUNDTRACK album switch ─────────────────────────────────────────────
  // The full track player lives behind a logo long-press (easy to miss), so
  // surface the album switch here in Settings where it's actually findable.
  const albumWrap = el('div', { parent: panel });
  albumWrap.style.cssText = 'display:flex;justify-content:space-between;align-items:center;gap:14px;border-top:1px solid rgba(180,140,255,0.2);padding-top:14px;flex-wrap:wrap;';
  const albumLab = el('span', { parent: albumWrap, text: 'SOUNDTRACK' });
  albumLab.style.cssText = 'font-size:0.95rem;color:rgba(180,140,255,0.95);letter-spacing:0.22em;';
  const albumBtnRow = el('div', { parent: albumWrap });
  albumBtnRow.style.cssText = 'display:flex;gap:8px;flex-wrap:wrap;';
  const albumButtons: Array<{ id: string; el: HTMLButtonElement }> = [];
  function paintAlbums(): void {
    const active = getActiveAlbumId();
    for (const b of albumButtons) {
      const on = b.id === active;
      b.el.style.cssText = [
        'background:' + (on ? 'rgba(255,216,74,0.16)' : 'transparent'),
        'border:2px solid ' + (on ? '#ffd84a' : 'rgba(180,140,255,0.4)'),
        'color:' + (on ? '#ffd84a' : 'rgba(220,210,255,0.85)'),
        "font-family:'VT323',ui-monospace,monospace",
        'font-size:0.95rem', 'padding:6px 14px', 'letter-spacing:0.14em',
        'cursor:pointer', 'border-radius:6px',
      ].join(';');
    }
  }
  for (const a of listAlbums()) {
    const b = el('button', { parent: albumBtnRow, text: a.label }) as HTMLButtonElement;
    albumButtons.push({ id: a.id, el: b });
    b.addEventListener('click', () => { void audio.unlockAudio(); setActiveAlbum(a.id); paintAlbums(); });
  }
  paintAlbums();
  const albumNote = el('p', { parent: panel, text: 'Switch the in-game music album. Long-press the logo for the full track player.' });
  albumNote.style.cssText = 'font-size:0.78rem;color:rgba(180,140,255,0.7);letter-spacing:0.06em;margin:0;text-align:center;';

  // ── EVENT LOBBY ────────────────────────────────────────────────────────
  // Operator-only kiosk/private-lobby mode. Defaults to two stations, but the
  // name and station count can be changed for events or a one-station lobby.
  const eventHeading = el('p', { parent: overlay, text: 'LOBBY MODE' });
  eventHeading.style.cssText = 'font-size:0.78rem;letter-spacing:0.4em;color:rgba(180,140,255,0.85);margin:6px 0 -10px;';
  const eventPanel = el('div', { parent: overlay });
  eventPanel.style.cssText = 'display:flex;flex-direction:column;gap:10px;align-items:stretch;min-width:0;width:100%;max-width:480px;';
  const eventStateRow = el('div', { parent: eventPanel });
  eventStateRow.style.cssText = 'display:grid;grid-template-columns:minmax(90px,140px) minmax(0,1fr);gap:14px;align-items:center;';
  el('label', { parent: eventStateRow, text: 'LOBBY' })
    .style.cssText = 'font-size:0.85rem;color:rgba(180,140,255,0.95);letter-spacing:0.18em;';
  const eventStateBtns = el('div', { parent: eventStateRow });
  eventStateBtns.style.cssText = 'display:flex;gap:6px;justify-content:flex-end;';
  const eventOnBtn = el('button', { className: 'menu-btn secondary', parent: eventStateBtns, text: 'ON' }) as HTMLButtonElement;
  const eventOffBtn = el('button', { className: 'menu-btn secondary', parent: eventStateBtns, text: 'OFF' }) as HTMLButtonElement;

  const eventNameRow = el('div', { parent: eventPanel });
  eventNameRow.style.cssText = 'display:grid;grid-template-columns:minmax(90px,140px) minmax(0,1fr);gap:14px;align-items:center;';
  el('label', { parent: eventNameRow, text: 'NAME' })
    .style.cssText = 'font-size:0.85rem;color:rgba(180,140,255,0.95);letter-spacing:0.18em;';
  const eventNameInput = el('input', { parent: eventNameRow }) as HTMLInputElement;
  eventNameInput.type = 'text';
  eventNameInput.maxLength = 32;
  eventNameInput.value = getEventLobbyConfig().lobbyName;
  eventNameInput.style.cssText = 'min-width:0;padding:8px 10px;font:0.82rem/1.4 ui-monospace,monospace;letter-spacing:0.1em;text-transform:uppercase;background:rgba(15,8,32,0.65);color:#ffd84a;border:1px solid rgba(255,216,74,0.35);border-radius:6px;outline:none;';

  const eventCountRow = el('div', { parent: eventPanel });
  eventCountRow.style.cssText = 'display:grid;grid-template-columns:minmax(90px,140px) minmax(0,1fr);gap:14px;align-items:center;';
  el('label', { parent: eventCountRow, text: 'STATIONS' })
    .style.cssText = 'font-size:0.85rem;color:rgba(180,140,255,0.95);letter-spacing:0.18em;';
  const eventCountBtns = el('div', { parent: eventCountRow });
  eventCountBtns.style.cssText = 'display:flex;gap:6px;justify-content:flex-end;flex-wrap:wrap;';
  const countBtns = new Map<number, HTMLButtonElement>();

  const eventBoothRow = el('div', { parent: eventPanel });
  eventBoothRow.style.cssText = 'display:grid;grid-template-columns:minmax(90px,140px) minmax(0,1fr);gap:14px;align-items:center;';
  el('label', { parent: eventBoothRow, text: 'THIS SCREEN' })
    .style.cssText = 'font-size:0.85rem;color:rgba(180,140,255,0.95);letter-spacing:0.18em;';
  const eventBoothBtns = el('div', { parent: eventBoothRow });
  eventBoothBtns.style.cssText = 'display:flex;gap:6px;justify-content:flex-end;flex-wrap:wrap;';
  const boothBtns = new Map<number, HTMLButtonElement>();
  for (const count of [1, 2, 4, 6]) {
    const btn = el('button', { className: 'menu-btn secondary', parent: eventCountBtns, text: String(count) }) as HTMLButtonElement;
    btn.style.cssText += 'font-size:0.72rem;padding:6px 10px;letter-spacing:0.12em;min-width:36px;';
    btn.addEventListener('click', () => {
      setEventLobbyConfig({ enabled: true, boothCount: count });
      paintEventLobbySettings();
    });
    countBtns.set(count, btn);
  }
  const eventActions = el('div', { parent: eventPanel });
  eventActions.style.cssText = 'display:flex;gap:8px;justify-content:center;flex-wrap:wrap;';
  const openEventBtn = el('button', { className: 'menu-btn secondary', parent: eventActions, text: 'OPEN EVENT LOBBY' });
  openEventBtn.style.cssText += 'font-size:0.78rem;padding:7px 14px;letter-spacing:0.12em;';
  const eventHint = el('p', { parent: eventPanel });
  eventHint.style.cssText = 'font-size:0.7rem;color:rgba(180,140,255,0.6);letter-spacing:0.04em;margin:0;text-align:center;line-height:1.4;';

  const paintEventSegment = (btn: HTMLButtonElement, on: boolean): void => {
    btn.style.background = on ? 'rgba(255,216,74,0.18)' : 'rgba(20,12,36,0.6)';
    btn.style.color = on ? '#ffd84a' : 'rgba(220,210,255,0.7)';
    btn.style.borderColor = on ? '#ffd84a' : 'rgba(180,140,255,0.45)';
  };
  function paintEventLobbySettings(): void {
    const cfg = getEventLobbyConfig();
    eventNameInput.value = cfg.lobbyName;
    paintEventSegment(eventOnBtn, cfg.enabled);
    paintEventSegment(eventOffBtn, !cfg.enabled);
    for (const [count, btn] of countBtns) paintEventSegment(btn, cfg.enabled && cfg.boothCount === count);
    eventBoothBtns.replaceChildren();
    boothBtns.clear();
    for (let booth = 1; booth <= cfg.boothCount; booth++) {
      const btn = el('button', { className: 'menu-btn secondary', parent: eventBoothBtns, text: cfg.boothCount === 1 ? 'PRIVATE' : String(booth) }) as HTMLButtonElement;
      btn.style.cssText += 'font-size:0.72rem;padding:6px 10px;letter-spacing:0.12em;min-width:36px;';
      btn.addEventListener('click', () => {
        setEventLobbyConfig({ enabled: true, booth });
        paintEventLobbySettings();
      });
      boothBtns.set(booth, btn);
      paintEventSegment(btn, cfg.enabled && cfg.booth === booth);
    }
    eventHint.textContent = cfg.enabled
      ? `Boots into ${cfg.lobbyName} ${cfg.boothCount > 1 ? `station ${cfg.booth}` : 'private lobby'}. Use NORMAL MENU on the kiosk to bypass.`
      : 'Off for normal players. Turn on for booth screens, other events, or a private lobby.';
  }
  eventOnBtn.addEventListener('click', () => {
    setEventLobbyConfig({ enabled: true, lobbyName: eventNameInput.value || DEFAULT_EVENT_LOBBY.lobbyName });
    paintEventLobbySettings();
  });
  eventOffBtn.addEventListener('click', () => {
    setEventLobbyConfig({ enabled: false });
    paintEventLobbySettings();
  });
  eventNameInput.addEventListener('change', () => {
    setEventLobbyConfig({ enabled: true, lobbyName: eventNameInput.value || DEFAULT_EVENT_LOBBY.lobbyName });
    paintEventLobbySettings();
  });
  eventNameInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      setEventLobbyConfig({ enabled: true, lobbyName: eventNameInput.value || DEFAULT_EVENT_LOBBY.lobbyName });
      paintEventLobbySettings();
      e.preventDefault();
    }
  });
  openEventBtn.addEventListener('click', () => renderEventLobby((window as unknown as { __pallasiteState?: GameState }).__pallasiteState ?? ({} as GameState)));
  paintEventLobbySettings();

  // Ship skins -- earned cosmetics drawn in render.ts. Locked entries show
  // their unlock criterion; unlocked ones are click-to-select.
  renderSkinsPanel(overlay);

  // Gamepad flight model — pickup-and-go (push the stick to fly) vs aim +
  // throttle (stick aims, right trigger throttles). The booth forces
  // pickup-and-go at runtime; this is the preference everywhere else.
  const padHeading = el('p', { parent: overlay, text: 'GAMEPAD' });
  padHeading.style.cssText = 'font-size:0.78rem;letter-spacing:0.4em;color:rgba(180,140,255,0.85);margin:6px 0 -10px;';

  const padPanel = el('div', { parent: overlay });
  padPanel.style.cssText = 'display:flex;gap:8px;align-items:center;justify-content:center;flex-wrap:wrap;';
  const padOpts: ReadonlyArray<{ value: PadFlightMode; label: string; hint: string }> = [
    { value: 'flydirect', label: 'POINT & FLY',    hint: 'Pickup-and-go — push the left stick to aim and fly together' },
    { value: 'throttle',  label: 'AIM + THROTTLE', hint: 'Left stick aims, right trigger throttles — hold a firing line' },
    { value: 'classic',   label: 'CLASSIC',        hint: 'Arcade rotate — d-pad turns (stick too), right trigger or d-pad up thrusts' },
  ];
  const padHint = el('p', { parent: overlay });
  padHint.style.cssText = 'font-size:0.75rem;color:rgba(180,140,255,0.65);letter-spacing:0.04em;margin:0;min-height:1em;text-align:center;';

  const padBtns = new Map<PadFlightMode, HTMLButtonElement>();
  function paintPad(): void {
    const cur = getPadFlightMode();
    for (const [v, btn] of padBtns) {
      const on = v === cur;
      btn.style.cssText = [
        'background:' + (on ? 'rgba(88,255,88,0.20)' : 'transparent'),
        'border:2px solid ' + (on ? '#58ff58' : 'rgba(180,140,255,0.4)'),
        'color:' + (on ? '#58ff58' : 'rgba(220,210,255,0.85)'),
        "font-family:'VT323',ui-monospace,monospace",
        'font-size:1rem', 'padding:6px 18px', 'letter-spacing:0.18em',
        'cursor:pointer', 'border-radius:6px', 'min-width:104px',
        on ? 'box-shadow:0 0 12px rgba(88,255,88,0.3);text-shadow:0 0 6px rgba(88,255,88,0.55)' : '',
      ].filter(Boolean).join(';');
    }
    padHint.textContent = padOpts.find(o => o.value === cur)?.hint ?? '';
  }
  for (const opt of padOpts) {
    const btn = el('button', { parent: padPanel, text: opt.label });
    padBtns.set(opt.value, btn);
    btn.addEventListener('click', () => { setPadFlightMode(opt.value); paintPad(); });
  }
  paintPad();
  const padNote = el('p', { parent: overlay, text: 'Right stick aims + auto-fires in every mode. Booth kiosks always use Point & Fly.' });
  padNote.style.cssText = 'font-size:0.72rem;color:rgba(180,140,255,0.5);letter-spacing:0.04em;margin:2px 0 0;text-align:center;';

  // Auto-thrust — accessibility (XAG-107): tap the throttle to cruise instead
  // of holding the trigger. Tap again to stop.
  const autoWrap = el('div', { parent: overlay });
  autoWrap.style.cssText = 'display:flex;justify-content:center;align-items:center;gap:14px;margin-top:6px;';
  const autoLab = el('span', { parent: autoWrap, text: 'AUTO-THRUST' });
  autoLab.style.cssText = 'font-size:0.85rem;color:rgba(180,140,255,0.95);letter-spacing:0.18em;';
  const autoBtn = el('button', { parent: autoWrap, text: 'OFF' }) as HTMLButtonElement;
  function paintAuto(): void {
    const on = getPadAutoThrust();
    autoBtn.textContent = on ? 'ON' : 'OFF';
    autoBtn.style.cssText = [
      'background:' + (on ? 'rgba(88,255,88,0.18)' : 'transparent'),
      'border:2px solid ' + (on ? '#58ff58' : 'rgba(180,140,255,0.4)'),
      'color:' + (on ? '#58ff58' : 'rgba(220,210,255,0.85)'),
      "font-family:'VT323',ui-monospace,monospace",
      'font-size:1rem', 'padding:6px 18px', 'letter-spacing:0.18em',
      'cursor:pointer', 'border-radius:6px', 'min-width:64px',
    ].join(';');
  }
  paintAuto();
  autoBtn.addEventListener('click', () => { setPadAutoThrust(!getPadAutoThrust()); paintAuto(); });
  const autoNote = el('p', { parent: overlay, text: 'Tap the thrust trigger to cruise, tap again to stop — no holding.' });
  autoNote.style.cssText = 'font-size:0.72rem;color:rgba(180,140,255,0.5);letter-spacing:0.04em;margin:2px 0 0;text-align:center;';

  // Remap buttons — accessibility (XAG-107): full in-game rebinding. Rotate/aim
  // live on the sticks (the GAMEPAD mode above); these are the action buttons.
  const remapHeading = el('p', { parent: overlay, text: 'REMAP BUTTONS' });
  remapHeading.style.cssText = 'font-size:0.78rem;letter-spacing:0.4em;color:rgba(180,140,255,0.85);margin:8px 0 -4px;';
  const remapPanel = el('div', { parent: overlay });
  remapPanel.style.cssText = 'display:flex;flex-direction:column;gap:6px;min-width:0;width:100%;max-width:480px;';
  const ACTION_LABELS: Record<PadAction, string> = { fire: 'FIRE', thrust: 'THRUST', shield: 'SHIELD', hyperspace: 'HYPERSPACE', pause: 'PAUSE' };
  let capturing: PadAction | null = null;
  let captureBaseline: boolean[] = [];
  const remapBtns = new Map<PadAction, HTMLButtonElement>();
  const bindingLabel = (a: PadAction): string =>
    getPadBindings()[a].map((i) => PAD_BUTTON_NAMES[i] ?? ('#' + i)).join(' / ') || '—';
  function paintRemap(): void {
    for (const a of PAD_ACTIONS) {
      const btn = remapBtns.get(a);
      if (!btn) continue;
      const live = capturing === a;
      btn.textContent = live ? 'PRESS A BUTTON…' : bindingLabel(a);
      btn.style.color = live ? '#ffd84a' : 'rgba(220,210,255,0.9)';
      btn.style.borderColor = live ? '#ffd84a' : 'rgba(180,140,255,0.4)';
    }
  }
  function padSnapshot(): boolean[] {
    const pads = navigator.getGamepads ? navigator.getGamepads() : [];
    for (const p of pads) if (p && p.connected) return Array.from(p.buttons, (b) => b.pressed || b.value > 0.5);
    return [];
  }
  function stopCapture(): void { capturing = null; setPadInputSuppressed(false); paintRemap(); }
  function captureLoop(): void {
    if (!capturing || !remapPanel.isConnected) { stopCapture(); return; }
    const pads = navigator.getGamepads ? navigator.getGamepads() : [];
    let pad: Gamepad | null = null;
    for (const p of pads) if (p && p.connected) { pad = p; break; }
    if (pad) {
      for (let i = 0; i < pad.buttons.length; i++) {
        const down = pad.buttons[i].pressed || pad.buttons[i].value > 0.5;
        if (down && !captureBaseline[i]) { setPadBinding(capturing, [i]); stopCapture(); return; }
      }
    }
    requestAnimationFrame(captureLoop);
  }
  for (const a of PAD_ACTIONS) {
    const row = el('div', { parent: remapPanel });
    row.style.cssText = 'display:grid;grid-template-columns:120px 1fr;gap:12px;align-items:center;';
    el('label', { parent: row, text: ACTION_LABELS[a] })
      .style.cssText = 'font-size:0.82rem;color:rgba(180,140,255,0.95);letter-spacing:0.16em;';
    const btn = el('button', { parent: row, text: bindingLabel(a) }) as HTMLButtonElement;
    btn.style.cssText = "font-family:'VT323',ui-monospace,monospace;font-size:0.95rem;padding:6px 12px;letter-spacing:0.1em;background:rgba(15,8,32,0.5);color:rgba(220,210,255,0.9);border:2px solid rgba(180,140,255,0.4);border-radius:6px;cursor:pointer;";
    btn.addEventListener('click', () => {
      if (capturing) stopCapture();
      capturing = a;
      captureBaseline = padSnapshot();
      setPadInputSuppressed(true);
      paintRemap();
      requestAnimationFrame(captureLoop);
    });
    remapBtns.set(a, btn);
  }
  const resetBtn = el('button', { parent: remapPanel, text: 'RESET TO DEFAULTS' });
  resetBtn.style.cssText = "align-self:center;margin-top:4px;font-family:'VT323',ui-monospace,monospace;font-size:0.9rem;padding:6px 16px;letter-spacing:0.12em;background:transparent;color:rgba(255,120,120,0.9);border:2px solid rgba(255,120,120,0.5);border-radius:6px;cursor:pointer;";
  resetBtn.addEventListener('click', () => { resetPadBindings(); stopCapture(); });
  const remapNote = el('p', { parent: overlay, text: 'Rotate / aim stay on the sticks (set by the GAMEPAD mode above).' });
  remapNote.style.cssText = 'font-size:0.72rem;color:rgba(180,140,255,0.5);letter-spacing:0.04em;margin:2px 0 0;text-align:center;';

  // Live controller diagnostic — pad id, mapping (standard vs non-standard), axes,
  // and which physical button each index is. The fix-it tool for a booth pad whose
  // buttons land in the wrong place. Ⓑ / BACK returns here to Settings.
  const testBtn = el('button', { className: 'menu-btn secondary', parent: overlay, text: '🎮 CONTROLLER TEST' });
  testBtn.style.cssText += 'margin-top:8px;';
  testBtn.addEventListener('click', () => { stopCapture(); renderGamepadTestPage(() => renderSettings(onBack)); });

  // Touch input mode — only meaningful on touch devices but harmless to show
  // on desktop (it just doesn't apply until a touch event reveals controls).
  const touchHeading = el('p', { parent: overlay, text: 'TOUCH INPUT' });
  touchHeading.style.cssText = 'font-size:0.78rem;letter-spacing:0.4em;color:rgba(180,140,255,0.85);margin:6px 0 -10px;';

  const touchPanel = el('div', { parent: overlay });
  touchPanel.style.cssText = 'display:flex;gap:8px;align-items:center;justify-content:center;';
  const inputOpts: ReadonlyArray<{ value: TouchInputMode; label: string; hint: string }> = [
    { value: 'buttons',  label: 'BUTTONS',  hint: 'Authentic 1979 cabinet — discrete d-pad + fire' },
    { value: 'joystick', label: 'JOYSTICK', hint: 'Point + go — stick angle = heading, push to thrust, tap to fire' },
  ];
  const inputHint = el('p', { parent: overlay });
  inputHint.style.cssText = 'font-size:0.75rem;color:rgba(180,140,255,0.65);letter-spacing:0.04em;margin:0;min-height:1em;text-align:center;';

  const inputBtns = new Map<TouchInputMode, HTMLButtonElement>();
  function paintInput(): void {
    const cur = getTouchMode();
    for (const [v, btn] of inputBtns) {
      const on = v === cur;
      btn.style.cssText = [
        'background:' + (on ? 'rgba(91,157,255,0.22)' : 'transparent'),
        'border:2px solid ' + (on ? '#5b9dff' : 'rgba(180,140,255,0.4)'),
        'color:' + (on ? '#5b9dff' : 'rgba(220,210,255,0.85)'),
        "font-family:'VT323',ui-monospace,monospace",
        'font-size:1rem', 'padding:6px 18px', 'letter-spacing:0.18em',
        'cursor:pointer', 'border-radius:6px', 'min-width:104px',
        on ? 'box-shadow:0 0 12px rgba(91,157,255,0.35);text-shadow:0 0 6px rgba(91,157,255,0.6)' : '',
      ].filter(Boolean).join(';');
    }
    inputHint.textContent = inputOpts.find(o => o.value === cur)?.hint ?? '';
  }
  for (const opt of inputOpts) {
    const btn = el('button', { parent: touchPanel, text: opt.label });
    inputBtns.set(opt.value, btn);
    btn.addEventListener('click', () => { setTouchMode(opt.value); paintInput(); });
  }
  paintInput();

  // Display mode — retro pixelated vs smooth uncapped modern
  const displayHeading = el('p', { parent: overlay, text: 'DISPLAY' });
  displayHeading.style.cssText = 'font-size:0.78rem;letter-spacing:0.4em;color:rgba(180,140,255,0.85);margin:6px 0 -10px;';

  const displayPanel = el('div', { parent: overlay });
  displayPanel.style.cssText = 'display:flex;gap:8px;align-items:center;justify-content:center;';
  const displayOpts: ReadonlyArray<{ value: DisplayMode; label: string; hint: string }> = [
    { value: 'retro',  label: 'RETRO',  hint: 'Pixelated, hard-edged — faithful to the arcade-cabinet look' },
    { value: 'modern', label: 'MODERN', hint: 'Smooth scaling — fills viewport, supersampled, sharper backgrounds' },
  ];
  const displayHint = el('p', { parent: overlay });
  displayHint.style.cssText = 'font-size:0.75rem;color:rgba(180,140,255,0.65);letter-spacing:0.04em;margin:0;min-height:1em;text-align:center;';
  const displayBtns = new Map<DisplayMode, HTMLButtonElement>();
  function paintDisplay(): void {
    const cur = getDisplayMode();
    for (const [v, btn] of displayBtns) {
      const on = v === cur;
      btn.style.cssText = [
        'background:' + (on ? 'rgba(91,157,255,0.22)' : 'transparent'),
        'border:2px solid ' + (on ? '#5b9dff' : 'rgba(180,140,255,0.4)'),
        'color:' + (on ? '#5b9dff' : 'rgba(220,210,255,0.85)'),
        "font-family:'VT323',ui-monospace,monospace",
        'font-size:1rem', 'padding:6px 18px', 'letter-spacing:0.18em',
        'cursor:pointer', 'border-radius:6px', 'min-width:104px',
        on ? 'box-shadow:0 0 12px rgba(91,157,255,0.35);text-shadow:0 0 6px rgba(91,157,255,0.6)' : '',
      ].filter(Boolean).join(';');
    }
    displayHint.textContent = displayOpts.find(o => o.value === cur)?.hint ?? '';
  }
  for (const opt of displayOpts) {
    const btn = el('button', { parent: displayPanel, text: opt.label });
    displayBtns.set(opt.value, btn);
    btn.addEventListener('click', () => {
      setDisplayMode(opt.value);
      paintDisplay();
      // Re-fit the canvas so the new mode takes effect immediately rather
      // than waiting for the next resize/orientation event.
      (window as unknown as { __pallasiteFit?: () => void }).__pallasiteFit?.();
    });
  }
  paintDisplay();

  // ── VISUAL STYLE ───────────────────────────────────────────────────────
  // Per-category tier picker. Top row is a quick preset (sets all four
  // categories at once); rows below let advanced players mix tiers.
  // The 'mesh' (3D) tier is shown disabled with "SOON" until the WebGL
  // renderer ships. Toggling any button repaints the canvas on the next
  // frame — no reload needed; render.ts reads getVisualStyle() per draw.
  const styleHeading = el('p', { parent: overlay, text: 'VISUAL STYLE' });
  styleHeading.style.cssText = 'font-size:0.78rem;letter-spacing:0.4em;color:rgba(180,140,255,0.85);margin:6px 0 -10px;';

  const stylePanel = el('div', { parent: overlay });
  stylePanel.style.cssText = 'display:flex;flex-direction:column;gap:8px;align-items:stretch;min-width:0;width:100%;max-width:480px;';

  const styleHint = el('p', { parent: overlay, text: 'AUTO adapts as you play. LOW uses shaded 2D for older devices.' });
  styleHint.style.cssText = 'font-size:0.7rem;color:rgba(180,140,255,0.55);letter-spacing:0.06em;margin:0;text-align:center;';

  const styleBtnCss = (on: boolean, locked: boolean): string => [
    'background:' + (on ? 'rgba(91,157,255,0.22)' : 'transparent'),
    'border:2px solid ' + (on ? '#5b9dff' : (locked ? 'rgba(180,140,255,0.22)' : 'rgba(180,140,255,0.4)')),
    'color:' + (on ? '#5b9dff' : (locked ? 'rgba(220,210,255,0.45)' : 'rgba(220,210,255,0.85)')),
    "font-family:'VT323',ui-monospace,monospace",
    'font-size:0.9rem',
    'padding:5px 12px',
    'letter-spacing:0.14em',
    'cursor:' + (locked ? 'not-allowed' : 'pointer'),
    'border-radius:5px',
    'flex:1',
    on ? 'box-shadow:0 0 10px rgba(91,157,255,0.3);text-shadow:0 0 6px rgba(91,157,255,0.6)' : '',
  ].filter(Boolean).join(';');

  const tierLabels: Record<VisualTier, string> = {
    vector: 'VECTOR',
    shaded: 'SHADED',
    mesh: '3D MESH',
  };

  // Quick-preset row — single click sets all four categories to the
  // same tier. Highlighted only when every category currently matches.
  const presetRow = el('div', { className: 'visual-style-row', parent: stylePanel });
  presetRow.style.cssText = 'display:flex;gap:6px;align-items:center;';
  const presetLabel = el('span', { parent: presetRow, text: 'ALL' });
  presetLabel.style.cssText = 'font-size:0.75rem;letter-spacing:0.18em;color:rgba(180,140,255,0.65);min-width:80px;';
  const presetBtns: Partial<Record<VisualTier, HTMLButtonElement>> = {};
  for (const tier of ['vector', 'shaded', 'mesh'] as const) {
    const btn = el('button', { parent: presetRow, text: tierLabels[tier] }) as HTMLButtonElement;
    presetBtns[tier] = btn;
    btn.addEventListener('click', () => {
      setAllVisualStyles(tier);
      paintStyle();
    });
  }

  // Per-category rows
  const catBtns = new Map<VisualCategory, Partial<Record<VisualTier, HTMLButtonElement>>>();
  const catLabels: Record<VisualCategory, string> = {
    asteroid: 'Asteroids',
    ship: 'Ship',
    bullet: 'Bullets',
    particle: 'Particles',
  };
  for (const cat of VISUAL_CATEGORIES) {
    const row = el('div', { className: 'visual-style-row', parent: stylePanel });
    row.style.cssText = 'display:flex;gap:6px;align-items:center;';
    const lbl = el('span', { parent: row, text: catLabels[cat] });
    lbl.style.cssText = 'font-size:0.75rem;letter-spacing:0.12em;color:rgba(220,210,255,0.85);min-width:80px;';
    const map: Partial<Record<VisualTier, HTMLButtonElement>> = {};
    catBtns.set(cat, map);
    for (const tier of ['vector', 'shaded', 'mesh'] as const) {
      const btn = el('button', { parent: row, text: tierLabels[tier] }) as HTMLButtonElement;
      map[tier] = btn;
      btn.addEventListener('click', () => {
        setVisualStyle(cat, tier);
        paintStyle();
      });
    }
  }

  function paintStyle(): void {
    for (const tier of ['vector', 'shaded', 'mesh'] as const) {
      const btn = presetBtns[tier];
      if (!btn) continue;
      btn.style.cssText = styleBtnCss(visualStyleIsUniform(tier), false);
    }
    for (const cat of VISUAL_CATEGORIES) {
      const map = catBtns.get(cat);
      if (!map) continue;
      const current = getVisualStyleRaw(cat);
      for (const tier of ['vector', 'shaded', 'mesh'] as const) {
        const btn = map[tier];
        if (!btn) continue;
        btn.style.cssText = styleBtnCss(current === tier, false);
      }
    }
  }
  paintStyle();

  const qualityRow = el('div', { parent: overlay });
  qualityRow.style.cssText = 'display:flex;flex-wrap:wrap;gap:8px;align-items:center;width:100%;max-width:480px;';
  const qualityLabel = el('label', { parent: qualityRow, text: 'PERFORMANCE' });
  qualityLabel.setAttribute('for', 'graphics-quality');
  qualityLabel.style.cssText = 'font-size:0.8rem;letter-spacing:0.14em;';
  const qualitySelect = el('select', { parent: qualityRow }) as HTMLSelectElement;
  qualitySelect.id = 'graphics-quality';
  qualitySelect.style.cssText = 'min-height:44px;background:#101722;color:#e0ece8;border:1px solid #668d88;padding:8px;font:inherit;max-width:100%;';
  for (const [value, label] of [['auto', 'AUTO · adapts as you play'], ['high', 'ULTRA · live 3D and full effects'], ['balanced', 'BALANCED · baked 3D'], ['low', 'LOW · light baked 3D']]) {
    const option = el('option', { parent: qualitySelect, text: label }) as HTMLOptionElement;
    option.value = value;
  }
  qualitySelect.value = getQualityPreference();
  qualitySelect.addEventListener('change', () => setQualityPreference(qualitySelect.value as ReturnType<typeof getQualityPreference>));

  // ── PRESENTATION ───────────────────────────────────────────────────────
  // Post-process look (CRT etc.), a separate axis from the fidelity tiers
  // above. Applies to the game canvas via the main loop's postfx pass.
  const themeHeading = el('p', { parent: overlay, text: 'PRESENTATION' });
  themeHeading.style.cssText = 'font-size:0.78rem;letter-spacing:0.4em;color:rgba(180,140,255,0.85);margin:6px 0 -10px;';
  const themeRow = el('div', { parent: overlay });
  themeRow.style.cssText = 'display:flex;flex-wrap:wrap;gap:6px;align-items:center;min-width:0;width:100%;max-width:480px;';
  const themeLabel = el('span', { parent: themeRow, text: 'LOOK' });
  themeLabel.style.cssText = 'font-size:0.75rem;letter-spacing:0.18em;color:rgba(180,140,255,0.65);min-width:80px;';
  const themeBtns = new Map<ThemeId, HTMLButtonElement>();
  for (const t of THEMES) {
    const btn = el('button', { parent: themeRow, text: t.label }) as HTMLButtonElement;
    themeBtns.set(t.id, btn);
    btn.addEventListener('click', () => {
      setTheme(t.id);
      paintTheme();
    });
  }
  // Compositor brightness multiplier. This is separate from the post-FX
  // theme so STANDARD can be tuned without paying a canvas post-process pass.
  const brightnessRow = el('div', { parent: overlay });
  brightnessRow.style.cssText = 'display:grid;grid-template-columns:minmax(90px,140px) minmax(0,1fr) 48px;gap:14px;align-items:center;min-width:0;width:100%;max-width:480px;';
  el('label', { parent: brightnessRow, text: 'BRIGHTNESS' })
    .style.cssText = 'font-size:0.85rem;color:rgba(180,140,255,0.95);letter-spacing:0.18em;';
  const brightnessSlider = el('input', { parent: brightnessRow });
  brightnessSlider.type = 'range';
  brightnessSlider.min = String(BRIGHTNESS.min);
  brightnessSlider.max = String(BRIGHTNESS.max);
  brightnessSlider.step = String(BRIGHTNESS.step);
  brightnessSlider.value = String(getBrightness());
  brightnessSlider.style.cssText = 'width:100%;accent-color:#ffd84a;cursor:pointer;';
  const brightnessVal = el('span', { parent: brightnessRow, text: `${Math.round(getBrightness() * 100)}%` });
  brightnessVal.style.cssText = 'font-size:0.85rem;color:#ffd84a;text-align:right;font-variant-numeric:tabular-nums;';
  brightnessSlider.addEventListener('input', () => {
    const value = Number(brightnessSlider.value);
    setBrightness(value);
    brightnessVal.textContent = `${Math.round(getBrightness() * 100)}%`;
  });

  // ASCII resolution — the character grid is the dial: finer grids trade
  // frame budget for detail. Only shown while ASCII is the active look,
  // since it does nothing for the other themes.
  const asciiRow = el('div', { parent: overlay });
  asciiRow.style.cssText = 'display:grid;grid-template-columns:minmax(90px,140px) minmax(0,1fr) 44px;gap:14px;align-items:center;min-width:0;width:100%;max-width:480px;';
  el('label', { parent: asciiRow, text: 'RESOLUTION' })
    .style.cssText = 'font-size:0.85rem;color:rgba(180,140,255,0.95);letter-spacing:0.18em;';
  const asciiSlider = el('input', { parent: asciiRow });
  asciiSlider.type = 'range';
  asciiSlider.min = String(ASCII_COLS.min);
  asciiSlider.max = String(ASCII_COLS.max);
  asciiSlider.step = String(ASCII_COLS.step);
  asciiSlider.value = String(getAsciiCols());
  asciiSlider.style.cssText = 'width:100%;accent-color:#b48cff;cursor:pointer;';
  const asciiVal = el('span', { parent: asciiRow, text: String(getAsciiCols()) });
  asciiVal.style.cssText = 'font-size:0.85rem;color:#ffd84a;text-align:right;font-variant-numeric:tabular-nums;';
  asciiSlider.addEventListener('input', () => {
    const n = Number(asciiSlider.value);
    setAsciiCols(n);
    asciiVal.textContent = String(n);
  });

  // Bit-depth controls — a depth slider (1 / 2 / 4 / 8 / 16 / 32-bit) and
  // a greyscale/colour toggle. Shown only while BIT DEPTH is the look.
  const bitRow = el('div', { parent: overlay });
  bitRow.style.cssText = 'display:grid;grid-template-columns:minmax(90px,140px) minmax(0,1fr) 60px;gap:14px;align-items:center;min-width:0;width:100%;max-width:480px;';
  el('label', { parent: bitRow, text: 'DEPTH' })
    .style.cssText = 'font-size:0.85rem;color:rgba(180,140,255,0.95);letter-spacing:0.18em;';
  const bitSlider = el('input', { parent: bitRow });
  bitSlider.type = 'range';
  bitSlider.min = '0';
  bitSlider.max = String(BIT_DEPTH.stops.length - 1);
  bitSlider.step = '1';
  bitSlider.value = String(Math.max(0, (BIT_DEPTH.stops as readonly number[]).indexOf(getBitDepth())));
  bitSlider.style.cssText = 'width:100%;accent-color:#b48cff;cursor:pointer;';
  const bitVal = el('span', { parent: bitRow, text: `${getBitDepth()}-BIT` });
  bitVal.style.cssText = 'font-size:0.85rem;color:#ffd84a;text-align:right;font-variant-numeric:tabular-nums;';
  bitSlider.addEventListener('input', () => {
    const depth = BIT_DEPTH.stops[Number(bitSlider.value)];
    setBitDepth(depth);
    bitVal.textContent = `${depth}-BIT`;
  });

  const bitColourRow = el('div', { parent: overlay });
  bitColourRow.style.cssText = 'display:grid;grid-template-columns:minmax(90px,140px) minmax(0,1fr);gap:14px;align-items:center;min-width:0;width:100%;max-width:480px;';
  el('label', { parent: bitColourRow, text: 'PALETTE' })
    .style.cssText = 'font-size:0.85rem;color:rgba(180,140,255,0.95);letter-spacing:0.18em;';
  const bitColourWrap = el('div', { parent: bitColourRow });
  bitColourWrap.style.cssText = 'display:flex;gap:6px;justify-content:flex-end;';
  const bitColourBtns = new Map<boolean, HTMLButtonElement>();
  const paintBitColour = (): void => {
    const cur = getBitColour();
    for (const [v, btn] of bitColourBtns) {
      const on = v === cur;
      btn.style.background = on ? 'rgba(255,216,74,0.18)' : 'rgba(20,12,36,0.6)';
      btn.style.color = on ? '#ffd84a' : 'rgba(220,210,255,0.7)';
      btn.style.borderColor = on ? '#ffd84a' : 'rgba(180,140,255,0.45)';
    }
  };
  for (const o of [{ value: false, label: 'B&W' }, { value: true, label: 'COLOUR' }] as const) {
    const btn = el('button', { className: 'menu-btn secondary', parent: bitColourWrap, text: o.label }) as HTMLButtonElement;
    btn.style.cssText += 'font-size:0.72rem;padding:6px 14px;letter-spacing:0.14em;';
    btn.addEventListener('click', () => {
      setBitColour(o.value);
      paintBitColour();
    });
    bitColourBtns.set(o.value, btn);
  }
  paintBitColour();

  function paintTheme(): void {
    const active = getTheme();
    for (const [id, btn] of themeBtns) btn.style.cssText = styleBtnCss(id === active, false);
    asciiRow.style.display = active === 'ascii' ? 'grid' : 'none';
    bitRow.style.display = active === 'bitdepth' ? 'grid' : 'none';
    bitColourRow.style.display = active === 'bitdepth' ? 'grid' : 'none';
  }
  paintTheme();

  // ── PARALLAX ───────────────────────────────────────────────────────────
  // Three-state toggle: OFF (current behaviour, no depth bands),
  // SUBTLE (default — a handful of decorative rocks drift in the
  // background/foreground), DRAMATIC (richer depth, more decorative
  // rocks per wave). Collision behaviour on the gameplay plane is
  // independent of this setting.
  const parallaxHeading = el('p', { parent: overlay, text: 'PARALLAX DEPTH' });
  parallaxHeading.style.cssText = 'font-size:0.78rem;letter-spacing:0.4em;color:rgba(180,140,255,0.85);margin:6px 0 -10px;';
  const parallaxRow = el('div', { parent: overlay });
  parallaxRow.style.cssText = 'display:grid;grid-template-columns:minmax(90px,140px) minmax(0,1fr);gap:14px;align-items:center;min-width:0;width:100%;max-width:480px;';
  el('label', { parent: parallaxRow, text: 'DEPTH BANDS' })
    .style.cssText = 'font-size:0.85rem;color:rgba(180,140,255,0.95);letter-spacing:0.18em;';
  const parallaxBtnWrap = el('div', { parent: parallaxRow });
  parallaxBtnWrap.style.cssText = 'display:flex;gap:6px;justify-content:flex-end;';
  const parallaxOpts: ReadonlyArray<{ value: ParallaxTier; label: string }> = [
    { value: 'off',      label: 'OFF' },
    { value: 'subtle',   label: 'SUBTLE' },
    { value: 'dramatic', label: 'DRAMATIC' },
  ];
  const parallaxBtns = new Map<ParallaxTier, HTMLButtonElement>();
  const paintParallax = (): void => {
    const cur = getParallaxTier();
    for (const [tier, btn] of parallaxBtns) {
      const on = tier === cur;
      btn.style.background = on ? 'rgba(255,216,74,0.18)' : 'rgba(20,12,36,0.6)';
      btn.style.color = on ? '#ffd84a' : 'rgba(220,210,255,0.7)';
      btn.style.borderColor = on ? '#ffd84a' : 'rgba(180,140,255,0.45)';
    }
  };
  for (const o of parallaxOpts) {
    const btn = el('button', { className: 'menu-btn secondary', parent: parallaxBtnWrap, text: o.label }) as HTMLButtonElement;
    btn.style.cssText += 'font-size:0.72rem;padding:6px 14px;letter-spacing:0.14em;';
    btn.addEventListener('click', () => {
      setParallaxTier(o.value);
      paintParallax();
    });
    parallaxBtns.set(o.value, btn);
  }
  paintParallax();

  // ── ASTEROID BOUNCE ──────────────────────────────────────────────────────
  // Gameplay setting (not visual): whether gameplay-plane asteroids bounce
  // off each other. AUTO derives from difficulty — off on easy, on for
  // normal / hard. ON / OFF override. Hidden in 600bn (always on there).
  if (getFlavour() !== '600bn') {
    const bounceHeading = el('p', { parent: overlay, text: 'ASTEROID BOUNCE' });
    bounceHeading.style.cssText = 'font-size:0.78rem;letter-spacing:0.4em;color:rgba(180,140,255,0.85);margin:6px 0 -10px;';
    const bounceRow = el('div', { parent: overlay });
    bounceRow.style.cssText = 'display:grid;grid-template-columns:minmax(90px,140px) minmax(0,1fr);gap:14px;align-items:center;min-width:0;width:100%;max-width:480px;';
    el('label', { parent: bounceRow, text: 'ROCKS COLLIDE' })
      .style.cssText = 'font-size:0.85rem;color:rgba(180,140,255,0.95);letter-spacing:0.18em;';
    const bounceBtnWrap = el('div', { parent: bounceRow });
    bounceBtnWrap.style.cssText = 'display:flex;gap:6px;justify-content:flex-end;';
    const bounceOpts: ReadonlyArray<{ value: BounceMode; label: string }> = [
      { value: 'auto', label: 'AUTO' },
      { value: 'on',   label: 'ON' },
      { value: 'off',  label: 'OFF' },
    ];
    const bounceBtns = new Map<BounceMode, HTMLButtonElement>();
    const paintBounce = (): void => {
      const cur = getBounceMode();
      for (const [mode, btn] of bounceBtns) {
        const on = mode === cur;
        btn.style.background = on ? 'rgba(255,216,74,0.18)' : 'rgba(20,12,36,0.6)';
        btn.style.color = on ? '#ffd84a' : 'rgba(220,210,255,0.7)';
        btn.style.borderColor = on ? '#ffd84a' : 'rgba(180,140,255,0.45)';
      }
    };
    for (const o of bounceOpts) {
      const btn = el('button', { className: 'menu-btn secondary', parent: bounceBtnWrap, text: o.label }) as HTMLButtonElement;
      btn.style.cssText += 'font-size:0.72rem;padding:6px 14px;letter-spacing:0.14em;';
      btn.addEventListener('click', () => {
        setBounceMode(o.value);
        paintBounce();
      });
      bounceBtns.set(o.value, btn);
    }
    paintBounce();
    const bounceHint = el('p', { parent: overlay, text: 'AUTO: off on easy · on for normal / hard' });
    bounceHint.style.cssText = 'font-size:0.62rem;letter-spacing:0.1em;color:rgba(180,140,255,0.5);margin:-6px 0 0;';
  }

  // ── PORTRAIT RADAR ───────────────────────────────────────────────────────
  // Display setting: the Defender-style radar drawn by the portrait follow
  // camera. On by default. Three rows here: MINIMAP toggle (visible at all),
  // LANDSCAPE toggle (off by default — groundwork for the future landscape
  // bonus wave) and TILT intensity (off / subtle / cabinet for the
  // trapezoid scanline warp).
  {
    const radarHeading = el('p', { parent: overlay, text: 'RADAR' });
    radarHeading.style.cssText = 'font-size:0.78rem;letter-spacing:0.4em;color:rgba(180,140,255,0.85);margin:6px 0 -10px;';

    // Generic two-column row helper, keyed labels share visual style.
    const makeRow = (label: string): HTMLElement => {
      const row = el('div', { parent: overlay });
      row.style.cssText = 'display:grid;grid-template-columns:minmax(90px,140px) minmax(0,1fr);gap:14px;align-items:center;min-width:0;width:100%;max-width:480px;';
      el('label', { parent: row, text: label }).style.cssText = 'font-size:0.85rem;color:rgba(180,140,255,0.95);letter-spacing:0.18em;';
      const btnWrap = el('div', { parent: row });
      btnWrap.style.cssText = 'display:flex;gap:6px;justify-content:flex-end;';
      return btnWrap;
    };
    const styleSegBtn = (btn: HTMLButtonElement, active: boolean): void => {
      btn.style.background = active ? 'rgba(255,216,74,0.18)' : 'rgba(20,12,36,0.6)';
      btn.style.color = active ? '#ffd84a' : 'rgba(220,210,255,0.7)';
      btn.style.borderColor = active ? '#ffd84a' : 'rgba(180,140,255,0.45)';
    };

    // MINIMAP on/off
    {
      const wrap = makeRow('MINIMAP');
      const opts: ReadonlyArray<{ value: boolean; label: string }> = [
        { value: true,  label: 'ON' },
        { value: false, label: 'OFF' },
      ];
      const btns = new Map<boolean, HTMLButtonElement>();
      const paint = (): void => { for (const [v, b] of btns) styleSegBtn(b, v === getRadarVisible()); };
      for (const o of opts) {
        const btn = el('button', { className: 'menu-btn secondary', parent: wrap, text: o.label }) as HTMLButtonElement;
        btn.style.cssText += 'font-size:0.72rem;padding:6px 14px;letter-spacing:0.14em;';
        btn.addEventListener('click', () => { setRadarVisible(o.value); paint(); });
        btns.set(o.value, btn);
      }
      paint();
    }

    // TILT off/subtle/cabinet
    {
      const wrap = makeRow('TILT');
      const opts: ReadonlyArray<{ value: RadarTilt; label: string }> = [
        { value: 'off',     label: 'FLAT'    },
        { value: 'subtle',  label: 'SUBTLE'  },
        { value: 'cabinet', label: 'CABINET' },
      ];
      const btns = new Map<RadarTilt, HTMLButtonElement>();
      const paint = (): void => { for (const [v, b] of btns) styleSegBtn(b, v === getRadarTilt()); };
      for (const o of opts) {
        const btn = el('button', { className: 'menu-btn secondary', parent: wrap, text: o.label }) as HTMLButtonElement;
        btn.style.cssText += 'font-size:0.72rem;padding:6px 14px;letter-spacing:0.14em;';
        btn.addEventListener('click', () => { setRadarTilt(o.value); paint(); });
        btns.set(o.value, btn);
      }
      paint();
    }

    // LANDSCAPE on/off (foundation for future landscape bonus wave)
    {
      const wrap = makeRow('LANDSCAPE');
      const opts: ReadonlyArray<{ value: boolean; label: string }> = [
        { value: true,  label: 'ON' },
        { value: false, label: 'OFF' },
      ];
      const btns = new Map<boolean, HTMLButtonElement>();
      const paint = (): void => { for (const [v, b] of btns) styleSegBtn(b, v === getRadarLandscape()); };
      for (const o of opts) {
        const btn = el('button', { className: 'menu-btn secondary', parent: wrap, text: o.label }) as HTMLButtonElement;
        btn.style.cssText += 'font-size:0.72rem;padding:6px 14px;letter-spacing:0.14em;';
        btn.addEventListener('click', () => { setRadarLandscape(o.value); paint(); });
        btns.set(o.value, btn);
      }
      paint();
    }

    const radarHint = el('p', { parent: overlay, text: 'Whole-world map. Landscape on prepares for a future wide-arena bonus wave.' });
    radarHint.style.cssText = 'font-size:0.62rem;letter-spacing:0.1em;color:rgba(180,140,255,0.5);margin:-6px 0 0;';
  }

  // ── SHOT KNOCKBACK ───────────────────────────────────────────────────────
  // Gameplay setting (not visual): whether a bullet's momentum is blended
  // into the rock it hits and into any fragments. AUTO derives from
  // difficulty — off on easy, on for normal / hard. ON / OFF override.
  // Hidden in 600bn (always on there).
  if (getFlavour() !== '600bn') {
    const knockHeading = el('p', { parent: overlay, text: 'SHOT KNOCKBACK' });
    knockHeading.style.cssText = 'font-size:0.78rem;letter-spacing:0.4em;color:rgba(180,140,255,0.85);margin:6px 0 -10px;';
    const knockRow = el('div', { parent: overlay });
    knockRow.style.cssText = 'display:grid;grid-template-columns:minmax(90px,140px) minmax(0,1fr);gap:14px;align-items:center;min-width:0;width:100%;max-width:480px;';
    el('label', { parent: knockRow, text: 'SHOTS NUDGE' })
      .style.cssText = 'font-size:0.85rem;color:rgba(180,140,255,0.95);letter-spacing:0.18em;';
    const knockBtnWrap = el('div', { parent: knockRow });
    knockBtnWrap.style.cssText = 'display:flex;gap:6px;justify-content:flex-end;';
    const knockOpts: ReadonlyArray<{ value: KnockbackMode; label: string }> = [
      { value: 'auto', label: 'AUTO' },
      { value: 'on',   label: 'ON' },
      { value: 'off',  label: 'OFF' },
    ];
    const knockBtns = new Map<KnockbackMode, HTMLButtonElement>();
    const paintKnock = (): void => {
      const cur = getKnockbackMode();
      for (const [mode, btn] of knockBtns) {
        const on = mode === cur;
        btn.style.background = on ? 'rgba(255,216,74,0.18)' : 'rgba(20,12,36,0.6)';
        btn.style.color = on ? '#ffd84a' : 'rgba(220,210,255,0.7)';
        btn.style.borderColor = on ? '#ffd84a' : 'rgba(180,140,255,0.45)';
      }
    };
    for (const o of knockOpts) {
      const btn = el('button', { className: 'menu-btn secondary', parent: knockBtnWrap, text: o.label }) as HTMLButtonElement;
      btn.style.cssText += 'font-size:0.72rem;padding:6px 14px;letter-spacing:0.14em;';
      btn.addEventListener('click', () => {
        setKnockbackMode(o.value);
        paintKnock();
      });
      knockBtns.set(o.value, btn);
    }
    paintKnock();
    const knockHint = el('p', { parent: overlay, text: 'AUTO: off on easy · on for normal / hard' });
    knockHint.style.cssText = 'font-size:0.62rem;letter-spacing:0.1em;color:rgba(180,140,255,0.5);margin:-6px 0 0;';
  }

  // ── ACCESSIBILITY ───────────────────────────────────────────────────────
  // Three independent axes:
  //   reduced motion: kills shake / chromatic split / future bloom pulses.
  //     'AUTO' follows the OS prefers-reduced-motion media query.
  //   high-contrast palette: shifts the four asteroid hues into a
  //     deuteranopia/protanopia-safer set (red, blue, cyan, yellow).
  //   haptics: toggle whether navigator.vibrate fires on impact events.
  //     Hidden entirely on platforms without vibrate support (iOS Safari).
  const a11yHeading = el('p', { parent: overlay, text: 'ACCESSIBILITY' });
  a11yHeading.style.cssText = 'font-size:0.78rem;letter-spacing:0.4em;color:rgba(180,140,255,0.85);margin:6px 0 -10px;';

  const a11yPanel = el('div', { parent: overlay });
  a11yPanel.style.cssText = 'display:flex;flex-direction:column;gap:14px;align-items:stretch;min-width:0;width:100%;max-width:480px;';

  // Reduced-motion tri-state
  const motionRow = el('div', { parent: a11yPanel });
  motionRow.style.cssText = 'display:grid;grid-template-columns:minmax(90px,140px) minmax(0,1fr);gap:14px;align-items:center;';
  const motionLab = el('label', { parent: motionRow, text: 'REDUCED MOTION' });
  motionLab.style.cssText = 'font-size:0.85rem;color:rgba(180,140,255,0.95);letter-spacing:0.18em;';
  const motionBtnWrap = el('div', { parent: motionRow });
  motionBtnWrap.style.cssText = 'display:flex;gap:6px;justify-content:flex-end;';
  const motionOpts: ReadonlyArray<{ value: ReducedMotionPref; label: string }> = [
    { value: 'auto', label: 'AUTO' },
    { value: 'on',   label: 'ON' },
    { value: 'off',  label: 'OFF' },
  ];
  const motionBtns = new Map<ReducedMotionPref, HTMLButtonElement>();
  function paintMotion(): void {
    const cur = getReducedMotionPref();
    for (const [v, btn] of motionBtns) {
      const on = v === cur;
      btn.style.cssText = [
        'background:' + (on ? 'rgba(91,157,255,0.22)' : 'transparent'),
        'border:2px solid ' + (on ? '#5b9dff' : 'rgba(180,140,255,0.4)'),
        'color:' + (on ? '#5b9dff' : 'rgba(220,210,255,0.85)'),
        "font-family:'VT323',ui-monospace,monospace",
        'font-size:0.9rem', 'padding:5px 12px', 'letter-spacing:0.16em',
        'cursor:pointer', 'border-radius:6px', 'min-width:62px',
      ].join(';');
    }
  }
  for (const opt of motionOpts) {
    const btn = el('button', { parent: motionBtnWrap, text: opt.label });
    motionBtns.set(opt.value, btn);
    btn.addEventListener('click', () => { setReducedMotionPref(opt.value); paintMotion(); });
  }
  paintMotion();

  // High-contrast palette toggle
  const paletteRow = el('div', { parent: a11yPanel });
  paletteRow.style.cssText = 'display:grid;grid-template-columns:minmax(90px,140px) minmax(0,1fr);gap:14px;align-items:center;';
  const paletteLab = el('label', { parent: paletteRow, text: 'PALETTE' });
  paletteLab.style.cssText = 'font-size:0.85rem;color:rgba(180,140,255,0.95);letter-spacing:0.18em;';
  const paletteBtnWrap = el('div', { parent: paletteRow });
  paletteBtnWrap.style.cssText = 'display:flex;gap:6px;justify-content:flex-end;';
  const paletteOpts: ReadonlyArray<{ value: ColourPalette; label: string }> = [
    { value: 'default',        label: 'DEFAULT' },
    { value: 'high-contrast',  label: 'HI-CONTRAST' },
  ];
  const paletteBtns = new Map<ColourPalette, HTMLButtonElement>();
  function paintPalette(): void {
    const cur = getPalette();
    for (const [v, btn] of paletteBtns) {
      const on = v === cur;
      btn.style.cssText = [
        'background:' + (on ? 'rgba(91,157,255,0.22)' : 'transparent'),
        'border:2px solid ' + (on ? '#5b9dff' : 'rgba(180,140,255,0.4)'),
        'color:' + (on ? '#5b9dff' : 'rgba(220,210,255,0.85)'),
        "font-family:'VT323',ui-monospace,monospace",
        'font-size:0.9rem', 'padding:5px 12px', 'letter-spacing:0.16em',
        'cursor:pointer', 'border-radius:6px', 'min-width:108px',
      ].join(';');
    }
  }
  for (const opt of paletteOpts) {
    const btn = el('button', { parent: paletteBtnWrap, text: opt.label });
    paletteBtns.set(opt.value, btn);
    btn.addEventListener('click', () => { setPalette(opt.value); paintPalette(); });
  }
  paintPalette();

  // Haptics toggle — only render on platforms where vibrate is available
  if (hapticsSupported()) {
    const hapticsRow = el('div', { parent: a11yPanel });
    hapticsRow.style.cssText = 'display:grid;grid-template-columns:minmax(90px,140px) minmax(0,1fr);gap:14px;align-items:center;';
    const hapticsLab = el('label', { parent: hapticsRow, text: 'HAPTICS' });
    hapticsLab.style.cssText = 'font-size:0.85rem;color:rgba(180,140,255,0.95);letter-spacing:0.18em;';
    const hapticsBtnWrap = el('div', { parent: hapticsRow });
    hapticsBtnWrap.style.cssText = 'display:flex;gap:6px;justify-content:flex-end;';
    const hapticsBtn = el('button', { parent: hapticsBtnWrap, text: getHapticsEnabled() ? 'ON' : 'OFF' });
    function paintHaptics(): void {
      const on = getHapticsEnabled();
      hapticsBtn.textContent = on ? 'ON' : 'OFF';
      hapticsBtn.style.cssText = [
        'background:' + (on ? 'rgba(91,157,255,0.22)' : 'transparent'),
        'border:2px solid ' + (on ? '#5b9dff' : 'rgba(180,140,255,0.4)'),
        'color:' + (on ? '#5b9dff' : 'rgba(220,210,255,0.85)'),
        "font-family:'VT323',ui-monospace,monospace",
        'font-size:0.9rem', 'padding:5px 12px', 'letter-spacing:0.16em',
        'cursor:pointer', 'border-radius:6px', 'min-width:62px',
      ].join(';');
    }
    paintHaptics();
    hapticsBtn.addEventListener('click', () => { setHapticsEnabled(!getHapticsEnabled()); paintHaptics(); });
  }


  // ── DATA ────────────────────────────────────────────────────────────────
  // Single destructive action — clearing the local top-10 list. Two-tap
  // confirm so a stray click doesn't blow it away. Auto-resets after 3s
  // if the second tap doesn't happen.
  const dataHeading = el('p', { parent: overlay, text: 'DATA' });
  dataHeading.style.cssText = 'font-size:0.78rem;letter-spacing:0.4em;color:rgba(180,140,255,0.85);margin:6px 0 -10px;';

  const dataPanel = el('div', { parent: overlay });
  dataPanel.style.cssText = 'display:flex;flex-direction:column;gap:6px;align-items:center;';

  const clearBtn = el('button', { parent: dataPanel, text: 'CLEAR LOCAL SCORES' }) as HTMLButtonElement;
  function paintClear(state: 'idle' | 'arming' | 'cleared'): void {
    const colour = state === 'cleared' ? '#58ff58' : '#ff5050';
    const fill = state === 'arming' ? 'rgba(255,80,80,0.18)'
      : state === 'cleared' ? 'rgba(88,255,88,0.12)'
      : 'transparent';
    clearBtn.style.cssText = [
      'background:' + fill,
      'border:2px solid ' + (state === 'idle' ? 'rgba(255,80,80,0.4)' : colour),
      'color:' + colour,
      "font-family:'VT323',ui-monospace,monospace",
      'font-size:0.95rem', 'padding:6px 18px', 'letter-spacing:0.18em',
      'cursor:' + (state === 'cleared' ? 'default' : 'pointer'),
      'border-radius:6px',
    ].join(';');
    clearBtn.textContent =
      state === 'idle' ? 'CLEAR LOCAL SCORES'
      : state === 'arming' ? 'TAP AGAIN TO CONFIRM'
      : 'CLEARED';
    clearBtn.disabled = state === 'cleared';
  }
  paintClear('idle');

  let arming = false;
  let armTimer: number | null = null;
  clearBtn.addEventListener('click', () => {
    if (!arming) {
      arming = true;
      paintClear('arming');
      armTimer = window.setTimeout(() => {
        arming = false;
        paintClear('idle');
      }, 3000);
      return;
    }
    if (armTimer !== null) { clearTimeout(armTimer); armTimer = null; }
    arming = false;
    clearLocalHighScores();
    clearLocalHighScores('coop-campaign');
    paintClear('cleared');
  });

  const dataNote = el('p', { parent: dataPanel, text: "Removes the local solo and co-op top-10 lists shown on the title screen. Doesn't touch your relays, profile cache, or any Nostr-published scores." });
  dataNote.style.cssText = 'font-size:0.7rem;color:rgba(180,140,255,0.6);letter-spacing:0.04em;margin:0;text-align:center;max-width:420px;';

  const row2 = el('div', { className: 'menu-row', parent: overlay });
  // Push the action row clear of the DISPLAY hint above so the buttons don't
  // visually clash with text. The .overlay flex gap is 28px; this adds more.
  row2.style.marginTop = '24px';
  // Open the Nostr relay editor without leaving settings
  const relayBtn = el('button', { className: 'menu-btn secondary', parent: row2, text: 'NOSTR RELAYS' });
  relayBtn.addEventListener('click', () => renderRelaySettings(() => renderSettings(onBack)));

  const back = el('button', { className: 'menu-btn', parent: row2, text: 'BACK' });
  back.addEventListener('click', onBack);

  // Bind musicRow for type checker satisfaction (keep handle in case we later want to react to external volume changes)
  void musicRow;
}

/**
 * Nostr relay editor — toggle list with add/remove. Default relays can be
 * toggled on/off but not deleted (only hidden from `active`); custom-added
 * ones can be removed entirely. Each row is a wss:// URL.
 */
export function renderRelaySettings(onBack: () => void): void {
  clearOverlay();
  const overlay = el('div', { className: 'overlay', parent: root });
  setupOverlayArrowNav(overlay);
  el('h2', { parent: overlay, text: 'NOSTR RELAYS' });

  const intro = el('p', { parent: overlay, text: 'Where this game publishes scores, follows, shares and zap requests. Toggle to disable, add your own, remove the ones you brought.' });
  intro.style.cssText = 'font-size:0.85rem;color:rgba(180,140,255,0.85);max-width:480px;margin:0;line-height:1.5;';

  const list = el('div', { parent: overlay });
  list.style.cssText = 'display:flex;flex-direction:column;gap:6px;width:100%;max-width:520px;';

  const status = el('p', { parent: overlay, text: '' });
  status.style.cssText = 'font-size:0.78rem;color:rgba(180,140,255,0.75);min-height:1.1em;margin:0;';

  function setStatus(text: string, colour: string): void {
    status.textContent = text;
    status.style.color = colour;
  }

  function paint(): void {
    list.innerHTML = '';
    const relays = getKnownRelays();
    if (relays.length === 0) {
      const empty = el('p', { parent: list, text: 'No relays. Add one below or RESET to defaults.' });
      empty.style.cssText = 'font-size:0.85rem;color:rgba(180,140,255,0.65);text-align:center;margin:8px 0;';
    }
    for (const url of relays) {
      const enabled = isRelayEnabled(url);
      const isDefault = isDefaultRelay(url);
      const row = el('div', { parent: list, attrs: { role: 'group' } });
      row.style.cssText = 'display:grid;grid-template-columns:auto 1fr auto;gap:10px;align-items:center;background:rgba(0,0,0,0.4);border:1px solid rgba(91,157,255,0.25);border-radius:8px;padding:8px 12px;';

      const toggle = el('button', { parent: row });
      toggle.setAttribute('aria-pressed', String(enabled));
      toggle.textContent = enabled ? 'ON' : 'OFF';
      toggle.style.cssText = [
        'min-width:54px',
        'padding:5px 10px', 'border-radius:14px',
        'border:2px solid ' + (enabled ? '#58ff58' : 'rgba(180,140,255,0.4)'),
        'background:' + (enabled ? 'rgba(88,255,88,0.16)' : 'transparent'),
        'color:' + (enabled ? '#58ff58' : 'rgba(220,210,255,0.65)'),
        "font-family:'VT323',ui-monospace,monospace",
        'font-size:0.85rem', 'letter-spacing:0.18em',
        'cursor:pointer',
      ].join(';');
      toggle.addEventListener('click', () => {
        setRelayEnabled(url, !enabled);
        setStatus(enabled ? `Disabled ${shortRelay(url)}` : `Enabled ${shortRelay(url)}`, '#58ff58');
        paint();
      });

      const label = el('div', { parent: row });
      label.style.cssText = 'display:flex;flex-direction:column;align-items:flex-start;text-align:left;min-width:0;';
      const urlSpan = el('span', { parent: label, text: url });
      urlSpan.style.cssText = "font-family:ui-monospace,monospace;font-size:0.82rem;color:rgba(220,210,255,0.92);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:100%;letter-spacing:0;";
      const tag = el('span', { parent: label, text: isDefault ? 'BUNDLED' : 'CUSTOM' });
      tag.style.cssText = 'font-size:0.62rem;letter-spacing:0.2em;color:' + (isDefault ? 'rgba(91,157,255,0.7)' : 'rgba(255,138,58,0.85)') + ';margin-top:2px;';

      if (!isDefault) {
        const remove = el('button', { parent: row, text: '✕' });
        remove.setAttribute('aria-label', 'Remove relay');
        remove.style.cssText = [
          'background:transparent', 'border:none',
          'color:rgba(255,80,80,0.7)',
          "font-family:'VT323',ui-monospace,monospace",
          'font-size:1.1rem', 'cursor:pointer',
          'padding:4px 8px', 'border-radius:4px',
        ].join(';');
        remove.addEventListener('click', () => {
          removeRelay(url);
          setStatus(`Removed ${shortRelay(url)}`, '#ff8a3a');
          paint();
        });
      } else {
        // Empty cell so the grid keeps three columns aligned across rows
        el('span', { parent: row, text: '' });
      }
    }
  }

  paint();

  // Add-new row
  const addRow = el('div', { parent: overlay });
  addRow.style.cssText = 'display:flex;gap:8px;align-items:center;width:100%;max-width:520px;';
  const input = el('input', { parent: addRow, attrs: { type: 'url', placeholder: 'wss://relay.example.com' } });
  input.style.cssText = 'flex:1;background:rgba(0,0,0,0.5);border:2px solid rgba(91,157,255,0.4);color:#cfd6ff;font-family:ui-monospace,monospace;font-size:0.85rem;padding:8px 12px;border-radius:6px;letter-spacing:0;';
  const addBtn = el('button', { className: 'menu-btn secondary', parent: addRow, text: 'ADD' });
  function commit(): void {
    const result = addRelay(input.value);
    if (result) {
      input.value = '';
      setStatus(`Added relay`, '#58ff58');
      paint();
    } else {
      setStatus('Must be a valid wss:// URL', '#ff5050');
    }
  }
  addBtn.addEventListener('click', commit);
  input.addEventListener('keydown', (e: Event) => {
    if ((e as KeyboardEvent).code === 'Enter') { e.preventDefault(); commit(); }
  });

  const row = el('div', { className: 'menu-row', parent: overlay });
  const reset = el('button', { className: 'menu-btn secondary', parent: row, text: 'RESET' });
  reset.addEventListener('click', () => {
    resetRelays();
    setStatus('Restored bundled defaults.', 'rgba(180,140,255,0.85)');
    paint();
  });
  const back = el('button', { className: 'menu-btn', parent: row, text: 'BACK' });
  back.addEventListener('click', onBack);
}

function shortRelay(url: string): string {
  return url.replace(/^wss:\/\//, '').replace(/\/$/, '');
}

// ── Game over screen ──────────────────────────────────────────────────────────

/** Solo arcade modes that publish a Gamestr score on game-over. Co-op has its
 *  own game-signed path (submitCoopScore); deathmatch/duel are PvP; sanctum/
 *  defender are special surfaces. Matches the faucet's /api/score mode enum. */
const GAMESTR_SOLO_MODES = new Set<RunMode>(['campaign', 'drift', 'bossrush', 'arena']);
/** Re-entering the recap must not enqueue the same run twice. */
let lastSoloScoreQueuedRun = '';
let soloScoreOutbox: SoloScoreOutbox | null = null;
const scoreOutbox = (): SoloScoreOutbox => soloScoreOutbox ??= new SoloScoreOutbox();

/** Only the currently selected account can see and retry its pending runs. */
export function refreshScoreRecovery(state: GameState): void {
  const previous = document.getElementById('pal-score-recovery');
  const hadFocus = !!previous?.contains(document.activeElement);
  previous?.remove();
  const session = state.session;
  if (!session) return;
  if (!['title', 'gameover', 'completed', 'paused'].includes(state.phase)) return;
  const outbox = scoreOutbox();
  const pending = outbox.pending(session.pubkey);
  if (!pending.length) {
    if (hadFocus) root.querySelector<HTMLButtonElement>('button:not([disabled])')?.focus({ preventScroll: true });
    return;
  }
  const banner = el('div', { parent: document.body, attrs: { id: 'pal-score-recovery', role: 'status', 'aria-live': 'polite' } });
  banner.style.cssText = 'position:fixed;bottom:max(12px,env(safe-area-inset-bottom,0px));left:12px;right:12px;z-index:9998;max-width:560px;margin:auto;padding:12px;background:#100820;color:#ffd84a;border:1px solid #ffd84a;border-radius:8px;display:flex;flex-wrap:wrap;gap:10px;align-items:center;pointer-events:auto;touch-action:manipulation;font-family:ui-monospace,monospace;';
  const status = el('span', { parent: banner, text: `${pending.length} SCORE${pending.length === 1 ? '' : 'S'} WAITING TO PUBLISH${outbox.durable ? '' : ' · KEEP THIS PAGE OPEN TO RETRY'}` });
  if (outbox.storageProblem === 'corrupt') {
    el('span', { parent: banner, text: 'Existing saved data could not be read safely and has been preserved. Keep this page open for new runs; removal is unavailable.' });
  }
  const list = el('div', { parent: banner });
  list.style.cssText = 'width:100%;max-height:35vh;overflow:auto;overflow-wrap:anywhere;';
  let firstRetry: HTMLButtonElement | null = null;
  for (const receipt of pending) {
    const row = el('div', { parent: list, attrs: { role: 'group' } });
    row.style.cssText = 'display:flex;flex-wrap:wrap;gap:8px;align-items:center;padding:8px 0;border-top:1px solid #71592a;';
    const finished = receipt.input.finished_at ? receipt.input.finished_at * 1000 : Number(receipt.input.run_id);
    const date = finished >= 946684800000 && Number.isFinite(finished)
      ? new Date(finished).toLocaleString() : 'Original date unavailable';
    row.setAttribute('aria-label', `Saved score: ${receipt.input.score.toLocaleString()} points · ${date}`);
    el('span', { parent: row, text: `${receipt.input.score.toLocaleString()} points · ${date}` });
    if (receipt.lastError) {
      el('span', { parent: row, text: `${receipt.rejected ? 'Score rejected' : 'Not published'}: ${receipt.lastError.replace(/[_-]/g, ' ')}. Your saved run is retained.` });
    }
    const retry = el('button', { parent: row, className: 'menu-btn secondary', text: session.signer.capabilities.canSignEvents ? 'RETRY SCORE' : 'RECONNECT TO RETRY' });
    firstRetry ??= retry;
    retry.style.cssText = 'min-height:44px;touch-action:manipulation;font:inherit;letter-spacing:normal;white-space:normal;';
    const remove = el('button', { parent: row, className: 'menu-btn secondary', text: 'REMOVE SAVED SCORE' });
    remove.style.cssText = retry.style.cssText;
    remove.disabled = outbox.storageProblem === 'corrupt';
    remove.addEventListener('click', () => {
      if (state.session?.pubkey !== receipt.pubkey) return;
      if (!window.confirm(`Remove the saved retry for ${receipt.input.score.toLocaleString()} points (${date})?\n\nThis removes this device's retry copy. It does not delete an already published score.`)) return;
      if (state.session?.pubkey !== receipt.pubkey) return;
      if (!outbox.remove(receipt, state.session.pubkey)) {
        status.textContent = 'Could not save this removal. Your score is retained; check this device’s storage and try again.';
        return;
      }
      refreshScoreRecovery(state);
    });
    retry.addEventListener('click', () => {
      if (state.session?.pubkey !== receipt.pubkey) return;
      if (!state.session.signer.capabilities.canSignEvents) {
        renderSignerRecovery(state);
        return;
      }
      retry.disabled = true;
      remove.disabled = true;
      status.textContent = 'PUBLISHING YOUR SCORE…';
      // Recheck the owner and signer, using this selected original run snapshot.
      void outbox.retry(receipt, state.session, submitSoloScore).finally(() => refreshScoreRecovery(state));
    });
  }
  if (isArcadeSignerSession(session)) {
    el('span', { parent: banner, text: 'Reconnect in the arcade, then launch Pallasite to retry this saved score.' });
    const reconnect = el('button', { parent: banner, className: 'menu-btn secondary', text: 'RECONNECT IN ARCADE' });
    reconnect.addEventListener('click', () => window.location.assign(`${ARCADE_ORIGIN}/game/pallasite`));
    reconnect.style.cssText = 'display:inline-flex;align-items:center;min-height:44px;max-width:100%;box-sizing:border-box;font:inherit;letter-spacing:normal;white-space:normal;touch-action:manipulation;text-decoration:none;';
  }
  if (hadFocus) firstRetry?.focus({ preventScroll: true });
}

/** Preserve the earned run and account before asynchronous signing or navigation. */
function publishSoloScoreForRun(state: GameState): void {
  const soloMode = currentMode();
  if (!GAMESTR_SOLO_MODES.has(soloMode)) return;
  if (state.players[0].score <= 0 || state.runStartedAt <= 0) return;
  const runId = String(state.runStartedAt);
  if (runId === lastSoloScoreQueuedRun) return;
  lastSoloScoreQueuedRun = runId;
  const owner = state.runScorePubkey === undefined ? state.session?.pubkey : state.runScorePubkey;
  const sameAccount = !!owner && state.session?.pubkey === owner;
  const input: import('./faucet.js').SoloScoreInput = {
    score: state.players[0].score,
    wave: state.wave,
    duration_ms: Math.max(0, Math.floor(state.runTimeMs)),
    run_id: runId,
    started_at: Math.floor(state.runStartedAt / 1000),
    finished_at: Math.floor(Date.now() / 1000),
    mode: soloMode,
    player_name: sameAccount ? scoreboardNameFor(state) : owner?.slice(0, 8) ?? 'Anonymous',
    ...(owner && state.profile?.pubkey === owner && state.profile.nip05 ? { nip05: state.profile.nip05 } : {}),
    ...(getFlavour() === '600bn' ? { room: '600bn' as const } : {}),
    cheated: state.cheatedThisRun,
  };
  // Capture the selected signer now; never read a different account after await.
  const selected = state.runScorePubkey === null ? null : state.session;
  void (async () => {
    let session = selected;
    // A genuinely anonymous run can obtain a guest key. A selected account
    // with an unavailable signer must keep its own identity instead.
    if (!owner && !session) {
      session = await loadOrCreateGuest({ name: input.player_name ?? 'Anonymous', followPallasite: false });
      if (!state.session) state.session = session;
    }
    const pubkey = owner ?? session?.pubkey;
    if (!pubkey) return;
    const outbox = scoreOutbox();
    const receipt = outbox.enqueue(pubkey, input);
    const result = await outbox.retry(receipt, session, submitSoloScore);
    console.log('[solo-score] result=' + JSON.stringify(result) + ' pk=' + pubkey.slice(0, 12));
    refreshScoreRecovery(state);
  })().catch(() => { refreshScoreRecovery(state); });
}

export function renderGameOver(state: GameState): void {
  // Push the current skin unlock set to Nostr at the end of every run.
  // kind 30764 is replaceable (d="pallasite-skins") so this is idempotent
  // when nothing has changed; when something has (e.g. halo just unlocked
  // mid-run), the new set propagates without a separate signal path.
  if (state.session) {
    void publishSkinUnlocks(state.session).catch(() => undefined);
  }

  // Publish a verified Gamestr score for this run (death path). Decoupled
  // from the sat-claim flow. Failed signing retains the selected account and
  // earned run for an explicit retry; it never republishes as another account.
  publishSoloScoreForRun(state);

  // High-score handling — auto-save under the player's session display
  // name (guest's chosen name, or NIP-01 best-name for a real signer)
  // and advance straight to the recap. The previous 4-char arcade
  // initials picker is gone: every player now arrives with an
  // identity already (seamless guest or signed-in Nostr), and a
  // separate 3-letter pseudonym for the leaderboard was duplicate
  // identity churn. `initialsEnteredThisRun` is repurposed as "high
  // score for this run has been written" so REPLAY KILL re-renders
  // don't double-count.
  const board = activeScoreBoard();
  const leaderboardScore = runLeaderboardScore(state);
  const isNewHigh = currentMode() !== 'deathmatch' && isHighScore(leaderboardScore, board) && leaderboardScore > 0;
  if (isNewHigh && !state.initialsEnteredThisRun) {
    addLocalHighScore({
      name: scoreboardNameFor(state),
      score: leaderboardScore,
      sats: runLeaderboardSats(state),
      wave: state.wave,
      at: new Date().toISOString(),
      pubkey: state.session?.pubkey,
    }, board);
    state.initialsEnteredThisRun = true;
  }
  renderGameOverRecap(state);
}

/**
 * Derive the name to write into the local high-score table for the
 * current session. Order of preference:
 *   1. session.displayName (set by guest signup, or by Signet for some
 *      sign-in paths)
 *   2. NIP-01 kind 0 metadata via the cached profile + bestName helper
 *   3. truncated pubkey hex as a last-resort fallback
 *   4. literal 'PLAYER' for sessionless runs (shouldn't happen in the
 *      new attract → auth flow, but keeps the table populated)
 *
 * Upper-cased + capped at 25 chars to match the table's column width.
 */
function scoreboardNameFor(state: GameState): string {
  const session = state.session;
  if (!session) return 'PLAYER';
  const raw = session.displayName
    ?? (state.profile ? bestName(state.profile, session.pubkey) : null)
    ?? shortPubkey(session.pubkey);
  return raw.toUpperCase().slice(0, 25);
}

function activeScoreBoard(): ScoreBoardId {
  return isCoopCampaignMode() ? 'coop-campaign' : 'solo';
}

function runLeaderboardScore(state: GameState): number {
  return isCoopCampaignMode()
    ? state.players.reduce((sum, p) => sum + p.score, 0)
    : state.players[0].score;
}

function runLeaderboardSats(state: GameState): number {
  return isCoopCampaignMode() ? 0 : state.players[0].sats;
}

function renderGameOverRecap(state: GameState): void {
  // Non-high-score gameovers land directly on the credits stage — no
  // initials to enter, but the credits + zap flow still applies.
  if (currentMode() === 'deathmatch') {
    renderDeathmatchGameOver(state);
    return;
  }
  // Booth kiosk: a walk-up wants three obvious choices, not the full online
  // recap (share clip/card, leaderboard, endorse, honours). Scores are already
  // published by renderGameOver above, so this is purely the visual recap.
  if (boothKiosk) {
    renderBoothGameOver(state);
    return;
  }
  renderRunCredits(state, { headerText: 'GAME OVER' });
}

/** Stripped booth game-over — score recap + exactly three walk-up actions:
 *  PLAY AGAIN (re-run, preserving solo / 2-player), DONATE (zap QR), and LOGOUT
 *  (sign out → the join lobby for the next walk-up). */
function renderBoothGameOver(state: GameState): void {
  clearOverlay();
  const overlay = el('div', { className: 'overlay', parent: root });
  setupOverlayArrowNav(overlay);
  document.body.dataset.surface = 'event';

  el('h2', { parent: overlay, text: 'GAME OVER' });

  const twoPlayer = state.players.length >= 2;
  if (twoPlayer) {
    renderVersusBanner(overlay, state);
  } else {
    if (state.session) {
      const idRow = el('div', { parent: overlay });
      idRow.style.cssText = 'display:flex;justify-content:center;margin:6px 0 4px;';
      renderIdentityChip(idRow, state.session.pubkey, state.session.displayName ?? '', 'PLAYER 1', PLAYER_COLOURS[0]);
    }
    const stat = el('p', { parent: overlay, text: `${state.players[0].score.toLocaleString()} · WAVE ${state.wave}` });
    stat.style.cssText = 'font-size:1.7rem;letter-spacing:0.06em;color:var(--hud-yellow);text-shadow:0 0 10px rgba(255,216,74,0.45);margin:8px 0 2px;';
    if (state.players[0].sats > 0) {
      const sats = el('p', { parent: overlay, text: `${state.players[0].sats} sats earned` });
      sats.style.cssText = 'font:0.88rem ui-monospace,monospace;color:rgba(255,216,74,0.78);letter-spacing:0.1em;margin:0 0 4px;';
    }
  }

  const row = el('div', { className: 'menu-row', parent: overlay });
  const again = el('button', { className: 'menu-btn', parent: row, text: '▶ PLAY AGAIN' }) as HTMLButtonElement;
  onTap(again, () => {
    void audio.unlockAudio();
    // Replay with the same crew: a 2-up run goes back through the couch start
    // (keeps both name chips); solo uses the standard start (which clears any
    // leftover couch routing).
    if (twoPlayer) startCouchInPlace();
    else onStartCb?.();
  });
  setTimeout(() => tryFocusVisible(again), 0);

  const donate = el('button', { className: 'menu-btn secondary', parent: row, text: '⚡ DONATE' });
  onTap(donate, () => renderBoothDonate(state));

  const logout = el('button', { className: 'menu-btn secondary', parent: row, text: 'LOGOUT' });
  onTap(logout, () => { void boothLogout(state); });

  renderBoothControlsLegend(overlay);
}

type BoothDonateMethod = 'zap' | 'onchain' | 'silent';

/** DONATE sub-screen — zap / on-chain / silent payment, each a big scannable
 *  QR with a way back. Kept separate so the game-over stays to three buttons. */
function renderBoothDonate(state: GameState, method: BoothDonateMethod = 'zap'): void {
  clearOverlay();
  const overlay = el('div', { className: 'overlay', parent: root });
  setupOverlayArrowNav(overlay);
  document.body.dataset.surface = 'event';
  const headings: Record<BoothDonateMethod, string> = {
    zap: 'ZAP US ⚡',
    onchain: 'SEND BITCOIN ₿',
    silent: 'SILENT PAYMENT',
  };
  el('h2', { parent: overlay, text: headings[method] });

  // Method chips — a full re-render per pick keeps the gamepad arrow nav and
  // focus seeding on the same path as every other booth screen.
  const chipRow = el('div', { className: 'menu-row', parent: overlay });
  const chips: Array<{ key: BoothDonateMethod; label: string }> = [
    { key: 'zap', label: '⚡ ZAP' },
    { key: 'onchain', label: '₿ ON-CHAIN' },
    { key: 'silent', label: 'SILENT' },
  ];
  for (const chip of chips) {
    const b = el('button', { className: chip.key === method ? 'menu-btn' : 'menu-btn secondary', parent: chipRow, text: chip.label });
    if (chip.key !== method) onTap(b, () => renderBoothDonate(state, chip.key));
  }

  if (method === 'zap') {
    renderZapUsQR(overlay, state, { caption: 'Scan to send sats — cheers!', size: 220 });
  } else {
    renderOnchainDonateQR(overlay, method, { size: 220 });
  }

  const row = el('div', { className: 'menu-row', parent: overlay });
  const back = el('button', { className: 'menu-btn', parent: row, text: 'BACK' });
  onTap(back, () => renderBoothGameOver(state));
  setTimeout(() => tryFocusVisible(back), 0);
}

/** On-chain / silent-payment QR block. Same shape as renderZapUsQR but
 *  static — no amount picker, the donor's wallet handles the rest. */
function renderOnchainDonateQR(parent: HTMLElement, method: 'onchain' | 'silent', opts?: { size?: number }): HTMLElement {
  const size = opts?.size ?? 168;
  const onchain = method === 'onchain';
  const value = onchain ? DEV.onchainAddress : DEV.silentPayment;
  const wrap = el('div', { parent });
  wrap.style.cssText = 'display:flex;flex-direction:column;align-items:center;gap:6px;margin:16px auto 8px;max-width:340px;';
  const cap = el('p', { parent: wrap, text: onchain ? 'Scan with any Bitcoin wallet' : 'Private on-chain · silent payments' });
  cap.style.cssText = 'margin:0;font-size:0.76rem;letter-spacing:0.16em;color:rgba(255,216,74,0.9);text-align:center;';
  const box = el('div', { parent: wrap });
  box.style.cssText = `width:${size}px;height:${size}px;background:#fff;padding:8px;border-radius:10px;box-sizing:content-box;`;
  box.setAttribute('aria-label', onchain ? 'Bitcoin address QR code' : 'Silent payment QR code');
  try { void renderQRInto(box, onchain ? `bitcoin:${value}` : value); } catch { /* ignore */ }
  const addr = el('p', { parent: wrap, text: onchain ? value : `${value.slice(0, 18)}...${value.slice(-12)}` });
  addr.style.cssText = 'margin:0;font-size:0.72rem;color:rgba(220,210,255,0.7);user-select:all;word-break:break-all;text-align:center;';
  const hint = el('p', {
    parent: wrap,
    text: onchain ? 'On-chain fees apply · best for larger amounts' : 'Needs a silent-payments wallet — Cake, BlueWallet',
  });
  hint.style.cssText = 'margin:0;font-size:0.64rem;color:rgba(200,200,200,0.5);letter-spacing:0.04em;text-align:center;';
  return wrap;
}

/** LOGOUT from the booth game-over — clears the run, signs the pilot out, and
 *  drops back to the join lobby so the next walk-up starts clean. */
async function boothLogout(state: GameState): Promise<void> {
  // 600bn was entered via the "600B" name cheat — a ?flavour=600bn reload that
  // also dropped ?p1. getFlavour() is cached, so the ONLY clean way back to the
  // normal booth is a reload that strips ?flavour and restores the ?p1 join
  // wizard. Without this, LOGOUT just lands on the Sanctum attract (still 600bn)
  // and the next walk-up is stuck in it on a gamepad-only booth.
  if (getFlavour() === '600bn') {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('flavour');
      url.searchParams.set('p1', '1');
      window.location.href = url.toString();
      return;
    } catch { /* fall through to the normal logout */ }
  }
  clearEntitiesForTitle(state);
  state.phase = 'title';
  // signOutAndReturnToAttract → renderAttract → renderBoothLobby (boothKiosk),
  // which also clears the 2nd identity, pad bindings, and couch routing.
  await signOutAndReturnToAttract(state);
}

function deathmatchRows(state: GameState): Array<{ slot: number; kills: number; deaths: number; streak: number; score: number; alive: boolean }> {
  return state.players
    .map((p, slot) => ({
      slot,
      kills: p.deathmatchKills,
      deaths: p.deathmatchDeaths,
      streak: p.deathmatchStreak,
      score: p.score,
      alive: p.ship.alive,
    }))
    .sort((a, b) => b.kills - a.kills || b.score - a.score || a.deaths - b.deaths || a.slot - b.slot);
}

function renderDeathmatchSummary(parent: HTMLElement, state: GameState): void {
  const rows = deathmatchRows(state);
  const winner = state.deathmatchWinnerSlot !== null
    ? rows.find((r) => r.slot === state.deathmatchWinnerSlot) ?? rows[0]
    : rows[0];
  const tied = state.deathmatchWinnerSlot === null || (rows.length > 1 && rows[1].kills === winner.kills && rows[1].score === winner.score);
  const wrap = el('div', { parent });
  wrap.style.cssText = [
    'display:flex', 'flex-direction:column', 'align-items:center', 'gap:12px',
    'width:100%', 'max-width:720px',
    'margin:4px auto 14px', 'padding:14px 16px',
    'background:rgba(4,8,18,0.72)',
    'border:1px solid rgba(120,150,255,0.34)',
    'border-radius:8px',
  ].join(';');

  const verdict = el('p', { parent: wrap, text: tied ? 'DRAW' : `WINNER · P${winner.slot + 1}` });
  verdict.style.cssText = 'margin:0;font-size:1.35rem;letter-spacing:0.24em;color:var(--hud-yellow);text-shadow:0 0 10px rgba(255,216,74,0.45);';

  const reason = state.deathmatchEndedReason === 'kill-limit'
    ? 'kill limit'
    : state.deathmatchEndedReason === 'time-limit'
    ? 'time limit'
    : state.deathmatchEndedReason === 'last-player-standing'
    ? 'last pilot standing'
    : 'round ended';
  const rules = state.deathmatchRules;
  const rulesText = rules
    ? `${Math.round(rules.timeLimitMs / 60_000)} min · ${rules.killLimit} kill cap · ${rules.respawns} respawns`
    : `${state.players.length} pilots`;
  const meta = el('p', { parent: wrap, text: `${reason} · ${rulesText} · ${rows.reduce((sum, r) => sum + r.kills, 0)} confirmed kills` });
  meta.style.cssText = 'margin:-4px 0 0;font:0.78rem ui-monospace,monospace;color:rgba(220,230,255,0.72);letter-spacing:0.12em;text-align:center;';

  const table = el('div', { parent: wrap });
  table.style.cssText = 'display:grid;grid-template-columns:54px repeat(4, minmax(58px, 1fr));gap:0;width:100%;font-family:ui-monospace,monospace;font-size:0.86rem;overflow:hidden;border:1px solid rgba(180,200,255,0.16);border-radius:6px;';
  const cell = (text: string, header = false, highlight = false): void => {
    const c = el('div', { parent: table, text });
    c.style.cssText = [
      'padding:7px 8px', 'text-align:center',
      'border-bottom:1px solid rgba(180,200,255,0.10)',
      'background:' + (header ? 'rgba(120,150,255,0.14)' : highlight ? 'rgba(255,216,74,0.08)' : 'rgba(2,5,13,0.30)'),
      'color:' + (header ? '#ffd84a' : highlight ? '#fff5d8' : 'rgba(230,238,255,0.86)'),
      'letter-spacing:' + (header ? '0.12em' : '0.04em'),
    ].join(';');
  };
  for (const h of ['PILOT', 'KILLS', 'DEATHS', 'STREAK', 'SCORE']) cell(h, true);
  for (const r of rows.slice(0, Math.min(12, rows.length))) {
    const top = !tied && r.slot === winner.slot;
    cell(`P${r.slot + 1}`, false, top);
    cell(String(r.kills), false, top);
    cell(String(r.deaths), false, top);
    cell(r.streak > 0 ? `x${r.streak}` : '-', false, top);
    cell(String(r.score), false, top);
  }
}

function renderDeathmatchGameOver(state: GameState): void {
  clearOverlay();
  const overlay = el('div', { className: 'overlay', parent: root });
  setupOverlayArrowNav(overlay);
  el('h2', { parent: overlay, text: 'DEATHMATCH OVER' });
  renderDeathmatchSummary(overlay, state);

  const row = el('div', { className: 'menu-row', parent: overlay });
  const again = el('button', { className: 'menu-btn', parent: row, text: 'REMATCH' });
  onTap(again, () => {
    void audio.unlockAudio();
    onStartCb?.();
  });
  const title = el('button', { className: 'menu-btn secondary', parent: row, text: 'BACK TO TITLE' });
  onTap(title, () => {
    clearEntitiesForTitle(state);
    state.phase = 'title';
    renderTitle(state);
  });
}




async function maybePublishCoopCampaignScore(
  state: GameState,
  parent: HTMLElement,
): Promise<void> {
  const status = el('p', { parent });
  status.style.cssText = 'font-size:0.85rem;margin:8px 0 0 0;color:rgba(220,210,255,0.72);letter-spacing:0.04em;text-align:center;';
  const score = runLeaderboardScore(state);
  if (score <= 0) {
    status.textContent = 'Co-op score was zero — nothing to publish.';
    return;
  }
  if (!state.session) {
    status.textContent = 'Co-op score saved locally. Sign in to publish to the co-op board.';
    return;
  }
  if (!state.session.signer.capabilities.canSignEvents) {
    status.textContent = 'Co-op score saved locally. This session cannot sign leaderboard events.';
    return;
  }
  if (state.cheatedThisRun) {
    status.style.color = '#ff8050';
    status.textContent = 'Cheats were used — co-op score stayed local.';
    return;
  }
  status.style.color = '#5b9dff';
  status.textContent = 'Banking co-op scores (game-signed)…';

  const durationMs = Math.max(0, Math.floor(state.runTimeMs));
  const seed = getActiveSeed() || undefined;
  const common = { wave: state.wave, duration_ms: durationMs, daily_seed: seed, cheated: state.cheatedThisRun };
  const p2 = state.coopIdentity2;
  const members = p2?.pubkey ? [state.session.pubkey, p2.pubkey] : [state.session.pubkey];
  const p1Individual = state.players[0]?.score ?? 0;
  const p2Individual = state.players[1]?.score ?? 0;

  // Game-signed path: P1 NIP-98 auths → the faucet game-signs their own score
  // AND the combined team event; P2 auths → the faucet game-signs their own.
  // The players' signers only ever prove identity, never sign the score.
  const r1 = await submitCoopScore(state.session, { ...common, score: p1Individual, team: { score, members } });

  if (r1.ok) {
    status.style.color = '#58ff58';
    status.textContent = '✓ Team score + your score game-signed to the leaderboard · no sats payout';
    if (p2?.session && p2Individual > 0 && p2.session.signer.capabilities.canSignEvents) {
      const line = el('p', { parent });
      line.style.cssText = 'font-size:0.78rem;margin:4px 0 0;color:rgba(180,200,255,0.72);letter-spacing:0.04em;text-align:center;';
      line.textContent = "Banking Player 2's score to their npub…";
      const r2 = await submitCoopScore(p2.session, { ...common, score: p2Individual });
      line.style.color = r2.ok ? '#58ff58' : '#ff8050';
      line.textContent = r2.ok ? "✓ Player 2's score game-signed under their own npub." : "Player 2's score couldn't publish.";
    }
    return;
  }

  // Endpoint not deployed yet (or a transient network failure) → fall back to
  // the legacy frontend-signed combined publish so co-op scoring still works
  // during the roll-out. (Other errors surface as-is.)
  if (r1.error === 'not_deployed' || r1.error === 'network_error') {
    try {
      const result = await publishCoopCampaignScore(state.session, {
        score, wave: state.wave, durationMs, players: state.players.length, seed: getActiveSeed(), cheated: state.cheatedThisRun,
      });
      if (!result) {
        status.style.color = '#ffd84a';
        status.textContent = 'Co-op score saved locally · sign in with a live signer (extension/bunker) to publish it.';
      } else {
        status.style.color = result.publishedTo.length > 0 ? '#58ff58' : '#ff8050';
        status.textContent = result.publishedTo.length > 0
          ? `✓ Co-op score published to ${result.publishedTo.length} relays · no sats payout`
          : 'Co-op score saved locally; relays rejected the publish.';
      }
    } catch (err) {
      status.style.color = '#ff8050';
      status.textContent = `Co-op publish failed: ${err instanceof Error ? err.message : String(err)}`;
    }
    return;
  }

  status.style.color = '#ff8050';
  status.textContent = `Co-op publish failed: ${r1.error}${r1.detail ? ' — ' + r1.detail : ''}`;
}


// ── Completion screen (wave 25 cleared) ──────────────────────────────────────


/**
 * Unified post-run credits stage.
 *
 * Replaces the old gameover-recap and completion-recap screens with a
 * single auto-scrolling credits screen that centres the zap CTA, kicks
 * off the score-publish flow in the background, and either auto-returns
 * to the title after `idleSeconds` of no input or lets the player skip
 * sooner via SKIP TO TITLE / Enter.
 *
 * - Header sets the tone: GAME OVER vs PALLASITE COMPLETE
 * - Faucet / publish flow renders inline so a Nostr-mode player sees the
 *   sats land before the auto-skip fires
 * - Zap row is the most prominent action — that's the whole point of this
 *   stage
 * - Honours strip surfaces only on completion (PERFECT RUN / NO LURK /
 *   COMPLETIONIST) — gameovers don't earn the COMPLETIONIST badge so
 *   the strip is mostly empty there anyway
 */
/** 600bn attract screen — sacred number wordmark, terse mission brief,
 *  single PLAY. Bypasses the standard renderAttract → renderAuth →
 *  renderTitle chain because the Sanctum is a one-level teaser; the
 *  full mission-select / leaderboard rotation reads wrong here.
 *  Guest path: PLAY → startGame() (startSanctumRun fires inside),
 *  claim flow at game-over surfaces the sign-in option if needed. */
function renderSanctumAttract(state: GameState): void {
  clearOverlay();
  const overlay = el('div', { className: 'overlay', parent: root });
  setupOverlayArrowNav(overlay);
  mountAttractUtilityChips(overlay, () => renderSanctumAttract(state));

  // 4-line sacred number wordmark — same canon as game-over card.
  const number = el('div', { parent: overlay });
  number.style.cssText = 'font:bold 56px ui-monospace,monospace;color:#ffd84a;letter-spacing:0.18em;line-height:1.05;text-align:center;text-shadow:0 0 24px rgba(255,138,58,0.65);margin-top:20px;';
  const sigil=el('canvas',{parent:number}) as HTMLCanvasElement;
  sigil.width=sigil.height=256;sigil.style.cssText='width:176px;height:176px;max-width:30vh;max-height:30vh;filter:drop-shadow(0 8px 18px rgba(0,0,0,.65));';
  sigil.setAttribute('role','img');sigil.setAttribute('aria-label','600 billion · The Signal');
  sigil.getContext('2d')!.drawImage(signalRelicFaces().front,0,0);

  const banner = el('div', { parent: overlay, text: 'THE SANCTUM' });
  banner.style.cssText = 'font:bold 22px ui-monospace,monospace;color:#fff5d8;letter-spacing:0.28em;margin-top:24px;text-shadow:0 0 12px rgba(255,138,58,0.5);';

  const brief = el('div', { parent: overlay });
  brief.style.cssText = 'display:flex;flex-direction:column;gap:4px;margin:16px 0;text-align:center;';
  const line1 = el('p', { parent: brief, text: 'ONE LIFE · 11 COUNCIL · ENDLESS CYCLE' });
  line1.style.cssText = 'font:bold 13px ui-monospace,monospace;color:#ffd84a;letter-spacing:0.2em;margin:0;';
  const line2 = el('p', { parent: brief, text: 'Shatter pallasites for score. The council always returns.' });
  line2.style.cssText = 'font:11px ui-monospace,monospace;color:rgba(255,245,216,0.75);letter-spacing:0.14em;margin:0;';
  const line3 = el('p', { parent: brief, text: '"600B was never just the price. It was the signal."' });
  line3.style.cssText = 'font:11px ui-monospace,monospace;color:rgba(255,245,216,0.55);letter-spacing:0.12em;margin:0;font-style:italic;';

  // Big PLAY button — triggers startGame via the shared bindings.
  const playRow = el('div', { className: 'menu-row', parent: overlay });
  playRow.style.cssText = 'margin-top:20px;';
  const playBtn = el('button', { className: 'menu-btn', parent: playRow, text: 'ENTER ▶' }) as HTMLButtonElement;
  playBtn.style.cssText += 'font-size:1.4rem;padding:14px 48px;letter-spacing:0.28em;background:rgba(255,138,58,0.18);border-color:#ff8a3a;color:#ffd84a;text-shadow:0 0 12px rgba(255,138,58,0.6);';
  playBtn.addEventListener('click', () => {
    void audio.unlockAudio();
    tryEnterFullscreen();
    // Standard Pallasite wave-1 entry — same ship/HUD/IGNITE banner
    // as the campaign. The 600bn theme is layered into wave 1 via
    // beginWave (council-member asteroids) + trackForState (the-cult
    // music) + a flavour check on the game-over recap (600bn card).
    onStartCb?.();
  });

  // How-to-play + music picker — same surfaces as the main title.
  // Music doubles as the iOS audio-unlock gesture: tapping it starts
  // the AudioContext, so when the player backs out, title music
  // begins playing. The picker itself lists every track including
  // the-cult (the Sanctum bed) so players can preview level music.
  const auxRow = el('div', { className: 'menu-row', parent: overlay });
  auxRow.style.cssText = 'margin-top:12px;display:flex;gap:10px;justify-content:center;flex-wrap:wrap;';
  const howBtn = el('button', { className: 'menu-btn secondary', parent: auxRow, text: 'HOW TO PLAY' });
  howBtn.addEventListener('click', () => renderHowToPlay(() => renderSanctumAttract(state)));
  const musicBtn = el('button', { className: 'menu-btn secondary', parent: auxRow, text: '🎵 MUSIC' });
  musicBtn.addEventListener('click', () => {
    void audio.unlockAudio();
    renderMusicPlayer(state, () => renderSanctumAttract(state));
  });

  // Footer — 600bn promo links straight to the canonical site.
  const footer = el('div', { parent: overlay });
  footer.style.cssText = 'margin-top:28px;display:flex;flex-direction:column;align-items:center;gap:4px;';
  const partyLink = el('a', { parent: footer, text: '600BN · OPEN THE SIGNAL ↗' }) as HTMLAnchorElement;
  partyLink.href = 'https://600000000000.com';
  partyLink.target = '_blank';
  partyLink.rel = 'noopener noreferrer';
  partyLink.style.cssText = 'font:11px ui-monospace,monospace;color:#ffd84a;letter-spacing:0.18em;text-decoration:none;border:1px solid rgba(255,216,74,0.35);padding:6px 14px;border-radius:3px;background:rgba(255,216,74,0.06);';

  const credit = el('p', { parent: footer, text: 'Pallasite × 600bn' });
  credit.style.cssText = 'font:10px ui-monospace,monospace;color:rgba(255,245,216,0.45);letter-spacing:0.16em;margin:6px 0 0;';

  void state;
}

/** 600bn card — appears on every Sanctum game-over. Sacred-number wordmark +
 *  QR code to the canonical site so phone players can tap or scan without
 *  typing a URL. */
function renderSixHundredBnCard(overlay: HTMLElement): void {
  const card = el('div', { parent: overlay });
  card.style.cssText = [
    'display:flex', 'flex-direction:column', 'align-items:center',
    'gap:8px', 'padding:16px 20px', 'margin:8px 0',
    'background:linear-gradient(180deg, rgba(255,138,58,0.12), rgba(40,16,8,0.85))',
    'border:1px solid rgba(255,216,74,0.5)',
    'border-radius:8px',
    'max-width:420px', 'width:100%',
    'box-shadow:0 0 30px rgba(255,138,58,0.25)',
  ].join(';');

  // 4-line sacred number wordmark — canon-formatted.
  const number = el('div', { parent: card });
  number.style.cssText = 'font:bold 18px ui-monospace,monospace;color:#ffd84a;letter-spacing:0.16em;line-height:1.1;text-align:center;text-shadow:0 0 10px rgba(255,138,58,0.5);';
  const sigil=el('canvas',{parent:number}) as HTMLCanvasElement;
  sigil.width=sigil.height=256;sigil.style.cssText='width:92px;height:92px;';
  sigil.setAttribute('role','img');sigil.setAttribute('aria-label','600 billion · The Signal');
  sigil.getContext('2d')!.drawImage(signalRelicFaces().front,0,0);

  const banner = el('div', { parent: card, text: '600BN · THE SANCTUM' });
  banner.style.cssText = 'font:bold 13px ui-monospace,monospace;color:#fff5d8;letter-spacing:0.22em;margin-top:6px;';

  const venue = el('div', { parent: card, text: 'THE COUNCIL ALWAYS RETURNS' });
  venue.style.cssText = 'font:11px ui-monospace,monospace;color:rgba(255,245,216,0.75);letter-spacing:0.16em;';

  // QR code anchor — clickable so desktop users can just tap.
  const link = el('a', { parent: card }) as HTMLAnchorElement;
  link.href = 'https://600000000000.com';
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  link.style.cssText = 'display:flex;flex-direction:column;align-items:center;gap:4px;margin-top:6px;text-decoration:none;';

  const qrCanvas = el('canvas', { parent: link }) as HTMLCanvasElement;
  qrCanvas.style.cssText = 'background:#fff5d8;padding:6px;border-radius:4px;';
  void QRCode.toCanvas(qrCanvas, 'https://600000000000.com', {
    width: 140,
    margin: 0,
    color: { dark: '#0a0418', light: '#fff5d8' },
  }).catch(() => undefined);

  const url = el('div', { parent: link, text: 'OPEN 600BN' });
  url.style.cssText = 'font:bold 12px ui-monospace,monospace;color:#ffd84a;letter-spacing:0.2em;margin-top:4px;';
}

/** Stripped 600bn game-over — drops the kofi tip / honours / streak /
 *  replay-status / endorse buttons and shows only the bits that matter
 *  for the Sanctum funnel: score, 600bn card with QR, PLAY AGAIN, and a
 *  footer link. Idle auto-skip to title is suppressed so the card stays
 *  visible until the player taps. */
function renderSanctumGameOver(
  state: GameState,
  opts: { headerText: string; subText?: string; isCompletion?: boolean },
): void {
  clearOverlay();
  const overlay = el('div', { className: 'overlay', parent: root });
  setupOverlayArrowNav(overlay);
  overlay.style.background = 'rgba(6, 2, 1, 0.92)';

  // Header — "$600B WAVE COMPLETE" on a clear; "GAME OVER" on a death.
  const headerText = opts.isCompletion ? '$600B WAVE COMPLETE' : opts.headerText;
  const header = el('h2', { parent: overlay, text: headerText });
  header.style.cssText = 'font-family:ui-monospace,monospace;letter-spacing:0.22em;color:#ffd84a;text-shadow:0 0 16px rgba(255,138,58,0.7);margin:0 0 6px;';

  if (opts.subText) {
    const sub = el('p', { parent: overlay, text: opts.subText });
    sub.style.cssText = 'font-size:1.05rem;color:var(--hud-yellow);letter-spacing:0.22em;margin:0 0 8px;';
  }

  // Score only — no sats, no nostr-claim flow on the 600bn end screen.
  // Sanctum uses score only, not the per-run sat-credit pipeline.
  // Score is the leaderboard signal; sats stay on the main pallasite.app campaign.
  const stats = el('div', { parent: overlay });
  stats.style.cssText = 'display:flex;gap:32px;font:bold 22px ui-monospace,monospace;letter-spacing:0.18em;margin:8px 0 6px;';
  stats.innerHTML = `<span style="color:#ffd84a;text-shadow:0 0 12px rgba(255,138,58,0.55);">${state.players[0].score.toLocaleString()} PTS</span>`;

  renderSixHundredBnCard(overlay);

  // Run-stats panel — sits beneath the 600bn card so the player has
  // a beat of "here's what you did" before the action row. Time
  // formatted mm:ss; council shown as X/11 so the cycle is legible.
  const sr = getSanctumStats();
  const elapsedMs = sr.startedAt > 0 ? Math.max(0, performance.now() - sr.startedAt) : 0;
  const totalSeconds = Math.floor(elapsedMs / 1000);
  const mm = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
  const ss = String(totalSeconds % 60).padStart(2, '0');
  const statsPanel = el('div', { parent: overlay });
  statsPanel.style.cssText = [
    'display:flex', 'gap:24px', 'flex-wrap:wrap', 'justify-content:center',
    'padding:10px 16px', 'margin:10px 0 0',
    'background:rgba(8,4,2,0.6)',
    'border:1px solid rgba(255,216,74,0.25)',
    'border-radius:6px',
    'font:11px ui-monospace,monospace',
    'letter-spacing:0.18em',
    'color:rgba(255,245,216,0.85)',
    'max-width:420px', 'width:100%',
  ].join(';');
  const statCell = (label: string, value: string): string =>
    `<div style="display:flex;flex-direction:column;align-items:center;gap:2px;">`
    + `<span style="font-size:9px;color:rgba(255,216,74,0.55);">${label}</span>`
    + `<span style="font-size:15px;color:#ffd84a;text-shadow:0 0 6px rgba(255,138,58,0.4);">${value}</span>`
    + `</div>`;
  statsPanel.innerHTML =
    statCell('TIME', `${mm}:${ss}`)
    + statCell('COUNCIL', `${sr.councilDefeated}/${sr.councilTotal || 11}`)
    + statCell('ASTEROIDS', String(sr.asteroidsDestroyed));

  // Action row — PLAY AGAIN restarts wave 1; BACK returns to the
  // attract screen so the player can choose to leave or replay.
  const row = el('div', { className: 'menu-row', parent: overlay });
  row.style.cssText = 'margin-top:18px;gap:12px;';

  const playBtn = el('button', { className: 'menu-btn', parent: row, text: 'PLAY AGAIN ▶' }) as HTMLButtonElement;
  playBtn.style.cssText += 'font-size:1.05rem;padding:12px 24px;letter-spacing:0.22em;background:rgba(255,216,74,0.18);border-color:#ffd84a;color:#ffd84a;text-shadow:0 0 8px rgba(255,216,74,0.5);';
  playBtn.addEventListener('click', () => {
    void audio.unlockAudio();
    onStartCb?.();
  });

  const backBtn = el('button', { className: 'menu-btn secondary', parent: row, text: 'BACK TO TITLE' }) as HTMLButtonElement;
  backBtn.style.cssText += 'font-size:0.95rem;padding:12px 24px;letter-spacing:0.2em;';
  backBtn.addEventListener('click', () => {
    state.phase = 'title';
    renderAttract(state);
  });

  // Footer — 600bn link as a discreet line beneath the row.
  const partyLink = el('a', { parent: overlay, text: '600BN · OPEN THE SIGNAL ↗' }) as HTMLAnchorElement;
  partyLink.href = 'https://600000000000.com';
  partyLink.target = '_blank';
  partyLink.rel = 'noopener noreferrer';
  partyLink.style.cssText = 'font:11px ui-monospace,monospace;color:rgba(255,216,74,0.7);letter-spacing:0.2em;margin-top:18px;text-decoration:none;';
}

function renderCoopCampaignBanner(parent: HTMLElement, state: GameState): void {
  const banner = el('div', { parent });
  banner.style.cssText = 'display:flex;flex-direction:column;align-items:center;gap:10px;margin:8px auto 18px;max-width:620px;';

  const verdict = el('p', { parent: banner, text: 'CO-OP CREW' });
  verdict.style.cssText = 'margin:0;font-size:1.45rem;letter-spacing:0.25em;color:var(--hud-yellow);text-shadow:0 0 10px rgba(255,216,74,0.45);';

  const total = runLeaderboardScore(state);
  const meta = el('p', { parent: banner, text: `${total.toLocaleString()} TEAM SCORE · WAVE ${state.wave} · NO SATS PAYOUT` });
  meta.style.cssText = 'margin:-4px 0 0;font:0.8rem ui-monospace,monospace;color:rgba(220,230,255,0.72);letter-spacing:0.12em;text-align:center;';

  const row = el('div', { parent: banner });
  row.style.cssText = 'display:flex;gap:18px;justify-content:center;align-items:stretch;flex-wrap:wrap;';
  for (let i = 0; i < Math.min(2, state.players.length); i++) {
    const wrap = el('div', { parent: row });
    wrap.style.cssText = 'min-width:160px;padding:10px 18px;border-radius:8px;text-align:center;border:1px solid rgba(140,255,180,0.32);background:rgba(60,200,140,0.06);';
    const label = el('div', { parent: wrap, text: `P${i + 1}` });
    label.style.cssText = 'font-family:monospace;font-size:0.8rem;letter-spacing:0.2em;color:rgba(140,255,180,0.72);';
    const score = el('div', { parent: wrap, text: state.players[i].score.toLocaleString() });
    score.style.cssText = 'font-size:1.55rem;letter-spacing:0.05em;color:#dcffe8;margin:2px 0;';
    const lives = el('div', { parent: wrap, text: `${Math.max(0, state.players[i].lives)} lives` });
    lives.style.cssText = 'font-size:0.78rem;color:rgba(220,255,232,0.62);font-family:monospace;';
  }
}

/** Side-by-side score panel for couch / duel runs. WINNER gets a yellow
 *  highlight; equal-score runs render DRAW. Inserts right after the
 *  gameover header so it's the first thing the player reads, then the
 *  standard recap (publish / claim / honours / replay etc.) follows. */
function renderVersusBanner(parent: HTMLElement, state: GameState): void {
  const banner = el('div', { parent });
  banner.style.cssText = 'display:flex;flex-direction:column;align-items:center;gap:10px;margin:8px auto 18px;max-width:560px;';

  const s0 = state.players[0].score;
  const s1 = state.players[1].score;
  const tie = s0 === s1;
  const winner = tie ? -1 : (s0 > s1 ? 0 : 1);
  const verdict = el('p', { parent: banner, text: tie ? 'DRAW' : `WINNER · P${winner + 1}` });
  verdict.style.cssText = 'margin:0;font-size:1.45rem;letter-spacing:0.25em;color:' +
    (tie ? 'rgba(220,210,255,0.85)' : 'var(--hud-yellow)') +
    ';text-shadow:0 0 10px rgba(255,216,74,0.45);';

  const row = el('div', { parent: banner });
  row.style.cssText = 'display:flex;gap:24px;justify-content:center;align-items:stretch;flex-wrap:wrap;';

  const card = (idx: number): HTMLElement => {
    const isWinner = winner === idx;
    const wrap = el('div', { parent: row });
    wrap.style.cssText = 'min-width:160px;padding:10px 18px;border-radius:8px;text-align:center;border:1px solid ' +
      (isWinner ? 'rgba(255,216,74,0.55)' : 'rgba(184,144,255,0.30)') +
      ';background:' + (isWinner ? 'rgba(255,216,74,0.08)' : 'rgba(120,90,200,0.06)') + ';';
    const label = el('div', { parent: wrap, text: `P${idx + 1}` });
    label.style.cssText = 'font-family:monospace;font-size:0.8rem;letter-spacing:0.2em;color:rgba(180,140,255,0.7);';
    const score = el('div', { parent: wrap, text: state.players[idx].score.toLocaleString() });
    score.style.cssText = 'font-size:1.7rem;letter-spacing:0.05em;color:' +
      (isWinner ? 'var(--hud-yellow)' : '#dccfff') + ';margin:2px 0;';
    const meta = el('div', { parent: wrap, text: `${state.players[idx].sats} sats · wave ${state.wave}` });
    meta.style.cssText = 'font-size:0.78rem;color:rgba(220,210,255,0.65);font-family:monospace;';
    return wrap;
  };
  card(0);
  card(1);
}

/** Sign out from an end-of-run surface: ends the Signet session (a no-op for
 *  guest identities), forgets a local guest so the device is a clean slate, and
 *  clears the in-memory session before returning to attract — the same reset the
 *  title's account panel performs, surfaced where a player on the public site
 *  actually lands after a run. */
async function signOutAndReturnToAttract(state: GameState): Promise<void> {
  const wasGuest = state.session ? isGuestSession(state.session) : false;
  try { await auth.signOut(state.session); } catch { /* ignore */ }
  if (wasGuest) clearGuestIdentity();
  state.session = null;
  state.profile = null;
  renderAttract(state);
}

function renderRunCredits(
  state: GameState,
  opts: { headerText: string; subText?: string; isCompletion?: boolean; idleSeconds?: number; endingChoice?: 'return' | 'forged' },
): void {
  // 600bn flavour gets a focused Sanctum game-over instead
  // of the standard run credits — no kofi tip, no honours, no replay
  // badge, no endorse buttons. Just stats + 600bn card + replay.
  if (getFlavour() === '600bn') {
    renderSanctumGameOver(state, opts);
    return;
  }
  const idleSeconds = opts.idleSeconds ?? 45;
  clearOverlay();
  const overlay = el('div', { className: 'overlay', parent: root });
  setupOverlayArrowNav(overlay);
  if (opts.isCompletion) overlay.style.background = 'rgba(0, 0, 0, 0.85)';

  // Logo on completion only — gameover keeps text-only header so the
  // wordmark remains a "you finished it" reward.
  if (opts.isCompletion) {
    const logo = el('img', { parent: overlay });
    logo.className = 'title-logo';
    (logo as HTMLImageElement).src = '/logo.webp';
    (logo as HTMLImageElement).alt = 'PALLASITE';
    (logo as HTMLImageElement).decoding = 'async';
  }

  el('h2', { parent: overlay, text: opts.headerText });
  if (opts.subText) {
    const sub = el('p', { parent: overlay, text: opts.subText });
    sub.style.cssText = 'font-size:1.1rem;color:var(--hud-yellow);letter-spacing:0.25em;text-shadow:0 0 8px rgba(255,216,74,0.5);margin:-10px 0 4px;';
  }

  // Booth: show who played — the signed-in player(s) avatar + name.
  if (boothKiosk && state.session) {
    const idRow = el('div', { parent: overlay });
    idRow.style.cssText = 'display:flex;gap:28px;align-items:center;justify-content:center;margin:8px 0 10px;flex-wrap:wrap;';
    renderIdentityChip(idRow, state.session.pubkey, state.session.displayName ?? '', 'PLAYER 1', PLAYER_COLOURS[0]);
    if (state.coopIdentity2) renderIdentityChip(idRow, state.coopIdentity2.pubkey, state.coopIdentity2.displayName, 'PLAYER 2', PLAYER_COLOURS[1]);
  }

  // Slice 5 — the chosen ending (Return / Step Through) reflected on the
  // completion card: a tag + the Pallasite Mark welcome line. The choice itself
  // is made on renderEndingChoice; see docs/pallasite-arc.md §6–7.
  if (opts.endingChoice) {
    const forged = opts.endingChoice === 'forged';
    const tag = el('p', { parent: overlay, text: forged ? 'YOU STEPPED THROUGH · FORGESWORN' : 'YOU RETURNED' });
    tag.style.cssText = `font-size:0.95rem;letter-spacing:0.22em;margin:6px 0 0;color:${forged ? '#9be15d' : '#8fd0ff'};text-shadow:0 0 8px ${forged ? 'rgba(155,225,93,0.45)' : 'rgba(143,208,255,0.45)'};`;
    const line = el('p', { parent: overlay, text: forged ? 'Welcome back, forgesworn.' : 'Welcome back. The chain is shorter than you remember.' });
    line.style.cssText = 'font:italic 0.9rem ui-monospace,monospace;color:rgba(255,245,216,0.65);letter-spacing:0.05em;margin:2px 0 8px;max-width:480px;text-align:center;';

    // Slice 6 — publish the Mark to Nostr (a self-signed NIP-85 kind 30382
    // assertion). The local Mark is already written by chooseEnding; this seals
    // it on relays. Best-effort + async: the status line below updates when it
    // resolves, and a failure never blocks the recap. See mark.ts.
    const markStatus = el('p', { parent: overlay, text: '' });
    markStatus.style.cssText = 'font:0.74rem ui-monospace,monospace;color:rgba(180,150,230,0.72);letter-spacing:0.1em;margin:0 0 10px;min-height:1.1em;';
    void sealPallasiteMark(state, opts.endingChoice, markStatus);
  }

  // Versus banner: when this was a 2-player run (couch or duel), show both
  // players' scores side-by-side with a WINNER call-out. The rest of the
  // recap stays as-is (player[0]-coupled ghost / claim flows; duel runs
  // are friendly only so no stakes attach to the credit anyway).
  if (state.players.length === 2) {
    if (isCoopCampaignMode()) renderCoopCampaignBanner(overlay, state);
    else renderVersusBanner(overlay, state);
  }

  // Arc-aware fall line — frames the loss as a story beat rather than a bare
  // wave number. Campaign game-overs only; gameOverArcLine returns null for
  // drift-mode waves (26+). See docs/pallasite-arc.md §5.
  if (!opts.isCompletion) {
    const arcLine = gameOverArcLine(state.wave);
    if (arcLine) {
      const arc = el('p', { parent: overlay, text: arcLine });
      arc.style.cssText = 'font:italic 0.92rem ui-monospace,monospace;color:rgba(255,245,216,0.6);letter-spacing:0.06em;margin:2px 0 12px;max-width:480px;text-align:center;';
    }
  }

  // 600bn flavour runs surface the 600bn card above the recap
  // picker so every Sanctum game-over funnels traffic back to 600.wtf.
  if (getFlavour() === '600bn') {
    renderSixHundredBnCard(overlay);
  }

  // Honours surface on completion runs only.
  if (opts.isCompletion) renderHonours(overlay, state, () => { /* no stagger */ });

  // Co-op campaign runs publish their own no-payout score here; solo scores
  // are queued separately by publishSoloScoreForRun. There is no claim flow.
  const publishWrap = el('div', { parent: overlay });
  if (isCoopCampaignMode()) void maybePublishCoopCampaignScore(state, publishWrap);

  // Best-effort kind 30763 ghost publish. Independent of the score claim so
  // sign-capable sessions leave a ghost trail even when they don't claim
  // (or the faucet is down). publishGhost no-ops on cheated / read-only /
  // sub-2-sample runs, so unconditional invocation is safe. v2 (pose) is
  // emitted automatically when the daily-mode pose stream is non-empty;
  // free runs publish v1 (score-only).
  // Personal ghost — always save when a daily seed was active so the
  // player can race themselves on their next attempt. The setting only
  // gates RENDERING; saving happens regardless so enabling later picks
  // up the most recent run rather than waiting for a fresh one.
  const seed = getActiveSeed();
  if (seed && state.ghostPoseSamples.length >= 2 && !state.cheatedThisRun) {
    savePersonalGhost({
      seed,
      score: state.players[0].score,
      wave: state.wave,
      durationMs: Math.max(0, Math.floor(state.runTimeMs)),
      poseSamples: state.ghostPoseSamples.slice(),
      lastSavedAt: 0,  // overwritten by savePersonalGhost
    });
  }
  // Mark today's daily run as completed for the streak counter. Cheated
  // runs don't count — streak should reflect honest play. Free-mode
  // runs (no seed) don't count either; only daily seeds drive the
  // streak. After the mark, check streak milestones and fire badges.
  if (seed && !state.cheatedThisRun) {
    markDailyCompleted(seed);
    const s = getStreak();
    if (s >= 30) markAchievement(state, 'streak-30');
    if (s >= 5)  markAchievement(state, 'streak-5');
  }
  // Replay-publish status badge — visible to the player on the
  // game-over screen so they don't need to open dev tools. Inserted
  // after the score/sats summary; replaced once publishReplay's
  // promise resolves.
  const replayStatus = el('div', { parent: overlay });
  replayStatus.style.cssText = 'margin:8px auto;padding:6px 12px;border-radius:6px;font-family:monospace;font-size:0.78rem;letter-spacing:0.08em;max-width:520px;text-align:center;';
  const setReplayBadge = (kind: 'pending' | 'ok' | 'skip' | 'fail', text: string): void => {
    const COLOURS: Record<typeof kind, [string, string]> = {
      pending: ['rgba(255,216,74,0.85)', 'rgba(255,216,74,0.18)'],
      ok:      ['#8cffb4',              'rgba(140,255,180,0.18)'],
      skip:    ['rgba(220,210,255,0.7)', 'rgba(180,140,255,0.10)'],
      fail:    ['rgba(255,120,120,0.95)', 'rgba(255,120,120,0.15)'],
    };
    const [fg, bg] = COLOURS[kind];
    replayStatus.style.color = fg;
    replayStatus.style.background = bg;
    replayStatus.style.border = `1px solid ${fg}55`;
    replayStatus.textContent = text;
  };

  if (state.session) {
    console.log(`[replay] game-over · session=${state.session.pubkey.slice(0, 8)}… cheated=${state.cheatedThisRun} score=${state.players[0].score} wave=${state.wave}`);
    void publishGhost({
      session: state.session,
      samples: state.ghostSamples,
      poseSamples: state.ghostPoseSamples,
      finalScore: state.players[0].score,
      finalWave: state.wave,
      durationMs: Math.max(0, Math.floor(state.runTimeMs)),
      seed: getActiveSeed(),
      cheated: state.cheatedThisRun,
    });
    // Full-world replay (kind 30764) — built from the buffered wire
    // frames we shipped at 30 Hz during the run. Cheated runs and
    // sub-2-frame runs are skipped (publishReplay validates internally).
    // Decoupled from the score claim — sign-capable sessions leave a
    // replay even when they don't claim, same policy as the ghost.
    if (state.cheatedThisRun) {
      console.warn('[replay] skipping kind 30764 — run was cheated');
      setReplayBadge('skip', '⚠ REPLAY SKIPPED · cheated this run (sat-void on first cheat)');
    } else {
      // Blossom-style single-blob publish: gzip the whole run → PUT to
      // faucet's content-addressed store → sign ONE kind 30764 pointer
      // event tagging the URL + hash. Two signs total for the replay
      // (NIP-98 auth + pointer event). Replaces the previous per-wave
      // chunking pipeline that signed 25+ events sequentially and
      // wedged bunker-backed signers under sustained load.
      const remainingBuffer = getReplayBuffer();
      console.log(`[replay] game-over · ${remainingBuffer.length} frame(s) in buffer`);
      if (remainingBuffer.length < 2) {
        setReplayBadge('fail', `✗ REPLAY EMPTY · only ${remainingBuffer.length} frame(s) buffered`);
      } else {
        const session = state.session;
        const score = state.players[0].score;
        const finalWave = state.wave;
        const durationMs = Math.max(0, Math.floor(state.runTimeMs));
        // Cache the gzipped frames across retries. First click gzips
        // (~500ms for a 14k-frame run on a phone); subsequent RETRY
        // clicks reuse the same bytes. Caller-side caching keeps
        // publishReplay's interface clean — it doesn't need to know
        // about retry semantics.
        let cachedGzip: Uint8Array | null = null;
        // Tells the player WHAT to do, not just THAT it failed. Most
        // common failure mode: bark's service worker fell asleep mid-
        // session and the signEvent times out. The remedy is opening
        // the extension to wake it.
        const signerHint = 'Your Nostr signer didn\'t respond. Open your extension (bark / nsec.app / your bunker), make sure it\'s unlocked, then click RETRY.';
        const renderRetryBadge = (msg: string, hint: string): void => {
          replayStatus.innerHTML = '';
          replayStatus.style.color = 'rgba(255,120,120,0.95)';
          replayStatus.style.background = 'rgba(255,120,120,0.15)';
          replayStatus.style.border = '1px solid rgba(255,120,120,0.55)';
          const line = el('span', { parent: replayStatus, text: msg + ' · ' });
          void line;
          const retry = el('button', { parent: replayStatus, text: 'RETRY' });
          retry.style.cssText = 'background:rgba(255,255,255,0.08);border:1px solid rgba(255,120,120,0.7);color:#fff;padding:2px 10px;border-radius:4px;font-family:monospace;font-size:0.78rem;letter-spacing:0.1em;cursor:pointer;margin-left:6px;';
          retry.addEventListener('click', tryPublish);
          const hintEl = el('div', { parent: replayStatus, text: hint });
          hintEl.style.cssText = 'font-size:0.72rem;margin-top:4px;color:rgba(220,210,255,0.75);letter-spacing:0.04em;line-height:1.4;';
        };
        const tryPublish = (): void => {
          setReplayBadge('pending', `📼 REPLAY · ${cachedGzip ? 'retrying' : 'uploading'} ${remainingBuffer.length} frames…`);
          void (async () => {
            try {
              if (!cachedGzip) {
                cachedGzip = await gzipReplayFrames(remainingBuffer);
                if (!cachedGzip) {
                  renderRetryBadge('✗ REPLAY FAILED · gzip error', 'Compression failed on your device. Try again, or play a shorter run.');
                  return;
                }
                console.log(`[replay] gzipped ${remainingBuffer.length} frames → ${cachedGzip.byteLength}B (cached for retries)`);
              }
              const signed = await publishReplay({
                session,
                finalScore: score,
                finalWave,
                durationMs,
                frames: remainingBuffer,
                gzippedFrames: cachedGzip,
              });
              if (signed) setReplayBadge('ok', `✓ REPLAY PUBLISHED · ${signed.id.slice(0, 8)}…`);
              else renderRetryBadge('✗ REPLAY FAILED', signerHint);
            } catch (err) {
              renderRetryBadge(`✗ REPLAY THREW · ${err instanceof Error ? err.message : String(err)}`, signerHint);
            }
          })();
        };
        tryPublish();
      }
    }
  } else {
    console.warn('[replay] no kind 30763/30764 published — guest run (state.session is null). Sign in to enable replays.');
    setReplayBadge('skip', '— REPLAY OFF · guest mode (sign in to publish replays)');
  }

  // Prominent zap CTA — the entire reason this stage exists per the user
  // brief: "make zaps easy and frictionless".
  const zapWrap = el('div', { parent: overlay });
  // Centered static LNURL-pay QR first — scan-to-send-any-amount, works with
  // no session (plain LNURL pay), the venue "pay us with sats" path. The
  // amount-preset buttons below stay as the in-app / WebLN one-click option.
  renderZapUsQR(zapWrap, state);
  renderZapButton(zapWrap, state);

  // Social actions only land for Nostr mode (follow + announce buttons).
  if (state.session) {
    const socialWrap = el('div', { parent: overlay });
    renderSocialActions(socialWrap, state);
  }

  // Credits scroll — 24 specimens + powered-by + lore. Completion only:
  // on a regular gameover the 460px panel sits empty for ~6s while the inner
  // scrolls in, which dominates the screen when there's little above it
  // (especially when not signed in, since faucet status + social actions
  // are skipped). Beating the game earns the credits; dying does not.
  if (opts.isCompletion) {
    renderCreditsRoll(overlay, undefined, state);
  }

  // Auto-skip timer — counts down to TITLE. Resets on any user input
  // (keydown OR pointerdown — mobile users tap, never fire keydown, so
  // a key-only listener kept ticking through their interaction). The
  // SKIP TO TITLE button exits immediately.
  let secondsLeft = idleSeconds;
  const skipBar = el('p', { parent: overlay });
  skipBar.style.cssText = 'font-size:0.85rem;letter-spacing:0.18em;color:#ffd84a;margin:6px 0 0;';
  const renderSkip = (): void => {
    skipBar.textContent = `RETURNING TO TITLE IN ${secondsLeft}`;
  };
  renderSkip();

  const goToTitle = (): void => {
    cleanup();
    clearEntitiesForTitle(state);
    state.phase = 'title';
    renderTitle(state);
  };
  const idleTick = window.setInterval(() => {
    secondsLeft -= 1;
    renderSkip();
    if (secondsLeft <= 0) goToTitle();
  }, 1000);
  const resetIdle = (): void => { secondsLeft = idleSeconds; renderSkip(); };
  const onActivity = (): void => resetIdle();
  window.addEventListener('keydown', onActivity);
  // Pointerdown covers mouse, pen, AND touch — so a tap on mobile
  // counts as "still here" the same way a key press does on desktop.
  window.addEventListener('pointerdown', onActivity);
  const cleanup = (): void => {
    window.clearInterval(idleTick);
    window.removeEventListener('keydown', onActivity);
    window.removeEventListener('pointerdown', onActivity);
  };

  const row = el('div', { className: 'menu-row', parent: overlay });
  if (!opts.isCompletion) {
    const again = el('button', { className: 'menu-btn', parent: row, text: 'SPAWN AGAIN' });
    onTap(again, () => {
      cleanup();
      const couchMode = new URLSearchParams(window.location.search).has('couch');
      startGame(state, undefined, { players: couchMode ? 2 : 1 });
      onStartCb?.();
    });
    if (state.deathReplay) {
      const replay = el('button', { className: 'menu-btn secondary', parent: row, text: 'REPLAY KILL' });
      onTap(replay, () => {
        cleanup();
        clearOverlay();
        startDeathReplay(state);
      });
      // SHARE CLIP — re-trigger the death replay AND record the canvas
      // for the same window via MediaRecorder, then hand the resulting
      // file to the system share sheet. Only shown when the recording
      // path is supported (MediaRecorder + canvas.captureStream).
      if (canCaptureClip()) {
        const clip = el('button', { className: 'menu-btn secondary', parent: row, text: 'SHARE CLIP' }) as HTMLButtonElement;
        onTap(clip, () => {
          clip.disabled = true;
          clip.textContent = 'CAPTURING…';
          clip.style.opacity = '0.6';
          const canvas = document.getElementById('game') as HTMLCanvasElement | null;
          if (!canvas) {
            clip.disabled = false;
            clip.textContent = 'SHARE CLIP';
            clip.style.opacity = '1';
            return;
          }
          // Trigger replay first so frames flow, then capture for the
          // full replay window plus a small tail so the explosion lands.
          startDeathReplay(state);
          const captureMs = REPLAY_TOTAL_WALL_MS + REPLAY_EXPLOSION_WALL_MS + 200;
          void captureClip(canvas, captureMs).then(async (captured) => {
            if (!captured) {
              clip.disabled = false;
              clip.textContent = 'SHARE CLIP';
              clip.style.opacity = '1';
              return;
            }
            const result = await shareClip(captured, {
              filenameStem: `pallasite-w${state.wave}-s${state.players[0].score}`,
              title: 'Pallasite',
              text: `W${state.wave} · ${state.players[0].score} score · pallasite.app`,
            });
            clip.disabled = false;
            clip.textContent = result === 'shared' ? 'SHARED ✓' : result === 'downloaded' ? 'DOWNLOADED ✓' : 'CLIP FAILED';
            clip.style.opacity = '1';
          });
        });
      }
    }
  } else {
    const again = el('button', { className: 'menu-btn', parent: row, text: 'IGNITE AGAIN' });
    onTap(again, () => {
      cleanup();
      const couchMode = new URLSearchParams(window.location.search).has('couch');
      startGame(state, undefined, { players: couchMode ? 2 : 1 });
      onStartCb?.();
    });
  }
  // SHARE CARD — system share sheet with a rendered run summary image.
  // Available to guests (no auth needed); complements the signed-in
  // SHARE / FOLLOW / ENDORSE row that publishes to Nostr relays.
  const shareBtn = el('button', { className: 'menu-btn secondary', parent: row, text: 'SHARE CARD' }) as HTMLButtonElement;
  onTap(shareBtn, () => {
    shareBtn.disabled = true;
    shareBtn.style.opacity = '0.6';
    void shareRunCard(state).finally(() => {
      shareBtn.disabled = false;
      shareBtn.style.opacity = '1';
    });
  });
  // SHARE STATS — Wordle-style one-liner. Daily-mode only (the format
  // depends on the daily ordinal); free runs would just be "Pallasite ·
  // W12 · 8.4k" with no comparison hook so we hide the button there.
  const dailySeed = getActiveSeed();
  if (dailySeed) {
    const statsBtn = el('button', { className: 'menu-btn secondary', parent: row, text: 'SHARE STATS' }) as HTMLButtonElement;
    onTap(statsBtn, () => {
      const text = buildDailyShareText({
        seed: dailySeed,
        score: state.players[0].score,
        wave: state.wave,
        bossDefeated: state.bossDefeated,
        largestCombo: state.players[0].runStats.largestCombo,
        veinsBroken: state.players[0].runStats.veinsBroken,
        cleared: opts.isCompletion === true,
      });
      statsBtn.disabled = true;
      statsBtn.style.opacity = '0.6';
      void shareDailyStats(text).then((result) => {
        statsBtn.disabled = false;
        statsBtn.style.opacity = '1';
        statsBtn.textContent = result === 'shared' ? 'SHARED ✓' : result === 'copied' ? 'COPIED ✓' : 'SHARE FAILED';
      });
    });
  }
  const home = el('button', { className: 'menu-btn secondary', parent: row, text: 'SKIP TO TITLE' });
  onTap(home, goToTitle);

  // Sign out — surfaced on the recap because this is where a player on the
  // public site actually wants to hand the device over or switch accounts; the
  // only other sign-out was buried in the title's account panel. Its own row so
  // it never sits beside SPAWN AGAIN as a mis-tap. Guests get it too (forgets
  // the local identity). Omitted on booth kiosks — those re-sign every group
  // through the join wizard, so a stray sign-out here would just confuse staff.
  if (state.session && !boothKiosk) {
    const signOutRow = el('div', { className: 'menu-row', parent: overlay });
    const signOut = el('button', { className: 'menu-btn secondary', parent: signOutRow, text: 'SIGN OUT' }) as HTMLButtonElement;
    signOut.style.cssText += 'font-size:0.78rem;color:rgba(255,120,120,0.85);border-color:rgba(255,120,120,0.4);';
    onTap(signOut, () => { cleanup(); void signOutAndReturnToAttract(state); });
  }

  renderLegalFooter(overlay);
}

export function renderCompletion(state: GameState): void {
  // Same identity-driven high-score handling as renderGameOver — auto-
  // save under the session display name, advance to celebration. The
  // 4-char arcade picker pre-stage is gone now that every run has a
  // signed-in identity attached at the front door.
  const board = activeScoreBoard();
  const leaderboardScore = runLeaderboardScore(state);
  const isNewHigh = isHighScore(leaderboardScore, board) && leaderboardScore > 0;
  if (isNewHigh && !state.initialsEnteredThisRun) {
    addLocalHighScore({
      name: scoreboardNameFor(state),
      score: leaderboardScore,
      sats: runLeaderboardSats(state),
      wave: 25,
      at: new Date().toISOString(),
      pubkey: state.session?.pubkey,
    }, board);
    state.initialsEnteredThisRun = true;
  }
  // Publish a verified Gamestr score for a COMPLETED run too (winning the
  // Forge is a player's best score — it must reach the leaderboard).
  publishSoloScoreForRun(state);
  // Slice 5 — beating the Forge earns THE CHOICE (Return / Step Through) before
  // the recap. 600bn (Sanctum) has no W25 gate, so it goes straight to credits.
  if (getFlavour() === '600bn') { renderCompletionRecap(state); return; }
  renderEndingChoice(state);
}

function renderCompletionRecap(state: GameState, choice?: 'return' | 'forged'): void {
  // 600bn flavour completion swaps the wave-25 'EVENT HORIZON ·
  // BREACHED' framing for canonical 600B copy — wave 1 IS the whole
  // experience here, so 'COMPLETE' is what mattered.
  const is600bn = getFlavour() === '600bn';
  renderRunCredits(state, {
    headerText: is600bn ? '$600B WAVE COMPLETE' : 'PALLASITE COMPLETE',
    subText: is600bn ? 'COUNCIL CLEARED' : 'EVENT HORIZON · BREACHED',
    isCompletion: true,
    endingChoice: choice,
  });
}

/** The Pallasite Mark — the player's chosen W25 ending, persisted locally.
 *  Slice 5 stores it; Slice 6 will publish it as a self-signed NIP-85 claim
 *  (docs/pallasite-arc.md §6–7). 'return' = carried the chain home; 'forged' =
 *  stepped through the gate. */
const PALLASITE_MARK_KEY = 'pallasite:mark';
export function getPallasiteMark(): { mark: 'return' | 'forged'; wave: number; at: string } | null {
  try {
    const raw = localStorage.getItem(PALLASITE_MARK_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    return (v && (v.mark === 'return' || v.mark === 'forged')) ? v : null;
  } catch { return null; }
}
function setPallasiteMark(mark: 'return' | 'forged'): void {
  try { localStorage.setItem(PALLASITE_MARK_KEY, JSON.stringify({ mark, wave: 25, at: new Date().toISOString() })); } catch { /* ignore */ }
}

/** THE GATE — the W25 ending choice. Beating the Forge earns it: carry the chain
 *  home (Return) or step through and become one of the makers (Forged). Cosmetic
 *  + narrative; the choice persists to the Mark. Two lines of arc copy per
 *  docs/pallasite-arc.md §3.4. */
function renderEndingChoice(state: GameState): void {
  clearOverlay();
  const overlay = el('div', { className: 'overlay', parent: root });
  overlay.style.background = 'rgba(0, 0, 0, 0.9)';
  setupOverlayArrowNav(overlay);

  el('h2', { parent: overlay, text: 'THE GATE' }).style.cssText = 'margin:0 0 6px;font-size:1.7rem;letter-spacing:0.32em;color:var(--hud-yellow);text-shadow:0 0 14px rgba(255,216,74,0.5);';
  el('p', { parent: overlay, text: 'The forge falls. The line ends here.' }).style.cssText = 'margin:0;font:italic 0.95rem ui-monospace,monospace;color:rgba(255,245,216,0.7);letter-spacing:0.08em;';
  el('p', { parent: overlay, text: 'Stand or fall. Then choose.' }).style.cssText = 'margin:2px 0 20px;font:italic 0.95rem ui-monospace,monospace;color:rgba(255,245,216,0.7);letter-spacing:0.08em;';

  const choices = el('div', { parent: overlay });
  choices.style.cssText = 'display:flex;flex-direction:column;gap:14px;width:100%;max-width:480px;';

  const makeChoice = (glyph: string, title: string, body: string, choice: 'return' | 'forged'): void => {
    const btn = el('button', { className: 'menu-btn', parent: choices });
    btn.style.cssText = 'display:flex;flex-direction:column;align-items:flex-start;gap:4px;text-align:left;padding:14px 18px;width:100%;white-space:normal;';
    el('span', { parent: btn, text: `${glyph}  ${title}` }).style.cssText = 'font-size:1.1rem;letter-spacing:0.12em;color:var(--hud-yellow);';
    el('span', { parent: btn, text: body }).style.cssText = 'font-size:0.82rem;color:rgba(220,210,255,0.78);letter-spacing:0.04em;line-height:1.45;';
    btn.addEventListener('click', () => chooseEnding(state, choice));
  };
  makeChoice('↩', 'RETURN', 'Carry the chain home. Warn the others. Bank the run.', 'return');
  makeChoice('⟴', 'STEP THROUGH', 'Keep the chain. Become one of the makers. The next run knows your name.', 'forged');
}

function chooseEnding(state: GameState, choice: 'return' | 'forged'): void {
  setPallasiteMark(choice);
  renderCompletionRecap(state, choice);
}

/** Seal the Mark on Nostr (Slice 6) and reflect progress on the recap. The local
 *  Mark is the source of truth (already written by chooseEnding); this is the
 *  publish layer. Best-effort: a read-only session, an unreachable relay, or a
 *  declined signature all fall back to "kept on this device" — the recap never
 *  breaks. Signing a guest event is silent; an extension/Signet signer may
 *  prompt, which is a fair consequence of the player's explicit gate choice. */
async function sealPallasiteMark(state: GameState, mark: 'return' | 'forged', statusEl: HTMLElement): Promise<void> {
  const session = state.session;
  if (!session || !session.signer.capabilities.canSignEvents) {
    statusEl.textContent = '◇ MARK KEPT ON THIS DEVICE';
    return;
  }
  statusEl.textContent = '◈ SEALING YOUR MARK…';
  try {
    const res = await publishPallasiteMark(session, mark);
    statusEl.textContent = res?.relayed ? '◆ MARK SEALED ON NOSTR' : '◇ MARK KEPT ON THIS DEVICE';
  } catch {
    statusEl.textContent = '◇ MARK KEPT ON THIS DEVICE';
  }
}

/**
 * Honours strip — small badges summarising notable run conditions. Surfaced
 * above the credits so a player who had a clean run gets immediate recognition
 * without waiting for the scroll. Quiet strip when no honours apply.
 */
function renderHonours(parent: HTMLElement, state: GameState, applyStage: (e: HTMLElement) => void): void {
  const honours: Array<{ label: string; cls: string }> = [];
  // Lives lost during the run = livesStart - lives remaining + extra-life pickups
  // is hard to know without telemetry, so a "PERFECT" badge is gated on still
  // holding all 3 (or whatever default difficulty grants). Easy / Hard differ.
  if (state.players[0].lives >= 3) honours.push({ label: 'PERFECT RUN', cls: 'honour-perfect' });
  if (!state.players[0].lurkEverDetected) honours.push({ label: 'NO LURKING', cls: 'honour-no-lurk' });
  honours.push({ label: 'COMPLETIONIST · 24/24', cls: 'honour-completionist' });

  if (honours.length === 0) return;
  const row = el('div', { className: 'honours-row', parent });
  for (const h of honours) {
    el('span', { className: `honour-badge ${h.cls}`, parent: row, text: h.label });
  }
  applyStage(row);
}

function renderSocialActions(parent: HTMLElement, state: GameState): void {
  const block = el('div', { parent });
  block.style.cssText = 'display:flex;flex-direction:column;align-items:center;gap:10px;margin-top:6px;';

  const status = el('p', { parent: block });
  status.style.cssText = 'font-size:0.85rem;color:rgba(180,140,255,0.7);letter-spacing:0.05em;margin:0;height:1em;';

  const row = el('div', { className: 'menu-row', parent: block });
  row.style.gap = '10px';

  const setStatus = (text: string, colour: string): void => {
    status.textContent = text;
    status.style.color = colour;
  };

  const session = state.session;
  const canSign = session?.signer.capabilities.canSignEvents === true;

  function makeBtn(label: string, sub: string, colour: string, run: () => Promise<void>): HTMLButtonElement {
    const btn = el('button', { parent: row });
    btn.innerHTML = `<span style="display:block;font-size:0.95rem;letter-spacing:0.12em;">${label}</span><span style="display:block;font-size:0.62rem;letter-spacing:0.08em;opacity:0.55;margin-top:2px;">${sub}</span>`;
    btn.style.cssText = [
      'background:transparent', `border:2px solid ${colour}`, `color:${colour}`,
      "font-family:'VT323',ui-monospace,monospace",
      'padding:8px 14px', 'cursor:pointer', 'border-radius:6px',
      'transition:all 0.12s ease', `text-shadow:0 0 6px ${colour}99`,
      `box-shadow:0 0 10px ${colour}33`,
    ].join(';');
    if (!canSign) btn.style.opacity = '0.45';
    onTap(btn, async () => {
      if (!canSign) {
        setStatus('Sign in with Bark or bunker to use Nostr actions.', '#ff8a3a');
        return;
      }
      const original = btn.innerHTML;
      btn.disabled = true;
      btn.style.opacity = '0.6';
      try {
        await run();
      } finally {
        btn.disabled = false;
        btn.style.opacity = '1';
        // Keep label change if run() updated innerHTML; else restore
        if (btn.innerHTML === '') btn.innerHTML = original;
      }
    });
    return btn;
  }

  makeBtn('⚡ FOLLOW', 'NIP-02 KIND 3', '#58ff58', async () => {
    if (!session) return;
    setStatus('Following…', '#5b9dff');
    try {
      const res = await followUser(session, DEV.pubkey);
      if (res.alreadyFollowing) {
        setStatus(`Already following ${DEV.name}.`, '#ffd84a');
      } else {
        setStatus(`✓ Followed ${DEV.name} on ${res.publishedTo.length}/${res.publishedTo.length + res.failed.length} relays.`, '#58ff58');
      }
    } catch (err) {
      setStatus(`✗ Follow failed: ${err instanceof Error ? err.message : String(err)}`, '#ff5050');
    }
  });

  makeBtn('📡 SHARE', 'KIND 1 · #pallasite', '#ffd84a', async () => {
    if (!session) return;
    setStatus('Posting note…', '#5b9dff');
    try {
      const runTimeSec = Math.floor(state.runTimeMs / 1000);
      const res = await shareCompletion(session, {
        score: state.players[0].score, sats: state.players[0].sats, wave: state.wave, runTimeSec,
      });
      setStatus(`✓ Posted on ${res.publishedTo.length}/${res.publishedTo.length + res.failed.length} relays.`, '#58ff58');
    } catch (err) {
      setStatus(`✗ Share failed: ${err instanceof Error ? err.message : String(err)}`, '#ff5050');
    }
  });

  makeBtn('✦ ENDORSE', 'NIP-85 KIND 30382', '#5b9dff', async () => {
    if (!session) return;
    const rank = rankFromWave(state.wave);
    setStatus(`Endorsing at rank ${rank}…`, '#5b9dff');
    try {
      const res = await endorseSubject(session, DEV.pubkey, rank, 'pallasite-completed');
      setStatus(`✓ Endorsed at rank ${rank} on ${res.publishedTo.length}/${res.publishedTo.length + res.failed.length} relays.`, '#58ff58');
    } catch (err) {
      setStatus(`✗ Endorse failed: ${err instanceof Error ? err.message : String(err)}`, '#ff5050');
    }
  });

  if (!canSign) {
    setStatus('Sign in with Bark or bunker on the title screen to enable these.', 'rgba(180,140,255,0.7)');
  }
}

function renderZapButton(parent: HTMLElement, state: GameState): void {
  // Without a Nostr session the LNURL-pay path still works, but the resulting
  // payment can't be signed as a NIP-57 zap — so the player gets no public
  // credit linking them to the game. Show a Ko-fi link instead so non-Nostr
  // players have a real path to support the project, not a dead-end prompt.
  if (!state.session) {
    const wrap = el('div', { parent });
    wrap.style.cssText = 'display:flex;flex-direction:column;align-items:center;gap:8px;margin-top:6px;';
    const label = el('p', { parent: wrap, text: '☕ TIP VIA KO-FI' });
    label.style.cssText = 'font-size:0.95rem;letter-spacing:0.22em;color:#ffd84a;text-shadow:0 0 8px rgba(255,216,74,0.5);margin:0;';
    const sub = el('p', { parent: wrap, text: 'no Nostr needed · card or PayPal' });
    sub.style.cssText = 'font-size:0.7rem;letter-spacing:0.12em;color:rgba(180,140,255,0.6);margin:0;text-align:center;';
    const ko = el('a', { parent: wrap, text: '☕ KO-FI ·  ko-fi.com/brays' }) as HTMLAnchorElement;
    ko.href = 'https://ko-fi.com/brays';
    ko.target = '_blank';
    ko.rel = 'noopener';
    ko.style.cssText = koFiButtonStyle();
    const orZap = el('p', { parent: wrap, text: 'or sign in with Nostr to zap' });
    orZap.style.cssText = 'font-size:0.66rem;letter-spacing:0.1em;color:rgba(180,140,255,0.45);margin:6px 0 0;text-align:center;';
    return;
  }

  const wrap = el('div', { parent });
  wrap.style.cssText = 'display:flex;flex-direction:column;align-items:center;gap:8px;margin-top:6px;';

  const label = el('p', { parent: wrap, text: '⚡ ZAP THE CRYPTO DONKEY ⚡' });
  label.style.cssText = 'font-size:0.95rem;letter-spacing:0.22em;color:#ffd84a;text-shadow:0 0 8px rgba(255,216,74,0.5);margin:0;';

  const sub = el('p', { parent: wrap });
  sub.innerHTML = `${hasWebLN() ? 'WebLN detected · one-click pay' : 'Click amount → opens your wallet'} · NIP-57`;
  sub.style.cssText = 'font-size:0.7rem;letter-spacing:0.15em;color:rgba(180,140,255,0.6);margin:0;';

  const row = el('div', { parent: wrap });
  row.style.cssText = 'display:flex;gap:8px;flex-wrap:wrap;justify-content:center;';

  const presets: Array<{ label: string; sats: number }> = [
    { label: '21',  sats: 21 },
    { label: '100', sats: 100 },
    { label: '1k',  sats: 1000 },
    { label: '10k', sats: 10000 },
  ];

  for (const p of presets) {
    const btn = el('button', { parent: row, text: `⚡ ${p.label}` });
    btn.style.cssText = zapPresetStyle();
    onTap(btn, () => quickZap(state, p.sats, btn));
  }

  const custom = el('button', { parent: row, text: 'CUSTOM…' });
  custom.style.cssText = [
    'background:transparent',
    'border:2px solid rgba(180,140,255,0.5)',
    'color:rgba(220,210,255,0.85)',
    "font-family:'VT323',ui-monospace,monospace",
    'font-size:0.95rem',
    'padding:8px 14px',
    'letter-spacing:0.12em',
    'cursor:pointer',
    'border-radius:6px',
    'transition:all 0.12s ease',
  ].join(';');
  onTap(custom, () => openZapModal(state));

  // Secondary Ko-fi link for signed-in players who'd rather pay via card —
  // no judgement, sats and ko-fi both fund the faucet float.
  const koRow = el('div', { parent: wrap });
  koRow.style.cssText = 'display:flex;justify-content:center;margin-top:4px;';
  const ko = el('a', { parent: koRow, text: '☕ or tip via ko-fi' }) as HTMLAnchorElement;
  ko.href = 'https://ko-fi.com/brays';
  ko.target = '_blank';
  ko.rel = 'noopener';
  ko.style.cssText = [
    'font-size:0.72rem', 'letter-spacing:0.16em',
    'color:rgba(180,140,255,0.7)', 'text-decoration:none',
    'cursor:pointer',
  ].join(';');
}

function koFiButtonStyle(): string {
  return [
    'background:rgba(255,216,74,0.1)',
    'border:2px solid #ffd84a',
    'color:#ffd84a',
    "font-family:'VT323',ui-monospace,monospace",
    'font-size:1rem',
    'padding:10px 18px',
    'letter-spacing:0.16em',
    'cursor:pointer', 'text-decoration:none',
    'border-radius:6px',
    'text-shadow:0 0 6px rgba(255,216,74,0.6)',
    'box-shadow:0 0 12px rgba(255,216,74,0.2)',
    'transition:all 0.12s ease',
  ].join(';');
}

function zapPresetStyle(): string {
  return [
    'background:rgba(255,216,74,0.1)',
    'border:2px solid #ffd84a',
    'color:#ffd84a',
    "font-family:'VT323',ui-monospace,monospace",
    'font-size:1.05rem',
    'padding:8px 16px',
    'letter-spacing:0.1em',
    'cursor:pointer',
    'border-radius:6px',
    'transition:all 0.12s ease',
    'text-shadow:0 0 6px rgba(255,216,74,0.6)',
    'box-shadow:0 0 12px rgba(255,216,74,0.2)',
    'min-width:74px',
  ].join(';');
}

/**
 * One-click zap. Shows the QR popover IMMEDIATELY (with a spinner placeholder)
 * so the user has something to scan as soon as the invoice arrives. Tries
 * WebLN in parallel — success dismisses the popover with a tick, failure
 * leaves the QR up for phone-scan.
 */
async function quickZap(state: GameState, amountSats: number, btn: HTMLButtonElement): Promise<void> {
  const originalHtml = btn.innerHTML;
  const restore = (): void => { btn.innerHTML = originalHtml; btn.disabled = false; btn.style.opacity = '1'; };
  btn.disabled = true;
  btn.style.opacity = '0.6';
  btn.innerHTML = `⚡ …`;

  // Show popover up-front so the user sees feedback even before the fetch lands
  const pop = createZapPopover(amountSats);

  try {
    const res = await requestZapInvoice(state.session, amountSats, '');
    populateZapPopover(pop, res.invoice, amountSats, res.isZap);
    restore();

    // Try WebLN in parallel — if it succeeds, dismiss the popover with a tick
    if (hasWebLN()) {
      try {
        await payViaWebLN(res.invoice);
        markPopoverPaid(pop, amountSats);
        return;
      } catch {
        // WebLN refused/failed — popover stays, user can scan QR or click open-wallet
      }
    }
  } catch (err) {
    failPopover(pop, err instanceof Error ? err.message : String(err));
    btn.innerHTML = '✗ FAIL';
    btn.style.borderColor = '#ff5050';
    btn.style.color = '#ff5050';
    setTimeout(() => {
      btn.style.borderColor = '#ffd84a';
      btn.style.color = '#ffd84a';
      restore();
    }, 2500);
  }
}

/** Build the popover skeleton with a spinner — invoice fills in when fetched. */
function createZapPopover(amountSats: number): HTMLElement {
  document.querySelectorAll('.zap-popover').forEach(n => n.remove());

  const pop = el('div', { className: 'zap-popover', parent: root });

  const heading = el('p', { parent: pop, text: `ZAP · ${amountSats} SATS` });
  heading.style.cssText = 'font-size:1.6rem;letter-spacing:0.2em;color:var(--hud-yellow);margin:0;text-shadow:0 0 8px rgba(255,216,74,0.55);';

  const sub = el('p', { className: 'zap-popover-sub', parent: pop, text: 'Generating invoice…' });
  sub.style.cssText = 'font-size:1rem;color:rgba(180,140,255,0.75);letter-spacing:0.1em;margin:0 0 6px;';

  // Big, viewport-scaled QR so it scans across a booth ("pay in passing"):
  // huge on a 4K TV, still sensible on a phone. The SVG is vector so it stays
  // crisp at any size (see renderQRInto).
  const qrSlot = el('div', { className: 'zap-popover-qr', parent: pop });
  qrSlot.style.cssText = 'display:flex;align-items:center;justify-content:center;width:min(72vmin,880px);height:min(72vmin,880px);background:rgba(0,0,0,0.4);border:1px solid rgba(180,140,255,0.3);border-radius:8px;';

  // Spinner
  const spinner = el('div', { className: 'zap-spinner', parent: qrSlot });
  spinner.style.cssText = 'width:36px;height:36px;border:3px solid rgba(255,216,74,0.2);border-top-color:#ffd84a;border-radius:50%;animation:zap-spin 0.8s linear infinite;';

  el('div', { className: 'zap-popover-actions', parent: pop }).style.cssText = 'display:flex;gap:8px;flex-wrap:wrap;justify-content:center;margin-top:6px;min-height:36px;';

  // Cancel button — always present
  const cancel = el('button', { className: 'zap-popover-close', parent: pop, text: '✕' });
  cancel.style.cssText = [
    'position:absolute', 'top:8px', 'right:8px',
    'background:transparent', 'border:none',
    'color:rgba(220,210,255,0.55)',
    "font-family:'VT323',ui-monospace,monospace",
    'font-size:1.1rem',
    'cursor:pointer', 'padding:2px 8px', 'border-radius:4px',
  ].join(';');
  onTap(cancel, () => pop.remove());

  // Auto-dismiss after 90s
  const timer = window.setTimeout(() => pop.remove(), 90_000);
  pop.dataset.timer = String(timer);

  return pop;
}

/** Fill the popover with the actual invoice + QR + action buttons. */
function populateZapPopover(pop: HTMLElement, invoice: string, amountSats: number, isZap: boolean): void {
  const sub = pop.querySelector<HTMLElement>('.zap-popover-sub');
  if (sub) sub.textContent = isZap ? 'Signed zap · scan or click' : 'LNURL pay · scan or click';

  const qrSlot = pop.querySelector<HTMLElement>('.zap-popover-qr');
  if (qrSlot) {
    qrSlot.innerHTML = '';
    qrSlot.style.background = '#fff';
    qrSlot.style.border = 'none';
    qrSlot.style.padding = '8px';
    void renderQRInto(qrSlot, invoice.toUpperCase());  // uppercase encodes more efficiently in alphanumeric mode
  }

  const actions = pop.querySelector<HTMLElement>('.zap-popover-actions');
  if (!actions) return;
  actions.innerHTML = '';

  const wallet = el('a', { parent: actions, text: '⚡ OPEN WALLET' });
  wallet.setAttribute('href', `lightning:${invoice}`);
  wallet.style.cssText = [
    'background:rgba(255,216,74,0.18)',
    'border:2px solid #ffd84a',
    'color:#ffd84a',
    "font-family:'VT323',ui-monospace,monospace",
    'font-size:0.92rem',
    'padding:7px 14px',
    'letter-spacing:0.12em',
    'cursor:pointer',
    'border-radius:6px',
    'text-decoration:none',
    'display:inline-block',
    'touch-action:manipulation',  // native link click — re-enable under body touch-action:none
  ].join(';');

  const copy = el('button', { parent: actions, text: 'COPY' });
  copy.style.cssText = [
    'background:transparent',
    'border:2px solid #5b9dff',
    'color:#5b9dff',
    "font-family:'VT323',ui-monospace,monospace",
    'font-size:0.92rem',
    'padding:7px 14px',
    'letter-spacing:0.12em',
    'cursor:pointer',
    'border-radius:6px',
  ].join(';');
  onTap(copy, async () => {
    try {
      await navigator.clipboard.writeText(invoice);
      copy.textContent = '✓';
      setTimeout(() => { copy.textContent = 'COPY'; }, 2000);
    } catch { copy.textContent = '✗'; }
  });

  // "I PAID" — manual confirmation for QR/external wallet flows where we
  // can't detect settlement automatically. WebLN paths short-circuit to
  // markPopoverPaid() directly and bypass this.
  const paid = el('button', { parent: actions, text: 'I PAID' });
  paid.style.cssText = [
    'background:rgba(88,255,88,0.12)',
    'border:2px solid #58ff58',
    'color:#58ff58',
    "font-family:'VT323',ui-monospace,monospace",
    'font-size:0.92rem',
    'padding:7px 14px',
    'letter-spacing:0.12em',
    'cursor:pointer',
    'border-radius:6px',
  ].join(';');
  onTap(paid, () => {
    pop.remove();
    renderZapThanks(amountSats);
  });
}

/** Mark the popover as paid (WebLN succeeded) and auto-dismiss after 2.5s. */
function markPopoverPaid(pop: HTMLElement, amountSats: number): void {
  const sub = pop.querySelector<HTMLElement>('.zap-popover-sub');
  if (sub) {
    sub.textContent = `✓ ZAPPED ${amountSats} SATS · THANK YOU`;
    sub.style.color = '#58ff58';
  }
  const qrSlot = pop.querySelector<HTMLElement>('.zap-popover-qr');
  if (qrSlot) {
    qrSlot.innerHTML = '<div style="font-size:3rem;color:#58ff58;text-shadow:0 0 12px rgba(88,255,88,0.6);">✓</div>';
    qrSlot.style.background = 'rgba(88,255,88,0.08)';
    qrSlot.style.border = '2px solid #58ff58';
    qrSlot.style.padding = '0';
  }
  const timer = pop.dataset.timer;
  if (timer) clearTimeout(parseInt(timer, 10));
  // Brief popover acknowledgement, then full thank-you overlay so the
  // celebration lands and the QR doesn't loiter.
  setTimeout(() => {
    pop.remove();
    renderZapThanks(amountSats);
  }, 900);
}

/**
 * Full-screen thank-you celebration. Heart icon, glow pulse, sat amount,
 * one-line gratitude. Auto-dismisses after 5s; click anywhere to dismiss
 * sooner. Sits above any other overlay.
 */
function renderZapThanks(amountSats: number): void {
  // Remove any existing celebration so a second tap doesn't stack
  document.querySelectorAll('.zap-thanks').forEach(n => n.remove());
  const wrap = el('div', { className: 'zap-thanks', parent: root });
  wrap.style.cssText = [
    'position:fixed', 'inset:0', 'z-index:200',
    'display:flex', 'flex-direction:column',
    'align-items:center', 'justify-content:center',
    'background:radial-gradient(circle at center, rgba(255,216,74,0.08), rgba(0,0,0,0.85) 70%)',
    'backdrop-filter:blur(3px)',
    'animation:zap-thanks-in 0.4s ease forwards',
    'cursor:pointer',
    'padding:32px',
  ].join(';');

  const heart = el('div', { parent: wrap, text: '⚡' });
  heart.style.cssText = [
    'font-size:7rem', 'color:#ffd84a',
    'text-shadow:0 0 30px rgba(255,216,74,0.7)',
    'animation:zap-thanks-pulse 1.4s ease-in-out infinite',
    'line-height:1',
  ].join(';');

  const big = el('h2', { parent: wrap, text: amountSats > 0 ? `${amountSats.toLocaleString()} SATS` : 'ZAPPED' });
  big.style.cssText = [
    "font-family:'VT323',ui-monospace,monospace",
    'font-size:clamp(1.8rem, 6vw, 3rem)',
    'color:#ffd84a', 'letter-spacing:0.18em',
    'margin:18px 0 6px', 'text-shadow:0 0 12px rgba(255,216,74,0.6)',
  ].join(';');

  const thanks = el('p', { parent: wrap, text: 'THANK YOU FOR THE ZAP' });
  thanks.style.cssText = [
    "font-family:'VT323',ui-monospace,monospace",
    'font-size:1.2rem', 'color:#58ff58',
    'letter-spacing:0.25em', 'margin:0',
    'text-shadow:0 0 8px rgba(88,255,88,0.5)',
  ].join(';');

  const sub = el('p', { parent: wrap, text: 'Tap anywhere to continue' });
  sub.style.cssText = 'font-size:0.78rem;color:rgba(180,140,255,0.7);letter-spacing:0.18em;margin:24px 0 0;';

  const dismiss = (): void => {
    wrap.style.animation = 'zap-thanks-out 0.3s ease forwards';
    setTimeout(() => wrap.remove(), 320);
  };
  wrap.addEventListener('click', dismiss);
  setTimeout(dismiss, 5000);
}

function failPopover(pop: HTMLElement, message: string): void {
  const sub = pop.querySelector<HTMLElement>('.zap-popover-sub');
  if (sub) {
    sub.textContent = `✗ ${message}`;
    sub.style.color = '#ff5050';
  }
  const qrSlot = pop.querySelector<HTMLElement>('.zap-popover-qr');
  if (qrSlot) {
    qrSlot.innerHTML = '<div style="font-size:2.5rem;color:#ff5050;">✗</div>';
    qrSlot.style.background = 'rgba(255,80,80,0.08)';
    qrSlot.style.border = '2px solid #ff5050';
    qrSlot.style.padding = '0';
  }
  setTimeout(() => pop.remove(), 4000);
}

async function renderQRInto(target: HTMLElement, text: string): Promise<void> {
  try {
    const svg = await QRCode.toString(text, {
      type: 'svg',
      // Explicit quiet zone — 2 modules of white border baked into the
      // SVG. Was margin: 0 previously, which relied on CSS padding for
      // the quiet zone. Most camera scanners examine the rendered
      // bitmap and want the quiet zone INSIDE the QR bounding box, so
      // CSS padding around an external div is not always enough on a
      // motion-blurred mobile capture.
      margin: 2,
      width: 256,
      // 'M' instead of 'L' — same Version 2 (25 modules) for our ~40
      // char pairing URL, but ~15% error correction so a partial
      // occlusion / motion blur / camera glare doesn't kill the
      // decode. Cheap insurance.
      errorCorrectionLevel: 'M',
      color: { dark: '#000000', light: '#ffffff' },
    });
    target.innerHTML = svg;
    const svgEl = target.querySelector('svg');
    if (svgEl) {
      svgEl.style.cssText = 'display:block;width:100%;height:100%;border-radius:4px;';
    }
  } catch (err) {
    target.innerHTML = `<p style="color:#ff5050;font-size:0.8rem;">QR render failed</p>`;
    console.warn('[qr] render failed:', err);
  }
}

/**
 * Modal that surfaces the player's guest identity in NIP-19 bech32
 * form (npub for the public key, nsec for the secret key). Read-only
 * — for backup / portability use only. The nsec is hidden behind a
 * "Show secret key" toggle so a casual screenshot or a shoulder-surf
 * doesn't leak it. Both fields have a one-tap copy button.
 *
 * Pasting the nsec into a Nostr client like Damus, Amethyst, nsec.app
 * or a NIP-46 bunker recreates this identity there — useful when a
 * player loved their guest profile and wants to keep it after
 * upgrading device, OR when they want to bind it to a real bunker
 * for cross-device portability.
 */
function openGuestKeyExport(pubkeyHex: string, privkeyHex: string): void {
  document.querySelectorAll('.guest-key-modal').forEach((n) => n.remove());

  const modal = el('div', { className: 'guest-key-modal', parent: root });
  modal.style.cssText = 'position:fixed;inset:0;background:rgba(2,5,13,0.92);display:flex;align-items:center;justify-content:center;z-index:1000;padding:18px;';
  const inner = el('div', { parent: modal });
  inner.style.cssText = 'background:#02050d;border:1px solid rgba(140,255,180,0.45);border-radius:12px;padding:20px;max-width:520px;width:100%;display:flex;flex-direction:column;gap:12px;font-family:ui-monospace,monospace;box-shadow:0 0 30px rgba(140,255,180,0.18);max-height:90vh;overflow-y:auto;';

  el('h2', { parent: inner, text: 'EXPORT IDENTITY' }).style.cssText = 'margin:0;font-size:1rem;letter-spacing:0.2em;color:#8cffb4;text-align:center;';
  const sub = el('p', { parent: inner, text: 'Back this identity up by copying the nsec into another Nostr client (Damus, Amethyst, nsec.app, your bunker). Anyone with this nsec controls your account.' });
  sub.style.cssText = 'font-size:0.78rem;color:rgba(220,210,255,0.7);line-height:1.5;margin:0;text-align:center;';

  // ── npub block — always visible, identifying ────────────────────────
  let npubBech: string;
  try { npubBech = encodeNpub(pubkeyHex); }
  catch { npubBech = `[failed to encode pubkey: ${pubkeyHex.slice(0, 8)}…]`; }
  const npubBlock = el('div', { parent: inner });
  npubBlock.style.cssText = 'display:flex;flex-direction:column;gap:4px;';
  el('span', { parent: npubBlock, text: '🔓 PUBLIC KEY (npub)' }).style.cssText = 'font-size:0.72rem;color:rgba(140,255,180,0.85);letter-spacing:0.18em;';
  const npubRow = el('div', { parent: npubBlock });
  npubRow.style.cssText = 'display:flex;align-items:stretch;gap:6px;';
  const npubText = el('div', { parent: npubRow, text: npubBech });
  npubText.style.cssText = 'flex:1;padding:8px 10px;background:rgba(2,5,13,0.6);border:1px solid rgba(140,255,180,0.25);border-radius:6px;font-size:0.78rem;color:#fff5d8;word-break:break-all;line-height:1.4;user-select:all;';
  const npubCopy = el('button', { parent: npubRow, text: 'COPY' }) as HTMLButtonElement;
  npubCopy.style.cssText = 'flex:0 0 auto;padding:8px 14px;background:rgba(140,255,180,0.12);border:1px solid rgba(140,255,180,0.55);border-radius:6px;color:#8cffb4;font-family:inherit;font-size:0.78rem;letter-spacing:0.14em;cursor:pointer;';
  onTap(npubCopy, () => {
    void navigator.clipboard?.writeText(npubBech).then(
      () => { npubCopy.textContent = 'COPIED'; window.setTimeout(() => { npubCopy.textContent = 'COPY'; }, 1400); },
      () => { npubCopy.textContent = 'FAIL'; },
    );
  });

  // ── nsec block — hidden behind reveal toggle ────────────────────────
  let nsecBech: string;
  try { nsecBech = encodeNsec(privkeyHex); }
  catch { nsecBech = `[failed to encode privkey]`; }
  const nsecBlock = el('div', { parent: inner });
  nsecBlock.style.cssText = 'display:flex;flex-direction:column;gap:4px;border-top:1px dashed rgba(255,120,120,0.3);padding-top:12px;';
  el('span', { parent: nsecBlock, text: '🔐 SECRET KEY (nsec) — KEEP PRIVATE' }).style.cssText = 'font-size:0.72rem;color:rgba(255,120,120,0.95);letter-spacing:0.18em;';
  const warning = el('p', { parent: nsecBlock, text: 'This is the master key for your identity. Anyone with it can post as you, send your sats, and delete your runs. Never share it. Never paste it into a website you don\'t trust.' });
  warning.style.cssText = 'font-size:0.72rem;color:rgba(255,160,120,0.85);line-height:1.5;margin:0;';

  const nsecRow = el('div', { parent: nsecBlock });
  nsecRow.style.cssText = 'display:flex;align-items:stretch;gap:6px;';
  // Hidden by default — replaced with a literal "REVEAL" placeholder
  // so a casual screenshot or shoulder-surf can't leak the nsec.
  const nsecText = el('div', { parent: nsecRow, text: '••••••••••••••••••••••••••••••••' });
  nsecText.style.cssText = 'flex:1;padding:8px 10px;background:rgba(2,5,13,0.6);border:1px solid rgba(255,120,120,0.35);border-radius:6px;font-size:0.78rem;color:rgba(255,160,120,0.85);word-break:break-all;line-height:1.4;user-select:all;';
  const revealBtn = el('button', { parent: nsecRow, text: 'REVEAL' }) as HTMLButtonElement;
  revealBtn.style.cssText = 'flex:0 0 auto;padding:8px 14px;background:rgba(255,120,120,0.12);border:1px solid rgba(255,120,120,0.55);border-radius:6px;color:#ff8a8a;font-family:inherit;font-size:0.78rem;letter-spacing:0.14em;cursor:pointer;';
  let revealed = false;
  onTap(revealBtn, () => {
    revealed = !revealed;
    nsecText.textContent = revealed ? nsecBech : '••••••••••••••••••••••••••••••••';
    nsecText.style.color = revealed ? '#fff5d8' : 'rgba(255,160,120,0.85)';
    revealBtn.textContent = revealed ? 'HIDE' : 'REVEAL';
  });
  const nsecCopy = el('button', { parent: nsecRow, text: 'COPY' }) as HTMLButtonElement;
  nsecCopy.style.cssText = 'flex:0 0 auto;padding:8px 14px;background:rgba(255,120,120,0.12);border:1px solid rgba(255,120,120,0.55);border-radius:6px;color:#ff8a8a;font-family:inherit;font-size:0.78rem;letter-spacing:0.14em;cursor:pointer;';
  onTap(nsecCopy, () => {
    void navigator.clipboard?.writeText(nsecBech).then(
      () => { nsecCopy.textContent = 'COPIED'; window.setTimeout(() => { nsecCopy.textContent = 'COPY'; }, 1400); },
      () => { nsecCopy.textContent = 'FAIL'; },
    );
  });

  // ── How to restore — short instruction block ────────────────────────
  const how = el('div', { parent: inner });
  how.style.cssText = 'border-top:1px dashed rgba(220,210,255,0.15);padding-top:10px;font-size:0.74rem;color:rgba(220,210,255,0.6);line-height:1.5;';
  how.innerHTML = 'To restore on another device: paste the nsec into any Nostr client that supports nsec import (Damus iOS, Amethyst Android, nsec.app web, your bunker). Or upgrade in-game by tapping <strong>UPGRADE TO NOSTR</strong> on the title screen.';

  // ── Close button ────────────────────────────────────────────────────
  const closeRow = el('div', { parent: inner });
  closeRow.style.cssText = 'display:flex;justify-content:center;margin-top:6px;';
  const closeBtn = el('button', { className: 'menu-btn', parent: closeRow, text: 'DONE' }) as HTMLButtonElement;
  closeBtn.style.cssText += 'font-size:0.85rem;padding:8px 24px;letter-spacing:0.18em;';
  const close = (): void => { modal.remove(); window.removeEventListener('keydown', onKey); };
  onTap(closeBtn, close);
  // Backdrop dismiss — click + non-mouse pointerup (body has touch-action:none,
  // which suppresses tap→click on mobile).
  const dismissBackdrop = (e: Event): void => { if (e.target === modal) close(); };
  modal.addEventListener('click', dismissBackdrop);
  modal.addEventListener('pointerup', (e) => { if (e.pointerType !== 'mouse') dismissBackdrop(e); });
  const onKey = (e: KeyboardEvent): void => { if (e.code === 'Escape') close(); };
  window.addEventListener('keydown', onKey);
}

/**
 * Centered "zap us" QR — a STATIC LNURL-pay for the operator's lightning
 * address. One always-valid QR: any wallet scans it and the payer chooses the
 * amount, so spectators/players can support the project without a payout from
 * us. Shown on the title screen and the solo game-over recap.
 * Tapping the QR opens the full zap modal (amount presets + optional NIP-57
 * zap receipt) for those who'd rather pick an amount in-app.
 */
function renderZapUsQR(parent: HTMLElement, state: GameState, opts?: { caption?: string; size?: number }): HTMLElement {
  const size = opts?.size ?? 168;
  const wrap = el('div', { parent });
  wrap.style.cssText = 'display:flex;flex-direction:column;align-items:center;gap:6px;margin:16px auto 8px;max-width:340px;';
  const cap = el('p', { parent: wrap, text: opts?.caption ?? 'ENJOYED IT? ZAP US ⚡' });
  cap.style.cssText = 'margin:0;font-size:0.76rem;letter-spacing:0.16em;color:rgba(255,216,74,0.9);text-align:center;';
  const box = el('div', { parent: wrap });
  box.style.cssText = `width:${size}px;height:${size}px;background:#fff;padding:8px;border-radius:10px;box-sizing:content-box;cursor:pointer;`;
  box.setAttribute('role', 'button');
  box.setAttribute('aria-label', 'Zap us — scan to send sats, or tap to choose an amount');
  // Static LNURL-pay; the wallet resolves it and picks the amount. Best-effort
  // draw — a render failure just leaves the caption + address visible.
  try { void renderQRInto(box, devLnurl()); } catch { /* ignore */ }
  const addr = el('p', { parent: wrap, text: DEV.lightningAddress });
  addr.style.cssText = 'margin:0;font-size:0.72rem;color:rgba(220,210,255,0.7);user-select:all;';
  const hint = el('p', { parent: wrap, text: 'Scan with any Lightning wallet · tap for amounts' });
  hint.style.cssText = 'margin:0;font-size:0.64rem;color:rgba(200,200,200,0.5);letter-spacing:0.04em;text-align:center;';
  onTap(box, () => openZapModal(state));
  return wrap;
}

function openZapModal(state: GameState): void {
  // Avoid double-open
  document.querySelectorAll('.zap-modal').forEach(n => n.remove());

  const modal = el('div', { className: 'zap-modal', parent: root });
  const inner = el('div', { className: 'zap-modal-inner', parent: modal });

  el('h2', { parent: inner, text: 'ZAP THE CRYPTO DONKEY' });
  const addr = el('p', { parent: inner, text: DEV.lightningAddress });
  addr.style.cssText = 'font-size:0.95rem;color:var(--hud-blue);letter-spacing:0.06em;margin-top:-4px;';

  const desc = el('p', { parent: inner, text: 'Send sats over Lightning. NIP-57 zap if signed in, plain LNURL pay otherwise.' });
  desc.style.cssText = 'font-size:0.85rem;color:rgba(220,210,255,0.75);letter-spacing:0.04em;line-height:1.5;max-width:420px;';

  // Amount selector
  const amountLabel = el('p', { parent: inner, text: 'AMOUNT' });
  amountLabel.style.cssText = 'font-size:0.78rem;color:rgba(180,140,255,0.85);letter-spacing:0.4em;margin:14px 0 6px;';

  let chosenAmount = 100;
  const presetRow = el('div', { parent: inner });
  presetRow.style.cssText = 'display:flex;gap:8px;justify-content:center;flex-wrap:wrap;';
  const presets = [21, 100, 1000, 10000];
  const presetButtons: HTMLButtonElement[] = [];

  const refreshPresets = (): void => {
    presetButtons.forEach((b, i) => {
      const selected = presets[i] === chosenAmount;
      b.style.cssText = [
        'background:' + (selected ? 'rgba(255,216,74,0.2)' : 'transparent'),
        'border:2px solid ' + (selected ? '#ffd84a' : 'rgba(180,140,255,0.4)'),
        'color:' + (selected ? '#ffd84a' : 'rgba(220,210,255,0.85)'),
        "font-family:'VT323',ui-monospace,monospace",
        'font-size:1rem',
        'padding:6px 14px',
        'letter-spacing:0.1em',
        'cursor:pointer',
        'border-radius:6px',
        'transition:all 0.12s ease',
        selected ? 'box-shadow:0 0 14px rgba(255,216,74,0.4)' : '',
      ].filter(Boolean).join(';');
    });
  };

  for (const amount of presets) {
    const b = el('button', { parent: presetRow, text: amount >= 1000 ? `${amount / 1000}k` : amount.toString() });
    presetButtons.push(b);
    onTap(b, () => {
      chosenAmount = amount;
      customInput.value = '';
      refreshPresets();
    });
  }

  const customWrap = el('div', { parent: inner });
  customWrap.style.cssText = 'display:flex;gap:8px;align-items:center;justify-content:center;margin-top:8px;';
  el('span', { parent: customWrap, text: 'CUSTOM:' }).style.cssText = 'font-size:0.78rem;color:rgba(180,140,255,0.7);letter-spacing:0.18em;';
  const customInput = el('input', { parent: customWrap, attrs: { type: 'number', min: '1', max: '500000', placeholder: 'sats' } });
  customInput.style.cssText = 'background:rgba(0,0,0,0.5);border:2px solid rgba(180,140,255,0.4);color:#cfd6ff;font-family:inherit;font-size:1rem;padding:5px 10px;width:130px;border-radius:6px;';
  customInput.addEventListener('input', () => {
    const v = parseInt(customInput.value, 10);
    if (!isNaN(v) && v > 0) {
      chosenAmount = v;
      refreshPresets();
    }
  });
  refreshPresets();

  // Message
  const msgLabel = el('p', { parent: inner, text: 'MESSAGE (optional)' });
  msgLabel.style.cssText = 'font-size:0.78rem;color:rgba(180,140,255,0.85);letter-spacing:0.4em;margin:14px 0 6px;';
  const msgInput = el('input', { parent: inner, attrs: { maxlength: '180', placeholder: 'Loved Pallasite!' } });
  msgInput.style.cssText = 'background:rgba(0,0,0,0.5);border:2px solid rgba(91,157,255,0.4);color:#cfd6ff;font-family:inherit;font-size:0.95rem;padding:6px 12px;width:340px;max-width:100%;border-radius:6px;text-align:center;';

  // Status / invoice section
  const status = el('p', { parent: inner });
  status.style.cssText = 'font-size:0.85rem;color:rgba(180,140,255,0.7);letter-spacing:0.05em;margin-top:10px;min-height:1em;';

  const invoiceSection = el('div', { parent: inner });
  invoiceSection.style.cssText = 'display:none;flex-direction:column;gap:8px;align-items:center;width:100%;margin-top:8px;';

  // QR for the modal — bigger than the popover, prominent
  const qrWrap = el('div', { parent: invoiceSection });
  qrWrap.style.cssText = 'background:#fff;padding:10px;border-radius:8px;display:flex;align-items:center;justify-content:center;width:240px;height:240px;';

  const invoiceField = el('textarea', { parent: invoiceSection });
  invoiceField.setAttribute('readonly', 'true');
  invoiceField.style.cssText = 'background:rgba(0,0,0,0.5);border:1px solid rgba(91,157,255,0.4);color:#cfd6ff;font-family:ui-monospace,monospace;font-size:0.7rem;padding:8px 12px;width:100%;max-width:480px;height:64px;resize:none;border-radius:6px;text-align:left;letter-spacing:0;line-height:1.4;word-break:break-all;';

  const invoiceActions = el('div', { parent: invoiceSection });
  invoiceActions.style.cssText = 'display:flex;gap:8px;flex-wrap:wrap;justify-content:center;';

  // Generate invoice button
  const genBtn = el('button', { parent: inner, text: 'GENERATE INVOICE' });
  genBtn.style.cssText = [
    'background:rgba(91,157,255,0.12)',
    'border:2px solid #5b9dff',
    'color:#5b9dff',
    "font-family:'VT323',ui-monospace,monospace",
    'font-size:1.05rem',
    'padding:8px 22px',
    'letter-spacing:0.15em',
    'cursor:pointer',
    'border-radius:6px',
    'margin-top:14px',
    'transition:all 0.12s ease',
  ].join(';');
  onTap(genBtn, async () => {
    if (chosenAmount < 1) {
      status.textContent = 'Pick an amount.';
      status.style.color = '#ff8a3a';
      return;
    }
    genBtn.disabled = true;
    genBtn.style.opacity = '0.5';
    status.textContent = 'Resolving lightning address…';
    status.style.color = '#5b9dff';

    try {
      const res = await requestZapInvoice(state.session, chosenAmount, msgInput.value || '');
      invoiceField.value = res.invoice;
      invoiceSection.style.display = 'flex';
      void renderQRInto(qrWrap, res.invoice.toUpperCase());
      status.textContent = res.isZap
        ? `✓ Zap request signed. Scan QR or click pay.`
        : `✓ Invoice ready. Scan QR or click pay (no zap receipt — sign in for one).`;
      status.style.color = '#58ff58';
      genBtn.style.display = 'none';
      buildInvoiceActions();

      // Try WebLN in parallel for desktop users
      if (hasWebLN()) {
        try {
          await payViaWebLN(res.invoice);
          status.textContent = `⚡ ZAPPED ${res.amountSats} SATS · THANK YOU`;
          status.style.color = '#ffd84a';
        } catch {
          // user cancelled or webln failed — QR + buttons remain
        }
      }
    } catch (err) {
      status.textContent = `✗ ${err instanceof Error ? err.message : String(err)}`;
      status.style.color = '#ff5050';
      genBtn.disabled = false;
      genBtn.style.opacity = '1';
    }
  });

  function buildInvoiceActions(): void {
    invoiceActions.innerHTML = '';
    const invoice = invoiceField.value;

    if (hasWebLN()) {
      const webln = el('button', { parent: invoiceActions, text: '⚡ PAY VIA WEBLN' });
      webln.style.cssText = [
        'background:rgba(255,216,74,0.18)',
        'border:2px solid #ffd84a',
        'color:#ffd84a',
        "font-family:'VT323',ui-monospace,monospace",
        'font-size:0.95rem',
        'padding:8px 18px',
        'letter-spacing:0.12em',
        'cursor:pointer',
        'border-radius:6px',
      ].join(';');
      onTap(webln, async () => {
        webln.disabled = true;
        webln.textContent = 'PAYING…';
        try {
          await payViaWebLN(invoice);
          status.textContent = '⚡ ZAPPED. Thank you.';
          status.style.color = '#ffd84a';
          webln.textContent = '✓ PAID';
        } catch (err) {
          status.textContent = `Payment cancelled or failed: ${err instanceof Error ? err.message : String(err)}`;
          status.style.color = '#ff5050';
          webln.disabled = false;
          webln.textContent = '⚡ PAY VIA WEBLN';
        }
      });
    }

    const copy = el('button', { parent: invoiceActions, text: 'COPY INVOICE' });
    copy.style.cssText = [
      'background:transparent',
      'border:2px solid #5b9dff',
      'color:#5b9dff',
      "font-family:'VT323',ui-monospace,monospace",
      'font-size:0.95rem',
      'padding:8px 18px',
      'letter-spacing:0.12em',
      'cursor:pointer',
      'border-radius:6px',
    ].join(';');
    onTap(copy, async () => {
      try {
        await navigator.clipboard.writeText(invoice);
        copy.textContent = '✓ COPIED';
        setTimeout(() => { copy.textContent = 'COPY INVOICE'; }, 2000);
      } catch {
        copy.textContent = 'COPY FAILED — SELECT MANUALLY';
      }
    });

    const wallet = el('a', { parent: invoiceActions, text: 'OPEN IN WALLET' });
    wallet.setAttribute('href', `lightning:${invoice}`);
    wallet.style.cssText = [
      'background:transparent',
      'border:2px solid rgba(180,140,255,0.6)',
      'color:rgba(220,210,255,0.85)',
      "font-family:'VT323',ui-monospace,monospace",
      'font-size:0.95rem',
      'padding:8px 18px',
      'letter-spacing:0.12em',
      'cursor:pointer',
      'border-radius:6px',
      'text-decoration:none',
      'display:inline-block',
      // Native link activation is a click; body has touch-action:none which
      // suppresses tap→click, so opt this back in or the wallet link is dead
      // on mobile (same class of bug as the buttons, which use onTap).
      'touch-action:manipulation',
    ].join(';');
  }

  // Close button
  const closeRow = el('div', { parent: inner });
  closeRow.style.cssText = 'display:flex;justify-content:center;margin-top:14px;';
  const close = el('button', { parent: closeRow, text: 'CLOSE' });
  close.style.cssText = [
    'background:transparent',
    'border:2px solid rgba(180,140,255,0.5)',
    'color:rgba(220,210,255,0.85)',
    "font-family:'VT323',ui-monospace,monospace",
    'font-size:0.95rem',
    'padding:6px 18px',
    'letter-spacing:0.12em',
    'cursor:pointer',
    'border-radius:6px',
  ].join(';');
  onTap(close, () => modal.remove());

  // Close on backdrop tap (click + non-mouse pointerup, since mobile click is
  // unreliable here — same reason the buttons use onTap).
  const dismissBackdrop = (e: Event): void => { if (e.target === modal) modal.remove(); };
  modal.addEventListener('click', dismissBackdrop);
  modal.addEventListener('pointerup', e => { if (e.pointerType !== 'mouse') dismissBackdrop(e); });

  // Close on Escape
  const escHandler = (e: KeyboardEvent): void => {
    if (e.code === 'Escape') {
      modal.remove();
      window.removeEventListener('keydown', escHandler);
    }
  };
  window.addEventListener('keydown', escHandler);
}

function renderCreditsRoll(parent: HTMLElement, applyStage?: (e: HTMLElement) => void, state?: GameState): void {
  const panel = el('div', { className: 'credits-roll', parent });
  if (applyStage) applyStage(panel);
  const inner = el('div', { className: 'credits-roll-inner', parent: panel });

  const escape = escapeHtml;
  // Read from the single WAVE_LORE table (waves 1-24; skip the boss arena
  // entry since the credits already have a dedicated FINAL ARENA section).
  const specimens = WAVE_LORE.slice(0, 24);
  const specimensHtml = specimens.map(s =>
    `<p class="specimen"><span class="specimen-name">${escape(s.name)}</span><br><span class="specimen-sub">${escape(s.subtitle)}</span></p>`
  ).join('');

  // Guest mode shows the X handle; Nostr mode shows the full npub.
  const creatorLink = state?.session
    ? `<a href="${escape(DEV.profileUrl)}" target="_blank" rel="noopener noreferrer">${escape(DEV.npub)}</a>`
    : `<a href="${escape(DEV.twitterUrl)}" target="_blank" rel="noopener noreferrer">@${escape(DEV.twitter)}</a>`;

  inner.innerHTML = `
    <p class="credits-spacer"></p>
    <h3>A PALLASITE PRODUCTION</h3>
    <p class="credits-divider">· · ·</p>

    <h3>CREATED BY</h3>
    <p class="name">${escape(DEV.name.toUpperCase())}</p>
    <p>${creatorLink}</p>

    <p class="credits-divider">· · ·</p>

    <h3>GAME DESIGN · CODE · ART DIRECTION</h3>
    <p>The Crypto Donkey</p>

    <p class="credits-divider">· · ·</p>

    <h3>24 SPECIMENS CHARTED</h3>
    ${specimensHtml}

    <p class="credits-divider">· · ·</p>

    <h3>FINAL ARENA</h3>
    <p class="name">EVENT HORIZON</p>

    <p class="credits-divider">· · ·</p>

    <h3>POWERED BY</h3>
    <p><strong>Identity</strong> — Signet</p>
    <p>NIP-46 · NIP-07 · mysignet.app</p>
    <p></p>
    <p><strong>Leaderboard</strong> — gamestr</p>
    <p>kind 30762 player-signed scores</p>
    <p></p>
    <p><strong>Reputation</strong> — NIP-85</p>
    <p>kind 30382 user assertions</p>
    <p></p>
    <p><strong>Privacy</strong> — nostr-veil</p>
    <p>LSAG ring signatures over NIP-85</p>

    <p class="credits-divider">· · ·</p>

    <h3>BUILT WITH</h3>
    <p>TypeScript · Vite</p>
    <p>Canvas 2D · Web Audio API</p>
    <p>Procedural sound, no samples</p>
    <p></p>
    <p>Backgrounds via gpt-image-2</p>
    <p>Pallasite-themed cosmic photography</p>

    <p class="credits-divider">· · ·</p>

    <h3>PALLASITES</h3>
    <p>First proven by Peter Pallas, 1772.</p>
    <p>Stony-iron meteorites — silicate olivine</p>
    <p>embedded in iron-nickel matrix.</p>
    <p></p>
    <p>Cosmic origin: collision-disrupted</p>
    <p>parent body, core-mantle boundary.</p>

    <p class="credits-divider">· · ·</p>

    <h3>forgesworn · 2026</h3>
    <p class="name">SHOOT ROCKS · CLIMB THE BOARD</p>

    <p class="credits-spacer"></p>
  `;
}

// ── Toast (tiny notification) ─────────────────────────────────────────────────

export function renderToast(state: GameState): void {
  // Remove old toast first
  document.querySelectorAll('.toast').forEach(t => t.remove());
  if (!state.toast) return;
  const t = el('div', { className: 'toast', parent: root, text: state.toast });
  setTimeout(() => t.remove(), 3000);
}

// Suppress unused warning — toastNow is re-exported for external triggers
void toastNow;

// ── Admin v2 panel ────────────────────────────────────────────────────────────
//
// NIP-98 + pubkey-allowlist gated. Lives at /admin. Lets TheCryptoDonkey
// (or whichever pubkey ADMIN_PUBKEY_HEX names server-side) flip caps,
// pause the pool, apply NORMAL / CONFERENCE / FROZEN presets without
// SSH-ing in for raw SQL. Authentication is implicit: the server only
// accepts events signed by the admin pubkey, so a non-admin who lands
// here just sees a 403 and a "not authorised" hint.

/** Format a unix-ms timestamp as a relative-time hint ("3m ago"). */
function relativeTime(unixMs: number): string {
  const dt = Date.now() - unixMs;
  if (dt < 0) {
    const ahead = Math.floor(-dt / 1000);
    if (ahead < 60) return `in ${ahead}s`;
    if (ahead < 3600) return `in ${Math.floor(ahead / 60)}m`;
    return `in ${Math.floor(ahead / 3600)}h`;
  }
  const s = Math.floor(dt / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export function renderAdminV2Panel(state: GameState): void {
  clearOverlay();
  const overlay = el('div', { className: 'overlay', parent: root });
  overlay.style.cssText = 'padding:20px;max-width:720px;width:100%;margin:0 auto;display:flex;flex-direction:column;gap:14px;';
  setupOverlayArrowNav(overlay);

  el('h2', { parent: overlay, text: 'ADMIN' }).style.cssText = 'margin:0;font-size:1.2rem;letter-spacing:0.22em;color:#ffd84a;';

  const session = state.session;
  if (!session) {
    const note = el('p', { parent: overlay });
    note.style.cssText = 'font-size:0.9rem;color:#ff8050';
    note.textContent = 'Sign in first — admin actions are NIP-98 signed.';
    const back = el('button', { className: 'menu-btn', parent: overlay, text: '← BACK' }) as HTMLButtonElement;
    back.addEventListener('click', () => { window.location.assign('/'); });
    return;
  }

  const status = el('p', { parent: overlay });
  status.style.cssText = 'min-height:1.2em;font-size:0.85rem;color:rgba(220,210,255,0.75)';
  const setStatus = (msg: string, color = '#5b9dff'): void => {
    status.textContent = msg;
    status.style.color = color;
  };

  const stateSlot = el('div', { parent: overlay });
  stateSlot.style.cssText = 'display:flex;flex-direction:column;gap:14px;';

  let cached: AdminStateResult | null = null;

  const refresh = async (): Promise<void> => {
    setStatus('Loading…');
    const r = await fetchAdminState(session);
    if (r.ok) {
      cached = r;
      render(r);
      setStatus('');
    } else {
      cached = null;
      stateSlot.replaceChildren();
      if (r.error === 'not_authorised' || (r as { status?: number }).status === 403) {
        setStatus('Not authorised — your pubkey is not on the admin allowlist.', '#ff5050');
      } else if (r.error === 'service_not_configured') {
        setStatus('Admin v2 not configured on the server (set ADMIN_PUBKEY_HEX).', '#ff8050');
      } else {
        setStatus(`Load failed: ${r.error}`, '#ff8050');
      }
    }
  };

  const render = (data: Extract<AdminStateResult, { ok: true }>): void => {
    stateSlot.replaceChildren();

    // ── Status card ─────────────────────────────────────────────
    const stCard = el('div', { parent: stateSlot });
    stCard.style.cssText = 'padding:14px;border:1px solid rgba(91,157,255,0.4);border-radius:10px;background:rgba(91,157,255,0.06);display:grid;grid-template-columns:1fr 1fr;gap:10px;';
    const stat = (label: string, value: string, accent = '#fff5d8'): void => {
      const cell = el('div', { parent: stCard });
      cell.style.cssText = 'display:flex;flex-direction:column;gap:2px;';
      const k = el('span', { parent: cell, text: label });
      k.style.cssText = 'font-size:0.66rem;letter-spacing:0.14em;color:rgba(220,210,255,0.6);';
      const v = el('span', { parent: cell, text: value });
      v.style.cssText = `font-size:0.95rem;color:${accent};font-family:monospace`;
    };
    stat('PHOENIXD BALANCE', data.phoenixd.balance_sat !== null ? `${data.phoenixd.balance_sat} sats` : '— unavailable —', data.phoenixd.balance_sat !== null ? '#8cffb4' : '#888');
    stat('LIFETIME PAID', `${data.pool.total_paid_sats} sats`);
    stat('TODAY SPENT', `${data.limits.today_spent_sats} / ${data.limits.daily_cap_sats}`, data.limits.today_spent_sats >= data.limits.daily_cap_sats ? '#ff8050' : '#fff5d8');
    stat('HOURLY CLAIMS', `${data.limits.hour_claims_count} / ${data.limits.hourly_cap_count}`, data.limits.hour_claims_count >= data.limits.hourly_cap_count ? '#ff8050' : '#fff5d8');
    stat('POOL STATE', data.pool.paused ? 'PAUSED' : 'LIVE', data.pool.paused ? '#ff5050' : '#58ff58');
    stat('FLAGGED PLAYERS', String(data.players.flagged_count), data.players.flagged_count > 0 ? '#ff8050' : '#fff5d8');
    const tokenSummary = data.withdraw_tokens.length === 0
      ? 'none'
      : data.withdraw_tokens.map(t => `${t.status}=${t.count}`).join(' · ');
    stat('LNURL TOKENS', tokenSummary);
    stat('LAST POOL SYNC', data.pool.last_synced_at ? relativeTime(data.pool.last_synced_at) : '—');

    // ── Presets ──────────────────────────────────────────────────
    const presetCard = el('div', { parent: stateSlot });
    presetCard.style.cssText = 'padding:14px;border:1px solid rgba(184,144,255,0.4);border-radius:10px;background:rgba(184,144,255,0.06);';
    const presetHead = el('h3', { parent: presetCard, text: 'PRESETS' });
    presetHead.style.cssText = 'margin:0 0 8px;font-size:0.9rem;letter-spacing:0.18em;color:#cbb6ff;';
    const presetRow = el('div', { parent: presetCard });
    presetRow.style.cssText = 'display:flex;gap:10px;flex-wrap:wrap;';
    const mkPreset = (label: string, profile: 'normal' | 'conference' | 'frozen', accent: string): void => {
      const btn = el('button', { className: 'menu-btn', parent: presetRow, text: label }) as HTMLButtonElement;
      const p = data.presets[profile];
      btn.style.cssText = `flex:1 1 30%;min-width:160px;padding:12px 10px;border:1.5px solid ${accent};background:rgba(2,5,13,0.5);cursor:pointer;font-size:0.85rem;letter-spacing:0.14em;`;
      btn.title = p ? `daily=${p.daily_cap_sats} · per-claim=${p.per_claim_cap_sats} · hourly=${p.hourly_cap_count} · pause=${p.pause}` : '';
      btn.addEventListener('click', () => {
        void (async () => {
          if (!window.confirm(`Apply ${profile} preset?`)) return;
          setStatus(`Applying ${profile}…`);
          const r = await applyAdminPreset(session, profile);
          if (r.ok) await refresh();
          else setStatus(`Preset failed: ${r.error}`, '#ff8050');
        })();
      });
    };
    mkPreset('🟢 NORMAL', 'normal', 'rgba(140,255,180,0.55)');
    mkPreset('🟡 CONFERENCE', 'conference', 'rgba(255,216,74,0.55)');
    mkPreset('🔴 FROZEN', 'frozen', 'rgba(255,120,120,0.55)');

    // ── Manual caps ──────────────────────────────────────────────
    const capsCard = el('div', { parent: stateSlot });
    capsCard.style.cssText = 'padding:14px;border:1px solid rgba(140,255,180,0.4);border-radius:10px;background:rgba(60,200,140,0.06);display:flex;flex-direction:column;gap:10px;';
    el('h3', { parent: capsCard, text: 'MANUAL CAPS' }).style.cssText = 'margin:0;font-size:0.9rem;letter-spacing:0.18em;color:#8cffb4;';
    const capInput = (label: string, value: number): HTMLInputElement => {
      const row = el('div', { parent: capsCard });
      row.style.cssText = 'display:flex;justify-content:space-between;align-items:center;gap:10px;';
      const lbl = el('label', { parent: row, text: label });
      lbl.style.cssText = 'font-size:0.78rem;color:rgba(220,210,255,0.8);letter-spacing:0.08em';
      const inp = document.createElement('input');
      inp.type = 'number';
      inp.min = '0';
      inp.step = '1';
      inp.value = String(value);
      inp.style.cssText = 'padding:8px 10px;font-family:monospace;font-size:0.95rem;background:#0a0a0a;color:#eee;border:1px solid #333;border-radius:6px;width:140px;text-align:right';
      row.appendChild(inp);
      return inp;
    };
    const dailyInp = capInput('Daily cap (sats)', data.limits.daily_cap_sats);
    const perClaimInp = capInput('Per-claim cap (sats)', data.limits.per_claim_cap_sats);
    const hourlyInp = capInput('Hourly claim count', data.limits.hourly_cap_count);
    const saveBtn = el('button', { className: 'menu-btn', parent: capsCard, text: 'SAVE CAPS' }) as HTMLButtonElement;
    saveBtn.style.cssText = 'padding:10px 14px;font-size:0.9rem;cursor:pointer;letter-spacing:0.14em';
    saveBtn.addEventListener('click', () => {
      void (async () => {
        const caps = {
          daily_cap_sats: Math.max(0, Math.floor(Number(dailyInp.value))),
          per_claim_cap_sats: Math.max(0, Math.floor(Number(perClaimInp.value))),
          hourly_cap_count: Math.max(0, Math.floor(Number(hourlyInp.value))),
        };
        setStatus('Saving caps…');
        const r = await setAdminCaps(session, caps);
        if (r.ok) await refresh();
        else setStatus(`Save failed: ${r.error}`, '#ff8050');
      })();
    });

    // ── Pause toggle ─────────────────────────────────────────────
    const pauseCard = el('div', { parent: stateSlot });
    pauseCard.style.cssText = `padding:14px;border:1px solid ${data.pool.paused ? 'rgba(255,120,120,0.6)' : 'rgba(220,210,255,0.3)'};border-radius:10px;background:${data.pool.paused ? 'rgba(255,120,120,0.08)' : 'rgba(2,5,13,0.4)'};display:flex;justify-content:space-between;align-items:center;gap:10px;`;
    const pauseLabel = el('div', { parent: pauseCard });
    pauseLabel.style.cssText = 'display:flex;flex-direction:column;gap:2px';
    const pHead = el('span', { parent: pauseLabel, text: 'CIRCUIT BREAKER' });
    pHead.style.cssText = 'font-size:0.78rem;letter-spacing:0.16em;color:rgba(220,210,255,0.85)';
    const pSub = el('span', { parent: pauseLabel, text: data.pool.paused ? 'All /api/claim returning 503' : 'Faucet live — claims paying out' });
    pSub.style.cssText = `font-size:0.72rem;color:${data.pool.paused ? '#ff8050' : 'rgba(220,210,255,0.55)'}`;
    const pauseBtn = el('button', { className: 'menu-btn', parent: pauseCard, text: data.pool.paused ? 'UNPAUSE' : 'PAUSE' }) as HTMLButtonElement;
    pauseBtn.style.cssText = `padding:10px 18px;font-size:0.9rem;cursor:pointer;letter-spacing:0.14em;border:1.5px solid ${data.pool.paused ? 'rgba(140,255,180,0.6)' : 'rgba(255,120,120,0.6)'};background:rgba(2,5,13,0.5);`;
    pauseBtn.addEventListener('click', () => {
      void (async () => {
        const target = !data.pool.paused;
        const reason = target ? window.prompt('Reason for pause? (optional)') ?? undefined : undefined;
        setStatus(target ? 'Pausing…' : 'Unpausing…');
        const r = await setAdminPause(session, target, reason);
        if (r.ok) await refresh();
        else setStatus(`Toggle failed: ${r.error}`, '#ff8050');
      })();
    });

    // ── Live settings (wave B) ──────────────────────────────────
    // Static-today constants made tunable: withdraw threshold, LNURL
    // bounds, per-tier lifetime caps + multipliers. Each row shows
    // current vs default. SAVE ALL fires one signed batch update.
    const settingsCard = el('div', { parent: stateSlot });
    settingsCard.style.cssText = 'padding:14px;border:1px solid rgba(220,210,255,0.3);border-radius:10px;background:rgba(2,5,13,0.4);display:flex;flex-direction:column;gap:10px;';
    el('h3', { parent: settingsCard, text: 'LIVE SETTINGS' }).style.cssText = 'margin:0;font-size:0.9rem;letter-spacing:0.18em;color:rgba(220,210,255,0.85);';
    const settingsHint = el('p', { parent: settingsCard, text: 'Tunable without redeploy. Defaults shown after each value when modified.' });
    settingsHint.style.cssText = 'margin:0;font-size:0.72rem;color:rgba(220,210,255,0.55);';
    const settingInputs: { key: string; input: HTMLInputElement }[] = [];
    const settingRow = (key: string, label: string, fractional = false): void => {
      const row = el('div', { parent: settingsCard });
      row.style.cssText = 'display:grid;grid-template-columns:1fr 140px 60px;gap:10px;align-items:center;';
      const lbl = el('label', { parent: row, text: label });
      lbl.style.cssText = 'font-size:0.78rem;color:rgba(220,210,255,0.8);letter-spacing:0.06em';
      const inp = document.createElement('input');
      inp.type = 'number';
      inp.step = fractional ? '0.01' : '1';
      inp.min = '0';
      inp.value = String(data.settings[key] ?? data.setting_defaults[key] ?? 0);
      inp.style.cssText = 'padding:8px 10px;font-family:monospace;font-size:0.9rem;background:#0a0a0a;color:#eee;border:1px solid #333;border-radius:6px;text-align:right;';
      row.appendChild(inp);
      const def = el('span', { parent: row });
      def.style.cssText = 'font-size:0.66rem;color:rgba(180,180,180,0.5);font-family:monospace;text-align:right;';
      const defaultVal = data.setting_defaults[key];
      const liveVal = data.settings[key];
      def.textContent = liveVal !== defaultVal ? `def ${defaultVal}` : '';
      settingInputs.push({ key, input: inp });
    };
    settingRow('withdraw_threshold_sats', 'Withdraw threshold (sats)');
    settingRow('lnurl_min_sats',          'LNURL min (sats)');
    settingRow('lnurl_max_sats',          'LNURL max (sats)');
    settingRow('lnurl_ttl_ms',            'LNURL TTL (ms)');
    settingRow('tier_lifetime_anon',      'Anon lifetime cap');
    settingRow('tier_lifetime_nip05',     'Nip05 lifetime cap');
    settingRow('tier_lifetime_close',     'Close lifetime cap');
    settingRow('tier_lifetime_verified',  'Verified lifetime cap');
    settingRow('tier_multiplier_anon',     'Anon multiplier',     true);
    settingRow('tier_multiplier_nip05',    'Nip05 multiplier',    true);
    settingRow('tier_multiplier_close',    'Close multiplier',    true);
    settingRow('tier_multiplier_verified', 'Verified multiplier', true);
    // Gameplay knobs — exposed via /api/game-config (no auth) so the
    // client can read them at boot. Add new ones here as they ship.
    settingRow('bonus_wave_chance',         'Bonus wave chance (0-1)',         true);
    settingRow('powerup_drop_chance',       'Powerup drop chance (0-1)',       true);
    settingRow('sat_drop_denom',            'Sat drop 1-in-N');
    settingRow('starting_lives',            'Starting lives (0=difficulty)');
    settingRow('ufo_first_spawn_ms',        'UFO first spawn (ms)');
    settingRow('ufo_respawn_base_ms',       'UFO respawn base (ms)');
    settingRow('ufo_respawn_per_wave_ms',   'UFO respawn per-wave (ms)');
    settingRow('ufo_respawn_min_ms',        'UFO respawn min (ms)');
    settingRow('asteroid_count_multiplier', 'Asteroid count multiplier',       true);
    settingRow('checkin_sats',              'Daily check-in stipend (sats)');
    settingRow('daily_cap_600bn',           '600bn Sanctum daily cap (sats)');
    const saveSettingsBtn = el('button', { className: 'menu-btn', parent: settingsCard, text: 'SAVE ALL SETTINGS' }) as HTMLButtonElement;
    saveSettingsBtn.style.cssText = 'padding:10px 14px;font-size:0.85rem;cursor:pointer;letter-spacing:0.14em;align-self:flex-start';
    saveSettingsBtn.addEventListener('click', () => {
      void (async () => {
        const payload = settingInputs.map(({ key, input }) => ({
          key,
          value: Number(input.value),
        }));
        setStatus('Saving settings…');
        const r = await saveAdminSettings(session, payload);
        if (r.ok) {
          const appliedCount = r.applied?.length ?? 0;
          const skipped = r.skipped ?? [];
          if (skipped.length > 0) {
            setStatus(`Saved ${appliedCount}, skipped ${skipped.length} (${skipped.map(s => `${s.key}:${s.reason}`).join(', ')})`, '#ff8050');
          }
          await refresh();
        } else {
          setStatus(`Save failed: ${r.error}`, '#ff8050');
        }
      })();
    });

    // ── Player management ───────────────────────────────────────
    const playerCard = el('div', { parent: stateSlot });
    playerCard.style.cssText = 'padding:14px;border:1px solid rgba(255,140,200,0.4);border-radius:10px;background:rgba(255,140,200,0.06);display:flex;flex-direction:column;gap:10px;';
    el('h3', { parent: playerCard, text: 'PLAYER' }).style.cssText = 'margin:0;font-size:0.9rem;letter-spacing:0.18em;color:#ffacd5;';
    const lookupRow = el('div', { parent: playerCard });
    lookupRow.style.cssText = 'display:flex;gap:8px;';
    const npubInput = document.createElement('input');
    npubInput.type = 'text';
    npubInput.placeholder = 'npub1… or 64-char hex';
    npubInput.spellcheck = false;
    npubInput.autocapitalize = 'off';
    npubInput.autocomplete = 'off';
    npubInput.style.cssText = 'flex:1;padding:8px 10px;font-family:monospace;font-size:0.85rem;background:#0a0a0a;color:#eee;border:1px solid #333;border-radius:6px';
    lookupRow.appendChild(npubInput);
    const lookupBtn = el('button', { className: 'menu-btn', parent: lookupRow, text: 'LOOKUP' }) as HTMLButtonElement;
    lookupBtn.style.cssText = 'padding:8px 14px;font-size:0.85rem;cursor:pointer;letter-spacing:0.12em';
    const playerSlot = el('div', { parent: playerCard });
    playerSlot.style.cssText = 'display:flex;flex-direction:column;gap:8px;';

    const renderPlayerCard = (p: AdminPlayer): void => {
      playerSlot.replaceChildren();
      const head = el('div', { parent: playerSlot });
      head.style.cssText = 'display:grid;grid-template-columns:1fr 1fr;gap:6px;font-family:monospace;font-size:0.8rem;';
      const stat = (k: string, v: string, color = '#fff5d8'): void => {
        const cell = el('div', { parent: head });
        cell.style.cssText = 'display:flex;flex-direction:column;gap:2px';
        const kEl = el('span', { parent: cell, text: k });
        kEl.style.cssText = 'font-size:0.62rem;letter-spacing:0.14em;color:rgba(220,210,255,0.55)';
        const vEl = el('span', { parent: cell, text: v });
        vEl.style.cssText = `font-size:0.85rem;color:${color}`;
      };
      stat('PUBKEY', `${p.pubkey.slice(0, 12)}…${p.pubkey.slice(-6)}`);
      stat('TIER', p.tier_override ? `${p.tier_override} (override)` : p.tier, p.tier_override ? '#ffd84a' : '#fff5d8');
      stat('BALANCE', `${p.balance_sats} sats`);
      stat('LIFETIME PAID', `${p.lifetime_paid_sats} sats`);
      stat('CLAIMS', String(p.claims_count));
      stat('BEST', `${p.best_score} · W${p.best_wave}`);
      stat('FLAGGED', p.flagged ? 'YES' : 'no', p.flagged ? '#ff5050' : '#fff5d8');
      stat('OPEN LNURL', String(p.open_withdraw_tokens), p.open_withdraw_tokens > 0 ? '#ffd84a' : '#fff5d8');

      const actions = el('div', { parent: playerSlot });
      actions.style.cssText = 'display:flex;flex-wrap:wrap;gap:8px;margin-top:6px;';

      // Flag / unflag
      const flagBtn = el('button', { className: 'menu-btn', parent: actions, text: p.flagged ? 'UNFLAG' : 'FLAG' }) as HTMLButtonElement;
      flagBtn.style.cssText = `padding:8px 14px;font-size:0.78rem;cursor:pointer;letter-spacing:0.1em;border:1.5px solid ${p.flagged ? 'rgba(140,255,180,0.55)' : 'rgba(255,120,120,0.55)'}`;
      flagBtn.addEventListener('click', () => {
        void (async () => {
          const reason = window.prompt(p.flagged ? 'Reason for unflagging?' : 'Reason for flagging?') ?? undefined;
          setStatus(p.flagged ? 'Unflagging…' : 'Flagging…');
          const r = await setAdminPlayerFlag(session, p.pubkey, !p.flagged, reason);
          if (r.ok) {
            setStatus(p.flagged ? 'Unflagged.' : 'Flagged.', '#58ff58');
            await loadPlayer(p.pubkey);
            void refresh();
          } else {
            setStatus(`Action failed: ${r.error}`, '#ff8050');
          }
        })();
      });

      // Balance adjust
      const balanceBtn = el('button', { className: 'menu-btn', parent: actions, text: 'ADJUST BALANCE' }) as HTMLButtonElement;
      balanceBtn.style.cssText = 'padding:8px 14px;font-size:0.78rem;cursor:pointer;letter-spacing:0.1em;border:1.5px solid rgba(255,216,74,0.55)';
      balanceBtn.addEventListener('click', () => {
        void (async () => {
          const raw = window.prompt('Delta sats (negative to debit, positive to credit):');
          if (raw === null) return;
          const delta = Math.floor(Number(raw));
          if (!Number.isFinite(delta) || delta === 0) {
            setStatus('Invalid delta.', '#ff5050');
            return;
          }
          const reason = window.prompt('Reason? (optional)') ?? undefined;
          setStatus('Adjusting balance…');
          const r = await adjustAdminPlayerBalance(session, p.pubkey, delta, reason);
          if (r.ok) {
            setStatus(`Balance ${delta > 0 ? '+' : ''}${delta} → ${r.balance_sats}`, '#58ff58');
            await loadPlayer(p.pubkey);
          } else {
            setStatus(`Adjust failed: ${r.error}${r.detail ? ` (${r.detail})` : ''}`, '#ff8050');
          }
        })();
      });

      // Tier override
      const tierWrap = el('div', { parent: actions });
      tierWrap.style.cssText = 'display:flex;gap:6px;align-items:center;border:1.5px solid rgba(184,144,255,0.55);border-radius:6px;padding:6px 10px;';
      const tierLbl = el('span', { parent: tierWrap, text: 'TIER OVERRIDE' });
      tierLbl.style.cssText = 'font-size:0.66rem;letter-spacing:0.12em;color:rgba(184,144,255,0.8)';
      const tierSel = document.createElement('select');
      tierSel.style.cssText = 'background:#0a0a0a;color:#eee;border:1px solid #333;border-radius:4px;padding:4px 6px;font-size:0.78rem;font-family:monospace';
      const opts: Array<['', 'anon', 'nip05', 'close', 'verified']> = [['', 'anon', 'nip05', 'close', 'verified']];
      for (const v of opts[0]) {
        const o = document.createElement('option');
        o.value = v;
        o.text = v === '' ? '— none —' : v;
        if ((p.tier_override ?? '') === v) o.selected = true;
        tierSel.appendChild(o);
      }
      tierWrap.appendChild(tierSel);
      const tierApply = el('button', { className: 'menu-btn secondary', parent: tierWrap, text: 'APPLY' }) as HTMLButtonElement;
      tierApply.style.cssText = 'padding:4px 10px;font-size:0.72rem;cursor:pointer';
      tierApply.addEventListener('click', () => {
        void (async () => {
          const v = tierSel.value as '' | 'anon' | 'nip05' | 'close' | 'verified';
          const tier = v === '' ? null : v;
          const reason = window.prompt('Reason for tier override?') ?? undefined;
          setStatus('Updating tier override…');
          const r = await setAdminPlayerTier(session, p.pubkey, tier, reason);
          if (r.ok) {
            setStatus(`Tier override = ${tier ?? 'cleared'}`, '#58ff58');
            await loadPlayer(p.pubkey);
          } else {
            setStatus(`Tier update failed: ${r.error}`, '#ff8050');
          }
        })();
      });
    };

    const loadPlayer = async (pubkey: string): Promise<void> => {
      setStatus('Loading player…');
      const r = await fetchAdminPlayer(session, pubkey);
      if (r.ok) {
        renderPlayerCard(r.player);
        setStatus('');
      } else {
        playerSlot.replaceChildren();
        if (r.error === 'not_found') {
          setStatus('Player not found.', '#ff8050');
        } else {
          setStatus(`Lookup failed: ${r.error}`, '#ff8050');
        }
      }
    };

    lookupBtn.addEventListener('click', () => {
      const raw = npubInput.value.trim();
      if (!raw) return;
      // Accept npub1… or 64-char hex.
      let pubkey: string | null = null;
      if (/^[0-9a-f]{64}$/i.test(raw)) {
        pubkey = raw.toLowerCase();
      } else if (raw.startsWith('npub1')) {
        pubkey = decodeNpub(raw);
      }
      if (!pubkey) {
        setStatus('Invalid pubkey — expecting npub1… or 64-char hex.', '#ff5050');
        return;
      }
      void loadPlayer(pubkey);
    });
    npubInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') lookupBtn.click();
    });

    // ── Footer ───────────────────────────────────────────────────
    const footer = el('div', { parent: stateSlot });
    footer.style.cssText = 'display:flex;gap:10px;margin-top:6px;';
    const refreshBtn = el('button', { className: 'menu-btn secondary', parent: footer, text: '↻ REFRESH' }) as HTMLButtonElement;
    refreshBtn.style.cssText = 'padding:8px 14px;font-size:0.78rem;cursor:pointer;letter-spacing:0.12em';
    refreshBtn.addEventListener('click', () => { void refresh(); });
    const backBtn = el('button', { className: 'menu-btn secondary', parent: footer, text: '← BACK TO TITLE' }) as HTMLButtonElement;
    backBtn.style.cssText = 'padding:8px 14px;font-size:0.78rem;cursor:pointer;letter-spacing:0.12em';
    backBtn.addEventListener('click', () => { window.location.assign('/'); });
  };

  void refresh();
  // Suppress unused warning if cached is touched elsewhere later.
  void cached;
}

// ── Duel lobby (M4) ─────────────────────────────────────────────────────────
// HOST + JOIN matchmaking on the `/duel` route. Self-contained: the lobby
// never opens a peer connection of its own, it just assembles the duel URL
// that the apex domain consumes. The game's peer-joined handler then
// auto-IGNITEs both clients off the same session-derived seed so the
// shared arena lands frame-for-frame.

/** Pick the broker base URL. In production points at the dedicated
 *  controller-ws subdomain; on localhost / 127.0.0.1 it falls back to
 *  the local dev broker at `ws://<host>:8788/` so the lobby's invite
 *  links + peerwatch socket work end-to-end without touching production. */
function defaultBrokerWsUrl(): string {
  // Desktop builds are served from 127.0.0.1 by an embedded static server, which
  // would otherwise auto-target a LOCAL broker on :8788 — correct for a booth
  // (bundled broker / linked booths), wrong for a public download whose players
  // have no peers there. The public build injects __PALLASITE_BROKER_URL__ (the
  // production broker) so multiplayer reaches real opponents. Booth omits it.
  const override = (globalThis as { __PALLASITE_BROKER_URL__?: unknown }).__PALLASITE_BROKER_URL__;
  if (typeof override === 'string' && override) return override;
  const h = window.location.hostname;
  if (h === 'localhost' || h === '127.0.0.1' || h === '0.0.0.0') {
    return `ws://${h}:8788/`;
  }
  return CONTROLLER_WS_ENDPOINT_DEFAULT;
}

/** Build the broker URL for a given session. The broker's `peer` role
 *  takes `?s=<session>&r=peer` and waits for hello-peer to bind a slot. */
function buildBrokerPeerUrl(session: string): string {
  const base = defaultBrokerWsUrl().replace(/\/$/, '');
  return `${base}/?s=${encodeURIComponent(session)}&r=peer`;
}

function appendDeathmatchUrlParams(params: URLSearchParams, players: number, rules?: Partial<DeathmatchRules>): void {
  params.set('mode', 'deathmatch');
  params.set('deathmatchPlayers', String(players));
  params.set('peerBatch', '1');
  params.set('aiFill', '1');
  const resolved = makeDeathmatchRules(players, rules);
  params.set('deathmatchTime', String(Math.round(resolved.timeLimitMs / 1000)));
  params.set('deathmatchKills', String(resolved.killLimit));
  params.set('deathmatchRespawns', String(resolved.respawns));
  params.set('deathmatchAiSkill', String(resolved.aiSkill));
}

type LobbyMatchKind = 'duel' | 'coop-campaign' | 'deathmatch';

function appendLobbyModeUrlParams(params: URLSearchParams, kind: LobbyMatchKind, players: number, rules?: Partial<DeathmatchRules>): void {
  if (kind === 'coop-campaign') {
    params.set('mode', 'coop-campaign');
    params.set('players', '2');
    params.set('peerBatch', '1');
    return;
  }
  if (kind === 'deathmatch') appendDeathmatchUrlParams(params, players, rules);
}

/** Build the page URL the partner opens to join an existing session. Slot
 *  is the partner's slot (the OTHER slot from the inviter's). */
function buildDuelInviteUrl(session: string, partnerSlot: number, players = 2, rules?: Partial<DeathmatchRules>, kind: LobbyMatchKind = players > 2 ? 'deathmatch' : 'duel', humanSlots?: readonly number[]): string {
  const peer = buildBrokerPeerUrl(session);
  const origin = window.location.origin;
  const params = new URLSearchParams({
    peer,
    session,
    slot: String(partnerSlot),
    players: String(players),
  });
  appendLobbyModeUrlParams(params, kind, players, rules);
  if (kind === 'deathmatch' && humanSlots && humanSlots.length > 0) {
    params.set('humanSlots', Array.from(humanSlots).join(','));
  }
  return `${origin}/?${params.toString()}`;
}

/** Build the spectator page URL. Anyone opening this connects via the
 *  broker peerwatch role and watches the duel read-only. No slot — the
 *  spectator sees BOTH ships. The encoded `peer` param carries the FULL
 *  peerwatch URL (broker base + `?s=…&r=peerwatch`) so the spectator's
 *  SpectatorPeer.connect can open it directly; it must NOT reuse the
 *  duel peer URL (that one has `r=peer`, which the broker treats as a
 *  half-handshake peer and the spectator never resolves). */
function buildSpectateUrl(session: string, players = 2, rules?: Partial<DeathmatchRules>, kind: LobbyMatchKind = players > 2 ? 'deathmatch' : 'duel'): string {
  const base = defaultBrokerWsUrl().replace(/\/$/, '');
  const peerWatchUrl = `${base}/?s=${encodeURIComponent(session)}&r=peerwatch&binaryFrames=1`;
  const origin = window.location.origin;
  const params = new URLSearchParams({
    spectate: session,
    peer: peerWatchUrl,
    players: String(players),
  });
  appendLobbyModeUrlParams(params, kind, players, rules);
  return `${origin}/?${params.toString()}`;
}

/** 8-char crockford-base32-ish session id. Short enough to type, long
 *  enough that a casual collision in the broker's open-session set is
 *  vanishingly unlikely. */
function generateSessionId(): string {
  const alphabet = 'abcdefghjkmnpqrstuvwxyz23456789';
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  let out = '';
  for (let i = 0; i < bytes.length; i++) out += alphabet[bytes[i] % alphabet.length];
  return out;
}

export function renderDuelLobby(): void {
  clearOverlay();
  const overlay = el('div', { className: 'overlay', parent: root });
  setupOverlayArrowNav(overlay);
  const lobbyParams = new URLSearchParams(window.location.search);
  const coopLobby = lobbyParams.get('coop') === '1';
  const deathmatchLobby = !coopLobby && lobbyParams.get('deathmatch') === '1';

  // Wordmark logo — same image the title uses, mix-blend-mode:screen so the
  // baked-in starfield drops out over the page background. Reusing the
  // title's identity here so the lobby feels like part of the game rather
  // than a separate plain configuration page.
  const lobbyLogo = el('img', { parent: overlay });
  lobbyLogo.className = 'title-logo';
  (lobbyLogo as HTMLImageElement).src = '/logo.webp';
  (lobbyLogo as HTMLImageElement).alt = 'PALLASITE';
  (lobbyLogo as HTMLImageElement).decoding = 'async';
  lobbyLogo.style.cssText = 'max-width:min(360px, 70vw);width:auto;height:auto;';

  const subtitle = el('p', { parent: overlay, text: coopLobby ? 'CO-OP CAMPAIGN' : deathmatchLobby ? 'DEATHMATCH' : 'DUEL / DEATHMATCH' });
  subtitle.style.cssText = 'font-size:1.2rem;color:var(--hud-yellow);letter-spacing:0.3em;text-shadow:0 0 8px rgba(255,216,74,0.5);margin:-8px 0 4px;';

  const tag = el('p', { parent: overlay, text: coopLobby ? '2 PILOTS · 25 WAVES · SHARED SCORE' : deathmatchLobby ? '2 OR 4 PILOTS · FREE-FOR-ALL' : '2 OR 4 PILOTS · ONE ARENA' });
  tag.style.cssText = 'font-size:0.85rem;color:rgba(180,140,255,0.85);letter-spacing:0.22em;margin:0 0 14px;font-family:ui-monospace,monospace;';

  const rules = el('p', { parent: overlay });
  rules.style.cssText = 'margin:0 auto 18px;font-size:0.85rem;color:rgba(220,210,255,0.78);max-width:560px;line-height:1.55;text-align:center;';
  rules.innerHTML = coopLobby
    ? 'Campaign waves together over deterministic lockstep. Shared co-op leaderboard, no sats payouts.'
    : deathmatchLobby
    ? 'Shared arena, deterministic seed, frame-perfect lockstep. Host 2 or 4 pilots; empty slots become AI until humans join.'
    : 'Shared arena, deterministic seed, frame-perfect lockstep. ' +
      'Deathmatch rounds use time, kill-cap, and elimination rules. No sats, no stakes — bragging rights only.';

  const tabRow = el('div', { className: 'menu-row', parent: overlay });
  const hostTab = el('button', { className: 'menu-btn', parent: tabRow, text: 'HOST' });
  const joinTab = el('button', { className: 'menu-btn secondary', parent: tabRow, text: 'JOIN' });

  const panel = el('div', { parent: overlay });
  panel.style.cssText = 'display:flex;flex-direction:column;align-items:center;gap:14px;margin:14px auto 4px;max-width:560px;width:100%;';

  // Each panel render may attach long-lived resources (the host panel
  // opens a peerwatch WebSocket so it can show the partner's join state
  // live). setTab calls the previous render's teardown before clearing
  // the DOM so we don't leak sockets when the user toggles HOST ↔ JOIN.
  let teardown: (() => void) | null = null;
  const setTab = (next: 'host' | 'join'): void => {
    hostTab.className = `menu-btn${next === 'host' ? '' : ' secondary'}`;
    joinTab.className = `menu-btn${next === 'join' ? '' : ' secondary'}`;
    try { teardown?.(); } catch { /* ignore */ }
    teardown = null;
    panel.innerHTML = '';
    teardown = next === 'host' ? renderHostPanel(panel, coopLobby, deathmatchLobby) : renderJoinPanel(panel, coopLobby, deathmatchLobby);
  };
  hostTab.addEventListener('click', () => setTab('host'));
  joinTab.addEventListener('click', () => setTab('join'));

  const footer = el('div', { className: 'menu-row', parent: overlay });
  const backBtn = el('button', { className: 'menu-btn secondary', parent: footer, text: '◀ BACK TO TITLE' });
  backBtn.addEventListener('click', () => {
    try { teardown?.(); } catch { /* ignore */ }
    window.location.assign('/');
  });

  setTab('host');
}

function renderHostPanel(parent: HTMLElement, coopLobby = false, deathmatchLobby = false): (() => void) {
  const session = generateSessionId();
  let playerCount = deathmatchLobby ? 4 : 2;
  let deathmatchRules = makeDeathmatchRules(playerCount);
  const matchKind = (): LobbyMatchKind => coopLobby ? 'coop-campaign' : deathmatchLobby || playerCount > 2 ? 'deathmatch' : 'duel';
  const joinedSlots = new Set<number>();
  const humanSlotsForStart = (): number[] => {
    const slots = new Set<number>([0]);
    for (const slot of joinedSlots) {
      if (slot > 0 && slot < playerCount) slots.add(slot);
    }
    return Array.from(slots).sort((a, b) => a - b);
  };
  let qrInviteSlot = 1;
  const inviteUrlForSlot = (slot: number, humanSlots?: readonly number[]): string =>
    buildDuelInviteUrl(session, slot, playerCount, deathmatchRules, matchKind(), humanSlots);
  const partnerUrl = (): string => inviteUrlForSlot(1);
  const inviterUrl = (): string => buildDuelInviteUrl(
    session,
    0,
    playerCount,
    deathmatchRules,
    matchKind(),
    matchKind() === 'deathmatch' ? humanSlotsForStart() : undefined,
  );
  const startUrl = (): string => {
    return inviterUrl();
  };
  const spectateUrl = (): string => buildSpectateUrl(session, playerCount, deathmatchRules, matchKind());
  const inviteBundle = (): string => {
    if (playerCount <= 2) return partnerUrl();
    const lines: string[] = [];
    for (let slot = 1; slot < playerCount; slot++) {
      lines.push(`P${slot + 1}: ${inviteUrlForSlot(slot)}`);
    }
    return lines.join('\n');
  };

  // ── Partner-status badge ──────────────────────────────────────────────
  // Live indicator that flips the moment slot 1 binds at the broker. The
  // panel opens a peerwatch socket on the same session id; the broker
  // forwards a peer-joined message when slot 1 connects (and peer-left
  // when they drop). Without this, the host clicks READY blind, with no
  // way to know if their partner has even scanned the QR yet.
  const status = el('div', { parent });
  status.style.cssText = 'display:flex;align-items:center;gap:10px;padding:10px 18px;border-radius:999px;border:1.5px solid rgba(255,216,74,0.45);background:rgba(255,216,74,0.08);';
  const statusDot = el('span', { parent: status });
  statusDot.style.cssText = 'width:10px;height:10px;border-radius:50%;background:#ffd84a;box-shadow:0 0 12px rgba(255,216,74,0.85);animation:pallasiteWaitPulse 1.4s ease-in-out infinite;';
  const statusText = el('span', { parent: status, text: coopLobby ? 'WAITING FOR CO-PILOT...' : deathmatchLobby ? 'WAITING FOR PILOTS...' : 'WAITING FOR OPPONENT...' });
  statusText.style.cssText = "font-family:'VT323',ui-monospace,monospace;font-size:0.95rem;letter-spacing:0.18em;color:#ffd84a;";
  ensurePulseKeyframes();

  const setPartnerStatus = (connected: boolean): void => {
    if (connected) {
      statusDot.style.background = '#8cffb4';
      statusDot.style.boxShadow = '0 0 14px rgba(140,255,180,0.9)';
      statusDot.style.animation = 'none';
      statusText.textContent = coopLobby ? 'CO-PILOT CONNECTED ✓' : deathmatchLobby ? 'PILOT CONNECTED ✓' : 'OPPONENT CONNECTED ✓';
      statusText.style.color = '#8cffb4';
      status.style.borderColor = 'rgba(140,255,180,0.55)';
      status.style.background = 'rgba(140,255,180,0.1)';
    } else {
      statusDot.style.background = '#ffd84a';
      statusDot.style.boxShadow = '0 0 12px rgba(255,216,74,0.85)';
      statusDot.style.animation = 'pallasiteWaitPulse 1.4s ease-in-out infinite';
      statusText.textContent = coopLobby ? 'WAITING FOR CO-PILOT...' : deathmatchLobby ? 'WAITING FOR PILOTS...' : 'WAITING FOR OPPONENT...';
      statusText.style.color = '#ffd84a';
      status.style.borderColor = 'rgba(255,216,74,0.45)';
      status.style.background = 'rgba(255,216,74,0.08)';
    }
  };

  const renderSessionStatus = (): void => {
    if (playerCount === 2) {
      setPartnerStatus(joinedSlots.has(1));
      return;
    }
    const connected = Array.from(joinedSlots).filter(slot => slot > 0 && slot < playerCount).length;
    const missing = Math.max(0, playerCount - 1 - connected);
    const allConnected = missing === 0;
    statusDot.style.background = allConnected ? '#8cffb4' : '#ffd84a';
    statusDot.style.boxShadow = allConnected ? '0 0 14px rgba(140,255,180,0.9)' : '0 0 12px rgba(255,216,74,0.85)';
    statusDot.style.animation = allConnected ? 'none' : 'pallasiteWaitPulse 1.4s ease-in-out infinite';
    statusText.textContent = allConnected
      ? 'ALL PILOTS CONNECTED ✓'
      : `WAITING FOR ${missing} PILOT${missing === 1 ? '' : 'S'}…`;
    statusText.style.color = allConnected ? '#8cffb4' : '#ffd84a';
    status.style.borderColor = allConnected ? 'rgba(140,255,180,0.55)' : 'rgba(255,216,74,0.45)';
    status.style.background = allConnected ? 'rgba(140,255,180,0.1)' : 'rgba(255,216,74,0.08)';
  };

  // Open the peerwatch socket. Best-effort: if the broker is down we just
  // leave the badge in WAITING and let READY work as before. The watch is
  // a purely informational channel — nothing about the duel flow depends
  // on it succeeding.
  const watchUrl = `${defaultBrokerWsUrl().replace(/\/$/, '')}/?s=${encodeURIComponent(session)}&r=peerwatch&binaryFrames=1`;
  let watchSocket: WebSocket | null = null;
  try {
    watchSocket = new WebSocket(watchUrl);
    watchSocket.addEventListener('message', (ev: MessageEvent) => {
      try {
        const msg = JSON.parse(typeof ev.data === 'string' ? ev.data : '') as { type: string; slot?: number };
        // Only slot 1 binding/leaving matters — slot 0 is the host, which
        // doesn't bind until READY anyway.
        if (msg.type === 'peer-joined' && typeof msg.slot === 'number') {
          joinedSlots.add(msg.slot);
          renderSessionStatus();
        } else if (msg.type === 'peer-left' && typeof msg.slot === 'number') {
          joinedSlots.delete(msg.slot);
          renderSessionStatus();
        }
      } catch { /* malformed — ignore */ }
    });
    // Silent failures are OK; the badge just stays in WAITING.
    watchSocket.addEventListener('error', () => { /* leave badge as-is */ });
  } catch { /* socket construction itself failed */ }

  // ── INVITE block ──────────────────────────────────────────────────────
  // Card-shaped section so the invite reads as one unit (QR + session +
  // copy) rather than three loose rows stacked on top of each other.
  const inviteCard = lobbyCard(parent, coopLobby ? 'INVITE CO-PILOT' : 'INVITE PILOTS');

  const sizeRow = el('div', { className: 'menu-row', parent: inviteCard });
  sizeRow.style.cssText = 'gap:8px;flex-wrap:wrap;margin:-2px 0 2px;';
  if (coopLobby) sizeRow.style.display = 'none';
  const sizeButtons = new Map<number, HTMLButtonElement>();
  const timeButtons = new Map<number, HTMLButtonElement>();
  const killButtons = new Map<number, HTMLButtonElement>();
  const respawnButtons = new Map<number, HTMLButtonElement>();
  const slotButtons = new Map<number, HTMLButtonElement>();
  const renderInviteQr = (): void => {
    const maxSlot = Math.max(1, playerCount - 1);
    qrInviteSlot = Math.max(1, Math.min(qrInviteSlot, maxSlot));
    qrCanvas.dataset.slot = String(qrInviteSlot);
    qrCanvas.title = playerCount > 2 ? `P${qrInviteSlot + 1} invite QR` : 'P2 invite QR';
    qrLabel.textContent = playerCount > 2 ? `QR · P${qrInviteSlot + 1}` : 'QR · P2';
    for (const [slot, btn] of slotButtons) btn.className = `menu-btn${slot === qrInviteSlot ? '' : ' secondary'}`;
    void QRCode.toCanvas(qrCanvas, inviteUrlForSlot(qrInviteSlot), { width: 220, margin: 1 })
      .catch((err: unknown) => { console.warn('[duel] QR render failed:', err); });
  };
  const updateInviteViews = (): void => {
    const isDeathmatch = matchKind() === 'deathmatch';
    qrInviteSlot = Math.max(1, Math.min(qrInviteSlot, Math.max(1, playerCount - 1)));
    for (const [count, btn] of sizeButtons) btn.className = `menu-btn${count === playerCount ? '' : ' secondary'}`;
    for (const [seconds, btn] of timeButtons) btn.className = `menu-btn${deathmatchRules.timeLimitMs === seconds * 1000 ? '' : ' secondary'}`;
    for (const [kills, btn] of killButtons) btn.className = `menu-btn${deathmatchRules.killLimit === kills ? '' : ' secondary'}`;
    for (const [respawns, btn] of respawnButtons) btn.className = `menu-btn${deathmatchRules.respawns === respawns ? '' : ' secondary'}`;
    copyBtn.textContent = playerCount === 2 ? 'COPY INVITE LINK' : 'COPY INVITE LINKS';
    watchBtn.textContent = 'COPY WATCH LINK';
    matchRules.style.display = isDeathmatch ? 'flex' : 'none';
    rulesSummary.textContent = isDeathmatch
      ? `${Math.round(deathmatchRules.timeLimitMs / 60_000)} min · ${deathmatchRules.killLimit} kills · ${deathmatchRules.respawns} respawns · FFA`
      : coopLobby ? 'Co-op campaign · shared score · no sats payouts' : 'Classic two-player duel';
    watchInfo.textContent = playerCount === 2
      ? coopLobby ? 'Anyone with this link can watch the co-op campaign live, no account needed.' : isDeathmatch ? 'Anyone with this link can watch both pilots live, no account needed.' : 'Anyone with this link can watch the duel live, no account needed.'
      : `Anyone with this link can watch all ${playerCount} pilots live, no account needed.`;
    slotLinks.replaceChildren();
    slotButtons.clear();
    if (playerCount > 2) {
      for (let slot = 1; slot < playerCount; slot++) {
        const slotBtn = el('button', { className: `menu-btn${slot === qrInviteSlot ? '' : ' secondary'}`, parent: slotLinks, text: `P${slot + 1}` }) as HTMLButtonElement;
        slotBtn.style.cssText = 'padding:5px 9px;font-size:0.72rem;letter-spacing:0.08em;';
        slotBtn.title = `Show invite QR for player ${slot + 1}`;
        slotButtons.set(slot, slotBtn);
        slotBtn.addEventListener('click', () => {
          qrInviteSlot = slot;
          renderInviteQr();
        });
      }
    }
    renderInviteQr();
    renderSessionStatus();
  };
  for (const count of [2, 4]) {
    const btn = el('button', { className: `menu-btn${count === playerCount ? '' : ' secondary'}`, parent: sizeRow, text: `${count}P` });
    sizeButtons.set(count, btn);
    btn.addEventListener('click', () => {
      playerCount = count;
      deathmatchRules = makeDeathmatchRules(playerCount, { timeLimitMs: deathmatchRules.timeLimitMs, aiSkill: deathmatchRules.aiSkill });
      for (const slot of Array.from(joinedSlots)) if (slot >= playerCount) joinedSlots.delete(slot);
      refreshRuleChoices();
      updateInviteViews();
    });
  }

  const matchRules = el('div', { parent: inviteCard });
  matchRules.style.cssText = 'display:none;flex-direction:column;align-items:center;gap:8px;width:100%;max-width:420px;padding:10px 0 2px;border-top:1px solid rgba(184,144,255,0.18);border-bottom:1px solid rgba(184,144,255,0.18);';
  el('p', { parent: matchRules, text: 'MATCH RULES' }).style.cssText = 'margin:0;font-size:0.68rem;letter-spacing:0.22em;color:rgba(180,140,255,0.82);';
  const rulesSummary = el('p', { parent: matchRules, text: 'Classic two-player duel' });
  rulesSummary.style.cssText = 'margin:-2px 0 0;font:0.76rem ui-monospace,monospace;color:rgba(220,230,255,0.72);letter-spacing:0.08em;text-align:center;';
  const makeRuleRow = (label: string): HTMLDivElement => {
    const row = el('div', { parent: matchRules }) as HTMLDivElement;
    row.style.cssText = 'display:flex;flex-wrap:wrap;align-items:center;justify-content:center;gap:6px;width:100%;';
    el('div', { parent: row, text: label }).style.cssText = 'min-width:74px;text-align:right;font:0.68rem ui-monospace,monospace;letter-spacing:0.12em;color:rgba(180,140,255,0.72);';
    return row;
  };
  const timeRow = makeRuleRow('TIME');
  for (const seconds of [120, DEATHMATCH_DEFAULT_TIME_LIMIT_MS / 1000, 300]) {
    const btn = el('button', { className: 'menu-btn secondary', parent: timeRow, text: `${Math.round(seconds / 60)}M` });
    btn.style.cssText = 'padding:5px 9px;font-size:0.72rem;letter-spacing:0.08em;';
    timeButtons.set(seconds, btn);
    btn.addEventListener('click', () => {
      deathmatchRules = makeDeathmatchRules(playerCount, { ...deathmatchRules, timeLimitMs: seconds * 1000 });
      updateInviteViews();
    });
  }
  const killRow = makeRuleRow('KILLS');
  const renderKillChoices = (): void => {
    killRow.querySelectorAll('button').forEach((button) => button.remove());
    killButtons.clear();
    const base = defaultDeathmatchKillLimit(playerCount);
    const choices = Array.from(new Set([base, Math.max(base + 4, Math.round(base * 1.5)), Math.max(base + 8, Math.round(base * 2))]));
    for (const kills of choices) {
      const btn = el('button', { className: 'menu-btn secondary', parent: killRow, text: String(kills) });
      btn.style.cssText = 'padding:5px 9px;font-size:0.72rem;letter-spacing:0.08em;';
      killButtons.set(kills, btn);
      btn.addEventListener('click', () => {
        deathmatchRules = makeDeathmatchRules(playerCount, { ...deathmatchRules, killLimit: kills });
        updateInviteViews();
      });
    }
  };
  const respawnRow = makeRuleRow('RESPAWNS');
  const renderRespawnChoices = (): void => {
    respawnRow.querySelectorAll('button').forEach((button) => button.remove());
    respawnButtons.clear();
    const base = defaultDeathmatchRespawns(playerCount);
    const choices = Array.from(new Set([base, base + 2, base + 4]));
    for (const respawns of choices) {
      const btn = el('button', { className: 'menu-btn secondary', parent: respawnRow, text: String(respawns) });
      btn.style.cssText = 'padding:5px 9px;font-size:0.72rem;letter-spacing:0.08em;';
      respawnButtons.set(respawns, btn);
      btn.addEventListener('click', () => {
        deathmatchRules = makeDeathmatchRules(playerCount, { ...deathmatchRules, respawns });
        updateInviteViews();
      });
    }
  };
  const refreshRuleChoices = (): void => {
    renderKillChoices();
    renderRespawnChoices();
  };
  refreshRuleChoices();

  const info = el('p', { parent: inviteCard });
  info.style.cssText = 'margin:0;font-size:0.85rem;color:rgba(220,210,255,0.78);line-height:1.45;text-align:center;max-width:420px;';
  info.textContent = coopLobby
    ? 'Send the link or have your co-pilot scan the code.'
    : deathmatchLobby
    ? 'Send the slot links or have pilots scan the code.'
    : 'Send the link or have them scan the code.';

  const qrCanvas = el('canvas', { parent: inviteCard });
  qrCanvas.style.cssText = 'background:#fff;padding:10px;border-radius:8px;max-width:220px;width:100%;height:auto;';
  const qrLabel = el('p', { parent: inviteCard, text: 'QR · P2' });
  qrLabel.style.cssText = 'margin:-6px 0 0;font:0.72rem ui-monospace,monospace;letter-spacing:0.18em;color:rgba(255,216,74,0.86);';

  const sessionRow = el('div', { parent: inviteCard });
  sessionRow.style.cssText = 'display:flex;align-items:center;gap:8px;font-family:ui-monospace,monospace;font-size:0.85rem;color:rgba(220,210,255,0.85);';
  el('span', { parent: sessionRow, text: 'SESSION' }).style.cssText = 'color:rgba(180,140,255,0.7);letter-spacing:0.18em;font-size:0.72rem;';
  el('span', { parent: sessionRow, text: session.toUpperCase() }).style.cssText = 'letter-spacing:0.08em;color:#ffd84a;';

  const copyBtn = el('button', { className: 'menu-btn secondary', parent: inviteCard, text: 'COPY INVITE LINK' });
  copyBtn.addEventListener('click', () => {
    const original = playerCount === 2 ? 'COPY INVITE LINK' : 'COPY INVITE LINKS';
    void navigator.clipboard.writeText(inviteBundle())
      .then(() => { copyBtn.textContent = 'COPIED ✓'; setTimeout(() => { copyBtn.textContent = original; }, 1500); })
      .catch(() => { copyBtn.textContent = 'COPY FAILED'; setTimeout(() => { copyBtn.textContent = original; }, 1500); });
  });

  const slotLinks = el('div', { parent: inviteCard });
  slotLinks.style.cssText = 'display:flex;flex-wrap:wrap;justify-content:center;gap:6px;max-width:440px;';

  // ── SPECTATE block ────────────────────────────────────────────────────
  // Coequal section, not a buried footer. Anyone with the spectate link
  // can watch the duel live via a read-only peerwatch socket — this is
  // a genuinely novel feature and deserves visible billing in the lobby
  // instead of getting tucked under the READY button.
  const watchCard = lobbyCard(parent, coopLobby ? '👁  SHARE THE RUN' : deathmatchLobby ? '👁  SHARE THE MATCH' : '👁  SHARE THE FIGHT');

  const watchInfo = el('p', { parent: watchCard });
  watchInfo.style.cssText = 'margin:0;font-size:0.85rem;color:rgba(220,210,255,0.78);line-height:1.45;text-align:center;max-width:420px;';
  watchInfo.textContent = coopLobby
    ? 'Anyone with this link can watch the co-op campaign live, no account needed.'
    : deathmatchLobby
    ? `Anyone with this link can watch all ${playerCount} pilots live, no account needed.`
    : 'Anyone with this link can watch the duel live, no account needed.';

  const watchRow = el('div', { className: 'menu-row', parent: watchCard });
  watchRow.style.cssText = 'gap:8px;flex-wrap:wrap;margin:0;';
  const watchBtn = el('button', { className: 'menu-btn secondary', parent: watchRow, text: 'COPY WATCH LINK' });
  watchBtn.addEventListener('click', () => {
    void navigator.clipboard.writeText(spectateUrl())
      .then(() => { watchBtn.textContent = 'COPIED ✓'; setTimeout(() => { watchBtn.textContent = 'COPY WATCH LINK'; }, 1500); })
      .catch(() => { watchBtn.textContent = 'COPY FAILED'; setTimeout(() => { watchBtn.textContent = 'COPY WATCH LINK'; }, 1500); });
  });
  const openWatchBtn = el('button', { className: 'menu-btn secondary', parent: watchRow, text: 'OPEN WATCH' });
  openWatchBtn.addEventListener('click', () => {
    window.open(spectateUrl(), '_blank', 'noopener,noreferrer');
  });

  // ── READY ─────────────────────────────────────────────────────────────
  // Promoted out of the copy row to its own full-width block so it reads
  // as THE primary action. Tapping READY here drops the host into the
  // arena as slot 0; the lockstep buffers their input until slot 1
  // connects, so it's safe to press immediately even before the partner
  // has scanned the QR.
  const readyRow = el('div', { className: 'menu-row', parent });
  readyRow.style.cssText = 'margin-top:6px;';
  const readyBtn = el('button', { className: 'menu-btn', parent: readyRow, text: coopLobby ? 'READY · START CO-OP ▶' : deathmatchLobby ? 'READY · START DEATHMATCH ▶' : 'READY · ENTER THE ARENA ⚔' });
  readyBtn.style.cssText = 'font-size:1.05rem;padding:12px 28px;letter-spacing:0.2em;';
  readyBtn.addEventListener('click', () => { window.location.assign(startUrl()); });

  // ── While-you-wait ────────────────────────────────────────────────────
  // The host might sit here for tens of seconds waiting for the partner
  // to scan and join. Give them something to read + a hook back to the
  // single-player draw (today's daily leader) so the page doesn't feel
  // dead in the meantime.
  const tipStripTeardown = lobbyTipStrip(parent, coopLobby, deathmatchLobby);
  renderDailyLeaderChip(parent);
  updateInviteViews();

  // Teardown — close the peerwatch socket when the panel is swapped out
  // (tab toggle or back to title). Without this the socket lingers until
  // GC + ws timeout, leaving an orphan watcher on the broker for minutes.
  // Also stops the tip rotation timer.
  return () => {
    if (watchSocket) {
      try { watchSocket.close(); } catch { /* already closed */ }
      watchSocket = null;
    }
    try { tipStripTeardown(); } catch { /* ignore */ }
  };
}

/** A purple-tinted strip below the READY button that cycles through a
 *  short tour of duel-specific tips. Tries to fill the "ok now what"
 *  dead time between READY-being-pressable and the partner actually
 *  joining, without adding heavy chrome.
 *
 *  Returns a teardown that stops the rotation timer; the panel switch
 *  in renderDuelLobby calls it so we don't leak intervals across tabs. */
function lobbyTipStrip(parent: HTMLElement, coopLobby = false, deathmatchLobby = false): (() => void) {
  const TIPS: readonly string[] = coopLobby
    ? [
        'You enter as slot 0. Lockstep buffers your input until your co-pilot joins, so READY is safe to press now.',
        'Co-op campaign uses the normal 25-wave route with a shared final score.',
        'No sats pay out in co-op. Scores publish to the separate co-op board.',
        'Share the spectate link so anyone can watch the whole run live.',
        'The run ends when both ships are out of lives.',
        'Hyperspace doubles as a panic button. Shield burns a cooldown.',
        'Both clients see the EXACT same campaign: same seed, same rocks, same waves.',
      ]
    : deathmatchLobby
    ? [
        'You enter as slot 0. READY fills empty slots with AI, and late pilots take over their slot when they arrive.',
        'Use the 2P and 4P presets for real human deathmatch sessions.',
        'Every pilot gets a separate invite link; copy the bundle or send one slot link at a time.',
        'Deathmatch is free-for-all: every other pilot is a valid target.',
        'Rounds end on the time cap, kill cap, or last pilot standing.',
        'Share the spectate link so anyone can watch every pilot live.',
      ]
    : [
        'You enter as slot 0. Lockstep buffers your input until the remote slots join, so READY is safe to press now.',
        'Deathmatch is free-for-all: every other pilot is a valid target.',
        'No sats, no stakes. Bragging rights only.',
        'Share the spectate link so anyone can watch every pilot live.',
        'Rounds end on the time cap, kill cap, or last pilot standing.',
        'Hyperspace doubles as a panic button. Shield burns a cooldown.',
        'Every client sees the EXACT same arena: same seed, same rocks, same rules.',
      ];

  const tip = el('p', { parent });
  tip.style.cssText = [
    'margin:6px 0 0', 'min-height:2.2em',
    'font-size:0.82rem', 'color:rgba(180,140,255,0.7)',
    'text-align:center', 'line-height:1.5', 'max-width:460px',
    'transition:opacity 280ms ease-in-out',
  ].join(';');
  let idx = 0;
  tip.textContent = TIPS[0];

  // Start on second tip after the first interval; fade out then in.
  const interval = window.setInterval(() => {
    if (!document.body.contains(tip)) {
      window.clearInterval(interval);
      return;
    }
    tip.style.opacity = '0';
    window.setTimeout(() => {
      idx = (idx + 1) % TIPS.length;
      tip.textContent = TIPS[idx];
      tip.style.opacity = '1';
    }, 280);
  }, 5_000);

  return (): void => { window.clearInterval(interval); };
}

/** Inject the @keyframes the partner-status badge uses for its "waiting"
 *  dot pulse. Idempotent — only attaches once per page. Lobby-only style;
 *  not worth a CSS file. */
function ensurePulseKeyframes(): void {
  if (document.getElementById('pallasite-lobby-keyframes')) return;
  const style = document.createElement('style');
  style.id = 'pallasite-lobby-keyframes';
  style.textContent = '@keyframes pallasiteWaitPulse { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.45; transform: scale(0.85); } }';
  document.head.appendChild(style);
}

/** Card-style container used by the lobby's host panel to group related
 *  controls (INVITE block, SHARE block). Visual border + label keep the
 *  three sections distinguishable without burying any of them. */
function lobbyCard(parent: HTMLElement, label: string): HTMLDivElement {
  const card = el('div', { parent }) as HTMLDivElement;
  card.style.cssText = [
    'display:flex', 'flex-direction:column', 'align-items:center', 'gap:10px',
    'width:100%', 'max-width:480px',
    'padding:14px 16px 16px',
    'background:rgba(15,8,32,0.55)',
    'border:1px solid rgba(184,144,255,0.25)',
    'border-radius:10px',
  ].join(';');
  const cardLabel = el('p', { parent: card, text: label });
  cardLabel.style.cssText = 'margin:0 0 2px;font-size:0.72rem;letter-spacing:0.28em;color:rgba(180,140,255,0.85);';
  return card;
}

function renderJoinPanel(parent: HTMLElement, coopLobby = false, deathmatchLobby = false): (() => void) {
  const info = el('p', { parent });
  info.style.cssText = 'margin:0;font-size:0.88rem;color:rgba(220,210,255,0.8);line-height:1.5;text-align:center;max-width:480px;';
  info.innerHTML = coopLobby
    ? 'Scan the QR on your co-pilot\'s screen, or paste the invite link they sent you.'
    : deathmatchLobby
    ? 'Scan the QR on the host screen, or paste the deathmatch slot invite they sent you.'
    : 'Scan the QR on your opponent\'s screen, or paste the invite link they sent you.';

  const input = el('input', { parent }) as HTMLInputElement;
  input.type = 'url';
  input.placeholder = 'https://pallasite.app/?peer=…&session=…&slot=1';
  input.style.cssText = 'width:100%;max-width:480px;padding:10px 12px;font:0.85rem/1.4 ui-monospace,monospace;background:rgba(15,8,32,0.65);color:#dccfff;border:1px solid rgba(184,144,255,0.35);border-radius:6px;outline:none;';

  const codeWrap = el('div', { parent });
  codeWrap.style.cssText = 'display:flex;gap:8px;align-items:center;justify-content:center;flex-wrap:wrap;width:100%;max-width:480px;';
  const codeInput = el('input', { parent: codeWrap }) as HTMLInputElement;
  codeInput.type = 'text';
  codeInput.inputMode = 'text';
  codeInput.autocapitalize = 'characters';
  codeInput.autocomplete = 'off';
  codeInput.placeholder = '8-CHAR SESSION CODE';
  codeInput.maxLength = 16;
  codeInput.style.cssText = 'flex:1 1 180px;min-width:0;padding:10px 12px;font:0.88rem/1.4 ui-monospace,monospace;letter-spacing:0.14em;text-transform:uppercase;background:rgba(15,8,32,0.65);color:#ffd84a;border:1px solid rgba(255,216,74,0.35);border-radius:6px;outline:none;';
  const codeBtn = el('button', { className: 'menu-btn secondary', parent: codeWrap, text: coopLobby ? 'JOIN CO-OP CODE' : deathmatchLobby ? 'JOIN P2 CODE' : 'JOIN CODE' });

  const err = el('p', { parent });
  err.style.cssText = 'margin:0;min-height:1.2em;font-size:0.78rem;color:#ff8aa8;text-align:center;';
  const setError = (msg: string | null): void => { err.textContent = msg ?? ''; };

  /** Validate that `raw` is a Pallasite duel invite URL with the full
   *  peer/session/slot triplet. Returns the URL on success, null on
   *  failure (with the reason written to setError). */
  const validateInvite = (raw: string): string | null => {
    let parsed: URL;
    try { parsed = new URL(raw); } catch { setError('That does not look like a URL.'); return null; }
    const peer = parsed.searchParams.get('peer');
    const session = parsed.searchParams.get('session');
    const slot = parsed.searchParams.get('slot');
    const slotNum = slot === null ? NaN : Number(slot);
    const players = Number(parsed.searchParams.get('players') ?? parsed.searchParams.get('deathmatchPlayers') ?? '2');
    const maxPlayers = Number.isFinite(players) ? Math.max(2, Math.min(64, Math.floor(players))) : 2;
    if (!peer || !session || !Number.isInteger(slotNum) || slotNum < 0 || slotNum >= maxPlayers) {
      setError('URL is missing the peer / session / slot params.');
      return null;
    }
    return raw;
  };

  const validateSessionCode = (raw: string): string | null => {
    const code = raw.trim().replace(/\s+/g, '').toLowerCase();
    if (!/^[a-z0-9_-]{4,128}$/i.test(code)) {
      setError('Enter the short session code from the host screen.');
      return null;
    }
    return code;
  };

  const inviteFromCode = (code: string): string => {
    const kind: LobbyMatchKind = coopLobby ? 'coop-campaign' : deathmatchLobby ? 'deathmatch' : 'duel';
    const players = deathmatchLobby ? 4 : 2;
    const rules = deathmatchLobby ? makeDeathmatchRules(players) : undefined;
    return buildDuelInviteUrl(code, 1, players, rules, kind);
  };

  const row = el('div', { className: 'menu-row', parent });
  const joinBtn = el('button', { className: 'menu-btn', parent: row, text: 'JOIN ⚔' });
  const scanBtn = el('button', { className: 'menu-btn secondary', parent: row, text: '📷 SCAN QR' });
  const fileBtn = el('button', { className: 'menu-btn secondary', parent: row, text: '🖼 FROM PHOTO' });

  const tryJoinFromInput = (): void => {
    setError(null);
    const raw = input.value.trim();
    if (!raw) { setError('Paste the invite URL or scan a QR first.'); return; }
    const url = validateInvite(raw);
    if (url) window.location.assign(url);
  };
  const tryJoinFromCode = (): void => {
    setError(null);
    const code = validateSessionCode(codeInput.value);
    if (code) window.location.assign(inviteFromCode(code));
  };
  joinBtn.addEventListener('click', tryJoinFromInput);
  input.addEventListener('keydown', (e) => { if (e.key === 'Enter') tryJoinFromInput(); });
  codeBtn.addEventListener('click', tryJoinFromCode);
  codeInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') tryJoinFromCode(); });

  // ── QR scanner host (hidden until SCAN tapped) ─────────────────────────
  const scanHost = el('div', { parent });
  scanHost.style.cssText = 'display:none;flex-direction:column;gap:10px;align-items:stretch;width:100%;max-width:480px;';
  const video = el('video', { parent: scanHost }) as HTMLVideoElement;
  video.style.cssText = 'width:100%;max-height:48vh;border-radius:8px;background:#000;object-fit:cover;';
  video.playsInline = true;
  video.muted = true;
  const scanStatus = el('p', { parent: scanHost, text: 'Point at the QR on your opponent\'s screen' });
  scanStatus.style.cssText = 'margin:0;font-size:0.8rem;color:rgba(220,210,255,0.75);text-align:center;letter-spacing:0.08em;';
  const stopBtn = el('button', { className: 'menu-btn secondary', parent: scanHost, text: 'CANCEL SCAN' });

  // Hidden file input for FROM PHOTO. Capture attr nudges mobile browsers
  // toward the camera roll picker rather than full file-system access.
  const fileInput = el('input', { parent, attrs: { type: 'file', accept: 'image/*' } }) as HTMLInputElement;
  fileInput.setAttribute('capture', 'environment');
  fileInput.style.display = 'none';

  let stream: MediaStream | null = null;
  let scanRaf: number | null = null;
  let jsQRLib: typeof import('jsqr').default | null = null;

  const loadJsQR = async (): Promise<typeof import('jsqr').default> => {
    if (jsQRLib) return jsQRLib;
    const mod = await import('jsqr');
    jsQRLib = mod.default;
    return jsQRLib;
  };

  const stopScan = (): void => {
    if (scanRaf !== null) { cancelAnimationFrame(scanRaf); scanRaf = null; }
    if (stream) { for (const t of stream.getTracks()) t.stop(); stream = null; }
    // iOS Safari occasionally needs srcObject=null + pause() to drop the
    // MediaStream and turn off the camera indicator.
    try { video.pause(); } catch { /* ignore */ }
    try { video.srcObject = null; } catch { /* ignore */ }
    scanHost.style.display = 'none';
    row.style.display = 'flex';
    scanBtn.disabled = false;
    scanStatus.style.color = 'rgba(220,210,255,0.75)';
    scanStatus.textContent = 'Point at the QR on your opponent\'s screen';
  };
  stopBtn.addEventListener('click', stopScan);

  const onQrFound = (raw: string): boolean => {
    const url = validateInvite(raw);
    if (!url) return false;  // Wrong QR — keep scanning.
    scanStatus.textContent = 'Got it — connecting…';
    stopScan();
    window.location.assign(url);
    return true;
  };

  const startCameraScan = async (): Promise<void> => {
    scanBtn.disabled = true;
    setError(null);
    let jsQR: typeof import('jsqr').default;
    try { jsQR = await loadJsQR(); } catch {
      scanBtn.disabled = false;
      setError('QR library failed to load — use FROM PHOTO or paste the link.');
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      scanBtn.disabled = false;
      setError('This browser has no camera API — use FROM PHOTO or paste the link.');
      return;
    }
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false,
      });
    } catch (err) {
      scanBtn.disabled = false;
      setError(err instanceof Error && err.name === 'NotAllowedError'
        ? 'Camera permission denied — use FROM PHOTO or paste the link.'
        : 'Could not open the camera — use FROM PHOTO or paste the link.');
      return;
    }
    row.style.display = 'none';
    scanHost.style.display = 'flex';
    video.srcObject = stream;
    try { await video.play(); } catch { /* ignore */ }
    const SCAN_TARGET_W = 640;
    const canvas = document.createElement('canvas');
    const cctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!cctx) return;
    const scanStart = Date.now();
    let lastStatusTick = scanStart;
    const tick = (): void => {
      if (!stream) return;
      if (video.readyState >= 2 && video.videoWidth > 0) {
        const aspect = video.videoHeight / video.videoWidth;
        const w = Math.min(SCAN_TARGET_W, video.videoWidth);
        const h = Math.round(w * aspect);
        if (canvas.width !== w || canvas.height !== h) {
          canvas.width = w;
          canvas.height = h;
        }
        cctx.drawImage(video, 0, 0, w, h);
        try {
          const data = cctx.getImageData(0, 0, w, h);
          const found = jsQR(data.data, data.width, data.height, { inversionAttempts: 'attemptBoth' });
          if (found && onQrFound(found.data)) return;
        } catch { /* keep scanning */ }
      }
      const now = Date.now();
      if (now - scanStart > 8_000 && now - lastStatusTick > 1_000) {
        scanStatus.textContent = 'Still scanning… if it will not latch, CANCEL and paste the link instead.';
        scanStatus.style.color = 'rgba(255,216,74,0.85)';
        lastStatusTick = now;
      }
      scanRaf = requestAnimationFrame(tick);
    };
    tick();
  };
  scanBtn.addEventListener('click', () => { void startCameraScan(); });

  const handleFile = async (file: File): Promise<void> => {
    setError(null);
    let jsQR: typeof import('jsqr').default;
    try { jsQR = await loadJsQR(); } catch {
      setError('QR library failed to load.');
      return;
    }
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.src = url;
    try { await img.decode(); } catch {
      URL.revokeObjectURL(url);
      setError('Could not read that image.');
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const cctx = canvas.getContext('2d');
    if (!cctx) { URL.revokeObjectURL(url); return; }
    cctx.drawImage(img, 0, 0);
    URL.revokeObjectURL(url);
    let data: ImageData;
    try { data = cctx.getImageData(0, 0, canvas.width, canvas.height); }
    catch { setError('Could not read image data.'); return; }
    const found = jsQR(data.data, data.width, data.height);
    if (!found || !onQrFound(found.data)) {
      setError('No usable invite QR found in that photo — try again or paste the link.');
    }
  };
  fileBtn.addEventListener('click', () => fileInput.click());
  fileInput.addEventListener('change', () => {
    const file = fileInput.files?.[0];
    if (file) void handleFile(file);
    fileInput.value = '';
  });

  const hint = el('p', { parent });
  hint.style.cssText = 'margin:0;font-size:0.78rem;color:rgba(180,140,255,0.6);text-align:center;line-height:1.5;max-width:420px;';
  hint.textContent = 'Live camera works on iOS Safari, Android Chrome, and desktop browsers with a webcam.';

  // Teardown — stop the camera stream if it's still live. Otherwise
  // toggling tabs (or backing out to title) leaves the camera indicator
  // lit and the MediaStream pinning the device. stopScan handles both
  // the rAF loop and the track.stop() calls.
  return () => { try { stopScan(); } catch { /* ignore */ } };
}
